import elevatedRails from '../../shared/train-elevations.json' with { type: 'json' };
import { buildingAssetURL, isCustomBuildingAsset } from './BuildingAssetURL';
import { CREATIVE_WORLD } from '../world/CreativeBiome';
import { isCreativeVillage } from '../world/CreativeMode';
import { parseSiteswap } from '../world/Siteswap';
import { isPreferredLanguageCode, type PreferredLanguageCode } from '../profile/PreferredLanguages';
import { parseUserSettings, type UserSettings } from '../settings/UserSettings';
import type { AdminTruckUnderglowSettings } from '../settings/AdminTruckUnderglow';

export const BROWSER_PROTOCOL_VERSION = '2';

// New sign edits remain capped at 100 characters, but gifts and imported system text legitimately
// use the wider legacy 190-character bound. Applying the edit limit while reading would reject the
// building's entire streamed chunk.
const LEGACY_ENTITY_TEXT_READ_MAXIMUM_LENGTH = 190;

export type VillageSummary = Readonly<{
  id: number;
  name: string;
  ownerPlayerId?: number;
}>;

export type BrowserSession = Readonly<{
  token: string;
  playerId: number;
  level: number;
  spins: number;
  vip: boolean;
  moderator?: boolean;
  nordHelper?: boolean;
  administrator?: boolean;
  godMode?: boolean;
  preLounge?: boolean;
  enhancedLightingAllowed?: boolean;
  activeVillage?: VillageSummary;
  mountPurchaseSequence: number;
  mountTransitionSequence: number;
  petPurchaseSequence: number;
  themePurchaseSequence: number;
  contextorialCompletedTypes: readonly number[];
  villages: readonly VillageSummary[];
  createdAt: string;
  announcement: string;
  userSettings: UserSettings;
}>;

export type VIPWebsiteDocument = Readonly<{
  title: string;
  tagline: string;
  accent: string;
  background: string;
  cta: string;
  sections: readonly Readonly<{ heading: string; body: string }>[];
  revision: number;
  updatedAt: number;
}>;

export type VIPWebsitePayload = Readonly<{
  villageId: number;
  port: number;
  url: string;
  editable: boolean;
  aiReady: boolean;
  document: VIPWebsiteDocument;
}>;

export type VIPWebsiteAgentResult = Readonly<{
  website: VIPWebsitePayload;
  message: string;
}>;

export type SupportIdentityPayload = Readonly<{
  username: string;
  email: string;
}>;

export type LegacyRedemptionPayload = Readonly<{
  available: boolean;
  redeemed: boolean;
  legacyUsername?: string;
  legacyLevel?: number;
  coinsAwarded?: number;
  creditsAfter?: number;
  redeemedAt?: string;
}>;

export type UserSettingsPayload = UserSettings;

export type LoginPresentationTextPayload = Readonly<{
  usernameLabel: string;
  passwordLabel: string;
  controlPanelLabel: string;
  rememberMeLabel: string;
  latestNewsLabel: string;
  createAccountLabel: string;
  startLabel: string;
  downloadingUpdatesLabel: string;
  downloadFinishedLabel: string;
}>;

export type WorldToolbarPresentationTextPayload = Readonly<{
  backLabel: string;
  forwardLabel: string;
  homeLabel: string;
  villagesLabel: string;
  onlineListLabel: string;
  buildLabel: string;
  buildTooltip: string;
  giveGiftLabel: string;
  settingsLabel: string;
  chatLabel: string;
  wardrobeLabel: string;
  currentlyVisitingLabel: string;
  helpLabel: string;
  screenshotLabel: string;
  coinsLabel: string;
  fullscreenLabel: string;
  manCharacter: string;
  womanCharacter: string;
  currentNordTimeTemplate: string;
  timeConverterLabel: string;
  nordToLocalLabel: string;
  localToNordLabel: string;
  fromNordTimeTemplate: string;
  toNordTimeTemplate: string;
}>;

export type AccountCreationTextPayload = Readonly<{
  cancelLabel: string;
  createAccountLabel: string;
  boyLabel: string;
  girlLabel: string;
  skinLabel: string;
  hairLabel: string;
  topLabel: string;
  pantsLabel: string;
  usernameLabel: string;
  passwordLabel: string;
  repeatPasswordLabel: string;
  emailLabel: string;
  birthdayLabel: string;
  yearPlaceholder: string;
  monthPlaceholder: string;
  dayPlaceholder: string;
  agreementPrefix: string;
  agreementLinkLabel: string;
  illegalUsernameMessage: string;
  usernameTakenMessage: string;
  emailTakenMessage: string;
}>;

export type AccountCreationPresentationPayload = Readonly<{
  termsAsset: '/legacy/startup/EULA.txt' | '/legacy/startup/EULAENG.txt';
  termsVersion: string;
  texts: AccountCreationTextPayload;
}>;

export type ProfileNavigationTextPayload = Readonly<{
  badgesLabel: string;
  bannedPlayersLabel: string;
  friendsLabel: string;
  guestbookLabel: string;
  villagesLabel: string;
  travelTemplate: string;
  removeFriendLabel: string;
  kickLabel: string;
  buyCoinsForTemplate: string;
  yourselfLabel: string;
  setStatusLabel: string;
  levelLabel: string;
  banTemplate: string;
  unbanTemplate: string;
  changePictureLabel: string;
  confirmBanTemplate: string;
  confirmUnbanTemplate: string;
  confirmRemoveFriendTemplate: string;
  confirmKickMessage: string;
  yesLabel: string;
  noLabel: string;
}>;

export type GuestbookPresentationTextPayload = Readonly<{
  writeMessageLabel: string;
  privateLabel: string;
  sendLabel: string;
  newerLabel: string;
  olderLabel: string;
  replyLabel: string;
  historyLabel: string;
  removeLabel: string;
  makePublicLabel: string;
  makePrivateLabel: string;
  messageSentLabel: string;
  coinGiftReceivedTemplate: string;
  fromLabel: string;
  todayLabel: string;
  yesterdayLabel: string;
  tomorrowLabel: string;
}>;

export type ProfilePresentationTextPayload = ProfileNavigationTextPayload &
  Readonly<{
    guestbook: GuestbookPresentationTextPayload;
  }>;

export type ForumPresentationTextPayload = Readonly<{
  forumLabel: string;
  searchLabel: string;
  searchResultsLabel: string;
  refreshLabel: string;
  stickyLabel: string;
  lockedLabel: string;
  noMessagesLabel: string;
  lastPostLabel: string;
  postsLabel: string;
  createThreadLabel: string;
  topicLabel: string;
  textLabel: string;
  editMessageLabel: string;
  confirmRemoveMessage: string;
  backLabel: string;
  goBackLabel: string;
  sendLabel: string;
  removeLabel: string;
  replyLabel: string;
  cancelLabel: string;
  loadingLabel: string;
  onlineTemplate: string;
  offlineTemplate: string;
  lockThreadLabel: string;
  unlockThreadLabel: string;
  stickifyThreadLabel: string;
  unstickifyThreadLabel: string;
  confirmLockThread: string;
  confirmUnlockThread: string;
  confirmStickifyThread: string;
  confirmUnstickifyThread: string;
}>;

export type LoginNewsPayload = Readonly<{
  refreshAfterMs?: number;
  paragraphs: readonly string[];
  imageAsset: string;
  language: LegacyLanguage;
  backdropAsset: string;
  texts: LoginPresentationTextPayload;
  accountCreation: AccountCreationPresentationPayload;
  worldToolbar: WorldToolbarPresentationTextPayload;
  profile: ProfilePresentationTextPayload;
  forum: ForumPresentationTextPayload;
  contextorial: ContextorialPresentationTextPayload;
  inventory: InventoryPresentationTextPayload;
  levelInformation: LevelInformationPresentationPayload;
}>;

export type ContextorialPresentationTextPayload = Readonly<{
  chat: string;
  onlineList: string;
  guestbookMessage: string;
  wardrobe: string;
  buyClothes: string;
  trashcan: string;
}>;

export type InventoryPresentationTextPayload = Readonly<{
  trashcanLabel: string;
  trashcanTooltip: string;
  trashcanCandidateTemplate: string;
  confirmRemove: string;
  levelOneMessage: string;
  vipRequired: string;
  inventoryFullTitle: string;
  inventoryFullMessage: string;
  yesLabel: string;
  noLabel: string;
}>;

export type LevelInformationPresentationPayload = Readonly<{
  levels: readonly Readonly<{ level: number; text: string }>[];
}>;

export type ContextorialCompletionPayload = Readonly<{
  completedTypes: readonly number[];
}>;

export type SessionResumePayload = Readonly<{
  status: 'resumed' | 'connected';
  village: VillageSummary;
  session: BrowserSession;
}>;

export type NordTimePayload = Readonly<{
  epochMillis: number;
}>;

export type PasswordChangePayload = Readonly<{
  status: 'updated';
  sequence: number;
  replayed: boolean;
  serverTime: number;
  session: BrowserSession;
}>;

export type AccountRegistration = Readonly<{
  referrerPlayerId?: number;
  username: string;
  password: string;
  email: string;
  countryId?: number;
  birthDate: number;
  birthMonth: number;
  birthYear: number;
  sex: number;
  hairGarmentId: number;
  pantsGarmentId: number;
  skinGarmentId: number;
  topGarmentId: number;
  villageTheme: 1 | 2 | 5 | 6;
  termsAccepted: true;
  termsVersion: string;
}>;

export type TerrainPayload = Readonly<{
  yeetEnabled?: boolean;
  adminTruckUnderglow?: AdminTruckUnderglowSettings;
  enhancedLightingForced?: boolean;
  villageId: number;
  theme: number;
  villageSize: number;
  revision: number;
  sampleWidth: number;
  heightEncoding: 'signed-int8-base64';
  heightSamples: string;
  creativeWorld?: typeof CREATIVE_WORLD;
  streamed?: boolean;
}>;

export type TerrainWritePayload = Readonly<{
  status: 'updated' | 'unchanged';
  terrain: TerrainPayload;
}>;

export type BuildingEntity = Readonly<{
  /** Absolute world height set by administrator godmode; omission follows terrain. */
  placementY?: number;
  placementOffsetX?: number;
  placementOffsetZ?: number;
  placementTiltX?: number;
  placementTiltZ?: number;
  id: number;
  type: number;
  remainingPoints: number;
  tileX: number;
  tileZ: number;
  parameters: string;
  offeringResources?: readonly ProductionResourcePayload[];
  /** Older gateway fixtures omit this source-defaulted flag; omission means true. */
  flattenTerrain?: boolean;
  entityText: string;
  interaction: BuildingInteraction;
  petTreatSpecies?: PetShopSpecies;
  keyColor?: KeyDoorColor | null;
  keyDoorTarget?: boolean;
  open: boolean | null;
  pictureId: number | null;
  gift: GiftBuildingDetailsPayload | null;
  rotation: number;
  consumed: boolean;
  placedOnMap: boolean;
  /** Server timestamp for the current placement; older gateway fixtures may omit it. */
  placedAt?: number;
  ready: boolean;
  delayStart: number;
  delayEnd: number;
  ingredients: number;
  sellRefund: number;
  readySellRefund: number;
}>;

export type BuildingInteraction =
  | 'open-close'
  | 'train-carriage'
  | 'entity-text'
  | 'sit'
  | 'cannon'
  | 'domino'
  | 'dice'
  | 'key-door'
  | 'friend-key-door'
  | 'code-key-door'
  | 'unmounted-key-door'
  | 'automatic-key-door'
  | 'self-unlock-door'
  | 'teleport'
  | 'friend-teleport'
  | 'unmounted-teleport'
  | 'teleport-step'
  | 'telefax'
  | 'automatic-telefax'
  | 'tutorial-sign'
  | 'vip-info'
  | 'player-panel'
  | 'theme-shop'
  | 'camera-toggle'
  | 'camera-drone'
  | 'wire-workshop'
  | 'circuit-wire'
  | 'circuit-machine'
  | 'circuit-button'
  | 'live-screen'
  | 'pet-house'
  | 'pet-treat'
  | 'external-hamsterpaj'
  | 'external-pimpland'
  | 'external-zed-radio'
  | 'external-arvika'
  | 'seasonal-badge'
  | 'enter-temple'
  | 'leave-temple'
  | 'treasure-island'
  | null;
export type KeyDoorColor =
  | 'blue'
  | 'yellow'
  | 'green'
  | 'pink'
  | 'orange'
  | 'purple'
  | 'white'
  | 'red'
  | 'brown'
  | 'gray'
  | 'olive'
  | 'black'
  | 'gold'
  | 'silver'
  | 'bronze'
  | 'custom_9'
  | 'custom_10'
  | 'custom_11'
  | 'custom_12'
  | 'custom_14'
  | 'custom_15'
  | 'custom_16';

export type GiftBuildingDetailsPayload = Readonly<{
  senderPlayerId: number;
  text: string;
  wrappingType: number | null;
  canOpenAt: number | null;
}>;

export type TreeEntity = Readonly<{
  id: number;
  batch: number;
  type: number;
  tileX: number;
  tileZ: number;
  scaleByte: number;
  scale: number;
}>;

export type CreativeSpatialPayload = Readonly<{
  treeOccupiedCells: string;
  regularBlockedCells: string;
  vipBlockedCells: string;
  occupiedCells: string;
  buildingOccupiedCells: string;
  creativeBlockedCells: string;
  noboHouses: readonly NoboHousePayload[];
}>;

export type TerrainChunkPayload = Readonly<{
  revision: number;
  sampleX: number;
  sampleZ: number;
  width: number;
  depth: number;
  heightSamples: string;
}>;

export type EntityChunkPayload = Readonly<{
  terrainChunk?: TerrainChunkPayload;
  creativeSpatial?: CreativeSpatialPayload;
  villageId: number;
  revision: number;
  chunkX: number;
  chunkZ: number;
  minimumTileX: number;
  minimumTileZ: number;
  maximumTileXExclusive: number;
  maximumTileZExclusive: number;
  snapshotBuildingCount: number;
  snapshotTreeCount: number;
  buildings: readonly BuildingEntity[];
  raceBuildings?: readonly RaceBuildingEntity[];
  trees: readonly TreeEntity[];
}>;

export type RaceBuildingEntity = Pick<
  BuildingEntity,
  'id' | 'type' | 'tileX' | 'tileZ' | 'rotation' | 'consumed' | 'placedOnMap'
>;
export type RaceBuildingManifestPayload = Readonly<{
  villageId: number;
  revision: number;
  buildings: readonly RaceBuildingEntity[];
}>;

export type RaceParticipantStatus = 'enrolled' | 'racing' | 'finished' | 'DNS' | 'DNF';
export type RaceHeatParticipantPayload = Readonly<{
  playerId: number;
  playerName: string;
  startMatBuildingId: number;
  status: RaceParticipantStatus;
  reachedCheckpointBuildingIds: readonly number[];
  elapsedMilliseconds: number;
  finishNonce?: string;
}>;
export type RaceHeatPayload = Readonly<{
  id: string;
  villageId: number;
  triggerBuildingId: number;
  startBuildingId: number;
  finishBuildingId: number;
  checkpointBuildingIds: readonly number[];
  participants: readonly RaceHeatParticipantPayload[];
  countdownAt: number;
  greenAt: number;
  crossingClosesAt: number;
  expiresAt: number;
  completedAt: number | null;
  serverTime: number;
  playerCap: 16;
  realtimeConnection?: RaceRealtimeConnectionPayload;
}>;
export type RaceRealtimeConnectionPayload = Readonly<{
  heatId: string;
  playerId: number;
  greenAt: number;
  realtimeProtocol: 'nord-race-v2';
  realtimePath: string;
  ticket: string;
  streamGeneration: number;
  sessionGeneration: number;
}>;
export type RaceQueueParticipantPayload = Readonly<{
  playerId: number;
  playerName: string;
  startMatBuildingId: number;
}>;
export type RaceQueuePayload = Readonly<{
  id: string;
  villageId: number;
  triggerBuildingId: number;
  participants: readonly RaceQueueParticipantPayload[];
  startMatBuildingIds: readonly number[];
  enqueuedAt: number;
  position: number;
  serverTime: number;
  activeCap: 5;
  queueCap: 5;
}>;
export type RaceStartPayload =
  | Readonly<{ status: 'started'; raceHeat: RaceHeatPayload }>
  | Readonly<{ status: 'queued'; raceQueue: RaceQueuePayload }>;
export type CurrentRacePayload =
  | Readonly<{ status: 'started'; raceHeat: RaceHeatPayload }>
  | Readonly<{ status: 'queued'; raceQueue: RaceQueuePayload }>;
export type RaceQueueUpdatePayload = Readonly<{
  status: 'queued' | 'position-updated' | 'promoted' | 'cancelled';
  queueId: string;
  villageId: number;
  raceQueue: RaceQueuePayload | null;
  raceHeat?: RaceHeatPayload;
}>;

export type EntityRevisionPayload = Readonly<{ villageId: number; revision: number }>;
export type TerrainChangesPayload = Readonly<{
  fromRevision: number;
  revision: number;
  dirtyChunks: readonly EntityChunkCoordinatePayload[];
  fullRefreshRequired: boolean;
}>;
export type TerrainRevisionPayload = Readonly<{
  villageId: number;
  revision: number;
  terrainChanges?: TerrainChangesPayload;
}>;
export type DecorationOccupancyPayload = Readonly<{
  villageId: number;
  revision: number;
  gridWidth: number;
  encoding: 'tw-bitset-base64';
  coverage: 'buildings-and-trees';
  sourceTreeCount: number;
  sourcePlacedBuildingCount: number;
  occupiedCells: string;
  buildingOccupiedCells?: string;
  creativeBlockedCells?: string;
  /** Exact plots that can accept one complementary crop or grow light. */
  growLightPlots?: readonly Readonly<{
    tileX: number;
    tileZ: number;
    occupant: 'plant' | 'light';
  }>[];
}>;
export type ObjectCollisionPayload = Readonly<{
  villageId: number;
  revision: number;
  gridWidth: number;
  encoding: 'tw-bitset-base64';
  coverage: 'buildings-and-trees';
  sourceTreeCount: number;
  sourcePlacedBuildingCount: number;
  regularBlockedCells: string;
  vipBlockedCells: string;
  noboHouses?: readonly NoboHousePayload[];
}>;
export type NoboHousePayload = Readonly<{
  buildingId: number;
  tileX: number;
  tileZ: number;
  tileWidth: number;
  tileDepth: number;
}>;
export type AvatarPositionSource =
  'unknown' | 'server-replay' | 'server-notification' | 'client-predicted';
export type AvatarGender = 'man' | 'woman' | 'unknown';
export type AvatarAppearanceLayer = Readonly<{
  garmentId: number;
  hue: number;
  saturation: number;
  brightness: number;
  opacity: number;
}>;
export type AvatarJugglingPayload = Readonly<{
  sequence: number;
  siteswap: string;
  prop: 'b' | 'c' | 'r';
  elapsedMilliseconds: number;
}>;
export type CustomVehiclePayload = Readonly<{
  seatX: number;
  seatZ: number;
  rotation: number;
  parts: readonly Readonly<{ type: number; tileX: number; tileZ: number; rotation: number }>[];
}>;
export type CustomVehiclePickupSubmissionPayload = Readonly<{
  presence: VillagePresencePayload;
  status: 'picked-up';
  sequence: number;
  characterId: number;
  buildingCount: number;
  characters: VillageCharactersPayload;
}>;

export type AvatarPresencePayload = Readonly<{
  policeLightsActive?: boolean;
  customVehicle?: CustomVehiclePayload;
  underglow?: AdminTruckUnderglowSettings;
  playerId: number;
  name: string;
  friend: boolean;
  /** True only for the local staff avatar while authoritative ghost mode is active. */
  ghostMode?: boolean;
  speedBoostMode?: boolean;
  mountType: number;
  mountCharacterId: number;
  petType: number;
  petNetworkId: number;
  noboType?: number;
  avatarData: string;
  gender: AvatarGender;
  appearanceLayers: readonly AvatarAppearanceLayer[];
  mountName: string;
  petName: string;
  petLevel: number;
  petPointsWithinLevel: number;
  petCoolIdleUnlockedUntil: number;
  level: number;
  posX: number;
  /** Administrator-controlled height offset in legacy protocol units. */
  posY?: number;
  posZ: number;
  rotation: number;
  positionKnown: boolean;
  positionSource: AvatarPositionSource;
  pendingMovementSequence: number;
  acceptedMovementSequence?: number;
  away: boolean;
  emote?: AvatarEmoteAction | null;
  sit?: AvatarSitPayload | null;
  juggling?: AvatarJugglingPayload | null;
}>;
export type AvatarSitAction =
  'sit' | 'sit-afk' | 'roman-rings' | 'upside-down-high' | 'upside-down-low';
export type AvatarSitPayload = Readonly<{
  buildingId: number;
  action: AvatarSitAction;
  swingIntensity?: number;
  swingAngleMilli?: number;
  swingVelocityMilli?: number;
}>;
export type AvatarPetEventPayload = Readonly<{
  sequence: number;
  playerId: number;
  eventId: 40 | 41 | 42;
  animationState:
    | 'TRICK1'
    | 'TRICK2'
    | 'TRICK3'
    | 'TRICK4'
    | 'TRICK5'
    | 'TRICK6'
    | 'TRICK7'
    | 'PET'
    | 'PET_ROYAL';
  pointsGained: number;
}>;
export type AvatarFriendEventPayload = Readonly<{
  sequence: number;
  senderPlayerId: number;
  targetPlayerId: number;
  eventId: 1 | 2 | 3;
}>;
export type AvatarVisitorKickEventPayload = Readonly<{
  sequence: number;
  senderPlayerId: number;
  targetPlayerId: number;
}>;
export type AvatarGestureAction = 'wave' | 'blow-kiss';
export type AvatarGestureEventPayload = Readonly<{
  sequence: number;
  senderPlayerId: number;
  targetPlayerId: number;
  action: AvatarGestureAction;
}>;
export type AvatarSillyGooseStareEventPayload = Readonly<{
  sequence: number;
  viewerPlayerId: number;
  goosePlayerId: number;
  viewerPosX: number;
  viewerPosZ: number;
  startsInMilliseconds: number;
  elapsedMilliseconds: number;
  durationMilliseconds: number;
}>;
export type AvatarSpawnTeleportAction = 'normal' | 'home' | 'friend';
export type AvatarSpawnPointPayload = Readonly<{
  buildingId: number;
  buildingType: -221 | 898 | 3018;
  posX: number;
  posZ: number;
}>;
export type AvatarSpawnPointsPayload = Readonly<{
  normal: AvatarSpawnPointPayload | null;
  home: AvatarSpawnPointPayload | null;
  friend: AvatarSpawnPointPayload | null;
}>;
export type AvatarRelocationEventPayload = Readonly<{
  sequence: number;
  playerId: number;
  posX: number;
  posZ: number;
  rotation: number;
}>;
export type AvatarSpawnTeleportEventPayload = Readonly<{
  sequence: number;
  senderPlayerId: number;
  targetPlayerId: number;
  action: AvatarSpawnTeleportAction;
}>;
export type AvatarEmoteAction =
  | 'dance-1'
  | 'dance-2'
  | 'dance-3'
  | 'dance-4'
  | 'dance-5'
  | 'wacky-1'
  | 'wacky-2'
  | 'wacky-3'
  | 'wacky-4'
  | 'wacky-5'
  | 'wacky-6'
  | 'relax-1'
  | 'relax-2'
  | 'relax-3'
  | 'relax-4'
  | 'relax-5';
export type AvatarRaceEventPayload = Readonly<{
  sequence: number;
  playerId: number;
  playerName: string;
  elapsedMilliseconds: number;
}>;
export type WeatherKind = 'sunshine' | 'rain' | 'lightning';
export type AvatarWeatherEventPayload = Readonly<{
  sequence: number;
  playerId: number;
  kind: WeatherKind;
  elapsedMilliseconds: number;
  effects: readonly WeatherBuildingTimingPayload[];
}>;
export type AvatarDoorbellEventPayload = Readonly<{
  sequence: number;
  playerId: number;
  buildingId: number;
}>;
export type AvatarKeyDoorEventPayload = Readonly<{
  sequence: number;
  playerId: number;
  color: KeyDoorColor;
}>;
export type AvatarSelfUnlockDoorEventPayload = Readonly<{
  sequence: number;
  playerId: number;
  buildingId: number;
}>;
export type AvatarCannonEventPayload = Readonly<{
  yeet?: boolean;
  warning?: boolean;
  sequence: number;
  playerId: number;
  buildingId: number;
  startX: number;
  startZ: number;
  cannonX: number;
  cannonZ: number;
  landingX: number;
  landingZ: number;
  rotation: number;
  elapsedMilliseconds: number;
}>;
export type DiceRollPayload = Readonly<{
  pieceId?: number;
  buildingId: number;
  placedAt: number;
  startedAt: number;
  value: number;
  previousValue: number;
  elapsedMilliseconds: number;
}>;

export type TabletopDragPayload = Readonly<{
  groundStamp?: string;
  tilt?: number;
  yaw?: number;
  tableId: number;
  pieceId: number;
  playerId: number;
  revision: number;
  dragId: string;
  sequence: number;
  x: number;
  y: number;
  z: number;
  held: boolean;
  remainingMilliseconds: number;
}>;

export type VillagePresencePayload = Readonly<{
  tabletopDrags?: readonly TabletopDragPayload[];
  diceRolls?: readonly DiceRollPayload[];
  streamGeneration: number;
  villageId: number;
  localPlayerId: number;
  revision: number;
  avatars: readonly AvatarPresencePayload[];
  petEvents: readonly AvatarPetEventPayload[];
  friendEvents?: readonly AvatarFriendEventPayload[];
  raceEvents?: readonly AvatarRaceEventPayload[];
  weatherEvents?: readonly AvatarWeatherEventPayload[];
  doorbellEvents?: readonly AvatarDoorbellEventPayload[];
  keyDoorEvents?: readonly AvatarKeyDoorEventPayload[];
  selfUnlockDoorEvents?: readonly AvatarSelfUnlockDoorEventPayload[];
  cannonEvents?: readonly AvatarCannonEventPayload[];
  visitorKickEvents?: readonly AvatarVisitorKickEventPayload[];
  gestureEvents?: readonly AvatarGestureEventPayload[];
  sillyGooseStareEvents?: readonly AvatarSillyGooseStareEventPayload[];
  spawnPoints?: AvatarSpawnPointsPayload;
  spawnTeleportEvents?: readonly AvatarSpawnTeleportEventPayload[];
  avatarRelocationEvents?: readonly AvatarRelocationEventPayload[];
}>;
export type VillagePresenceDeltaPayload = VillagePresencePayload &
  Readonly<{ removedPlayerIds: readonly number[] }>;
export type SumoStatusPayload = Readonly<{
  ringId: number;
  phase: 'waiting' | 'countdown' | 'active' | 'finished';
  remaining: number;
  countdown: number;
  eliminated: boolean;
  spectating: boolean;
  winnerId: number;
  winnerName: string;
  spinMilliseconds: number;
  velocityX: number;
  velocityZ: number;
}>;

export type MovementPosePayload = Readonly<{
  sumo?: SumoStatusPayload;
  playerId: number;
  posX: number;
  /** Administrator-controlled height offset in legacy protocol units. */
  posY?: number;
  posZ: number;
  rotation: number;
  away: boolean;
  emote?: AvatarEmoteAction | null;
  sit?: AvatarSitPayload | null;
  juggling?: null;
}>;
export type MovementUpdatePayload = Readonly<{
  villageId: number;
  streamGeneration: number;
  revision: number;
  poses: readonly MovementPosePayload[];
  footballRealtime?: true;
}>;
export type BallVectorPayload = Readonly<{ x: number; y: number; z: number }>;
export type BallQuaternionPayload = BallVectorPayload & Readonly<{ w: number }>;
export type BallPosePayload = Readonly<{
  buildingId: number;
  position: BallVectorPayload;
  velocity: BallVectorPayload;
  rotation: BallQuaternionPayload;
  sleeping: boolean;
}>;
export type VillageFootballStatePayload = Readonly<{
  active: boolean;
  expiresAt: number;
  playerLimit: number;
  playerCount: number;
}>;
export type FootballGoalEventPayload = Readonly<{
  eventId: number;
  villageId: number;
  goalBuildingId: number;
  ballBuildingId: number;
  playerId: number;
  epoch: number;
  sequence: number;
  occurredAt: number;
}>;
export type BallUpdatePayload = Readonly<{
  villageId: number;
  streamGeneration: number;
  revision: number;
  serverTime: number;
  balls: readonly BallPosePayload[];
  respawn?: true;
  football?: VillageFootballStatePayload;
}>;
export type FootballContactBlockedPayload = Readonly<{
  playerCount: number;
  playerLimit: number;
}>;
export type MovementSubmissionPayload = Readonly<{
  status: 'submitted';
  sequence: number;
  authoritativePose: MovementPosePayload;
  /** Football building ids whose local contact prediction was accepted for this movement. */
  footballContactAcknowledgements: readonly number[];
  raceAdmission?: RaceAdmissionPayload;
  footballAdmission?: FootballAdmissionPayload;
  physicsLease?: PhysicsLeasePayload;
  footballParticipantPoses?: readonly MovementPosePayload[];
  ballUpdate?: BallUpdatePayload;
  footballContactBlocked?: FootballContactBlockedPayload;
}>;
export type FootballAdmissionPayload = Readonly<{
  playerId: number;
  villageId: number;
  admittedAt: number;
  expiresAt: number;
  authoritativePose: MovementPosePayload;
  realtimeProtocol: 'nord-football-v1';
  realtimePath: string;
  ticket: string;
  streamGeneration: number;
  sessionGeneration: number;
}>;
export type PhysicsScopePayload = 'football';
export type PhysicsVectorPayload = Readonly<{ x: number; y: number; z: number }>;
export type PhysicsQuaternionPayload = PhysicsVectorPayload & Readonly<{ w: number }>;
export type PhysicsBodySnapshotPayload = Readonly<{
  id: number;
  position: PhysicsVectorPayload;
  rotation: PhysicsQuaternionPayload;
  linearVelocity: PhysicsVectorPayload;
  angularVelocity: PhysicsVectorPayload;
  sleeping: boolean;
}>;
export type PhysicsSnapshotBatchPayload = Readonly<{
  villageId: number;
  sessionId: string;
  scope: PhysicsScopePayload;
  scopeId: number;
  playerId: number;
  sessionGeneration: number;
  epoch: number;
  sequence: number;
  clientTime: number;
  bodies: readonly PhysicsBodySnapshotPayload[];
}>;
export type PhysicsLeasePayload = Readonly<{
  villageId: number;
  sessionId: string;
  scope: PhysicsScopePayload;
  scopeId: number;
  playerId: number;
  sessionGeneration: number;
  epoch: number;
  sequence: number;
  expiresAt: number;
  ticket: string;
  realtimePath: '/api/v2/physics-realtime';
  realtimeProtocol: 'nord-physics-v1';
  allowedBodyIds: readonly number[];
  transferred: boolean;
  checkpoint?: PhysicsSnapshotBatchPayload;
}>;
export type RaceAdmissionPayload = Readonly<{
  heatId: string;
  playerId: number;
  admittedAt: number;
  greenAt: number;
  authoritativePose: MovementPosePayload;
  realtimeProtocol: 'nord-race-v2';
  realtimePath: string;
  ticket: string;
  streamGeneration: number;
  sessionGeneration: number;
}>;
export type MoonwalkRaceTelemetryPayload = Readonly<{
  startEnabled: boolean;
  mountedSeen: boolean;
  accurateDistanceMilli: number;
  totalDistanceMilli: number;
  sampledMilliseconds: number;
}>;
export type PetTrickAnimationState =
  'NONE' | 'TRICK1' | 'TRICK2' | 'TRICK3' | 'TRICK4' | 'TRICK5' | 'TRICK6' | 'TRICK7';
export type PetTrickSubmissionPayload = Readonly<{
  status: 'submitted' | 'cool-idle-unlocked';
  trick: number;
  animationState: PetTrickAnimationState;
  petLevel: number;
  petPointsWithinLevel: number;
  petCoolIdleUnlockedUntil: number;
  presence: VillagePresencePayload;
}>;
export type PetTrainingSubmissionPayload = Readonly<{
  status: 'success' | 'level-up' | 'failed' | 'remote-pat';
  targetPlayerId: number;
  animationState: 'PET' | 'PET_ROYAL';
  pointsGained: number;
  petLevel: number;
  petPointsWithinLevel: number;
  petCoolIdleUnlockedUntil: number;
  nextAvailableAt: number;
  presence: VillagePresencePayload;
}>;
export type PetTreatSubmissionPayload = Readonly<{
  status: 'consumed' | 'level-up';
  sequence: number;
  buildingId: number;
  buildingType: number;
  buildingName: string;
  pointsGained: number;
  petLevel: number;
  petPointsWithinLevel: number;
  entityRevision: number;
  presence: VillagePresencePayload;
}>;
export type TreasureRewardKind = 'coins' | 'sunshine' | 'rain' | 'lightning' | 'building';
export type TreasurePickupSubmissionPayload = Readonly<{
  status: 'picked-up';
  sequence: number;
  buildingId: number;
  buildingType: number;
  buildingName: string;
  rewardKind: TreasureRewardKind;
  rewardBuildingType?: number;
  rewardBuildingName?: string;
  rewardBuildingAsset?: string;
  amount: number;
  creditsAfter: number;
  weather: WeatherInventoryPayload;
  entityRevision: number;
  worldUpdate?: WorldNotificationUpdatePayload;
}>;
export type SeasonalBadgeKind =
  'halloween-2012' | 'halloween-2026' | 'christmas-2012' | 'valentine-2013' | 'clover-2013';
export type SeasonalBadgePickupSubmissionPayload = Readonly<{
  status: 'awarded' | 'already-owned';
  villageId: number;
  buildingId: number;
  buildingType: number;
  badge: SeasonalBadgeKind;
  badgeCategory: number;
  badgeType: number;
}>;
export type BuildingPurchaseSubmissionPayload = Readonly<{
  status: 'purchased';
  sequence: number;
  buildingId: number;
  buildingType: number;
  buildingName: string;
  price: number;
  creditsAfter: number;
  entityRevision: number;
  codeSetupRequired: boolean;
  worldUpdate?: WorldNotificationUpdatePayload;
  productionDelta?: BuildingProductionDeltaPayload;
}>;
export type NoboPurchaseSubmissionPayload = Readonly<{
  status: 'purchased';
  sequence: number;
  characterId: number;
  productType: number;
  noboType: 1 | 2 | 6 | 7 | 11;
  name: string;
  price: number;
  creditsAfter: number;
  entityRevision: number;
  characters: VillageCharactersPayload;
}>;
export type NoboEditOperation = 'pickup' | 'place' | 'sell';
export type BuildInventoryItemRef = Readonly<{
  sourceVillageId: number;
  entityId: number;
  community?: boolean;
  liveScreenScale?: number;
}>;
export type MountSaleSubmissionPayload = Readonly<{
  status: 'sold';
  sequence: number;
  characterId: number;
  mountName: string;
  refund: number;
  creditsAfter: number;
  characters: VillageCharactersPayload;
}>;
export type NoboEditSubmissionPayload = Readonly<{
  status: 'picked-up' | 'placed' | 'sold';
  sequence: number;
  characterId: number;
  productType: number;
  noboType: 1 | 2 | 6 | 7 | 11;
  name: string;
  refund: number;
  creditsAfter: number;
  entityRevision: number;
  inventoryCharacterIds: readonly number[];
  inventoryItems: readonly BuildInventoryItemRef[];
  characters: VillageCharactersPayload;
}>;
export type BuildingCodeSubmissionPayload = Readonly<{
  status: 'configured';
  villageId: number;
  buildingId: number;
  buildingType: number;
  entityRevision: number;
}>;
export type BuildingTextSubmissionPayload = Readonly<{
  status: 'updated';
  villageId: number;
  buildingId: number;
  buildingType: number;
  text: string;
  entityRevision: number;
}>;
export type BuildingGiftPurchaseDetails = Readonly<{
  text: string;
  wrappingType: number | null;
  canOpenAt: number | null;
}>;
export type BuildingEditOperation =
  | 'pickup'
  | 'place'
  | 'sell'
  | 'picture'
  | 'unwrap'
  | 'open-close'
  | 'train-board'
  | 'train-leave'
  | 'train-visitors';
export type BuildingEditSubmissionPayload = Readonly<{
  status:
    | 'picked-up'
    | 'placed'
    | 'sold'
    | 'picture-changed'
    | 'unwrapped'
    | 'opened'
    | 'closed'
    | 'pet-boarded'
    | 'pet-left'
    | 'visitors-changed';
  sequence: number;
  buildingId: number;
  buildingType: number;
  buildingName: string;
  buttonAsset: string;
  tileWidth: number;
  tileDepth: number;
  allowRotation: boolean;
  refund: number;
  creditsAfter: number;
  entityRevision: number;
  open: boolean | null;
  codeSetupRequired: boolean;
  inventoryBuildingIds?: readonly number[];
  inventoryItems: readonly BuildInventoryItemRef[];
  worldUpdate?: WorldNotificationUpdatePayload;
  productionDelta?: BuildingProductionDeltaPayload;
  petFollowing?: PetFollowingSubmissionPayload;
}>;

export type VillageEditorBuildingPayload = Readonly<{
  id: number;
  type: number;
  name: string;
  tileX: number;
  tileZ: number;
  rotation: number;
  tileWidth: number;
  tileDepth: number;
  allowRotation: boolean;
  editable: boolean;
  wrappingType: number | null;
}>;

export type VillageEditorTreePayload = Readonly<{
  type?: number;
  id: number;
  tileX: number;
  tileZ: number;
  scaleByte: number;
}>;

export type VillageEditorPayload = Readonly<{
  area?: Readonly<{
    chunkX: number;
    chunkZ: number;
    minimumTileX: number;
    minimumTileZ: number;
    width: number;
    depth: number;
  }>;
  terrainChunks?: readonly TerrainChunkPayload[];
  villageId: number;
  villageName: string;
  theme: number;
  villageSize: number;
  entityRevision: number;
  buildings: readonly VillageEditorBuildingPayload[];
  trees: readonly VillageEditorTreePayload[];
}>;

export type BuildingBatchPlacementPayload = Readonly<{
  buildingId: number;
  tileX: number;
  tileZ: number;
  rotation: number;
  liveScreenScale?: number;
}>;

export type BuildingBatchEditSubmissionPayload = Readonly<{
  status: 'applied' | 'unchanged';
  sequence: number;
  villageId: number;
  entityRevision: number;
  placements: readonly BuildingBatchPlacementPayload[];
  worldUpdate?: WorldNotificationUpdatePayload;
}>;

export type BuildingBatchPickupSubmissionPayload = Readonly<{
  status: 'picked-up';
  sequence: number;
  villageId: number;
  entityRevision: number;
  buildingIds: readonly number[];
  worldUpdate?: WorldNotificationUpdatePayload;
}>;

export type BuildingCatalogProductPayload = Readonly<{
  /** New purchases use the gift composer in the destination village. */
  giftPlacement?: boolean;
  buildingType: number;
  kind?: 'building' | 'nobo';
  noboType?: number;
  name: string;
  description?: string;
  growLightPlant?: boolean;
  buttonAsset: string;
  keyColor: KeyDoorColor | null;
  petSpecies?: PetShopSpecies;
  price: number;
  requiredLevel: number;
  tileWidth: number;
  tileDepth: number;
  allowCount?: number;
  allowRotation: boolean;
  buildAnywhere: boolean;
  terrainModification?: boolean;
  requiredBadge: string;
  requiredBadgeImage: string;
  badgeMet: boolean;
  placementSupported: boolean;
  inventoryBuildingIds: readonly number[];
  inventoryItems: readonly BuildInventoryItemRef[];
}>;

export type BuildingCatalogCategoryPayload = Readonly<{
  id: number;
  name: string;
  caption: string;
  label: string;
  iconAsset: string;
  products: readonly BuildingCatalogProductPayload[];
}>;

export type VillageExpansionOfferPayload = Readonly<{
  currentSize: number;
  nextSize: number;
  maximumSize: number;
  price: number;
  requiredLevel: number;
  canAfford: boolean;
}>;

export type VillageExpansionSubmissionPayload = Readonly<{
  status: 'expanded';
  villageId: number;
  previousSize: number;
  villageSize: number;
  price: number;
  creditsAfter: number;
  terrainRevision: number;
}>;

export type LegacyLanguage = 'swedish' | 'english' | 'danish' | 'norwegian';

export type BuildingCatalogTextPayload = Readonly<{
  buildLabel: string;
  buildTooltip: string;
  rotateTooltip: string;
  pickupTooltip: string;
  sellTooltip: string;
  giveGiftLabel: string;
  giftMessageCaption: string;
  giftCannotOpenBefore: string;
  giftAnytimeLabel: string;
  giftSenderProfileTooltip: string;
  giftUnwrapTooltip: string;
  giftCannotOpenUntilPrefix: string;
  buyLabel: string;
}>;

export type GiftMessageTemplatePayload = Readonly<{
  label: string;
  text: string;
}>;

export type BuildingCatalogPayload = Readonly<{
  villageId: number;
  language: LegacyLanguage;
  mode: 'build' | 'gift';
  godMode?: boolean;
  creativeMode?: boolean;
  communityBuildAllowed?: boolean;
  playerLevel: number;
  credits: number;
  productCount: number;
  pickupLockedBuildingTypes: readonly number[];
  pictureBuildingTypes: readonly number[];
  mountSellRefunds?: Readonly<Record<number, number>>;
  villageExpansion: VillageExpansionOfferPayload | null;
  texts: BuildingCatalogTextPayload;
  wrappings: readonly GiftWrappingOptionPayload[];
  giftMessageTemplates?: readonly GiftMessageTemplatePayload[];
  categories: readonly BuildingCatalogCategoryPayload[];
}>;

export type GiftWrappingOptionPayload = Readonly<{
  id: number;
  model: string;
  textureAsset: string;
  price: number;
  requiredBadge: string;
  badgeMet: boolean;
}>;

export const PLAYER_PICTURE_CAPACITY = 50;

export type PlayerPicturesPayload = Readonly<{
  playerId: number;
  capacity: typeof PLAYER_PICTURE_CAPACITY;
  sex: 0 | 1;
  pictureIds: readonly number[];
}>;

export type PlayerProfilePayload = Readonly<{
  playerId: number;
  firstName: string;
  lastName: string;
  username: string;
  memberSince: number;
  age: number;
  presentation: string;
  presentationPictureId: number | null;
  totalScore: number;
  sex: number;
  homeVillageId: number;
  visitingVillageId: number;
  online: boolean;
  country: string;
  preferredLanguages: readonly PreferredLanguageCode[];
  city: string;
  wantsEmail: boolean;
  level: number;
  theme: number;
  hasYouBlocked: boolean;
  blockedByYou: boolean;
}>;

export type PlayerBadgeValuePayload = Readonly<{
  key: number;
  value: number;
}>;

export type PlayerResourceBadgePayload = Readonly<{
  resourceId: number;
  name: string;
  button: string;
  amount: number;
}>;

export type PlayerBadgesPayload = Readonly<{
  playerId: number;
  vip: boolean;
  values: readonly PlayerBadgeValuePayload[];
  resources: readonly PlayerResourceBadgePayload[];
  tooltips?: readonly Readonly<{ key: number; text: string }>[];
  contentCreatorUrl?: string;
  barnhjalpareUrl?: string;
}>;

export type BlockedPlayerListEntryPayload = Readonly<{
  playerId: number;
  username: string;
  online: boolean;
  age: number;
  sex: number;
  villageId: number;
  villageName: string;
  villageVisitors: number;
  city: string;
  country: string;
  pictureId: number | null;
  theme: number;
  level: number;
}>;

export type BlockedPlayerListPayload = Readonly<{
  ownerPlayerId: number;
  entries: readonly BlockedPlayerListEntryPayload[];
}>;

export type OnlinePlayerListEntryPayload = Readonly<{
  playerId: number;
  username: string;
  status: string;
  away: boolean;
  age: number;
  sex: number;
  villageId: number;
  villageName: string;
  villageVisitors: number;
  city: string;
  country: string;
  score: number;
  pictureId: number | null;
  theme: number;
  level: number;
}>;

export type OnlinePlayerListTextPayload = Readonly<{
  toolbarLabel: string;
  visitedLabel: string;
  visitorsLabel: string;
  onlineLabel: string;
  noobDescription: string;
  ockelbodaDescription: string;
  tikiTikiDescription: string;
  travelTitleTemplate: string;
  travelMessageTemplate: string;
}>;

export type FeaturedVillagePayload = Readonly<{
  imageUrl?: string;
  villageId: number;
  title: string;
  description: string;
}>;

export type OnlinePlayerListPayload = Readonly<{
  featuredVillages?: readonly FeaturedVillagePayload[];
  creativeModeEnabled?: boolean;
  cinemaEnabled?: boolean;
  texts: OnlinePlayerListTextPayload;
  viewerPlayerId: number;
  entries: readonly OnlinePlayerListEntryPayload[];
}>;

export type HelpDirectoryEntryPayload = Readonly<{
  playerId: number;
  username: string;
  online: boolean;
  sex: number;
  level: number;
  pictureId: number | null;
  status: string;
  theme: number;
  visitorCount: number;
}>;

export type HelpDirectoryTextPayload = Readonly<{
  toolbarLabel: string;
  helpersIntroduction: string;
  moderatorsIntroduction: string;
  discordIntroduction: string;
  discordLabel: string;
}>;

export type HelpDirectoryPayload = Readonly<{
  texts: HelpDirectoryTextPayload;
  rulesText?: string;
  helpers: readonly HelpDirectoryEntryPayload[];
  moderators: readonly HelpDirectoryEntryPayload[];
}>;

export type WardrobeOptionPayload = AvatarAppearanceLayer &
  Readonly<{
    name: string;
    free: boolean;
  }>;

export type WardrobeCategoryPayload = Readonly<{
  id: string;
  label: string;
  selectedIndex: number;
  options: readonly WardrobeOptionPayload[];
}>;

export type WardrobeAppearanceLayerPayload = AvatarAppearanceLayer &
  Readonly<{
    categoryId: string;
  }>;

export type WardrobeShopRequirementPayload = Readonly<{
  name: string;
  image: string;
  met: boolean;
}>;

export type WardrobeShopOptionPayload = Readonly<{
  garmentId: number;
  name: string;
  price: number;
  requirementsMet: boolean;
  requirements: readonly WardrobeShopRequirementPayload[];
  hueEnabled: boolean;
  saturationEnabled: boolean;
  brightnessEnabled: boolean;
  opacityEnabled: boolean;
  hues: readonly number[];
  saturationMin: number;
  saturationMax: number;
  brightnessMin: number;
  brightnessMax: number;
  opacityMin: number;
  opacityMax: number;
  hueIndex: number;
  saturationIndex: number;
  brightnessIndex: number;
  opacityIndex: number;
}>;

export type WardrobeShopCategoryPayload = Readonly<{
  id: string;
  label: string;
  options: readonly WardrobeShopOptionPayload[];
}>;

export type WardrobePurchaseSelection = Readonly<{
  hueIndex: number;
  saturationIndex: number;
  brightnessIndex: number;
  opacityIndex: number;
}>;

export type WardrobeTextPayload = Readonly<{
  toolbarLabel: string;
  buyClothesLabel: string;
  wardrobeLabel: string;
  buyLabel: string;
  confirmationMessage: string;
  yesLabel: string;
  noLabel: string;
}>;

export type WardrobePayload = Readonly<{
  texts: WardrobeTextPayload;
  gender: AvatarGender;
  credits: number;
  appearanceLayers: readonly WardrobeAppearanceLayerPayload[];
  categories: readonly WardrobeCategoryPayload[];
  shopCategories: readonly WardrobeShopCategoryPayload[];
}>;

export type VillageVisitorListEntryPayload = BlockedPlayerListEntryPayload &
  Readonly<{ status?: string }>;

export type VillageVisitorListPayload = Readonly<{
  viewerPlayerId: number;
  villageId: number;
  entries: readonly VillageVisitorListEntryPayload[];
}>;

export type FriendListEntryPayload = Readonly<{
  playerId: number;
  username: string;
  status?: string;
  online: boolean;
  age: number;
  sex: number;
  villageId: number;
  villageVisitors: number;
  city: string;
  country: string;
  pictureId: number | null;
  theme: number;
  level: number;
}>;

export type FriendListPayload = Readonly<{
  ownerPlayerId: number;
  entries: readonly FriendListEntryPayload[];
}>;

export type FriendRemovalPayload = Readonly<{
  status: 'removed';
  playerId: number;
  friendList: FriendListPayload;
}>;

export type FriendRelationshipPayload = Readonly<{
  playerId: number;
  state: 'available' | 'incoming' | 'friend';
}>;

export type FriendRequestMutationPayload = Readonly<{
  status: 'requested' | 'accepted' | 'declined';
  playerId: number;
  relationship: FriendRelationshipPayload;
  friendList?: FriendListPayload;
}>;

export type VillageTravelPayload = Readonly<{
  status: 'entered' | 'unchanged';
  playerId: number;
  sourceVillageId: number;
  sessionGeneration: number;
  village: VillageSummary;
}>;
export type OwnedVillageStatus =
  | 'ok'
  | 'already-unlocked'
  | 'wrong-slot'
  | 'locked-level'
  | 'locked-coins'
  | 'no-tier'
  | 'invalid-session'
  | 'server-error';
export type OwnedVillageSlotPayload = Readonly<{
  slot: number;
  villageId: number | null;
  name: string;
  requiredLevel: number;
  cost: number;
  unlocked: boolean;
  canUnlock: boolean;
}>;
export type OwnedVillagesPayload = Readonly<{
  status: OwnedVillageStatus;
  message: string;
  playerId: number;
  currentLevel: number;
  currentCoins: number;
  nextSlot: number | null;
  unlockedVillageId: number | null;
  slots: readonly OwnedVillageSlotPayload[];
}>;
export type OwnedVillagesResponsePayload = Readonly<{
  ownedVillages: OwnedVillagesPayload;
  session: BrowserSession;
}>;
export type PlayerVillagesPayload = Readonly<{
  playerId: number;
  blocked: boolean;
  villages: readonly VillageSummary[];
}>;
export type VisitorKickSubmissionPayload = Readonly<{
  status: 'kicked';
  villageId: number;
  playerId: number;
}>;
export type DoorbellSubmissionPayload = Readonly<{
  status: 'rung';
  villageId: number;
  playerId: number;
  buildingId: number;
  buildingType: number;
}>;
export type SitSubmissionPayload = Readonly<{
  status: 'seated' | 'assembled';
  characters?: VillageCharactersPayload;
  worldUpdate?: WorldNotificationUpdatePayload;
  villageId: number;
  playerId: number;
  buildingId: number;
  buildingType: number;
  action: AvatarSitAction;
  presence: VillagePresencePayload;
}>;
export type CannonLaunchSubmissionPayload = Readonly<{
  status: 'launched';
  villageId: number;
  playerId: number;
  buildingId: number;
  buildingType: 658;
  presence: VillagePresencePayload;
}>;
export type SwingIntensitySubmissionPayload = Readonly<{
  status: 'updated' | 'unchanged';
  buildingId: number;
  intensity: number;
  angleMilli: number;
  angularVelocityMilli: number;
  serverTime: number;
  run: Readonly<{
    status: 'ready' | 'active' | 'waiting_for_rest';
    startedAt: number;
    endsAt: number;
    spins: number;
    canStart: boolean;
  }>;
  leaderboard: readonly Readonly<{
    rank: number;
    playerId: number;
    username: string;
    spins: number;
  }>[];
}>;
export type AvatarAwayAction = 'sleep' | 'sit-afk';
export type AwaySubmissionPayload = Readonly<{
  status: 'away';
  villageId: number;
  playerId: number;
  action: AvatarAwayAction;
  presence: VillagePresencePayload;
}>;
export type AvatarGestureSubmissionPayload = Readonly<{
  status: 'performed';
  villageId: number;
  senderPlayerId: number;
  targetPlayerId: number;
  action: AvatarGestureAction;
  presence: VillagePresencePayload;
}>;
export type SillyGooseStareSubmissionPayload = Readonly<{
  status: 'queued';
  villageId: number;
  viewerPlayerId: number;
  goosePlayerId: number;
  startsInMilliseconds: number;
  durationMilliseconds: number;
}>;
export type AvatarSpawnTeleportSubmissionPayload = Readonly<{
  status: 'teleported';
  villageId: number;
  senderPlayerId: number;
  targetPlayerId: number;
  action: AvatarSpawnTeleportAction;
  destination: AvatarSpawnPointPayload | null;
}>;
export type AvatarEmoteSubmissionPayload = Readonly<{
  status: 'performed';
  villageId: number;
  playerId: number;
  action: AvatarEmoteAction;
  presence: VillagePresencePayload;
}>;
export type KeyDoorSubmissionPayload = Readonly<{
  status: 'activated' | 'cooldown';
  villageId: number;
  playerId: number;
  buildingId: number;
  buildingType: number;
  color: KeyDoorColor;
  openWindowMillis: 5000;
}>;
export type SelfUnlockDoorSubmissionPayload = Readonly<{
  villageId: number;
  playerId: number;
  buildingId: number;
  buildingType: number;
  openWindowMillis: 5000;
}>;

export type GuestbookEntryPayload = Readonly<{
  postId: number;
  posterPlayerId: number;
  posterName: string;
  posterPictureId: number | null;
  text: string;
  postedAt: number;
  sex: number;
  online: boolean;
  private: boolean;
  level: number;
  type: string;
}>;

export type GuestbookPayload = Readonly<{
  ownerPlayerId: number;
  ownerName: string;
  offset: number;
  hasNewerPosts: boolean;
  hasOlderPosts: boolean;
  entries: readonly GuestbookEntryPayload[];
}>;

export type GuestbookAvailabilityPayload = Readonly<{
  newEntriesAvailable: boolean;
}>;

export type GuestbookHistoryPayload = Readonly<{
  firstPlayerId: number;
  secondPlayerId: number;
  offset: number;
  hasNewerPosts: boolean;
  hasOlderPosts: boolean;
  entries: readonly GuestbookEntryPayload[];
}>;

export type GuestbookSubmissionPayload = Readonly<{
  status: 'posted';
  postId: number;
  guestbook: GuestbookPayload;
}>;

export type GuestbookMutationPayload = Readonly<{
  status: 'removed' | 'privacy-updated';
  postId: number;
  guestbook: GuestbookPayload;
}>;

export type PlayerPersonalInformationUpdate = Readonly<{
  firstName: string;
  lastName: string;
  birthYear: number | null;
  birthMonthIndex: number | null;
  birthDate: number | null;
  countryId: number | null;
  wantsEmail: boolean;
}>;

export type PictureDeletionPayload = Readonly<{
  status: 'deleted';
  pictureId: number;
  clearedBuildingCount: number;
  buildingReferencesCleared: boolean;
  entityRevision: number;
}>;

export type PictureUploadPayload = Readonly<{
  status: 'uploaded';
  pictureId: number;
}>;

export type ScreenshotUploadPayload = Readonly<{
  status: 'uploaded';
  shareId: number;
  shareUrl: string;
}>;

export type ProductionResourcePayload = Readonly<{
  resourceId: number;
  name: string;
  buttonAsset: string;
}>;

export type ProductionInventorySlotPayload = Readonly<{
  index: number;
  accessible: boolean;
  occupied: boolean;
  resourceId: number;
  resource?: ProductionResourcePayload;
}>;

export type ProductionShortcutPayload = Readonly<{
  index: number;
  resourceId: number;
  lit: boolean;
  resource?: ProductionResourcePayload;
}>;

export type ProductionInventoryPayload = Readonly<{
  capacity: 6 | 12;
  backpack: readonly ProductionInventorySlotPayload[];
  shortcuts: readonly ProductionShortcutPayload[];
}>;

export type ProductionIngredientPayload = ProductionResourcePayload &
  Readonly<{
    index: number;
    applied: boolean;
    availableCount: number;
    canApply: boolean;
  }>;

export type ProductionBuildingPayload = Readonly<{
  buildingId: number;
  buildingType: number;
  state: 'unavailable' | 'ready' | 'producing' | 'requires-ingredients';
  ready: boolean;
  effectiveReady: boolean;
  canHarvest: boolean;
  delayStart: number;
  delayEnd: number;
  remainingMs: number;
  ingredientsMask: number;
  recipeId: number;
  produceDelayMs: number;
  product: ProductionResourcePayload;
  ingredients: readonly ProductionIngredientPayload[];
}>;

export type BuildingProductionPayload = Readonly<{
  villageId: number;
  entityRevision: number;
  serverTime: number;
  inventory: ProductionInventoryPayload;
  buildings: readonly ProductionBuildingPayload[];
}>;

export type BuildingProductionDeltaPayload = Readonly<{
  villageId: number;
  entityRevision: number;
  serverTime: number;
  inventory: ProductionInventoryPayload;
  buildings: readonly ProductionBuildingPayload[];
  removedBuildingIds: readonly number[];
}>;

export type BuildingProductionAction = 'harvest' | 'apply-ingredient';

export type MarketplaceResourcePayload = Readonly<{
  weatherKind?: WeatherKind;
  resourceId: number;
  name: string;
  buttonAsset: string;
  tradable: boolean;
  referenceValue: number | null;
  unitPriceRange: Readonly<{ minimum: number; maximum: number }> | null;
}>;

export type MarketplaceInventoryEntryPayload = Readonly<{
  resource: MarketplaceResourcePayload;
  quantity: number;
}>;

export type MarketplaceInventorySlotPayload = Readonly<{
  index: number;
  resource: MarketplaceResourcePayload | null;
}>;

export type MarketplaceBackpackSlotPayload = Readonly<{
  index: number;
  accessible: boolean;
  resource: MarketplaceResourcePayload | null;
}>;

export type MarketplaceAuctionOptions = Readonly<{
  mode: 'auction';
  durationHours?: 3 | 8 | 12 | 24;
}>;

export type MarketplaceOrderPayload = Readonly<{
  mode?: 'fixed' | 'auction';
  highestBidUnitPrice?: number;
  highestBidderPlayerId?: number;
  bidCount?: number;
  expiresAt?: number;
  isHighestBidder?: boolean;
  orderId: number;
  ownerPlayerId: number;
  ownerName: string;
  isMine: boolean;
  side: 'buy' | 'sell';
  resource: MarketplaceResourcePayload;
  originalQuantity: number;
  remainingQuantity: number;
  unitPrice: number;
  status: 'active' | 'fulfilled' | 'cancelled' | 'expired';
  createdAt: number;
  updatedAt: number;
}>;

export type MarketplacePayload = Readonly<{
  weather?: WeatherInventoryPayload;
  weatherCapacity?: number;
  revision: number;
  credits: number;
  backpackCapacity: 6 | 12;
  backpackFreeSlots: number;
  backpack: readonly MarketplaceInventoryEntryPayload[];
  backpackSlots: readonly MarketplaceBackpackSlotPayload[];
  tradeInventoryCapacity: 24;
  tradeInventoryFreeSlots: number;
  tradeInventoryReservedSlots: number;
  tradeInventory: readonly MarketplaceInventorySlotPayload[];
  resources: readonly MarketplaceResourcePayload[];
  orders: readonly MarketplaceOrderPayload[];
  myOrders: readonly MarketplaceOrderPayload[];
}>;

export type MarketplaceTradeLimitPayload = Readonly<{
  resourceId: number;
  referenceValue: number;
  minimumUnitPrice: number;
  maximumUnitPrice: number;
}>;

export type BuildingProductionSubmissionPayload = Readonly<{
  status: 'applied';
  sequence: number;
  action: BuildingProductionAction;
  buildingId: number;
  buildingType: number;
  resourceId: number;
  ingredientIndex: number;
  replayed: boolean;
  buildingPresent: boolean;
  ready: boolean;
  delayStart: number;
  delayEnd: number;
  ingredients: number;
  serverTime: number;
  entityRevision: number;
  productionDelta: BuildingProductionDeltaPayload;
  worldUpdate?: WorldNotificationUpdatePayload;
}>;

export type InventoryMoveSubmissionPayload = Readonly<{
  status: 'applied';
  sequence: number;
  sourceIndex: number;
  destinationIndex: number;
  resourceId: number;
  replayed: boolean;
  serverTime: number;
  production: BuildingProductionPayload;
}>;

export type InventoryActivationSubmissionPayload = Readonly<{
  status: 'applied';
  sequence: number;
  sourceIndex: number;
  shortcutIndex: number;
  resourceId: number;
  replayed: boolean;
  serverTime: number;
  production: BuildingProductionPayload;
}>;

export type WeatherInventoryPayload = Readonly<{
  sunshine: number;
  rain: number;
  lightning: number;
}>;
export type WeatherTargetsPayload = Readonly<Record<WeatherKind, readonly number[]>>;
export type WeatherControlsPayload = Readonly<{
  weather: WeatherInventoryPayload;
  targets: WeatherTargetsPayload;
}>;
export type WeatherBuildingTimingPayload = Readonly<{
  buildingId: number;
  visualDelayMilliseconds: number;
  applyDelayMilliseconds: number;
}>;
export type WeatherBuildingEffectPayload = WeatherBuildingTimingPayload &
  Readonly<{
    buildingType: number;
    tileX: number;
    tileZ: number;
    rotation: number;
    ready: boolean;
    delayStart: number;
    delayEnd: number;
    ingredients: number;
  }>;
export type WeatherActivationSubmissionPayload = Readonly<{
  status: 'applied';
  sequence: number;
  kind: WeatherKind;
  replayed: boolean;
  eventSequence: number;
  elapsedMilliseconds: number;
  serverTime: number;
  weather: WeatherInventoryPayload;
  buildingEffects: readonly WeatherBuildingEffectPayload[];
}>;

export type ResourceSlotArea = 'shortcut' | 'backpack';
export type ResourceSlotEndpoint = Readonly<{
  area: ResourceSlotArea;
  index: number;
}>;
export type ResourceSlotMoveSubmissionPayload = Readonly<{
  status: 'applied';
  sequence: number;
  source: ResourceSlotEndpoint;
  destination: ResourceSlotEndpoint;
  resourceId: number;
  replayed: boolean;
  serverTime: number;
  production: BuildingProductionPayload;
}>;

export type TombolaAction = 'inspect' | 'start' | 'stop' | 'finish';
export type TombolaOutcome =
  | 'state'
  | 'started'
  | 'stopped'
  | 'finished'
  | 'no-active-resource'
  | 'tutorial-required'
  | 'spin-already-active'
  | 'spin-not-active'
  | 'stale-wheel'
  | 'idempotency-conflict';
export type TombolaSlicePayload = Readonly<{
  index: number;
  resourceId: number;
  lit: boolean;
}>;
export type TombolaPayload = Readonly<{
  action: TombolaAction;
  sequence: number;
  outcome: TombolaOutcome;
  replayed: boolean;
  serverTime: number;
  startedAt: number;
  angularSpeed: number;
  angularDeceleration: number;
  landingAngle: number;
  targetIndex: number;
  targetResourceId: number;
  won: boolean;
  points: number;
  levelCompleted: boolean;
  level: number;
  wins: number;
  requiredWins: number;
  bonusMultiplier: number;
  credits: number;
  slices: readonly TombolaSlicePayload[];
  productionDelta: BuildingProductionDeltaPayload;
}>;

export type PetFollowingSubmissionPayload = Readonly<{
  status: 'following' | 'staying';
  sequence: number;
  following: boolean;
  characterId: number;
  presence: VillagePresencePayload;
  characters: VillageCharactersPayload;
}>;
export type PetHouseSubmissionPayload = PetFollowingSubmissionPayload &
  Readonly<{
    buildingId: number;
    buildingType: number;
    buildingRotation: 0 | 1 | 2 | 3;
    posX: number;
    posZ: number;
  }>;
export type MountTransitionAction = 'mount' | 'dismount';
export type MountTransitionRejectionOutcome =
  | 'not-home-village'
  | 'position-unavailable'
  | 'position-changed'
  | 'too-far'
  | 'drop-blocked'
  | 'character-not-found'
  | 'character-unavailable'
  | 'not-mounted'
  | 'already-mounted'
  | 'mount-mismatch'
  | 'idempotency-conflict'
  | 'invalid-request'
  | 'server-error';
export type MountTransitionSubmissionPayload = Readonly<{
  status: 'applied';
  sequence: number;
  action: MountTransitionAction;
  characterId: number;
  mountType: number;
  mountName: string;
  mountCharacterId: number;
  characterPosX: number;
  characterPosZ: number;
  characterConsumed: boolean;
  replayed: boolean;
  serverTime: number;
  presence: VillagePresencePayload;
  characters: VillageCharactersPayload;
}>;
export type MountTransitionReconciliationPayload = Readonly<{
  status: MountTransitionRejectionOutcome;
  sequence: number;
  action: MountTransitionAction;
  characterId: number;
  replayed: boolean;
  serverTime: number;
  presence: VillagePresencePayload;
  characters: VillageCharactersPayload;
}>;
export type MountNameSubmissionPayload = Readonly<{
  status: 'updated';
  characterId: number;
  mountType: number;
  mountName: string;
  mountClass: string;
  characters: VillageCharactersPayload;
}>;
export type BuildingTeleportSubmissionPayload = Readonly<{
  status: 'applied';
  sequence: number;
  villageId: number;
  sourceBuildingId: number;
  destinationBuildingId: number;
  posX: number;
  posZ: number;
  rotation: number;
  serverTime: number;
  presence: VillagePresencePayload;
}>;
export type MountShopId =
  'stable' | 'racing' | 'bike' | 'arvika' | 'scooter' | 'santa' | 'cross-country-skis';
export type MountShopProductPayload = Readonly<{
  studioUUID?: string;
  studioHash?: string;
  name?: string;
  description?: string;
  icon?: string;
  mountType: number;
  price: number;
  speed: number;
  mountClass: string;
  requiredBadge: string;
  requiredBadgeImage: string;
  badgeMet: boolean;
  buyEnabled: boolean;
}>;
export type MountShopPayload = Readonly<{
  shop: MountShopId;
  title: string;
  credits: number;
  mounted: boolean;
  products: readonly MountShopProductPayload[];
}>;
export type MountPurchaseSubmissionPayload = Readonly<{
  status: 'applied';
  sequence: number;
  characterId: number;
  mountType: number;
  mountName: string;
  creditsAfter: number;
  replayed: boolean;
  serverTime: number;
  presence: VillagePresencePayload;
  characters: VillageCharactersPayload;
}>;
export type PetShopSpecies = 'dog' | 'monkey' | 'butterfly' | 'bat' | 'snowman' | 'spindel';
export type PetShopId = 'ockelboda' | 'new-pets' | 'spider';
export type PetShopProductPayload = Readonly<{
  petType: number;
  price: number;
  species: PetShopSpecies;
  requiredBadge: string;
  requiredBadgeImage: string;
  badgeMet: boolean;
  buyEnabled: boolean;
}>;
export type PetShopPayload = Readonly<{
  shop: PetShopId;
  buildingId: number;
  title: string;
  credits: number;
  petEquipped: boolean;
  products: readonly PetShopProductPayload[];
}>;
export type PetPurchaseSubmissionPayload = Readonly<{
  status: 'applied';
  sequence: number;
  characterId: number;
  petType: number;
  petName: string;
  creditsAfter: number;
  replayed: boolean;
  serverTime: number;
  presence: VillagePresencePayload;
  characters: VillageCharactersPayload;
}>;
export type ThemeShopPayload = Readonly<{
  villageId: number;
  sourceVillageId: number;
  buildingId: number;
  buildingType: number;
  themeId: number;
  title: string;
  description: string;
  previewImage: string;
  resourceImage: string;
  displayPrice: number;
  purchasePrice: number;
  credits: number;
  owned: boolean;
  active: boolean;
  targetVillages: readonly Readonly<{
    villageId: number;
    slot: number;
    name: string;
    active: boolean;
  }>[];
}>;
export type ThemePurchaseSubmissionPayload = Readonly<{
  status: 'applied';
  sequence: number;
  sourceVillageId: number;
  targetVillageId: number;
  villageId: number;
  buildingId: number;
  themeId: number;
  chargedPrice: number;
  creditsAfter: number;
  newlyOwned: boolean;
  replayed: boolean;
  serverTime: number;
}>;
export type CharacterEntityPayload = Readonly<{
  customVehicle?: CustomVehiclePayload;
  underglow?: AdminTruckUnderglowSettings;
  characterId: number;
  /** Current semantic gateway ownership; absent only on older cached snapshots. */
  ownerPlayerId?: number;
  posX: number;
  posZ: number;
  /** Legacy signed-short heading; older cached snapshots default to the old south-facing pose. */
  rotation?: number;
  data: string;
  familyId: number;
  homeId: number;
  inBuildingId: number;
  firstName: string;
  surname: string;
  type: number;
  consumed: boolean;
  pet: boolean;
  petType: number;
  petNetworkId: number;
  petName: string;
  petLevel: number;
  petPointsWithinLevel: number;
  mountType: number;
  mountName: string;
  /** Present on current gateways; optional only for pre-Nobo cached fixtures. */
  noboType?: number;
}>;
export type VillageCharactersPayload = Readonly<{
  streamGeneration: number;
  villageId: number;
  revision: number;
  characters: readonly CharacterEntityPayload[];
}>;
export type VillageChatMessagePayload = Readonly<{
  sequence: number;
  fromPlayerId: number;
  fromPlayerName: string;
  text: string;
  /** Unix epoch milliseconds when the speech was originally sent. */
  createdAt: number;
  /** Server-generated bell/gong finish marker, never player-authored speech. */
  bellRing?: boolean;
  /** Server-generated dice result, never player-authored speech. */
  diceRoll?: boolean;
  /** Nonzero when the transport projects a real player's speech through a Nobo character. */
  speakerCharacterId?: number;
}>;
export type ExtraSoundEffectCue = 'faah' | 'glassbil' | 'postmanpat' | 'rizz' | 'fart';
export type VillageChatSoundEffectPayload = Readonly<{
  sequence: number;
  cue: ExtraSoundEffectCue;
}>;
export type VillageChatMoonwalkStatePayload = Readonly<{
  sequence: number;
  playerId: number;
  enabled: boolean;
  synchronization: boolean;
}>;
export type VillageChatPayload = Readonly<{
  serverTime?: number;
  streamGeneration: number;
  villageId: number;
  revision: number;
  godMode?: boolean;
  messages: readonly VillageChatMessagePayload[];
  soundEffects?: readonly VillageChatSoundEffectPayload[];
  moonwalkStates?: readonly VillageChatMoonwalkStatePayload[];
}>;
export type LiveMessagePayload = Readonly<{
  sequence: number;
  signType: number;
  playerId: number;
  playerName: string;
  text: string;
  pictureId: number;
  sex: number;
  age: number;
  level: number;
  online: boolean;
  receivedAt: number;
  recipientPlayerId?: number;
}>;
export const LIVE_MESSAGE_MAXIMUM_CHARACTERS = 300;
export type LiveMessagesPayload = Readonly<{
  serverTime?: number;
  streamGeneration: number;
  revision: number;
  messages: readonly LiveMessagePayload[];
}>;
export type LiveMessageOutcome =
  | 'applied'
  | 'level-required'
  | 'insufficient-credits'
  | 'invalid-request'
  | 'idempotency-conflict'
  | 'server-error';
export type LiveMessageSubmissionPayload = Readonly<{
  sequence: number;
  outcome: LiveMessageOutcome;
  replayed: boolean;
  creditsAfter: number;
  serverTime: number;
  live: LiveMessagesPayload;
}>;
export type ForumIndexPayload = Readonly<{
  categories: readonly Readonly<{
    id: number;
    name: string;
    forums: readonly Readonly<{
      id: number;
      name: string;
      description: string;
      lastWrite: number | null;
      lastRead: number | null;
    }>[];
  }>[];
}>;
export type ForumThreadPagePayload = Readonly<{
  forumId: number;
  forumName: string;
  page: number;
  pageCount: number;
  canModerate: boolean;
  canCreate: boolean;
  threads: readonly Readonly<{
    id: number;
    name: string;
    lastWrite: number;
    lastRead: number | null;
    locked: boolean;
    sticky: boolean;
    messageCount: number;
  }>[];
}>;
export type ForumMessagePagePayload = Readonly<{
  forumId: number;
  threadId: number;
  threadName: string;
  forumName: string;
  page: number;
  pageCount: number;
  canModerate: boolean;
  locked: boolean;
  sticky: boolean;
  messages: readonly Readonly<{
    id: number;
    message: string;
    pictureId: number | null;
    username: string;
    playerId: number;
    online: boolean;
    written: number;
    sex: string;
  }>[];
}>;
export type ForumSearchPagePayload = Readonly<{
  query: string;
  page: number;
  pageCount: number;
  matches: readonly Readonly<{
    threadId: number;
    threadName: string;
    highlight: string;
    pictureId: number | null;
    username: string;
    playerId: number;
    online: boolean;
    written: number;
  }>[];
}>;
export type ForumThreadCreationPayload = Readonly<{
  status: 'created';
  forumId: number;
  threadId: number;
}>;
export type ForumReplyCreationPayload = Readonly<{
  status: 'created';
  threadId: number;
}>;
export type ForumMessageEditPayload = Readonly<{
  status: 'updated';
  threadId: number;
  messageId: number;
}>;
export type ForumMessageDeletionPayload = Readonly<{
  status: 'deleted';
  threadId: number;
  messageId: number;
  removedThread: boolean;
}>;
export type ForumThreadStatePayload = Readonly<{
  status: 'updated';
  threadId: number;
  value: boolean;
}>;
export type NotificationTopic =
  | 'world'
  | 'presence'
  | 'movement'
  | 'balls'
  | 'characters'
  | 'chat'
  | 'live'
  | 'guestbook'
  | 'admin'
  | 'village-lighting'
  | 'vehicle-lighting'
  | 'creative-mode'
  | 'cinema'
  | 'yeet'
  | 'announcement'
  | 'rules'
  | 'daily-challenges'
  | 'catalog'
  | 'race'
  | 'football-goal'
  | 'credits';
export type AdminNotificationPayload = Readonly<{
  id: number;
  kind: 'popup' | 'village-speech-bubble' | 'vehicle-message';
  senderName?: string;
  messageText: string;
  villageId?: number;
  tileX?: number;
  tileY?: number;
  mountType?: number;
  createdAt: number;
}>;
export type EntityChunkCoordinatePayload = Readonly<{ chunkX: number; chunkZ: number }>;
export type WorldResourceEffectPayload = ProductionResourcePayload &
  Readonly<{
    playerId: number;
    buildingId?: number;
  }>;
export type TreasureFeedbackPayload = Readonly<{
  rewardKind: TreasureRewardKind;
  amount: number;
  rewardBuildingAsset?: string;
}>;
export type WorldTreasureEffectPayload = TreasureFeedbackPayload & Readonly<{ playerId: number }>;
export type WorldNotificationUpdatePayload = Readonly<{
  villageId: number;
  revision: number;
  fullRefreshRequired: boolean;
  snapshotBuildingCount: number;
  snapshotTreeCount: number;
  occupancyChanged?: boolean;
  collisionChanged?: boolean;
  dirtyChunks: readonly EntityChunkCoordinatePayload[];
  /** Includes the old and new spatial footprints, even across chunk boundaries. */
  dirtyChunksComplete?: boolean;
  terrainChanges?: TerrainChangesPayload;
  resourceEffects?: readonly WorldResourceEffectPayload[];
  treasureEffects?: readonly WorldTreasureEffectPayload[];
}>;
export type NotificationCursorPayload = Readonly<{
  adminTruckUnderglow?: AdminTruckUnderglowSettings;
  enhancedLightingForced?: boolean;
  villageId: number;
  streamGeneration: number;
  cursor: number;
  reset: boolean;
  timedOut: boolean;
  topics: readonly NotificationTopic[];
  creditsUpdate?: number;
  worldUpdate?: WorldNotificationUpdatePayload;
  productionDelta?: BuildingProductionDeltaPayload;
  presenceUpdate?: VillagePresencePayload;
  presenceDelta?: VillagePresenceDeltaPayload;
  movementUpdate?: MovementUpdatePayload;
  ballUpdate?: BallUpdatePayload;
  footballGoalEvents?: readonly FootballGoalEventPayload[];
  characterUpdate?: VillageCharactersPayload;
  chatUpdate?: VillageChatPayload;
  liveUpdate?: LiveMessagesPayload;
  guestbookAvailabilityUpdate?: boolean;
  adminMessages?: readonly AdminNotificationPayload[];
  announcement?: string;
  creativeModeEnabled?: boolean;
  cinemaEnabled?: boolean;
  yeetEnabled?: boolean;
  communityBuildAllowed?: boolean;
  rulesText?: string;
  catalogRevision?: number;
  raceHeatUpdate?: RaceHeatPayload;
  raceQueueUpdate?: RaceQueueUpdatePayload;
}>;
export type ChatSubmissionPayload = Readonly<{
  status: 'submitted';
  godMode?: boolean;
  clientCommand?: 'freecam' | 'gpuwarn';
}>;

export type PaymentCoinOfferPayload = Readonly<{
  key: string;
  kind: 'coins';
  coins: number;
  euroCents: number;
  available: boolean;
}>;

export type PaymentCatalogPayload = Readonly<{
  enabled: boolean;
  customerPortalAvailable: boolean;
  coinOffers: readonly PaymentCoinOfferPayload[];
  subscription: Readonly<{
    key: 'nordrevip_monthly';
    kind: 'subscription';
    name: 'NordReVIP';
    priceLabel: string;
    paydayCoins: number;
    purchaseCoins?: number;
    oneTimeReset?: boolean;
    available: boolean;
  }>;
}>;

export type PaymentCheckoutSessionPayload = Readonly<{ id: string; url: string }>;
export type PaymentCustomerPortalSessionPayload = Readonly<{ url: string }>;
export type PromoRedemptionPayload = Readonly<{
  code: string;
  coinsAwarded: number;
  vipDaysAwarded: number;
  creditsAfter: number;
  vipExpiresAt: number;
}>;

export function parsePromoRedemptionResponse(value: unknown): PromoRedemptionPayload {
  const root = objectValue(value, 'promo redemption response');
  assertProtocol(root);
  const redemption = objectValue(root.promoRedemption, 'promoRedemption');
  return {
    code: stringValue(redemption.code, 'promoRedemption.code'),
    coinsAwarded: nonNegativeIntegerValue(redemption.coinsAwarded, 'promoRedemption.coinsAwarded'),
    vipDaysAwarded: nonNegativeIntegerValue(
      redemption.vipDaysAwarded,
      'promoRedemption.vipDaysAwarded',
    ),
    creditsAfter: nonNegativeIntegerValue(redemption.creditsAfter, 'promoRedemption.creditsAfter'),
    vipExpiresAt: nonNegativeIntegerValue(redemption.vipExpiresAt, 'promoRedemption.vipExpiresAt'),
  };
}

export function parsePaymentCatalogResponse(value: unknown): PaymentCatalogPayload {
  const root = objectValue(value, 'payment catalog response');
  assertProtocol(root);
  const paymentCatalog = objectValue(root.paymentCatalog, 'paymentCatalog');
  const subscription = objectValue(paymentCatalog.subscription, 'paymentCatalog.subscription');
  const subscriptionKey = stringValue(subscription.key, 'paymentCatalog.subscription.key');
  const subscriptionKind = stringValue(subscription.kind, 'paymentCatalog.subscription.kind');
  const subscriptionName = stringValue(subscription.name, 'paymentCatalog.subscription.name');
  if (
    subscriptionKey !== 'nordrevip_monthly' ||
    subscriptionKind !== 'subscription' ||
    subscriptionName !== 'NordReVIP'
  ) {
    throw new ContractError('paymentCatalog.subscription is invalid');
  }
  return {
    enabled: booleanValue(paymentCatalog.enabled, 'paymentCatalog.enabled'),
    customerPortalAvailable: booleanValue(
      paymentCatalog.customerPortalAvailable,
      'paymentCatalog.customerPortalAvailable',
    ),
    coinOffers: arrayValue(paymentCatalog.coinOffers, 'paymentCatalog.coinOffers').map((entry) => {
      const offer = objectValue(entry, 'paymentCatalog.coinOffer');
      if (stringValue(offer.kind, 'paymentCatalog.coinOffer.kind') !== 'coins')
        throw new ContractError('paymentCatalog.coinOffer.kind is invalid');
      return {
        key: stringValue(offer.key, 'paymentCatalog.coinOffer.key'),
        kind: 'coins' as const,
        coins: positiveIntegerValue(offer.coins, 'paymentCatalog.coinOffer.coins'),
        euroCents: positiveIntegerValue(offer.euroCents, 'paymentCatalog.coinOffer.euroCents'),
        available: booleanValue(offer.available, 'paymentCatalog.coinOffer.available'),
      };
    }),
    subscription: {
      key: subscriptionKey,
      kind: subscriptionKind,
      name: subscriptionName,
      priceLabel: stringValue(subscription.priceLabel, 'paymentCatalog.subscription.priceLabel'),
      paydayCoins: nonNegativeIntegerValue(
        subscription.paydayCoins,
        'paymentCatalog.subscription.paydayCoins',
      ),
      purchaseCoins:
        subscription.purchaseCoins === undefined
          ? 0
          : nonNegativeIntegerValue(
              subscription.purchaseCoins,
              'paymentCatalog.subscription.purchaseCoins',
            ),
      oneTimeReset:
        subscription.oneTimeReset === undefined
          ? false
          : booleanValue(subscription.oneTimeReset, 'paymentCatalog.subscription.oneTimeReset'),
      available: booleanValue(subscription.available, 'paymentCatalog.subscription.available'),
    },
  };
}

export function parsePaymentCustomerPortalSessionResponse(
  value: unknown,
): PaymentCustomerPortalSessionPayload {
  const root = objectValue(value, 'payment customer portal response');
  assertProtocol(root);
  const portal = objectValue(root.customerPortalSession, 'customerPortalSession');
  const url = stringValue(portal.url, 'customerPortalSession.url');
  if (!isSecureExternalURL(url)) {
    throw new ContractError(
      'customerPortalSession.url is not a secure subscription-management URL',
    );
  }
  return { url };
}

export function parsePaymentCheckoutSessionResponse(value: unknown): PaymentCheckoutSessionPayload {
  const root = objectValue(value, 'payment checkout response');
  assertProtocol(root);
  const checkout = objectValue(root.checkoutSession, 'checkoutSession');
  const url = stringValue(checkout.url, 'checkoutSession.url');
  if (!isTebexCheckoutURL(url))
    throw new ContractError('checkoutSession.url is not a Tebex Checkout URL');
  return { id: stringValue(checkout.id, 'checkoutSession.id'), url };
}

function isSecureExternalURL(value: string): boolean {
  try {
    const parsed = new URL(value);
    return (
      parsed.protocol === 'https:' &&
      parsed.hostname.length > 0 &&
      parsed.username === '' &&
      parsed.password === ''
    );
  } catch {
    return false;
  }
}

function isTebexCheckoutURL(value: string): boolean {
  try {
    const parsed = new URL(value);
    return (
      parsed.protocol === 'https:' &&
      parsed.username === '' &&
      parsed.password === '' &&
      (parsed.hostname === 'checkout.tebex.io' || parsed.hostname === 'pay.tebex.io')
    );
  } catch {
    return false;
  }
}

export function parseSessionResponse(value: unknown, existingToken?: string): BrowserSession {
  const root = objectValue(value, 'session response');
  assertProtocol(root);
  const session = objectValue(root.session, 'session');
  const villages = arrayValue(session.villages, 'session.villages').map((entry) => {
    const village = objectValue(entry, 'village');
    return {
      id: integerValue(village.id, 'village.id'),
      name: stringValue(village.name, 'village.name'),
      ...(village.ownerPlayerId === undefined
        ? {}
        : {
            ownerPlayerId: positiveIntegerValue(village.ownerPlayerId, 'village.ownerPlayerId'),
          }),
    };
  });
  const responseToken = session.token;
  const token = typeof responseToken === 'string' ? responseToken : existingToken;
  if (token === undefined || token.length === 0) {
    throw new ContractError('session.token is missing');
  }
  const contextorialCompletedTypes = arrayValue(
    session.contextorialCompletedTypes,
    'session.contextorialCompletedTypes',
  ).map((entry, index) => {
    const type = nonNegativeIntegerValue(
      entry,
      `session.contextorialCompletedTypes[${String(index)}]`,
    );
    if (type > 5) {
      throw new ContractError('session.contextorialCompletedTypes contains an unknown type');
    }
    return type;
  });
  if (
    new Set(contextorialCompletedTypes).size !== contextorialCompletedTypes.length ||
    contextorialCompletedTypes.some(
      (type, index) => index > 0 && type <= (contextorialCompletedTypes[index - 1] ?? -1),
    )
  ) {
    throw new ContractError('session.contextorialCompletedTypes is not strictly ordered');
  }
  const activeVillage =
    session.activeVillage === undefined
      ? undefined
      : (() => {
          const village = objectValue(session.activeVillage, 'session.activeVillage');
          return {
            id: positiveIntegerValue(village.id, 'session.activeVillage.id'),
            name: stringValue(village.name, 'session.activeVillage.name'),
            ...(village.ownerPlayerId === undefined
              ? {}
              : {
                  ownerPlayerId: positiveIntegerValue(
                    village.ownerPlayerId,
                    'session.activeVillage.ownerPlayerId',
                  ),
                }),
          };
        })();
  return {
    token,
    playerId: integerValue(session.playerId, 'session.playerId'),
    level: integerValue(session.level, 'session.level'),
    spins: integerValue(session.spins, 'session.spins'),
    vip: booleanValue(session.vip, 'session.vip'),
    ...(session.moderator === undefined
      ? {}
      : {
          moderator: booleanValue(session.moderator, 'session.moderator'),
        }),
    ...(session.nordHelper === undefined
      ? {}
      : { nordHelper: booleanValue(session.nordHelper, 'session.nordHelper') }),
    ...(session.administrator === undefined
      ? {}
      : {
          administrator: booleanValue(session.administrator, 'session.administrator'),
        }),
    ...(session.godMode === undefined
      ? {}
      : { godMode: booleanValue(session.godMode, 'session.godMode') }),
    ...(session.enhancedLightingAllowed === undefined
      ? {}
      : {
          enhancedLightingAllowed: booleanValue(
            session.enhancedLightingAllowed,
            'session.enhancedLightingAllowed',
          ),
        }),
    ...(session.preLounge === undefined
      ? {}
      : { preLounge: booleanValue(session.preLounge, 'session.preLounge') }),
    ...(activeVillage === undefined ? {} : { activeVillage }),
    mountPurchaseSequence: nonNegativeIntegerValue(
      session.mountPurchaseSequence,
      'session.mountPurchaseSequence',
    ),
    mountTransitionSequence: nonNegativeIntegerValue(
      session.mountTransitionSequence,
      'session.mountTransitionSequence',
    ),
    petPurchaseSequence:
      session.petPurchaseSequence === undefined
        ? 0
        : nonNegativeIntegerValue(session.petPurchaseSequence, 'session.petPurchaseSequence'),
    themePurchaseSequence:
      session.themePurchaseSequence === undefined
        ? 0
        : nonNegativeIntegerValue(session.themePurchaseSequence, 'session.themePurchaseSequence'),
    contextorialCompletedTypes,
    villages,
    createdAt: stringValue(session.createdAt, 'session.createdAt'),
    announcement:
      session.announcement === undefined
        ? ''
        : announcementText(session.announcement, 'session.announcement'),
    userSettings: parseUserSettings(session.userSettings),
  };
}

export function parseUserSettingsResponse(value: unknown): UserSettingsPayload {
  const root = objectValue(value, 'user settings response');
  assertProtocol(root);
  return parseUserSettings(root.userSettings);
}

export function parseLegacyRedemptionResponse(value: unknown): LegacyRedemptionPayload {
  const root = objectValue(value, 'legacy redemption response');
  assertProtocol(root);
  const redemption = objectValue(root.redemption, 'redemption');
  const redeemed = booleanValue(redemption.redeemed, 'redemption.redeemed');
  const result: LegacyRedemptionPayload = {
    available: booleanValue(redemption.available, 'redemption.available'),
    redeemed,
  };
  if (!redeemed) return result;
  return {
    ...result,
    legacyUsername: stringValue(redemption.legacyUsername, 'redemption.legacyUsername'),
    legacyLevel: positiveIntegerValue(redemption.legacyLevel, 'redemption.legacyLevel'),
    coinsAwarded: positiveIntegerValue(redemption.coinsAwarded, 'redemption.coinsAwarded'),
    creditsAfter: nonNegativeIntegerValue(redemption.creditsAfter, 'redemption.creditsAfter'),
    redeemedAt: stringValue(redemption.redeemedAt, 'redemption.redeemedAt'),
  };
}

export function parseLoginNewsResponse(value: unknown): LoginNewsPayload {
  const root = objectValue(value, 'login news response');
  assertProtocol(root);
  const news = objectValue(root.news, 'news');
  const language = legacyLanguageValue(news.language, 'news.language');
  const backdropAsset = loginBackdropAssetValue(news.backdropAsset, 'news.backdropAsset');
  const imageAsset = loginNewsImageAssetValue(news.imageAsset, 'news.imageAsset');
  const rawTexts = objectValue(news.texts, 'news.texts');
  const texts = {
    usernameLabel: boundedText(rawTexts.usernameLabel, 'news.texts.usernameLabel', 200, true),
    passwordLabel: boundedText(rawTexts.passwordLabel, 'news.texts.passwordLabel', 200, true),
    controlPanelLabel: boundedText(
      rawTexts.controlPanelLabel,
      'news.texts.controlPanelLabel',
      200,
      true,
    ),
    rememberMeLabel: boundedText(rawTexts.rememberMeLabel, 'news.texts.rememberMeLabel', 200, true),
    latestNewsLabel: boundedText(rawTexts.latestNewsLabel, 'news.texts.latestNewsLabel', 200, true),
    createAccountLabel: boundedText(
      rawTexts.createAccountLabel,
      'news.texts.createAccountLabel',
      200,
      true,
    ),
    startLabel: boundedText(rawTexts.startLabel, 'news.texts.startLabel', 200, true),
    downloadingUpdatesLabel: boundedText(
      rawTexts.downloadingUpdatesLabel,
      'news.texts.downloadingUpdatesLabel',
      200,
      true,
    ),
    downloadFinishedLabel: boundedText(
      rawTexts.downloadFinishedLabel,
      'news.texts.downloadFinishedLabel',
      200,
      true,
    ),
  };
  const rawAccountCreation = objectValue(news.accountCreation, 'news.accountCreation');
  const termsAsset = accountTermsAssetValue(
    rawAccountCreation.termsAsset,
    'news.accountCreation.termsAsset',
  );
  const rawAccountTexts = objectValue(rawAccountCreation.texts, 'news.accountCreation.texts');
  const accountCreation = {
    termsAsset,
    termsVersion: boundedText(
      rawAccountCreation.termsVersion,
      'news.accountCreation.termsVersion',
      50,
      true,
    ),
    texts: {
      cancelLabel: sourceText(rawAccountTexts.cancelLabel, 'cancelLabel'),
      createAccountLabel: sourceText(rawAccountTexts.createAccountLabel, 'createAccountLabel'),
      boyLabel: sourceText(rawAccountTexts.boyLabel, 'boyLabel'),
      girlLabel: sourceText(rawAccountTexts.girlLabel, 'girlLabel'),
      skinLabel: sourceText(rawAccountTexts.skinLabel, 'skinLabel'),
      hairLabel: sourceText(rawAccountTexts.hairLabel, 'hairLabel'),
      topLabel: sourceText(rawAccountTexts.topLabel, 'topLabel'),
      pantsLabel: sourceText(rawAccountTexts.pantsLabel, 'pantsLabel'),
      usernameLabel: sourceText(rawAccountTexts.usernameLabel, 'usernameLabel'),
      passwordLabel: sourceText(rawAccountTexts.passwordLabel, 'passwordLabel'),
      repeatPasswordLabel: sourceText(rawAccountTexts.repeatPasswordLabel, 'repeatPasswordLabel'),
      emailLabel: sourceText(rawAccountTexts.emailLabel, 'emailLabel'),
      birthdayLabel: sourceText(rawAccountTexts.birthdayLabel, 'birthdayLabel'),
      yearPlaceholder: sourceText(rawAccountTexts.yearPlaceholder, 'yearPlaceholder'),
      monthPlaceholder: sourceText(rawAccountTexts.monthPlaceholder, 'monthPlaceholder'),
      dayPlaceholder: sourceText(rawAccountTexts.dayPlaceholder, 'dayPlaceholder'),
      agreementPrefix: sourceText(rawAccountTexts.agreementPrefix, 'agreementPrefix'),
      agreementLinkLabel: sourceText(rawAccountTexts.agreementLinkLabel, 'agreementLinkLabel'),
      illegalUsernameMessage: sourceText(
        rawAccountTexts.illegalUsernameMessage,
        'illegalUsernameMessage',
      ),
      usernameTakenMessage: sourceText(
        rawAccountTexts.usernameTakenMessage,
        'usernameTakenMessage',
      ),
      emailTakenMessage: sourceText(rawAccountTexts.emailTakenMessage, 'emailTakenMessage'),
    },
  };
  const rawWorldToolbar = objectValue(news.worldToolbar, 'news.worldToolbar');
  const worldToolbar = {
    backLabel: sourceText(rawWorldToolbar.backLabel, 'worldToolbar.backLabel'),
    forwardLabel: sourceText(rawWorldToolbar.forwardLabel, 'worldToolbar.forwardLabel'),
    homeLabel: sourceText(rawWorldToolbar.homeLabel, 'worldToolbar.homeLabel'),
    villagesLabel: sourceText(rawWorldToolbar.villagesLabel, 'worldToolbar.villagesLabel'),
    onlineListLabel: sourceText(rawWorldToolbar.onlineListLabel, 'worldToolbar.onlineListLabel'),
    buildLabel: sourceText(rawWorldToolbar.buildLabel, 'worldToolbar.buildLabel'),
    buildTooltip: sourceText(rawWorldToolbar.buildTooltip, 'worldToolbar.buildTooltip'),
    giveGiftLabel: sourceText(rawWorldToolbar.giveGiftLabel, 'worldToolbar.giveGiftLabel'),
    settingsLabel: sourceText(rawWorldToolbar.settingsLabel, 'worldToolbar.settingsLabel'),
    chatLabel: sourceText(rawWorldToolbar.chatLabel, 'worldToolbar.chatLabel'),
    wardrobeLabel: sourceText(rawWorldToolbar.wardrobeLabel, 'worldToolbar.wardrobeLabel'),
    currentlyVisitingLabel: sourceText(
      rawWorldToolbar.currentlyVisitingLabel,
      'worldToolbar.currentlyVisitingLabel',
    ),
    helpLabel: sourceText(rawWorldToolbar.helpLabel, 'worldToolbar.helpLabel'),
    screenshotLabel: sourceText(rawWorldToolbar.screenshotLabel, 'worldToolbar.screenshotLabel'),
    coinsLabel: sourceText(rawWorldToolbar.coinsLabel, 'worldToolbar.coinsLabel'),
    fullscreenLabel: sourceText(rawWorldToolbar.fullscreenLabel, 'worldToolbar.fullscreenLabel'),
    manCharacter: sourceText(rawWorldToolbar.manCharacter, 'worldToolbar.manCharacter'),
    womanCharacter: sourceText(rawWorldToolbar.womanCharacter, 'worldToolbar.womanCharacter'),
    currentNordTimeTemplate: sourceText(
      rawWorldToolbar.currentNordTimeTemplate,
      'worldToolbar.currentNordTimeTemplate',
    ),
    timeConverterLabel: sourceText(
      rawWorldToolbar.timeConverterLabel,
      'worldToolbar.timeConverterLabel',
    ),
    nordToLocalLabel: sourceText(rawWorldToolbar.nordToLocalLabel, 'worldToolbar.nordToLocalLabel'),
    localToNordLabel: sourceText(rawWorldToolbar.localToNordLabel, 'worldToolbar.localToNordLabel'),
    fromNordTimeTemplate: sourceText(
      rawWorldToolbar.fromNordTimeTemplate,
      'worldToolbar.fromNordTimeTemplate',
    ),
    toNordTimeTemplate: sourceText(
      rawWorldToolbar.toNordTimeTemplate,
      'worldToolbar.toNordTimeTemplate',
    ),
  };
  const rawProfile = objectValue(news.profile, 'news.profile');
  const profile = {
    badgesLabel: sourceText(rawProfile.badgesLabel, 'profile.badgesLabel'),
    bannedPlayersLabel: sourceText(rawProfile.bannedPlayersLabel, 'profile.bannedPlayersLabel'),
    friendsLabel: sourceText(rawProfile.friendsLabel, 'profile.friendsLabel'),
    guestbookLabel: sourceText(rawProfile.guestbookLabel, 'profile.guestbookLabel'),
    villagesLabel: sourceText(rawProfile.villagesLabel, 'profile.villagesLabel'),
    travelTemplate: sourceText(rawProfile.travelTemplate, 'profile.travelTemplate'),
    removeFriendLabel: sourceText(rawProfile.removeFriendLabel, 'profile.removeFriendLabel'),
    kickLabel: sourceText(rawProfile.kickLabel, 'profile.kickLabel'),
    buyCoinsForTemplate: sourceText(rawProfile.buyCoinsForTemplate, 'profile.buyCoinsForTemplate'),
    yourselfLabel: sourceText(rawProfile.yourselfLabel, 'profile.yourselfLabel'),
    setStatusLabel: sourceText(rawProfile.setStatusLabel, 'profile.setStatusLabel'),
    levelLabel: sourceText(rawProfile.levelLabel, 'profile.levelLabel'),
    banTemplate: sourceText(rawProfile.banTemplate, 'profile.banTemplate'),
    unbanTemplate: sourceText(rawProfile.unbanTemplate, 'profile.unbanTemplate'),
    changePictureLabel: sourceText(rawProfile.changePictureLabel, 'profile.changePictureLabel'),
    confirmBanTemplate: sourceText(rawProfile.confirmBanTemplate, 'profile.confirmBanTemplate'),
    confirmUnbanTemplate: sourceText(
      rawProfile.confirmUnbanTemplate,
      'profile.confirmUnbanTemplate',
    ),
    confirmRemoveFriendTemplate: sourceText(
      rawProfile.confirmRemoveFriendTemplate,
      'profile.confirmRemoveFriendTemplate',
    ),
    confirmKickMessage: sourceText(rawProfile.confirmKickMessage, 'profile.confirmKickMessage'),
    yesLabel: sourceText(rawProfile.yesLabel, 'profile.yesLabel'),
    noLabel: sourceText(rawProfile.noLabel, 'profile.noLabel'),
    guestbook: (() => {
      const rawGuestbook = objectValue(rawProfile.guestbook, 'news.profile.guestbook');
      return {
        writeMessageLabel: sourceText(
          rawGuestbook.writeMessageLabel,
          'profile.guestbook.writeMessageLabel',
        ),
        privateLabel: sourceText(rawGuestbook.privateLabel, 'profile.guestbook.privateLabel'),
        sendLabel: sourceText(rawGuestbook.sendLabel, 'profile.guestbook.sendLabel'),
        newerLabel: sourceText(rawGuestbook.newerLabel, 'profile.guestbook.newerLabel'),
        olderLabel: sourceText(rawGuestbook.olderLabel, 'profile.guestbook.olderLabel'),
        replyLabel: sourceText(rawGuestbook.replyLabel, 'profile.guestbook.replyLabel'),
        historyLabel: sourceText(rawGuestbook.historyLabel, 'profile.guestbook.historyLabel'),
        removeLabel: sourceText(rawGuestbook.removeLabel, 'profile.guestbook.removeLabel'),
        makePublicLabel: sourceText(
          rawGuestbook.makePublicLabel,
          'profile.guestbook.makePublicLabel',
        ),
        makePrivateLabel: sourceText(
          rawGuestbook.makePrivateLabel,
          'profile.guestbook.makePrivateLabel',
        ),
        messageSentLabel: sourceText(
          rawGuestbook.messageSentLabel,
          'profile.guestbook.messageSentLabel',
        ),
        coinGiftReceivedTemplate: sourceText(
          rawGuestbook.coinGiftReceivedTemplate,
          'profile.guestbook.coinGiftReceivedTemplate',
        ),
        fromLabel: sourceText(rawGuestbook.fromLabel, 'profile.guestbook.fromLabel'),
        todayLabel: sourceText(rawGuestbook.todayLabel, 'profile.guestbook.todayLabel'),
        yesterdayLabel: sourceText(rawGuestbook.yesterdayLabel, 'profile.guestbook.yesterdayLabel'),
        tomorrowLabel: sourceText(rawGuestbook.tomorrowLabel, 'profile.guestbook.tomorrowLabel'),
      };
    })(),
  };
  const rawForum = objectValue(news.forum, 'news.forum');
  const forum = {
    forumLabel: sourceText(rawForum.forumLabel, 'forum.forumLabel'),
    searchLabel: sourceText(rawForum.searchLabel, 'forum.searchLabel'),
    searchResultsLabel: sourceText(rawForum.searchResultsLabel, 'forum.searchResultsLabel'),
    refreshLabel: sourceText(rawForum.refreshLabel, 'forum.refreshLabel'),
    stickyLabel: sourceText(rawForum.stickyLabel, 'forum.stickyLabel'),
    lockedLabel: sourceText(rawForum.lockedLabel, 'forum.lockedLabel'),
    noMessagesLabel: sourceText(rawForum.noMessagesLabel, 'forum.noMessagesLabel'),
    lastPostLabel: sourceText(rawForum.lastPostLabel, 'forum.lastPostLabel'),
    postsLabel: sourceText(rawForum.postsLabel, 'forum.postsLabel'),
    createThreadLabel: sourceText(rawForum.createThreadLabel, 'forum.createThreadLabel'),
    topicLabel: sourceText(rawForum.topicLabel, 'forum.topicLabel'),
    textLabel: sourceText(rawForum.textLabel, 'forum.textLabel'),
    editMessageLabel: sourceText(rawForum.editMessageLabel, 'forum.editMessageLabel'),
    confirmRemoveMessage: sourceText(rawForum.confirmRemoveMessage, 'forum.confirmRemoveMessage'),
    backLabel: sourceText(rawForum.backLabel, 'forum.backLabel'),
    goBackLabel: sourceText(rawForum.goBackLabel, 'forum.goBackLabel'),
    sendLabel: sourceText(rawForum.sendLabel, 'forum.sendLabel'),
    removeLabel: sourceText(rawForum.removeLabel, 'forum.removeLabel'),
    replyLabel: sourceText(rawForum.replyLabel, 'forum.replyLabel'),
    cancelLabel: sourceText(rawForum.cancelLabel, 'forum.cancelLabel'),
    loadingLabel: sourceText(rawForum.loadingLabel, 'forum.loadingLabel'),
    onlineTemplate: sourceText(rawForum.onlineTemplate, 'forum.onlineTemplate'),
    offlineTemplate: sourceText(rawForum.offlineTemplate, 'forum.offlineTemplate'),
    lockThreadLabel: sourceText(rawForum.lockThreadLabel, 'forum.lockThreadLabel'),
    unlockThreadLabel: sourceText(rawForum.unlockThreadLabel, 'forum.unlockThreadLabel'),
    stickifyThreadLabel: sourceText(rawForum.stickifyThreadLabel, 'forum.stickifyThreadLabel'),
    unstickifyThreadLabel: sourceText(
      rawForum.unstickifyThreadLabel,
      'forum.unstickifyThreadLabel',
    ),
    confirmLockThread: sourceText(rawForum.confirmLockThread, 'forum.confirmLockThread'),
    confirmUnlockThread: sourceText(rawForum.confirmUnlockThread, 'forum.confirmUnlockThread'),
    confirmStickifyThread: sourceText(
      rawForum.confirmStickifyThread,
      'forum.confirmStickifyThread',
    ),
    confirmUnstickifyThread: sourceText(
      rawForum.confirmUnstickifyThread,
      'forum.confirmUnstickifyThread',
    ),
  };
  const rawContextorial = objectValue(news.contextorial, 'news.contextorial');
  const contextorial = {
    chat: sourceText(rawContextorial.chat, 'contextorial.chat'),
    onlineList: sourceText(rawContextorial.onlineList, 'contextorial.onlineList'),
    guestbookMessage: sourceText(rawContextorial.guestbookMessage, 'contextorial.guestbookMessage'),
    wardrobe: sourceText(rawContextorial.wardrobe, 'contextorial.wardrobe'),
    buyClothes: sourceText(rawContextorial.buyClothes, 'contextorial.buyClothes'),
    trashcan: sourceText(rawContextorial.trashcan, 'contextorial.trashcan'),
  };
  const rawInventory = objectValue(news.inventory, 'news.inventory');
  const inventory = {
    trashcanLabel: sourceText(rawInventory.trashcanLabel, 'inventory.trashcanLabel'),
    trashcanTooltip: sourceText(rawInventory.trashcanTooltip, 'inventory.trashcanTooltip'),
    trashcanCandidateTemplate: sourceText(
      rawInventory.trashcanCandidateTemplate,
      'inventory.trashcanCandidateTemplate',
    ),
    confirmRemove: sourceText(rawInventory.confirmRemove, 'inventory.confirmRemove'),
    levelOneMessage: sourceText(rawInventory.levelOneMessage, 'inventory.levelOneMessage'),
    vipRequired: sourceText(rawInventory.vipRequired, 'inventory.vipRequired'),
    inventoryFullTitle: sourceText(rawInventory.inventoryFullTitle, 'inventory.inventoryFullTitle'),
    inventoryFullMessage: sourceText(
      rawInventory.inventoryFullMessage,
      'inventory.inventoryFullMessage',
    ),
    yesLabel: sourceText(rawInventory.yesLabel, 'inventory.yesLabel'),
    noLabel: sourceText(rawInventory.noLabel, 'inventory.noLabel'),
  };
  const rawLevelInformation = objectValue(news.levelInformation, 'news.levelInformation');
  const levelInformationRows = arrayValue(
    rawLevelInformation.levels,
    'news.levelInformation.levels',
  );
  if (levelInformationRows.length !== 20) {
    throw new ContractError('news.levelInformation.levels must contain 20 entries');
  }
  const levelInformation = {
    levels: levelInformationRows.map((entry, index) => {
      const row = objectValue(entry, `news.levelInformation.levels[${String(index)}]`);
      const level = positiveIntegerValue(
        row.level,
        `news.levelInformation.levels[${String(index)}].level`,
      );
      if (level !== (index + 1) * 10) {
        throw new ContractError('news.levelInformation.levels is not source ordered');
      }
      return {
        level,
        text: sourceText(row.text, `news.levelInformation.levels[${String(index)}].text`),
      };
    }),
  };
  const rows = arrayValue(news.paragraphs, 'news.paragraphs');
  if (rows.length > 20) throw new ContractError('news.paragraphs exceeds 20 entries');
  let totalLength = 0;
  const paragraphs = rows.map((row, index) => {
    const paragraph = boundedText(row, `news.paragraphs[${String(index)}]`, 8_192, false);
    totalLength += paragraph.length;
    return paragraph;
  });
  if (totalLength > 8_192) throw new ContractError('news.paragraphs exceeds 8192 characters');
  return {
    ...(news.refreshAfterMs === undefined
      ? {}
      : {
          refreshAfterMs: nonNegativeIntegerValue(news.refreshAfterMs, 'news.refreshAfterMs'),
        }),
    paragraphs,
    imageAsset,
    language,
    backdropAsset,
    texts,
    accountCreation,
    worldToolbar,
    profile,
    forum,
    contextorial,
    inventory,
    levelInformation,
  };
}

export function parseContextorialCompletionResponse(value: unknown): ContextorialCompletionPayload {
  const root = objectValue(value, 'contextorial completion response');
  assertProtocol(root);
  const contextorial = objectValue(root.contextorial, 'contextorial');
  const completedTypes = arrayValue(contextorial.completedTypes, 'contextorial.completedTypes').map(
    (entry, index) => {
      const type = nonNegativeIntegerValue(entry, `contextorial.completedTypes[${String(index)}]`);
      if (type > 5) throw new ContractError('contextorial.completedTypes contains an unknown type');
      return type;
    },
  );
  if (
    new Set(completedTypes).size !== completedTypes.length ||
    completedTypes.some((type, index) => index > 0 && type <= (completedTypes[index - 1] ?? -1))
  ) {
    throw new ContractError('contextorial.completedTypes is not strictly ordered');
  }
  return { completedTypes };
}

export function parseSessionResumeResponse(
  value: unknown,
  existingToken: string,
): SessionResumePayload {
  const root = objectValue(value, 'session resume response');
  assertProtocol(root);
  const resume = objectValue(root.resume, 'resume');
  const status = stringValue(resume.status, 'resume.status');
  if (status !== 'resumed' && status !== 'connected') {
    throw new ContractError(`Unsupported session resume status: ${status}`);
  }
  const village = objectValue(resume.village, 'resume.village');
  const session = parseSessionResponse(value, existingToken);
  return {
    status,
    village: {
      id: positiveIntegerValue(village.id, 'resume.village.id'),
      name: boundedText(village.name, 'resume.village.name', 8_192, true),
      ownerPlayerId: positiveIntegerValue(resume.ownerPlayerId, 'resume.ownerPlayerId'),
    },
    session,
  };
}

export function parseNordTimeResponse(value: unknown): NordTimePayload {
  const root = objectValue(value, 'Nord time response');
  assertProtocol(root);
  const nordTime = objectValue(root.nordTime, 'nordTime');
  return {
    epochMillis: nonNegativeIntegerValue(nordTime.epochMillis, 'nordTime.epochMillis'),
  };
}

export function parsePasswordChangeResponse(value: unknown): PasswordChangePayload {
  const root = objectValue(value, 'password change response');
  assertProtocol(root);
  const change = objectValue(root.passwordChange, 'passwordChange');
  const status = stringValue(change.status, 'passwordChange.status');
  if (status !== 'updated') {
    throw new ContractError(`Unsupported password change status: ${status}`);
  }
  return {
    status,
    sequence: positiveIntegerValue(change.sequence, 'passwordChange.sequence'),
    replayed: booleanValue(change.replayed, 'passwordChange.replayed'),
    serverTime: nonNegativeIntegerValue(change.serverTime, 'passwordChange.serverTime'),
    session: parseSessionResponse(value),
  };
}

export function parseTerrainResponse(value: unknown): TerrainPayload {
  const root = objectValue(value, 'terrain response');
  assertProtocol(root);
  return parseTerrainPayload(root.terrain, 'terrain');
}

export function parseTerrainWriteResponse(value: unknown): TerrainWritePayload {
  const root = objectValue(value, 'terrain write response');
  assertProtocol(root);
  const write = objectValue(root.terrainWrite, 'terrainWrite');
  const status = stringValue(write.status, 'terrainWrite.status');
  if (status !== 'updated' && status !== 'unchanged') {
    throw new ContractError(`Unsupported terrain write status: ${status}`);
  }
  return {
    status,
    terrain: parseTerrainPayload(write.terrain, 'terrainWrite.terrain'),
  };
}

function parseTerrainPayload(value: unknown, label: string): TerrainPayload {
  const terrain = objectValue(value, label);
  const encoding = stringValue(terrain.heightEncoding, `${label}.heightEncoding`);
  if (encoding !== 'signed-int8-base64') {
    throw new ContractError(`Unsupported terrain height encoding: ${encoding}`);
  }
  if (
    terrain.streamed === true &&
    (!isCreativeVillage(Number(terrain.villageId)) ||
      terrain.sampleWidth !== CREATIVE_WORLD.sampleWidth ||
      terrain.heightSamples !== '')
  ) {
    throw new ContractError('Invalid streamed Community World metadata');
  }
  if (terrain.creativeWorld !== undefined) {
    const world = objectValue(terrain.creativeWorld, `${label}.creativeWorld`);
    if (
      !isCreativeVillage(Number(terrain.villageId)) ||
      world.version !== CREATIVE_WORLD.version ||
      world.seed !== CREATIVE_WORLD.seed ||
      world.sampleWidth !== CREATIVE_WORLD.sampleWidth ||
      world.buildHalfWidth !== CREATIVE_WORLD.buildHalfWidth
    ) {
      throw new ContractError('Creative world version mismatch. Refresh the client.');
    }
  }
  return {
    ...(terrain.yeetEnabled === undefined
      ? {}
      : { yeetEnabled: booleanValue(terrain.yeetEnabled, `${label}.yeetEnabled`) }),
    ...(terrain.adminTruckUnderglow === undefined
      ? {}
      : { adminTruckUnderglow: parseAdminTruckUnderglow(terrain.adminTruckUnderglow) }),
    ...(terrain.enhancedLightingForced === undefined
      ? {}
      : {
          enhancedLightingForced: booleanValue(
            terrain.enhancedLightingForced,
            `${label}.enhancedLightingForced`,
          ),
        }),
    ...(terrain.creativeWorld === undefined ? {} : { creativeWorld: CREATIVE_WORLD }),
    ...(terrain.streamed === true ? { streamed: true } : {}),
    villageId: integerValue(terrain.villageId, `${label}.villageId`),
    theme: integerValue(terrain.theme, `${label}.theme`),
    villageSize: integerValue(terrain.villageSize, `${label}.villageSize`),
    revision: integerValue(terrain.revision, `${label}.revision`),
    sampleWidth: integerValue(terrain.sampleWidth, `${label}.sampleWidth`),
    heightEncoding: encoding,
    heightSamples: stringValue(terrain.heightSamples, `${label}.heightSamples`),
  };
}

export function parseTerrainChanges(value: unknown): TerrainChangesPayload {
  const changes = objectValue(value, 'terrainChanges');
  const chunks = arrayValue(changes.dirtyChunks, 'terrainChanges.dirtyChunks').map((entry) => {
    const chunk = objectValue(entry, 'terrainChanges.chunk');
    const chunkX = nonNegativeIntegerValue(chunk.chunkX, 'terrainChanges.chunkX');
    const chunkZ = nonNegativeIntegerValue(chunk.chunkZ, 'terrainChanges.chunkZ');
    if (chunkX >= 24 || chunkZ >= 24)
      throw new ContractError('Terrain chunk is outside Community World');
    return { chunkX, chunkZ };
  });
  if (chunks.length > 576) throw new ContractError('Terrain changes exceed Community World');
  return {
    fromRevision: nonNegativeIntegerValue(changes.fromRevision, 'terrainChanges.fromRevision'),
    revision: nonNegativeIntegerValue(changes.revision, 'terrainChanges.revision'),
    dirtyChunks: chunks,
    fullRefreshRequired: booleanValue(
      changes.fullRefreshRequired,
      'terrainChanges.fullRefreshRequired',
    ),
  };
}

export function parseTerrainRevisionResponse(value: unknown): TerrainRevisionPayload {
  const root = objectValue(value, 'terrain revision response');
  assertProtocol(root);
  const revision = objectValue(root.terrainRevision, 'terrainRevision');
  return {
    villageId: integerValue(revision.villageId, 'terrainRevision.villageId'),
    revision: integerValue(revision.revision, 'terrainRevision.revision'),
    ...(revision.terrainChanges === undefined
      ? {}
      : { terrainChanges: parseTerrainChanges(revision.terrainChanges) }),
  };
}

export function parseEntityChunkResponse(value: unknown): EntityChunkPayload {
  const root = objectValue(value, 'entity chunk response');
  assertProtocol(root);
  const chunk = objectValue(root.entityChunk, 'entityChunk');
  const buildings = arrayValue(chunk.buildings, 'entityChunk.buildings').map((entry) => {
    const building = objectValue(entry, 'building');
    const interaction =
      building.interaction === null || building.interaction === undefined
        ? null
        : buildingInteractionValue(building.interaction, 'building.interaction');
    const open =
      building.open === null || building.open === undefined
        ? null
        : booleanValue(building.open, 'building.open');
    const petTreatSpecies =
      building.petTreatSpecies === null || building.petTreatSpecies === undefined
        ? undefined
        : petShopSpeciesValue(building.petTreatSpecies, 'building.petTreatSpecies');
    if (interaction === 'open-close' && open === null) {
      throw new ContractError('building.open is required for an open-close interaction');
    }
    if (interaction === 'pet-treat' && petTreatSpecies === undefined) {
      throw new ContractError('building.petTreatSpecies is required for a pet-treat interaction');
    }
    if (interaction !== 'pet-treat' && petTreatSpecies !== undefined) {
      throw new ContractError('building.petTreatSpecies requires a pet-treat interaction');
    }
    return {
      id: integerValue(building.id, 'building.id'),
      type: integerValue(building.type, 'building.type'),
      remainingPoints: numberValue(building.remainingPoints, 'building.remainingPoints'),
      ...(building.placementOffsetX == null
        ? {}
        : {
            placementOffsetX: numberValue(building.placementOffsetX, 'building.placementOffsetX'),
          }),
      ...(building.placementOffsetZ == null
        ? {}
        : {
            placementOffsetZ: numberValue(building.placementOffsetZ, 'building.placementOffsetZ'),
          }),
      ...(building.placementTiltX == null
        ? {}
        : { placementTiltX: numberValue(building.placementTiltX, 'building.placementTiltX') }),
      ...(building.placementTiltZ == null
        ? {}
        : { placementTiltZ: numberValue(building.placementTiltZ, 'building.placementTiltZ') }),
      ...(building.placementY == null
        ? {}
        : { placementY: numberValue(building.placementY, 'building.placementY') }),
      tileX: integerValue(building.tileX, 'building.tileX'),
      tileZ: integerValue(building.tileZ, 'building.tileZ'),
      parameters: stringValue(building.parameters, 'building.parameters'),
      offeringResources:
        building.offeringResources === undefined
          ? []
          : arrayValue(building.offeringResources, 'building.offeringResources').map((r) =>
              productionResource(objectValue(r, 'offering resource'), 'offering resource'),
            ),
      flattenTerrain:
        building.flattenTerrain === undefined
          ? true
          : booleanValue(building.flattenTerrain, 'building.flattenTerrain'),
      entityText:
        building.entityText === undefined
          ? ''
          : boundedText(
              building.entityText,
              'building.entityText',
              LEGACY_ENTITY_TEXT_READ_MAXIMUM_LENGTH,
              false,
            ),
      interaction,
      ...(petTreatSpecies === undefined ? {} : { petTreatSpecies }),
      keyColor:
        building.keyColor === null || building.keyColor === undefined
          ? null
          : keyDoorColorValue(building.keyColor, 'building.keyColor'),
      keyDoorTarget:
        building.keyDoorTarget === undefined
          ? false
          : booleanValue(building.keyDoorTarget, 'building.keyDoorTarget'),
      open,
      pictureId:
        building.pictureId === null
          ? null
          : positiveIntegerValue(building.pictureId, 'building.pictureId'),
      gift: parseGiftBuildingDetails(building.gift, 'building.gift'),
      rotation: integerValue(building.rotation, 'building.rotation'),
      consumed: booleanValue(building.consumed, 'building.consumed'),
      placedOnMap: booleanValue(building.placedOnMap, 'building.placedOnMap'),
      placedAt:
        building.placedAt === undefined
          ? 0
          : nonNegativeIntegerValue(building.placedAt, 'building.placedAt'),
      ready: booleanValue(building.ready, 'building.ready'),
      delayStart: numberValue(building.delayStart, 'building.delayStart'),
      delayEnd: numberValue(building.delayEnd, 'building.delayEnd'),
      ingredients: numberValue(building.ingredients, 'building.ingredients'),
      sellRefund: nonNegativeIntegerValue(building.sellRefund, 'building.sellRefund'),
      readySellRefund: nonNegativeIntegerValue(
        building.readySellRefund,
        'building.readySellRefund',
      ),
    };
  });
  const trees = arrayValue(chunk.trees, 'entityChunk.trees').map((entry) => {
    const tree = objectValue(entry, 'tree');
    return {
      id: integerValue(tree.id, 'tree.id'),
      batch: integerValue(tree.batch, 'tree.batch'),
      type: integerValue(tree.type, 'tree.type'),
      tileX: integerValue(tree.tileX, 'tree.tileX'),
      tileZ: integerValue(tree.tileZ, 'tree.tileZ'),
      scaleByte: integerValue(tree.scaleByte, 'tree.scaleByte'),
      scale: numberValue(tree.scale, 'tree.scale'),
    };
  });
  const raceBuildings =
    chunk.raceBuildings === undefined
      ? undefined
      : arrayValue(chunk.raceBuildings, 'entityChunk.raceBuildings').map((entry) => {
          const building = objectValue(entry, 'raceBuilding');
          return {
            id: integerValue(building.id, 'raceBuilding.id'),
            type: integerValue(building.type, 'raceBuilding.type'),
            tileX: integerValue(building.tileX, 'raceBuilding.tileX'),
            tileZ: integerValue(building.tileZ, 'raceBuilding.tileZ'),
            rotation: integerValue(building.rotation, 'raceBuilding.rotation'),
            consumed: booleanValue(building.consumed, 'raceBuilding.consumed'),
            placedOnMap: booleanValue(building.placedOnMap, 'raceBuilding.placedOnMap'),
          };
        });
  return {
    ...(chunk.terrainChunk === undefined
      ? {}
      : { terrainChunk: parseTerrainChunk(chunk.terrainChunk) }),
    ...(chunk.creativeSpatial === undefined
      ? {}
      : { creativeSpatial: parseCreativeSpatial(chunk.creativeSpatial) }),
    villageId: integerValue(chunk.villageId, 'entityChunk.villageId'),
    revision: integerValue(chunk.revision, 'entityChunk.revision'),
    chunkX: integerValue(chunk.chunkX, 'entityChunk.chunkX'),
    chunkZ: integerValue(chunk.chunkZ, 'entityChunk.chunkZ'),
    minimumTileX: integerValue(chunk.minimumTileX, 'entityChunk.minimumTileX'),
    minimumTileZ: integerValue(chunk.minimumTileZ, 'entityChunk.minimumTileZ'),
    maximumTileXExclusive: integerValue(
      chunk.maximumTileXExclusive,
      'entityChunk.maximumTileXExclusive',
    ),
    maximumTileZExclusive: integerValue(
      chunk.maximumTileZExclusive,
      'entityChunk.maximumTileZExclusive',
    ),
    snapshotBuildingCount: integerValue(
      chunk.snapshotBuildingCount,
      'entityChunk.snapshotBuildingCount',
    ),
    snapshotTreeCount: integerValue(chunk.snapshotTreeCount, 'entityChunk.snapshotTreeCount'),
    buildings,
    ...(raceBuildings === undefined ? {} : { raceBuildings }),
    trees,
  };
}

export function parseRaceBuildingManifestResponse(value: unknown): RaceBuildingManifestPayload {
  const root = objectValue(value, 'race building manifest response');
  assertProtocol(root);
  const manifest = objectValue(root.raceBuildings, 'raceBuildings');
  const buildings = arrayValue(manifest.buildings, 'raceBuildings.buildings').map(
    (entry, index) => {
      const label = `raceBuildings.buildings[${String(index)}]`;
      const building = objectValue(entry, label);
      return {
        id: nonZeroSignedShortValue(building.id, `${label}.id`),
        type: signedShortValue(building.type, `${label}.type`),
        tileX: signedShortValue(building.tileX, `${label}.tileX`),
        tileZ: signedShortValue(building.tileZ, `${label}.tileZ`),
        rotation: rangedIntegerValue(building.rotation, `${label}.rotation`, 0, 3),
        consumed: booleanValue(building.consumed, `${label}.consumed`),
        placedOnMap: booleanValue(building.placedOnMap, `${label}.placedOnMap`),
      };
    },
  );
  if (new Set(buildings.map((building) => building.id)).size !== buildings.length) {
    throw new ContractError('raceBuildings.buildings contains duplicates');
  }
  return {
    villageId: positiveIntegerValue(manifest.villageId, 'raceBuildings.villageId'),
    revision: positiveIntegerValue(manifest.revision, 'raceBuildings.revision'),
    buildings,
  };
}

export function parseRaceHeatResponse(value: unknown): RaceHeatPayload | null {
  const root = objectValue(value, 'race heat response');
  assertProtocol(root);
  if (root.raceHeat === null) return null;
  const heat = objectValue(root.raceHeat, 'raceHeat');
  const statusValue = (value: unknown, label: string): RaceParticipantStatus => {
    const status = stringValue(value, label);
    if (!['enrolled', 'racing', 'finished', 'DNS', 'DNF'].includes(status)) {
      throw new ContractError(`${label} is unsupported`);
    }
    return status as RaceParticipantStatus;
  };
  const participants = arrayValue(heat.participants, 'raceHeat.participants').map(
    (entry, index) => {
      const label = `raceHeat.participants[${String(index)}]`;
      const participant = objectValue(entry, label);
      const finishNonce =
        participant.finishNonce === undefined
          ? undefined
          : stringValue(participant.finishNonce, `${label}.finishNonce`);
      return {
        playerId: positiveIntegerValue(participant.playerId, `${label}.playerId`),
        playerName: stringValue(participant.playerName, `${label}.playerName`),
        startMatBuildingId: nonZeroSignedShortValue(
          participant.startMatBuildingId,
          `${label}.startMatBuildingId`,
        ),
        status: statusValue(participant.status, `${label}.status`),
        reachedCheckpointBuildingIds: arrayValue(
          participant.reachedCheckpointBuildingIds,
          `${label}.reachedCheckpointBuildingIds`,
        ).map((id) => nonZeroSignedShortValue(id, `${label}.reachedCheckpointBuildingIds[]`)),
        elapsedMilliseconds: nonNegativeIntegerValue(
          participant.elapsedMilliseconds,
          `${label}.elapsedMilliseconds`,
        ),
        ...(finishNonce === undefined ? {} : { finishNonce }),
      };
    },
  );
  if (
    participants.length > 16 ||
    new Set(participants.map((p) => p.playerId)).size !== participants.length
  ) {
    throw new ContractError('raceHeat.participants is invalid');
  }
  const completedAt =
    heat.completedAt === null
      ? null
      : nonNegativeIntegerValue(heat.completedAt, 'raceHeat.completedAt');
  const playerCap = rangedIntegerValue(heat.playerCap, 'raceHeat.playerCap', 16, 16) as 16;
  const id = stringValue(heat.id, 'raceHeat.id');
  const greenAt = nonNegativeIntegerValue(heat.greenAt, 'raceHeat.greenAt');
  const realtimeConnection =
    heat.realtimeConnection === undefined
      ? undefined
      : parseRaceRealtimeConnection(heat.realtimeConnection, 'raceHeat.realtimeConnection');
  if (
    realtimeConnection !== undefined &&
    (realtimeConnection.heatId !== id ||
      realtimeConnection.greenAt !== greenAt ||
      !participants.some((participant) => participant.playerId === realtimeConnection.playerId))
  ) {
    throw new ContractError('raceHeat.realtimeConnection is inconsistent');
  }
  return {
    id,
    villageId: positiveIntegerValue(heat.villageId, 'raceHeat.villageId'),
    triggerBuildingId: nonZeroSignedShortValue(
      heat.triggerBuildingId,
      'raceHeat.triggerBuildingId',
    ),
    startBuildingId: nonZeroSignedShortValue(heat.startBuildingId, 'raceHeat.startBuildingId'),
    finishBuildingId: nonZeroSignedShortValue(heat.finishBuildingId, 'raceHeat.finishBuildingId'),
    checkpointBuildingIds: arrayValue(
      heat.checkpointBuildingIds,
      'raceHeat.checkpointBuildingIds',
    ).map((id) => nonZeroSignedShortValue(id, 'raceHeat.checkpointBuildingIds[]')),
    participants,
    countdownAt: nonNegativeIntegerValue(heat.countdownAt, 'raceHeat.countdownAt'),
    greenAt,
    crossingClosesAt: nonNegativeIntegerValue(heat.crossingClosesAt, 'raceHeat.crossingClosesAt'),
    expiresAt: nonNegativeIntegerValue(heat.expiresAt, 'raceHeat.expiresAt'),
    completedAt,
    serverTime: nonNegativeIntegerValue(heat.serverTime, 'raceHeat.serverTime'),
    playerCap,
    ...(realtimeConnection === undefined ? {} : { realtimeConnection }),
  };
}

function parseRaceRealtimeConnection(value: unknown, label: string): RaceRealtimeConnectionPayload {
  const connection = objectValue(value, label);
  const protocol = stringValue(connection.realtimeProtocol, `${label}.realtimeProtocol`);
  if (protocol !== 'nord-race-v2') {
    throw new ContractError('Unsupported race realtime protocol');
  }
  return {
    heatId: stringValue(connection.heatId, `${label}.heatId`),
    playerId: positiveIntegerValue(connection.playerId, `${label}.playerId`),
    greenAt: nonNegativeIntegerValue(connection.greenAt, `${label}.greenAt`),
    realtimeProtocol: 'nord-race-v2',
    realtimePath: stringValue(connection.realtimePath, `${label}.realtimePath`),
    ticket: stringValue(connection.ticket, `${label}.ticket`),
    streamGeneration: positiveIntegerValue(
      connection.streamGeneration,
      `${label}.streamGeneration`,
    ),
    sessionGeneration: positiveIntegerValue(
      connection.sessionGeneration,
      `${label}.sessionGeneration`,
    ),
  };
}

function parseRaceQueueValue(value: unknown, label: string): RaceQueuePayload {
  const queue = objectValue(value, label);
  const participants = arrayValue(queue.participants, `${label}.participants`).map(
    (entry, index) => {
      const participantLabel = `${label}.participants[${String(index)}]`;
      const participant = objectValue(entry, participantLabel);
      return {
        playerId: positiveIntegerValue(participant.playerId, `${participantLabel}.playerId`),
        playerName: stringValue(participant.playerName, `${participantLabel}.playerName`),
        startMatBuildingId: nonZeroSignedShortValue(
          participant.startMatBuildingId,
          `${participantLabel}.startMatBuildingId`,
        ),
      };
    },
  );
  if (
    participants.length === 0 ||
    participants.length > 16 ||
    new Set(participants.map((participant) => participant.playerId)).size !== participants.length
  ) {
    throw new ContractError(`${label}.participants is invalid`);
  }
  const startMatBuildingIds = arrayValue(
    queue.startMatBuildingIds,
    `${label}.startMatBuildingIds`,
  ).map((id) => nonZeroSignedShortValue(id, `${label}.startMatBuildingIds[]`));
  if (
    startMatBuildingIds.length !== participants.length ||
    !startMatBuildingIds.every((id, index) => id === participants[index]?.startMatBuildingId)
  ) {
    throw new ContractError(`${label}.startMatBuildingIds is inconsistent`);
  }
  return {
    id: stringValue(queue.id, `${label}.id`),
    villageId: positiveIntegerValue(queue.villageId, `${label}.villageId`),
    triggerBuildingId: nonZeroSignedShortValue(
      queue.triggerBuildingId,
      `${label}.triggerBuildingId`,
    ),
    participants,
    startMatBuildingIds,
    enqueuedAt: nonNegativeIntegerValue(queue.enqueuedAt, `${label}.enqueuedAt`),
    position: rangedIntegerValue(queue.position, `${label}.position`, 1, 5),
    serverTime: nonNegativeIntegerValue(queue.serverTime, `${label}.serverTime`),
    activeCap: rangedIntegerValue(queue.activeCap, `${label}.activeCap`, 5, 5) as 5,
    queueCap: rangedIntegerValue(queue.queueCap, `${label}.queueCap`, 5, 5) as 5,
  };
}

export function parseRaceQueueResponse(value: unknown): RaceQueuePayload | null {
  const root = objectValue(value, 'race queue response');
  assertProtocol(root);
  return root.raceQueue === null ? null : parseRaceQueueValue(root.raceQueue, 'raceQueue');
}

export function parseRaceStartResponse(value: unknown): RaceStartPayload {
  const root = objectValue(value, 'race start response');
  assertProtocol(root);
  const start = objectValue(root.raceStart, 'raceStart');
  const status = stringValue(start.status, 'raceStart.status');
  if (status === 'started') {
    const raceHeat = parseRaceHeatResponse({
      protocolVersion: root.protocolVersion,
      raceHeat: start.raceHeat,
    });
    if (raceHeat === null || start.raceQueue !== undefined) {
      throw new ContractError('raceStart started payload is invalid');
    }
    return { status, raceHeat };
  }
  if (status === 'queued') {
    if (start.raceHeat !== undefined)
      throw new ContractError('raceStart queued payload is invalid');
    return { status, raceQueue: parseRaceQueueValue(start.raceQueue, 'raceStart.raceQueue') };
  }
  throw new ContractError('raceStart.status is unsupported');
}

export function parseCurrentRaceResponse(value: unknown): CurrentRacePayload | null {
  const root = objectValue(value, 'current race response');
  assertProtocol(root);
  const heat =
    root.raceHeat === null
      ? null
      : parseRaceHeatResponse({ protocolVersion: root.protocolVersion, raceHeat: root.raceHeat });
  const queue = root.raceQueue === null ? null : parseRaceQueueValue(root.raceQueue, 'raceQueue');
  if (heat !== null && queue !== null)
    throw new ContractError('current race response is ambiguous');
  if (heat !== null) return { status: 'started', raceHeat: heat };
  if (queue !== null) return { status: 'queued', raceQueue: queue };
  return null;
}

function parseGiftBuildingDetails(
  value: unknown,
  label: string,
): GiftBuildingDetailsPayload | null {
  if (value === null) return null;
  const gift = objectValue(value, label);
  const wrappingType =
    gift.wrappingType === null
      ? null
      : rangedIntegerValue(gift.wrappingType, `${label}.wrappingType`, 1, 32_767);
  const canOpenAt = nullableNonNegativeInteger(gift.canOpenAt, `${label}.canOpenAt`);
  if (canOpenAt !== null && wrappingType === null) {
    throw new ContractError(`${label}.canOpenAt requires wrappingType`);
  }
  return {
    senderPlayerId: positiveIntegerValue(gift.senderPlayerId, `${label}.senderPlayerId`),
    text: stringValue(gift.text, `${label}.text`),
    wrappingType,
    canOpenAt,
  };
}

export function parseEntityRevisionResponse(value: unknown): EntityRevisionPayload {
  const root = objectValue(value, 'entity revision response');
  assertProtocol(root);
  const revision = objectValue(root.entityRevision, 'entityRevision');
  return {
    villageId: integerValue(revision.villageId, 'entityRevision.villageId'),
    revision: integerValue(revision.revision, 'entityRevision.revision'),
  };
}

export function parseDecorationOccupancyResponse(value: unknown): DecorationOccupancyPayload {
  const root = objectValue(value, 'decoration occupancy response');
  assertProtocol(root);
  const occupancy = objectValue(root.decorationOccupancy, 'decorationOccupancy');
  const encoding = stringValue(occupancy.encoding, 'decorationOccupancy.encoding');
  if (encoding !== 'tw-bitset-base64') {
    throw new ContractError(`Unsupported decoration occupancy encoding: ${encoding}`);
  }
  const coverage = stringValue(occupancy.coverage, 'decorationOccupancy.coverage');
  if (coverage !== 'buildings-and-trees') {
    throw new ContractError(`Unsupported decoration occupancy coverage: ${coverage}`);
  }
  const buildingOccupiedCells = occupancy.buildingOccupiedCells;
  const creativeBlockedCells = occupancy.creativeBlockedCells;
  const growLightPlots =
    occupancy.growLightPlots === undefined
      ? undefined
      : arrayValue(occupancy.growLightPlots, 'decorationOccupancy.growLightPlots').map(
          (value, index) => {
            const label = `decorationOccupancy.growLightPlots[${String(index)}]`;
            const plot = objectValue(value, label);
            const occupant = stringValue(plot.occupant, `${label}.occupant`);
            if (occupant !== 'plant' && occupant !== 'light') {
              throw new ContractError(`${label}.occupant must be plant or light`);
            }
            return {
              tileX: signedShortValue(plot.tileX, `${label}.tileX`),
              tileZ: signedShortValue(plot.tileZ, `${label}.tileZ`),
              occupant,
            } as const;
          },
        );
  if (
    growLightPlots !== undefined &&
    (growLightPlots.length >
      nonNegativeIntegerValue(
        occupancy.sourcePlacedBuildingCount,
        'decorationOccupancy.sourcePlacedBuildingCount',
      ) ||
      new Set(growLightPlots.map((plot) => `${String(plot.tileX)}:${String(plot.tileZ)}`)).size !==
        growLightPlots.length)
  ) {
    throw new ContractError(
      'decorationOccupancy.growLightPlots contains duplicate or excess plots',
    );
  }
  return {
    villageId: integerValue(occupancy.villageId, 'decorationOccupancy.villageId'),
    revision: integerValue(occupancy.revision, 'decorationOccupancy.revision'),
    gridWidth: integerValue(occupancy.gridWidth, 'decorationOccupancy.gridWidth'),
    encoding,
    coverage,
    sourceTreeCount: nonNegativeIntegerValue(
      occupancy.sourceTreeCount,
      'decorationOccupancy.sourceTreeCount',
    ),
    sourcePlacedBuildingCount: nonNegativeIntegerValue(
      occupancy.sourcePlacedBuildingCount,
      'decorationOccupancy.sourcePlacedBuildingCount',
    ),
    occupiedCells: stringValue(occupancy.occupiedCells, 'decorationOccupancy.occupiedCells'),
    ...(growLightPlots === undefined ? {} : { growLightPlots }),
    ...(creativeBlockedCells === undefined
      ? {}
      : {
          creativeBlockedCells: stringValue(
            creativeBlockedCells,
            'decorationOccupancy.creativeBlockedCells',
          ),
        }),
    ...(buildingOccupiedCells === undefined
      ? {}
      : {
          buildingOccupiedCells: stringValue(
            buildingOccupiedCells,
            'decorationOccupancy.buildingOccupiedCells',
          ),
        }),
  };
}

export function parseObjectCollisionResponse(value: unknown): ObjectCollisionPayload {
  const root = objectValue(value, 'object collision response');
  assertProtocol(root);
  const collision = objectValue(root.objectCollision, 'objectCollision');
  const encoding = stringValue(collision.encoding, 'objectCollision.encoding');
  if (encoding !== 'tw-bitset-base64') {
    throw new ContractError(`Unsupported object collision encoding: ${encoding}`);
  }
  const coverage = stringValue(collision.coverage, 'objectCollision.coverage');
  if (coverage !== 'buildings-and-trees') {
    throw new ContractError(`Unsupported object collision coverage: ${coverage}`);
  }
  const noboHouses =
    collision.noboHouses === undefined
      ? []
      : arrayValue(collision.noboHouses, 'objectCollision.noboHouses').map((value, index) => {
          const house = objectValue(value, `objectCollision.noboHouses[${String(index)}]`);
          const label = `objectCollision.noboHouses[${String(index)}]`;
          return {
            buildingId: integerValue(house.buildingId, `${label}.buildingId`),
            tileX: integerValue(house.tileX, `${label}.tileX`),
            tileZ: integerValue(house.tileZ, `${label}.tileZ`),
            tileWidth: positiveIntegerValue(house.tileWidth, `${label}.tileWidth`),
            tileDepth: positiveIntegerValue(house.tileDepth, `${label}.tileDepth`),
          };
        });
  return {
    villageId: integerValue(collision.villageId, 'objectCollision.villageId'),
    revision: integerValue(collision.revision, 'objectCollision.revision'),
    gridWidth: integerValue(collision.gridWidth, 'objectCollision.gridWidth'),
    encoding,
    coverage,
    sourceTreeCount: nonNegativeIntegerValue(
      collision.sourceTreeCount,
      'objectCollision.sourceTreeCount',
    ),
    sourcePlacedBuildingCount: nonNegativeIntegerValue(
      collision.sourcePlacedBuildingCount,
      'objectCollision.sourcePlacedBuildingCount',
    ),
    regularBlockedCells: stringValue(
      collision.regularBlockedCells,
      'objectCollision.regularBlockedCells',
    ),
    vipBlockedCells: stringValue(collision.vipBlockedCells, 'objectCollision.vipBlockedCells'),
    noboHouses,
  };
}

export function parseTabletopDrag(value: unknown): TabletopDragPayload {
  const drag = objectValue(value, 'tabletopDrag');
  return {
    ...(drag.groundStamp === undefined
      ? {}
      : { groundStamp: stringValue(drag.groundStamp, 'tabletopDrag.groundStamp') }),
    ...(drag.tilt === undefined ? {} : { tilt: numberValue(drag.tilt, 'tabletopDrag.tilt') }),
    ...(drag.yaw === undefined ? {} : { yaw: numberValue(drag.yaw, 'tabletopDrag.yaw') }),
    tableId: nonZeroSignedShortValue(drag.tableId, 'tabletopDrag.tableId'),
    pieceId: positiveIntegerValue(drag.pieceId, 'tabletopDrag.pieceId'),
    playerId: positiveIntegerValue(drag.playerId, 'tabletopDrag.playerId'),
    revision: positiveIntegerValue(drag.revision, 'tabletopDrag.revision'),
    dragId: stringValue(drag.dragId, 'tabletopDrag.dragId'),
    sequence: positiveIntegerValue(drag.sequence, 'tabletopDrag.sequence'),
    x: numberValue(drag.x, 'tabletopDrag.x'),
    y: numberValue(drag.y, 'tabletopDrag.y'),
    z: numberValue(drag.z, 'tabletopDrag.z'),
    held: booleanValue(drag.held, 'tabletopDrag.held'),
    remainingMilliseconds: nonNegativeIntegerValue(
      drag.remainingMilliseconds,
      'tabletopDrag.remainingMilliseconds',
    ),
  };
}

export function parseDiceRoll(value: unknown): DiceRollPayload {
  const roll = objectValue(value, 'diceRoll');
  const face = (value: unknown): number => {
    const result = positiveIntegerValue(value, 'diceRoll face');
    if (result > 6) throw new ContractError('diceRoll face exceeds 6');
    return result;
  };
  return {
    buildingId: nonZeroSignedShortValue(roll.buildingId, 'diceRoll.buildingId'),
    ...(roll.pieceId === undefined
      ? {}
      : { pieceId: nonNegativeIntegerValue(roll.pieceId, 'diceRoll.pieceId') }),
    placedAt: nonNegativeIntegerValue(roll.placedAt, 'diceRoll.placedAt'),
    startedAt: positiveIntegerValue(roll.startedAt, 'diceRoll.startedAt'),
    value: face(roll.value),
    previousValue: face(roll.previousValue),
    elapsedMilliseconds: nonNegativeIntegerValue(
      roll.elapsedMilliseconds,
      'diceRoll.elapsedMilliseconds',
    ),
  };
}

export function parseDiceRollResponse(value: unknown): DiceRollPayload {
  const root = objectValue(value, 'dice roll response');
  assertProtocol(root);
  return parseDiceRoll(root.diceRoll);
}

export function parsePresenceResponse(value: unknown): VillagePresencePayload {
  const root = objectValue(value, 'presence response');
  assertProtocol(root);
  return parsePresencePayload(root.presence, 'presence');
}

export function parseCharactersResponse(value: unknown): VillageCharactersPayload {
  const root = objectValue(value, 'characters response');
  assertProtocol(root);
  return parseCharactersPayload(root.characters, 'characters');
}

function parseCharactersPayload(value: unknown, label: string): VillageCharactersPayload {
  const snapshot = objectValue(value, label);
  const entries = arrayValue(snapshot.characters, `${label}.characters`);
  if (entries.length > 4_096) {
    throw new ContractError(`${label}.characters exceeds the bounded snapshot size`);
  }
  const characters = entries.map((entry, index) => {
    const characterLabel = `${label}.characters[${String(index)}]`;
    const character = objectValue(entry, characterLabel);
    const pet = booleanValue(character.pet, `${characterLabel}.pet`);
    const petType = signedShortValue(character.petType, `${characterLabel}.petType`);
    const petNetworkId = signedShortValue(character.petNetworkId, `${characterLabel}.petNetworkId`);
    const petName = stringValue(character.petName, `${characterLabel}.petName`);
    const petLevel =
      character.petLevel === undefined
        ? pet
          ? 1
          : 0
        : rangedIntegerValue(character.petLevel, `${characterLabel}.petLevel`, 0, 7);
    const petPointsWithinLevel =
      character.petPointsWithinLevel === undefined
        ? 0
        : rangedIntegerValue(
            character.petPointsWithinLevel,
            `${characterLabel}.petPointsWithinLevel`,
            0,
            320,
          );
    if (pet !== (petType > 0 || petNetworkId !== 0 || petName.trim().length > 0)) {
      throw new ContractError(`${characterLabel}.pet contradicts its decoded metadata`);
    }
    if ((!pet && (petLevel !== 0 || petPointsWithinLevel !== 0)) || (pet && petLevel === 0)) {
      throw new ContractError(`${characterLabel}.pet progression contradicts its decoded metadata`);
    }
    return {
      characterId: signedShortValue(character.characterId, `${characterLabel}.characterId`),
      ...(character.ownerPlayerId === undefined
        ? {}
        : {
            ownerPlayerId: positiveIntegerValue(
              character.ownerPlayerId,
              `${characterLabel}.ownerPlayerId`,
            ),
          }),
      posX: worldPositionValue(character.posX, `${characterLabel}.posX`),
      posZ: worldPositionValue(character.posZ, `${characterLabel}.posZ`),
      rotation:
        character.rotation === undefined
          ? 16_384
          : signedShortValue(character.rotation, `${characterLabel}.rotation`),
      data: stringValue(character.data, `${characterLabel}.data`),
      familyId: signedShortValue(character.familyId, `${characterLabel}.familyId`),
      homeId: signedShortValue(character.homeId, `${characterLabel}.homeId`),
      inBuildingId: signedShortValue(character.inBuildingId, `${characterLabel}.inBuildingId`),
      firstName: stringValue(character.firstName, `${characterLabel}.firstName`),
      surname: stringValue(character.surname, `${characterLabel}.surname`),
      type: signedShortValue(character.type, `${characterLabel}.type`),
      consumed: booleanValue(character.consumed, `${characterLabel}.consumed`),
      pet,
      petType,
      petNetworkId,
      petName,
      petLevel,
      petPointsWithinLevel,
      mountType: signedShortValue(character.mountType, `${characterLabel}.mountType`),
      ...parseOptionalCustomVehicle(character.customVehicle, `${characterLabel}.customVehicle`),
      ...(character.underglow === undefined
        ? {}
        : {
            underglow: parseAdminTruckUnderglow(character.underglow, `${characterLabel}.underglow`),
          }),
      mountName:
        character.mountName === undefined
          ? ''
          : stringValue(character.mountName, `${characterLabel}.mountName`),
      noboType:
        character.noboType === undefined
          ? 0
          : signedShortValue(character.noboType, `${characterLabel}.noboType`),
    };
  });
  const characterIds = new Set(characters.map((character) => character.characterId));
  if (characterIds.size !== characters.length) {
    throw new ContractError(`${label}.characters contains duplicate IDs`);
  }
  return {
    streamGeneration: positiveIntegerValue(snapshot.streamGeneration, `${label}.streamGeneration`),
    villageId: positiveIntegerValue(snapshot.villageId, `${label}.villageId`),
    revision: positiveIntegerValue(snapshot.revision, `${label}.revision`),
    characters,
  };
}

export function parseMovementSubmissionResponse(value: unknown): MovementSubmissionPayload {
  const root = objectValue(value, 'movement submission response');
  assertProtocol(root);
  const submission = objectValue(root.movementSubmission, 'movementSubmission');
  const status = stringValue(submission.status, 'movementSubmission.status');
  if (status !== 'submitted') {
    throw new ContractError(`Unsupported movement submission status: ${status}`);
  }
  const raceAdmission =
    submission.raceAdmission === undefined
      ? undefined
      : ((): RaceAdmissionPayload => {
          const admission = objectValue(
            submission.raceAdmission,
            'movementSubmission.raceAdmission',
          );
          const protocol = stringValue(
            admission.realtimeProtocol,
            'movementSubmission.raceAdmission.realtimeProtocol',
          );
          if (protocol !== 'nord-race-v2')
            throw new ContractError('Unsupported race realtime protocol');
          return {
            heatId: stringValue(admission.heatId, 'movementSubmission.raceAdmission.heatId'),
            playerId: positiveIntegerValue(
              admission.playerId,
              'movementSubmission.raceAdmission.playerId',
            ),
            admittedAt: nonNegativeIntegerValue(
              admission.admittedAt,
              'movementSubmission.raceAdmission.admittedAt',
            ),
            greenAt: nonNegativeIntegerValue(
              admission.greenAt,
              'movementSubmission.raceAdmission.greenAt',
            ),
            authoritativePose: parseMovementPose(
              admission.authoritativePose,
              'movementSubmission.raceAdmission.authoritativePose',
            ),
            realtimeProtocol: 'nord-race-v2',
            realtimePath: stringValue(
              admission.realtimePath,
              'movementSubmission.raceAdmission.realtimePath',
            ),
            ticket: stringValue(admission.ticket, 'movementSubmission.raceAdmission.ticket'),
            streamGeneration: positiveIntegerValue(
              admission.streamGeneration,
              'movementSubmission.raceAdmission.streamGeneration',
            ),
            sessionGeneration: positiveIntegerValue(
              admission.sessionGeneration,
              'movementSubmission.raceAdmission.sessionGeneration',
            ),
          };
        })();
  const footballAdmission =
    submission.footballAdmission === undefined
      ? undefined
      : parseFootballAdmission(
          submission.footballAdmission,
          'movementSubmission.footballAdmission',
        );
  const physicsLease =
    submission.physicsLease === undefined
      ? undefined
      : parsePhysicsLease(submission.physicsLease, 'movementSubmission.physicsLease');
  const footballContactBlocked =
    submission.footballContactBlocked === undefined
      ? undefined
      : (() => {
          const blocked = objectValue(
            submission.footballContactBlocked,
            'movementSubmission.footballContactBlocked',
          );
          return {
            playerCount: positiveIntegerValue(
              blocked.playerCount,
              'movementSubmission.footballContactBlocked.playerCount',
            ),
            playerLimit: positiveIntegerValue(
              blocked.playerLimit,
              'movementSubmission.footballContactBlocked.playerLimit',
            ),
          };
        })();
  const footballContactAcknowledgements =
    submission.footballContactAcknowledgements === undefined
      ? []
      : arrayValue(
          submission.footballContactAcknowledgements,
          'movementSubmission.footballContactAcknowledgements',
        ).map((buildingId, index) =>
          nonZeroSignedShortValue(
            buildingId,
            `movementSubmission.footballContactAcknowledgements[${String(index)}]`,
          ),
        );
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'movementSubmission.sequence'),
    authoritativePose: parseMovementPose(
      submission.authoritativePose,
      'movementSubmission.authoritativePose',
    ),
    footballContactAcknowledgements,
    ...(raceAdmission === undefined ? {} : { raceAdmission }),
    ...(footballAdmission === undefined ? {} : { footballAdmission }),
    ...(physicsLease === undefined ? {} : { physicsLease }),
    ...(footballContactBlocked === undefined ? {} : { footballContactBlocked }),
    ...(submission.footballParticipantPoses === undefined
      ? {}
      : {
          footballParticipantPoses: arrayValue(
            submission.footballParticipantPoses,
            'movementSubmission.footballParticipantPoses',
          ).map((pose, index) =>
            parseMovementPose(
              pose,
              `movementSubmission.footballParticipantPoses[${String(index)}]`,
            ),
          ),
        }),
    ...(submission.ballUpdate === undefined
      ? {}
      : { ballUpdate: parseBallUpdate(submission.ballUpdate, 'movementSubmission.ballUpdate') }),
  };
}

export function parseFootballAdmission(
  value: unknown,
  label = 'footballAdmission',
): FootballAdmissionPayload {
  const admission = objectValue(value, label);
  const protocol = stringValue(admission.realtimeProtocol, `${label}.realtimeProtocol`);
  if (protocol !== 'nord-football-v1')
    throw new ContractError('Unsupported football realtime protocol');
  const admittedAt = nonNegativeIntegerValue(admission.admittedAt, `${label}.admittedAt`);
  const expiresAt = nonNegativeIntegerValue(admission.expiresAt, `${label}.expiresAt`);
  if (expiresAt <= admittedAt) throw new ContractError(`${label}.expiresAt must follow admittedAt`);
  return {
    playerId: positiveIntegerValue(admission.playerId, `${label}.playerId`),
    villageId: positiveIntegerValue(admission.villageId, `${label}.villageId`),
    admittedAt,
    expiresAt,
    authoritativePose: parseMovementPose(admission.authoritativePose, `${label}.authoritativePose`),
    realtimeProtocol: 'nord-football-v1',
    realtimePath: stringValue(admission.realtimePath, `${label}.realtimePath`),
    ticket: stringValue(admission.ticket, `${label}.ticket`),
    streamGeneration: positiveIntegerValue(admission.streamGeneration, `${label}.streamGeneration`),
    sessionGeneration: positiveIntegerValue(
      admission.sessionGeneration,
      `${label}.sessionGeneration`,
    ),
  };
}

export function parsePhysicsLease(value: unknown, label = 'physicsLease'): PhysicsLeasePayload {
  const lease = objectValue(value, label);
  const scope = physicsScopeValue(lease.scope, `${label}.scope`);
  const realtimePath = stringValue(lease.realtimePath, `${label}.realtimePath`);
  const realtimeProtocol = stringValue(lease.realtimeProtocol, `${label}.realtimeProtocol`);
  if (realtimePath !== '/api/v2/physics-realtime' || realtimeProtocol !== 'nord-physics-v1') {
    throw new ContractError(`${label} has an unsupported transport`);
  }
  const allowedBodyIds = arrayValue(lease.allowedBodyIds, `${label}.allowedBodyIds`).map(
    (id, index) => nonZeroSafeIntegerValue(id, `${label}.allowedBodyIds[${String(index)}]`),
  );
  if (
    allowedBodyIds.length === 0 ||
    allowedBodyIds.length > 16 ||
    new Set(allowedBodyIds).size !== allowedBodyIds.length
  ) {
    throw new ContractError(`${label}.allowedBodyIds is invalid`);
  }
  const result: PhysicsLeasePayload = {
    villageId: positiveIntegerValue(lease.villageId, `${label}.villageId`),
    sessionId: boundedText(lease.sessionId, `${label}.sessionId`, 128, true),
    scope,
    scopeId: nonZeroSafeIntegerValue(lease.scopeId, `${label}.scopeId`),
    playerId: positiveIntegerValue(lease.playerId, `${label}.playerId`),
    sessionGeneration: positiveIntegerValue(lease.sessionGeneration, `${label}.sessionGeneration`),
    epoch: positiveIntegerValue(lease.epoch, `${label}.epoch`),
    sequence: nonNegativeIntegerValue(lease.sequence, `${label}.sequence`),
    expiresAt: positiveIntegerValue(lease.expiresAt, `${label}.expiresAt`),
    ticket: boundedText(lease.ticket, `${label}.ticket`, 256, true),
    realtimePath: '/api/v2/physics-realtime',
    realtimeProtocol: 'nord-physics-v1',
    allowedBodyIds,
    transferred: booleanValue(lease.transferred, `${label}.transferred`),
    ...(lease.checkpoint === undefined
      ? {}
      : { checkpoint: parsePhysicsSnapshotBatch(lease.checkpoint, `${label}.checkpoint`) }),
  };
  if (
    result.checkpoint !== undefined &&
    (result.checkpoint.villageId !== result.villageId ||
      result.checkpoint.sessionId !== result.sessionId ||
      result.checkpoint.scopeId !== result.scopeId ||
      result.checkpoint.epoch > result.epoch)
  ) {
    throw new ContractError(`${label}.checkpoint does not precede the lease`);
  }
  return result;
}

export function parsePhysicsSnapshotBatch(
  value: unknown,
  label = 'physicsSnapshot',
): PhysicsSnapshotBatchPayload {
  const batch = objectValue(value, label);
  const bodies = arrayValue(batch.bodies, `${label}.bodies`).map((entry, index) =>
    parsePhysicsBodySnapshot(entry, `${label}.bodies[${String(index)}]`),
  );
  if (
    bodies.length === 0 ||
    bodies.length > 16 ||
    new Set(bodies.map(({ id }) => id)).size !== bodies.length
  ) {
    throw new ContractError(`${label}.bodies is invalid`);
  }
  return {
    villageId: positiveIntegerValue(batch.villageId, `${label}.villageId`),
    sessionId: boundedText(batch.sessionId, `${label}.sessionId`, 128, true),
    scope: physicsScopeValue(batch.scope, `${label}.scope`),
    scopeId: nonZeroSafeIntegerValue(batch.scopeId, `${label}.scopeId`),
    playerId: positiveIntegerValue(batch.playerId, `${label}.playerId`),
    sessionGeneration: positiveIntegerValue(batch.sessionGeneration, `${label}.sessionGeneration`),
    epoch: positiveIntegerValue(batch.epoch, `${label}.epoch`),
    sequence: positiveIntegerValue(batch.sequence, `${label}.sequence`),
    clientTime: nonNegativeIntegerValue(batch.clientTime, `${label}.clientTime`),
    bodies,
  };
}

function parsePhysicsBodySnapshot(value: unknown, label: string): PhysicsBodySnapshotPayload {
  const body = objectValue(value, label);
  return {
    id: nonZeroSafeIntegerValue(body.id, `${label}.id`),
    position: parsePhysicsVector(body.position, `${label}.position`),
    rotation: parsePhysicsQuaternion(body.rotation, `${label}.rotation`),
    linearVelocity: parsePhysicsVector(body.linearVelocity, `${label}.linearVelocity`),
    angularVelocity: parsePhysicsVector(body.angularVelocity, `${label}.angularVelocity`),
    sleeping: booleanValue(body.sleeping, `${label}.sleeping`),
  };
}

function parsePhysicsVector(value: unknown, label: string): PhysicsVectorPayload {
  const vector = objectValue(value, label);
  return {
    x: integerValue(vector.x, `${label}.x`),
    y: integerValue(vector.y, `${label}.y`),
    z: integerValue(vector.z, `${label}.z`),
  };
}

function parsePhysicsQuaternion(value: unknown, label: string): PhysicsQuaternionPayload {
  const quaternion = objectValue(value, label);
  return {
    ...parsePhysicsVector(quaternion, label),
    w: integerValue(quaternion.w, `${label}.w`),
  };
}

function physicsScopeValue(value: unknown, label: string): PhysicsScopePayload {
  const scope = stringValue(value, label);
  if (scope === 'football') return scope;
  throw new ContractError(`${label} is unsupported`);
}

function parseBallVector(value: unknown, label: string): BallVectorPayload {
  const vector = objectValue(value, label);
  return {
    x: numberValue(vector.x, `${label}.x`),
    y: numberValue(vector.y, `${label}.y`),
    z: numberValue(vector.z, `${label}.z`),
  };
}

export function parseBallUpdate(value: unknown, label = 'balls'): BallUpdatePayload {
  const update = objectValue(value, label);
  const balls = arrayValue(update.balls, `${label}.balls`).map((value, index): BallPosePayload => {
    const ballLabel = `${label}.balls[${String(index)}]`;
    const ball = objectValue(value, ballLabel);
    const rotation = objectValue(ball.rotation, `${ballLabel}.rotation`);
    return {
      buildingId: nonZeroSignedShortValue(ball.buildingId, `${ballLabel}.buildingId`),
      position: parseBallVector(ball.position, `${ballLabel}.position`),
      velocity: parseBallVector(ball.velocity, `${ballLabel}.velocity`),
      rotation: {
        ...parseBallVector(rotation, `${ballLabel}.rotation`),
        w: numberValue(rotation.w, `${ballLabel}.rotation.w`),
      },
      sleeping: booleanValue(ball.sleeping, `${ballLabel}.sleeping`),
    };
  });
  if (balls.length > 3) throw new ContractError(`${label}.balls exceeds the village limit`);
  if (new Set(balls.map(({ buildingId }) => buildingId)).size !== balls.length)
    throw new ContractError(`${label}.balls contains duplicate buildings`);
  const respawn =
    update.respawn === undefined
      ? undefined
      : (() => {
          if (!booleanValue(update.respawn, `${label}.respawn`))
            throw new ContractError(`${label}.respawn must be true when present`);
          return true as const;
        })();
  const football =
    update.football === undefined
      ? undefined
      : (() => {
          const state = objectValue(update.football, `${label}.football`);
          const active = booleanValue(state.active, `${label}.football.active`);
          const expiresAt = nonNegativeIntegerValue(state.expiresAt, `${label}.football.expiresAt`);
          if (active && expiresAt === 0)
            throw new ContractError(`${label}.football.expiresAt must be positive while active`);
          return {
            active,
            expiresAt,
            playerLimit: positiveIntegerValue(state.playerLimit, `${label}.football.playerLimit`),
            playerCount: nonNegativeIntegerValue(
              state.playerCount,
              `${label}.football.playerCount`,
            ),
          };
        })();
  return {
    villageId: positiveIntegerValue(update.villageId, `${label}.villageId`),
    streamGeneration: positiveIntegerValue(update.streamGeneration, `${label}.streamGeneration`),
    revision: positiveIntegerValue(update.revision, `${label}.revision`),
    serverTime: nonNegativeIntegerValue(update.serverTime, `${label}.serverTime`),
    balls,
    ...(respawn === undefined ? {} : { respawn }),
    ...(football === undefined ? {} : { football }),
  };
}

export function parseBallsResponse(value: unknown): BallUpdatePayload {
  const root = objectValue(value, 'balls response');
  assertProtocol(root);
  return parseBallUpdate(root.balls, 'balls');
}

function parseSumoStatus(value: unknown, label: string): SumoStatusPayload {
  const status = objectValue(value, label);
  const phase = status.phase;
  if (phase !== 'waiting' && phase !== 'countdown' && phase !== 'active' && phase !== 'finished')
    throw new ContractError(`${label}.phase is invalid`);
  return {
    ringId: nonZeroSignedShortValue(status.ringId, `${label}.ringId`),
    phase,
    remaining: nonNegativeIntegerValue(status.remaining, `${label}.remaining`),
    countdown: nonNegativeIntegerValue(status.countdown, `${label}.countdown`),
    eliminated: booleanValue(status.eliminated, `${label}.eliminated`),
    spectating: booleanValue(status.spectating, `${label}.spectating`),
    winnerId: nonNegativeIntegerValue(status.winnerId, `${label}.winnerId`),
    winnerName: stringValue(status.winnerName, `${label}.winnerName`),
    spinMilliseconds: rangedIntegerValue(
      status.spinMilliseconds,
      `${label}.spinMilliseconds`,
      0,
      650,
    ),
    velocityX: numberValue(status.velocityX, `${label}.velocityX`),
    velocityZ: numberValue(status.velocityZ, `${label}.velocityZ`),
  };
}

function parseMovementPose(value: unknown, label: string): MovementPosePayload {
  const pose = objectValue(value, label);
  if (pose.juggling !== undefined && pose.juggling !== null) {
    throw new ContractError(`${label}.juggling must be null`);
  }
  const sit =
    pose.sit === undefined || pose.sit === null
      ? null
      : (() => {
          const entry = objectValue(pose.sit, `${label}.sit`);
          return {
            buildingId: nonZeroSignedShortValue(entry.buildingId, `${label}.sit.buildingId`),
            action: avatarSitActionValue(entry.action, `${label}.sit.action`),
            ...(entry.swingIntensity === undefined
              ? {}
              : {
                  swingIntensity: rangedIntegerValue(
                    entry.swingIntensity,
                    `${label}.sit.swingIntensity`,
                    0,
                    100,
                  ),
                }),
            ...(entry.swingAngleMilli === undefined
              ? {}
              : {
                  swingAngleMilli: rangedIntegerValue(
                    entry.swingAngleMilli,
                    `${label}.sit.swingAngleMilli`,
                    -2_000_000_000,
                    2_000_000_000,
                  ),
                }),
            ...(entry.swingVelocityMilli === undefined
              ? {}
              : {
                  swingVelocityMilli: rangedIntegerValue(
                    entry.swingVelocityMilli,
                    `${label}.sit.swingVelocityMilli`,
                    -6_500,
                    6_500,
                  ),
                }),
          };
        })();
  const away = pose.away === undefined ? false : booleanValue(pose.away, `${label}.away`);
  const emote =
    pose.emote === undefined || pose.emote === null
      ? null
      : avatarEmoteActionValue(pose.emote, `${label}.emote`);
  if (
    (sit?.action === 'sit-afk' && !away) ||
    (away && sit !== null && sit.action !== 'sit-afk') ||
    (emote !== null && (away || sit !== null))
  ) {
    throw new ContractError(`${label} has contradictory action state`);
  }
  const parsed: MovementPosePayload = {
    ...(pose.sumo == null ? {} : { sumo: parseSumoStatus(pose.sumo, `${label}.sumo`) }),
    playerId: positiveIntegerValue(pose.playerId, `${label}.playerId`),
    posX: worldPositionValue(pose.posX, `${label}.posX`),
    posY: pose.posY === undefined ? 0 : signedShortValue(pose.posY, `${label}.posY`),
    posZ: worldPositionValue(pose.posZ, `${label}.posZ`),
    rotation: signedShortValue(pose.rotation, `${label}.rotation`),
    away,
    emote,
    sit,
  };
  return pose.juggling === null ? { ...parsed, juggling: null } : parsed;
}

function parseMovementUpdate(value: unknown): MovementUpdatePayload {
  const update = objectValue(value, 'notifications.movementUpdate');
  const poses = arrayValue(update.poses, 'notifications.movementUpdate.poses').map((entry, index) =>
    parseMovementPose(entry, `notifications.movementUpdate.poses[${String(index)}]`),
  );
  if (poses.length > 100) {
    throw new ContractError('notifications.movementUpdate.poses exceeds village capacity');
  }
  if (new Set(poses.map((pose) => pose.playerId)).size !== poses.length) {
    throw new ContractError('notifications.movementUpdate.poses contains duplicate players');
  }
  const footballRealtime =
    update.footballRealtime === undefined
      ? false
      : booleanValue(update.footballRealtime, 'notifications.movementUpdate.footballRealtime');
  return {
    villageId: positiveIntegerValue(update.villageId, 'notifications.movementUpdate.villageId'),
    streamGeneration: positiveIntegerValue(
      update.streamGeneration,
      'notifications.movementUpdate.streamGeneration',
    ),
    revision: positiveIntegerValue(update.revision, 'notifications.movementUpdate.revision'),
    poses,
    ...(footballRealtime ? { footballRealtime: true } : {}),
  };
}

export function parsePetTrickSubmissionResponse(value: unknown): PetTrickSubmissionPayload {
  const root = objectValue(value, 'pet trick submission response');
  assertProtocol(root);
  const submission = objectValue(root.petTrickSubmission, 'petTrickSubmission');
  const status = stringValue(submission.status, 'petTrickSubmission.status');
  if (status !== 'submitted' && status !== 'cool-idle-unlocked') {
    throw new ContractError(`Unsupported pet trick submission status: ${status}`);
  }
  const trick = rangedIntegerValue(submission.trick, 'petTrickSubmission.trick', 1, 8);
  const animationState = stringValue(
    submission.animationState,
    'petTrickSubmission.animationState',
  );
  const expectedAnimationState = trick <= 7 ? `TRICK${String(trick)}` : 'NONE';
  if (animationState !== expectedAnimationState) {
    throw new ContractError('petTrickSubmission.animationState contradicts its trick');
  }
  if ((status === 'cool-idle-unlocked') !== (trick === 8)) {
    throw new ContractError('petTrickSubmission.status contradicts its trick');
  }
  const petLevel = rangedIntegerValue(submission.petLevel, 'petTrickSubmission.petLevel', 0, 7);
  const petPointsWithinLevel = rangedIntegerValue(
    submission.petPointsWithinLevel,
    'petTrickSubmission.petPointsWithinLevel',
    0,
    32_767,
  );
  const petCoolIdleUnlockedUntil = nonNegativeIntegerValue(
    submission.petCoolIdleUnlockedUntil,
    'petTrickSubmission.petCoolIdleUnlockedUntil',
  );
  const presence = parsePresencePayload(submission.presence, 'petTrickSubmission.presence');
  const local = presence.avatars.find((avatar) => avatar.playerId === presence.localPlayerId);
  if (
    local?.petLevel !== petLevel ||
    local.petPointsWithinLevel !== petPointsWithinLevel ||
    local.petCoolIdleUnlockedUntil !== petCoolIdleUnlockedUntil
  ) {
    throw new ContractError('petTrickSubmission progression contradicts local presence');
  }
  return {
    status,
    trick,
    animationState: animationState as PetTrickAnimationState,
    petLevel,
    petPointsWithinLevel,
    petCoolIdleUnlockedUntil,
    presence,
  };
}

export function parsePetTrainingSubmissionResponse(value: unknown): PetTrainingSubmissionPayload {
  const root = objectValue(value, 'pet training submission response');
  assertProtocol(root);
  const submission = objectValue(root.petTrainingSubmission, 'petTrainingSubmission');
  const status = stringValue(submission.status, 'petTrainingSubmission.status');
  if (
    status !== 'success' &&
    status !== 'level-up' &&
    status !== 'failed' &&
    status !== 'remote-pat'
  ) {
    throw new ContractError(`Unsupported pet training submission status: ${status}`);
  }
  const animationState = stringValue(
    submission.animationState,
    'petTrainingSubmission.animationState',
  );
  const targetPlayerId = positiveIntegerValue(
    submission.targetPlayerId,
    'petTrainingSubmission.targetPlayerId',
  );
  const expectedAnimationState =
    status === 'failed' || status === 'remote-pat' ? 'PET' : 'PET_ROYAL';
  if (animationState !== expectedAnimationState) {
    throw new ContractError('petTrainingSubmission.animationState contradicts its status');
  }
  const pointsGained = rangedIntegerValue(
    submission.pointsGained,
    'petTrainingSubmission.pointsGained',
    0,
    10,
  );
  if ((status === 'failed' || status === 'remote-pat') !== (pointsGained === 0)) {
    throw new ContractError('petTrainingSubmission.pointsGained contradicts its status');
  }
  const petLevel = rangedIntegerValue(submission.petLevel, 'petTrainingSubmission.petLevel', 1, 7);
  const petPointsWithinLevel = rangedIntegerValue(
    submission.petPointsWithinLevel,
    'petTrainingSubmission.petPointsWithinLevel',
    0,
    319,
  );
  const petCoolIdleUnlockedUntil = nonNegativeIntegerValue(
    submission.petCoolIdleUnlockedUntil,
    'petTrainingSubmission.petCoolIdleUnlockedUntil',
  );
  const nextAvailableAt = positiveIntegerValue(
    submission.nextAvailableAt,
    'petTrainingSubmission.nextAvailableAt',
  );
  const presence = parsePresencePayload(submission.presence, 'petTrainingSubmission.presence');
  const target = presence.avatars.find((avatar) => avatar.playerId === targetPlayerId);
  if (
    target?.petLevel !== petLevel ||
    target.petPointsWithinLevel !== petPointsWithinLevel ||
    target.petCoolIdleUnlockedUntil !== petCoolIdleUnlockedUntil
  ) {
    throw new ContractError('petTrainingSubmission progression contradicts target presence');
  }
  return {
    status,
    targetPlayerId,
    animationState,
    pointsGained,
    petLevel,
    petPointsWithinLevel,
    petCoolIdleUnlockedUntil,
    nextAvailableAt,
    presence,
  };
}

export function parsePetTreatSubmissionResponse(value: unknown): PetTreatSubmissionPayload {
  const root = objectValue(value, 'pet treat submission response');
  assertProtocol(root);
  const submission = objectValue(root.petTreatSubmission, 'petTreatSubmission');
  const status = stringValue(submission.status, 'petTreatSubmission.status');
  if (status !== 'consumed' && status !== 'level-up') {
    throw new ContractError(`Unsupported pet treat submission status: ${status}`);
  }
  const petLevel = rangedIntegerValue(submission.petLevel, 'petTreatSubmission.petLevel', 1, 7);
  const petPointsWithinLevel = rangedIntegerValue(
    submission.petPointsWithinLevel,
    'petTreatSubmission.petPointsWithinLevel',
    0,
    319,
  );
  const presence = parsePresencePayload(submission.presence, 'petTreatSubmission.presence');
  const local = presence.avatars.find((avatar) => avatar.playerId === presence.localPlayerId);
  if (local?.petLevel !== petLevel || local.petPointsWithinLevel !== petPointsWithinLevel) {
    throw new ContractError('petTreatSubmission progression contradicts local presence');
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'petTreatSubmission.sequence'),
    buildingId: signedShortValue(submission.buildingId, 'petTreatSubmission.buildingId'),
    buildingType: signedShortValue(submission.buildingType, 'petTreatSubmission.buildingType'),
    buildingName: stringValue(submission.buildingName, 'petTreatSubmission.buildingName'),
    pointsGained: positiveIntegerValue(submission.pointsGained, 'petTreatSubmission.pointsGained'),
    petLevel,
    petPointsWithinLevel,
    entityRevision: positiveIntegerValue(
      submission.entityRevision,
      'petTreatSubmission.entityRevision',
    ),
    presence,
  };
}

export function parseTreasurePickupSubmissionResponse(
  value: unknown,
): TreasurePickupSubmissionPayload {
  const root = objectValue(value, 'treasure pickup submission response');
  assertProtocol(root);
  const submission = objectValue(root.treasurePickupSubmission, 'treasurePickupSubmission');
  const status = stringValue(submission.status, 'treasurePickupSubmission.status');
  if (status !== 'picked-up') {
    throw new ContractError(`Unsupported treasure pickup status: ${status}`);
  }
  const rewardKind = stringValue(submission.rewardKind, 'treasurePickupSubmission.rewardKind');
  if (
    rewardKind !== 'coins' &&
    rewardKind !== 'sunshine' &&
    rewardKind !== 'rain' &&
    rewardKind !== 'lightning' &&
    rewardKind !== 'building'
  ) {
    throw new ContractError(`Unsupported treasure reward kind: ${rewardKind}`);
  }
  const buildingId = signedShortValue(submission.buildingId, 'treasurePickupSubmission.buildingId');
  if (buildingId === 0) {
    throw new ContractError('treasurePickupSubmission.buildingId must be non-zero');
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'treasurePickupSubmission.sequence'),
    buildingId,
    buildingType: signedShortValue(
      submission.buildingType,
      'treasurePickupSubmission.buildingType',
    ),
    buildingName: stringValue(submission.buildingName, 'treasurePickupSubmission.buildingName'),
    rewardKind,
    ...(rewardKind === 'building'
      ? {
          rewardBuildingType: signedShortValue(
            submission.rewardBuildingType,
            'treasurePickupSubmission.rewardBuildingType',
          ),
          rewardBuildingName: stringValue(
            submission.rewardBuildingName,
            'treasurePickupSubmission.rewardBuildingName',
          ),
          ...(submission.rewardBuildingAsset === undefined
            ? {}
            : { rewardBuildingAsset: buildingEditButtonAsset(submission.rewardBuildingAsset) }),
        }
      : {}),
    amount: positiveIntegerValue(submission.amount, 'treasurePickupSubmission.amount'),
    creditsAfter: nonNegativeIntegerValue(
      submission.creditsAfter,
      'treasurePickupSubmission.creditsAfter',
    ),
    weather: parseWeatherInventoryPayload(submission.weather, 'treasurePickupSubmission.weather'),
    entityRevision: positiveIntegerValue(
      submission.entityRevision,
      'treasurePickupSubmission.entityRevision',
    ),
    ...(submission.worldUpdate === undefined
      ? {}
      : { worldUpdate: parseWorldNotificationUpdate(submission.worldUpdate) }),
  };
}

export function parseSeasonalBadgePickupSubmissionResponse(
  value: unknown,
): SeasonalBadgePickupSubmissionPayload {
  const root = objectValue(value, 'seasonal badge pickup submission response');
  assertProtocol(root);
  const submission = objectValue(
    root.seasonalBadgePickupSubmission,
    'seasonalBadgePickupSubmission',
  );
  const status = stringValue(submission.status, 'seasonalBadgePickupSubmission.status');
  if (status !== 'awarded' && status !== 'already-owned') {
    throw new ContractError(`Unsupported seasonal badge pickup status: ${status}`);
  }
  const badge = stringValue(submission.badge, 'seasonalBadgePickupSubmission.badge');
  if (
    badge !== 'halloween-2012' &&
    badge !== 'halloween-2026' &&
    badge !== 'christmas-2012' &&
    badge !== 'valentine-2013' &&
    badge !== 'clover-2013'
  ) {
    throw new ContractError(`Unsupported seasonal badge: ${badge}`);
  }
  const buildingId = signedShortValue(
    submission.buildingId,
    'seasonalBadgePickupSubmission.buildingId',
  );
  if (buildingId === 0) {
    throw new ContractError('seasonalBadgePickupSubmission.buildingId must be non-zero');
  }
  const badgeCategory = nonNegativeIntegerValue(
    submission.badgeCategory,
    'seasonalBadgePickupSubmission.badgeCategory',
  );
  const badgeType = positiveIntegerValue(
    submission.badgeType,
    'seasonalBadgePickupSubmission.badgeType',
  );
  const expectedIdentity: Readonly<
    Record<SeasonalBadgeKind, Readonly<{ badgeType: number; buildingType: number }>>
  > = {
    'halloween-2012': { badgeType: 39, buildingType: 6005 },
    'halloween-2026': { badgeType: 49, buildingType: 6008 },
    'christmas-2012': { badgeType: 40, buildingType: 8700 },
    'valentine-2013': { badgeType: 42, buildingType: 511 },
    'clover-2013': { badgeType: 43, buildingType: 13169 },
  };
  const buildingType = signedShortValue(
    submission.buildingType,
    'seasonalBadgePickupSubmission.buildingType',
  );
  if (
    badgeCategory !== 6 ||
    badgeType !== expectedIdentity[badge].badgeType ||
    buildingType !== expectedIdentity[badge].buildingType
  ) {
    throw new ContractError('seasonalBadgePickupSubmission badge identity is contradictory');
  }
  return {
    status,
    villageId: positiveIntegerValue(
      submission.villageId,
      'seasonalBadgePickupSubmission.villageId',
    ),
    buildingId,
    buildingType,
    badge,
    badgeCategory,
    badgeType,
  };
}

export function parseBuildingPurchaseSubmissionResponse(
  value: unknown,
): BuildingPurchaseSubmissionPayload {
  const root = objectValue(value, 'building purchase submission response');
  assertProtocol(root);
  const submission = objectValue(root.buildingPurchaseSubmission, 'buildingPurchaseSubmission');
  const status = stringValue(submission.status, 'buildingPurchaseSubmission.status');
  if (status !== 'purchased') {
    throw new ContractError(`Unsupported building purchase submission status: ${status}`);
  }
  const buildingId = signedShortValue(
    submission.buildingId,
    'buildingPurchaseSubmission.buildingId',
  );
  if (buildingId === 0) {
    throw new ContractError('buildingPurchaseSubmission.buildingId must be non-zero');
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'buildingPurchaseSubmission.sequence'),
    buildingId,
    buildingType: signedShortValue(
      submission.buildingType,
      'buildingPurchaseSubmission.buildingType',
    ),
    buildingName: stringValue(submission.buildingName, 'buildingPurchaseSubmission.buildingName'),
    price: nonNegativeIntegerValue(submission.price, 'buildingPurchaseSubmission.price'),
    creditsAfter: nonNegativeIntegerValue(
      submission.creditsAfter,
      'buildingPurchaseSubmission.creditsAfter',
    ),
    entityRevision: positiveIntegerValue(
      submission.entityRevision,
      'buildingPurchaseSubmission.entityRevision',
    ),
    codeSetupRequired: booleanValue(
      submission.codeSetupRequired,
      'buildingPurchaseSubmission.codeSetupRequired',
    ),
    ...(submission.worldUpdate === undefined
      ? {}
      : { worldUpdate: parseWorldNotificationUpdate(submission.worldUpdate) }),
    ...(submission.productionDelta === undefined
      ? {}
      : {
          productionDelta: parseBuildingProductionDeltaPayload(
            submission.productionDelta,
            'buildingPurchaseSubmission.productionDelta',
          ),
        }),
  };
}

export function parseNoboPurchaseSubmissionResponse(value: unknown): NoboPurchaseSubmissionPayload {
  const root = objectValue(value, 'Nobo purchase submission response');
  assertProtocol(root);
  const submission = objectValue(root.noboPurchaseSubmission, 'noboPurchaseSubmission');
  const status = stringValue(submission.status, 'noboPurchaseSubmission.status');
  if (status !== 'purchased') {
    throw new ContractError(`Unsupported Nobo purchase submission status: ${status}`);
  }
  const noboType = rangedIntegerValue(
    submission.noboType,
    'noboPurchaseSubmission.noboType',
    1,
    11,
  );
  if (noboType !== 1 && noboType !== 2 && noboType !== 6 && noboType !== 7 && noboType !== 11) {
    throw new ContractError('noboPurchaseSubmission.noboType is unsupported');
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'noboPurchaseSubmission.sequence'),
    characterId: positiveIntegerValue(submission.characterId, 'noboPurchaseSubmission.characterId'),
    productType: signedShortValue(submission.productType, 'noboPurchaseSubmission.productType'),
    noboType,
    name: stringValue(submission.name, 'noboPurchaseSubmission.name'),
    price: nonNegativeIntegerValue(submission.price, 'noboPurchaseSubmission.price'),
    creditsAfter: nonNegativeIntegerValue(
      submission.creditsAfter,
      'noboPurchaseSubmission.creditsAfter',
    ),
    entityRevision: positiveIntegerValue(
      submission.entityRevision,
      'noboPurchaseSubmission.entityRevision',
    ),
    characters: parseCharactersPayload(submission.characters, 'noboPurchaseSubmission.characters'),
  };
}

function liveScreenScaleValue(value: unknown, label: string): number {
  const scale = numberValue(value, label);
  if (scale < 0.25 || scale > 8) throw new ContractError(`${label} must be between 0.25 and 8`);
  return scale;
}

function parseBuildInventoryItems(value: unknown, label: string): readonly BuildInventoryItemRef[] {
  if (value === undefined) return [];
  const keys = new Set<string>();
  return arrayValue(value, label).map((entry, index) => {
    const itemLabel = `${label}[${String(index)}]`;
    const item = objectValue(entry, itemLabel);
    const sourceVillageId = positiveIntegerValue(
      item.sourceVillageId,
      `${itemLabel}.sourceVillageId`,
    );
    const entityId = nonZeroSignedShortValue(item.entityId, `${itemLabel}.entityId`);
    const key = `${String(sourceVillageId)}:${String(entityId)}`;
    if (keys.has(key)) throw new ContractError(`${label} contains duplicate reference ${key}`);
    keys.add(key);
    return {
      sourceVillageId,
      entityId,
      ...(item.community === undefined
        ? {}
        : { community: booleanValue(item.community, `${itemLabel}.community`) }),
      ...(item.liveScreenScale === undefined
        ? {}
        : {
            liveScreenScale: liveScreenScaleValue(
              item.liveScreenScale,
              `${itemLabel}.liveScreenScale`,
            ),
          }),
    };
  });
}

export function parseMountSaleSubmissionResponse(value: unknown): MountSaleSubmissionPayload {
  const root = objectValue(value, 'Mount sale submission response');
  assertProtocol(root);
  const submission = objectValue(root.mountSaleSubmission, 'mountSaleSubmission');
  if (submission.status !== 'sold') throw new ContractError('Unsupported mount sale status');
  return {
    status: 'sold',
    sequence: positiveIntegerValue(submission.sequence, 'mountSaleSubmission.sequence'),
    characterId: rangedIntegerValue(
      submission.characterId,
      'mountSaleSubmission.characterId',
      1,
      32767,
    ),
    mountName: stringValue(submission.mountName, 'mountSaleSubmission.mountName'),
    refund: nonNegativeIntegerValue(submission.refund, 'mountSaleSubmission.refund'),
    creditsAfter: nonNegativeIntegerValue(
      submission.creditsAfter,
      'mountSaleSubmission.creditsAfter',
    ),
    characters: parseCharactersPayload(submission.characters, 'mountSaleSubmission.characters'),
  };
}

export function parseNoboEditSubmissionResponse(value: unknown): NoboEditSubmissionPayload {
  const root = objectValue(value, 'Nobo edit submission response');
  assertProtocol(root);
  const submission = objectValue(root.noboEditSubmission, 'noboEditSubmission');
  const status = stringValue(submission.status, 'noboEditSubmission.status');
  if (status !== 'picked-up' && status !== 'placed' && status !== 'sold') {
    throw new ContractError(`Unsupported Nobo edit submission status: ${status}`);
  }
  const noboType = rangedIntegerValue(submission.noboType, 'noboEditSubmission.noboType', 1, 11);
  if (noboType !== 1 && noboType !== 2 && noboType !== 6 && noboType !== 7 && noboType !== 11) {
    throw new ContractError('noboEditSubmission.noboType is unsupported');
  }
  const creditsAfter = integerValue(submission.creditsAfter, 'noboEditSubmission.creditsAfter');
  if (creditsAfter < -1) {
    throw new ContractError('noboEditSubmission.creditsAfter must be -1 or non-negative');
  }
  const inventoryCharacterIds = arrayValue(
    submission.inventoryCharacterIds,
    'noboEditSubmission.inventoryCharacterIds',
  ).map((value, index) =>
    nonZeroSignedShortValue(value, `noboEditSubmission.inventoryCharacterIds[${String(index)}]`),
  );
  if (new Set(inventoryCharacterIds).size !== inventoryCharacterIds.length) {
    throw new ContractError('noboEditSubmission.inventoryCharacterIds contains duplicates');
  }
  const inventoryItems = parseBuildInventoryItems(
    submission.inventoryItems,
    'noboEditSubmission.inventoryItems',
  );
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'noboEditSubmission.sequence'),
    characterId: nonZeroSignedShortValue(submission.characterId, 'noboEditSubmission.characterId'),
    productType: signedShortValue(submission.productType, 'noboEditSubmission.productType'),
    noboType,
    name: stringValue(submission.name, 'noboEditSubmission.name'),
    refund: nonNegativeIntegerValue(submission.refund, 'noboEditSubmission.refund'),
    creditsAfter,
    entityRevision: positiveIntegerValue(
      submission.entityRevision,
      'noboEditSubmission.entityRevision',
    ),
    inventoryCharacterIds,
    inventoryItems,
    characters: parseCharactersPayload(submission.characters, 'noboEditSubmission.characters'),
  };
}

export function parseBuildingCodeSubmissionResponse(value: unknown): BuildingCodeSubmissionPayload {
  const root = objectValue(value, 'building code submission response');
  assertProtocol(root);
  const submission = objectValue(root.buildingCodeSubmission, 'buildingCodeSubmission');
  const status = stringValue(submission.status, 'buildingCodeSubmission.status');
  if (status !== 'configured') {
    throw new ContractError(`Unsupported building-code submission status: ${status}`);
  }
  return {
    status,
    villageId: positiveIntegerValue(submission.villageId, 'buildingCodeSubmission.villageId'),
    buildingId: nonZeroSignedShortValue(submission.buildingId, 'buildingCodeSubmission.buildingId'),
    buildingType: signedShortValue(submission.buildingType, 'buildingCodeSubmission.buildingType'),
    entityRevision: nonNegativeIntegerValue(
      submission.entityRevision,
      'buildingCodeSubmission.entityRevision',
    ),
  };
}

export function parseBuildingTextSubmissionResponse(value: unknown): BuildingTextSubmissionPayload {
  const root = objectValue(value, 'building text submission response');
  assertProtocol(root);
  const submission = objectValue(root.buildingTextSubmission, 'buildingTextSubmission');
  const status = stringValue(submission.status, 'buildingTextSubmission.status');
  if (status !== 'updated') {
    throw new ContractError(`Unsupported building-text submission status: ${status}`);
  }
  return {
    status,
    villageId: positiveIntegerValue(submission.villageId, 'buildingTextSubmission.villageId'),
    buildingId: nonZeroSignedShortValue(submission.buildingId, 'buildingTextSubmission.buildingId'),
    buildingType: signedShortValue(submission.buildingType, 'buildingTextSubmission.buildingType'),
    // Owners still enter at most 100 characters, while server-rendered racing
    // toplists can expand the stored sign text to the legacy 190-character read limit.
    text: boundedText(
      submission.text,
      'buildingTextSubmission.text',
      LEGACY_ENTITY_TEXT_READ_MAXIMUM_LENGTH,
      false,
    ),
    entityRevision: positiveIntegerValue(
      submission.entityRevision,
      'buildingTextSubmission.entityRevision',
    ),
  };
}

export function parseBuildingEditSubmissionResponse(value: unknown): BuildingEditSubmissionPayload {
  const root = objectValue(value, 'building edit submission response');
  assertProtocol(root);
  const submission = objectValue(root.buildingEditSubmission, 'buildingEditSubmission');
  const status = stringValue(submission.status, 'buildingEditSubmission.status');
  if (
    status !== 'picked-up' &&
    status !== 'placed' &&
    status !== 'sold' &&
    status !== 'picture-changed' &&
    status !== 'unwrapped' &&
    status !== 'opened' &&
    status !== 'closed' &&
    status !== 'pet-boarded' &&
    status !== 'pet-left' &&
    status !== 'visitors-changed'
  ) {
    throw new ContractError(`Unsupported building edit submission status: ${status}`);
  }
  const inventoryItems = parseBuildInventoryItems(
    submission.inventoryItems,
    'buildingEditSubmission.inventoryItems',
  );
  const buildingId = signedShortValue(submission.buildingId, 'buildingEditSubmission.buildingId');
  if (buildingId === 0) {
    throw new ContractError('buildingEditSubmission.buildingId must be non-zero');
  }
  const creditsAfter = integerValue(submission.creditsAfter, 'buildingEditSubmission.creditsAfter');
  if (creditsAfter < -1) {
    throw new ContractError('buildingEditSubmission.creditsAfter must be -1 or non-negative');
  }
  const open =
    submission.open === null || submission.open === undefined
      ? null
      : booleanValue(submission.open, 'buildingEditSubmission.open');
  if ((status === 'opened' || status === 'closed') !== (open !== null)) {
    throw new ContractError('buildingEditSubmission.open contradicts its semantic status');
  }
  if ((status === 'opened' && open !== true) || (status === 'closed' && open !== false)) {
    throw new ContractError('buildingEditSubmission.open contradicts its open-close result');
  }
  const codeSetupRequired = booleanValue(
    submission.codeSetupRequired,
    'buildingEditSubmission.codeSetupRequired',
  );
  if (codeSetupRequired && status !== 'placed') {
    throw new ContractError(
      'buildingEditSubmission.codeSetupRequired contradicts its semantic status',
    );
  }
  const inventoryBuildingIds =
    submission.inventoryBuildingIds === undefined
      ? undefined
      : arrayValue(
          submission.inventoryBuildingIds,
          'buildingEditSubmission.inventoryBuildingIds',
        ).map((value, index) =>
          nonZeroSignedShortValue(
            value,
            `buildingEditSubmission.inventoryBuildingIds[${String(index)}]`,
          ),
        );
  if (
    inventoryBuildingIds !== undefined &&
    new Set(inventoryBuildingIds).size !== inventoryBuildingIds.length
  ) {
    throw new ContractError('buildingEditSubmission.inventoryBuildingIds contains duplicates');
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'buildingEditSubmission.sequence'),
    buildingId,
    buildingType: signedShortValue(submission.buildingType, 'buildingEditSubmission.buildingType'),
    buildingName: stringValue(submission.buildingName, 'buildingEditSubmission.buildingName'),
    buttonAsset: buildingEditButtonAsset(submission.buttonAsset),
    tileWidth: positiveIntegerValue(submission.tileWidth, 'buildingEditSubmission.tileWidth'),
    tileDepth: positiveIntegerValue(submission.tileDepth, 'buildingEditSubmission.tileDepth'),
    allowRotation: booleanValue(submission.allowRotation, 'buildingEditSubmission.allowRotation'),
    refund: nonNegativeIntegerValue(submission.refund, 'buildingEditSubmission.refund'),
    creditsAfter,
    entityRevision: positiveIntegerValue(
      submission.entityRevision,
      'buildingEditSubmission.entityRevision',
    ),
    open,
    codeSetupRequired,
    ...(inventoryBuildingIds === undefined ? {} : { inventoryBuildingIds }),
    inventoryItems,
    ...(submission.worldUpdate === undefined
      ? {}
      : { worldUpdate: parseWorldNotificationUpdate(submission.worldUpdate) }),
    ...(submission.petFollowing === undefined
      ? {}
      : {
          petFollowing: parsePetFollowingSubmission(
            submission.petFollowing,
            'buildingEditSubmission.petFollowing',
          ),
        }),
    ...(submission.productionDelta === undefined
      ? {}
      : {
          productionDelta: parseBuildingProductionDeltaPayload(
            submission.productionDelta,
            'buildingEditSubmission.productionDelta',
          ),
        }),
  };
}

export function parseVillageEditorResponse(value: unknown): VillageEditorPayload {
  const root = objectValue(value, 'village editor response');
  assertProtocol(root);
  const editor = objectValue(root.villageEditor, 'villageEditor');
  const tileLimit = isCreativeVillage(integerValue(editor.villageId, 'villageEditor.villageId'))
    ? CREATIVE_WORLD.sampleWidth - 1
    : 128;
  const buildingIds = new Set<number>();
  const buildings = arrayValue(editor.buildings, 'villageEditor.buildings').map((value, index) => {
    const label = `villageEditor.buildings[${String(index)}]`;
    const building = objectValue(value, label);
    const id = nonZeroSignedShortValue(building.id, `${label}.id`);
    if (buildingIds.has(id)) throw new ContractError('villageEditor.buildings contains duplicates');
    buildingIds.add(id);
    return {
      id,
      type: nonZeroSignedShortValue(building.type, `${label}.type`),
      name: stringValue(building.name, `${label}.name`),
      tileX: rangedIntegerValue(building.tileX, `${label}.tileX`, -tileLimit, tileLimit - 1),
      tileZ: rangedIntegerValue(building.tileZ, `${label}.tileZ`, -tileLimit, tileLimit - 1),
      rotation: rangedIntegerValue(building.rotation, `${label}.rotation`, 0, 3),
      tileWidth: positiveIntegerValue(building.tileWidth, `${label}.tileWidth`),
      tileDepth: positiveIntegerValue(building.tileDepth, `${label}.tileDepth`),
      allowRotation: booleanValue(building.allowRotation, `${label}.allowRotation`),
      editable: booleanValue(building.editable, `${label}.editable`),
      wrappingType:
        building.wrappingType === null
          ? null
          : rangedIntegerValue(building.wrappingType, `${label}.wrappingType`, 1, 32_767),
    };
  });
  const treeIds = new Set<number>();
  const trees = arrayValue(editor.trees, 'villageEditor.trees').map((value, index) => {
    const label = `villageEditor.trees[${String(index)}]`;
    const tree = objectValue(value, label);
    // Procedurally generated village trees are numbered from zero. Unlike buildings, zero is a
    // valid authoritative tree identity and is only used for rendering in the editor.
    const id = signedShortValue(tree.id, `${label}.id`);
    if (treeIds.has(id)) throw new ContractError('villageEditor.trees contains duplicates');
    treeIds.add(id);
    return {
      id,
      tileX: rangedIntegerValue(tree.tileX, `${label}.tileX`, -tileLimit, tileLimit - 1),
      tileZ: rangedIntegerValue(tree.tileZ, `${label}.tileZ`, -tileLimit, tileLimit - 1),
      scaleByte: rangedIntegerValue(tree.scaleByte, `${label}.scaleByte`, -127, 127),
      ...(tree.type === undefined ? {} : { type: integerValue(tree.type, `${label}.type`) }),
    };
  });
  const area = editor.area === undefined ? undefined : objectValue(editor.area, 'editor.area');
  return {
    ...(area === undefined
      ? {}
      : {
          area: {
            chunkX: rangedIntegerValue(area.chunkX, 'area.chunkX', 1, 22),
            chunkZ: rangedIntegerValue(area.chunkZ, 'area.chunkZ', 1, 22),
            minimumTileX: integerValue(area.minimumTileX, 'area.minimumTileX'),
            minimumTileZ: integerValue(area.minimumTileZ, 'area.minimumTileZ'),
            width: rangedIntegerValue(area.width, 'area.width', 1, 96),
            depth: rangedIntegerValue(area.depth, 'area.depth', 1, 96),
          },
        }),
    ...(editor.terrainChunks === undefined
      ? {}
      : {
          terrainChunks: arrayValue(editor.terrainChunks, 'editor.terrainChunks').map(
            parseTerrainChunk,
          ),
        }),
    villageId: positiveIntegerValue(editor.villageId, 'villageEditor.villageId'),
    villageName: stringValue(editor.villageName, 'villageEditor.villageName'),
    theme: integerValue(editor.theme, 'villageEditor.theme'),
    villageSize: rangedIntegerValue(editor.villageSize, 'villageEditor.villageSize', -1, 6),
    entityRevision: positiveIntegerValue(editor.entityRevision, 'villageEditor.entityRevision'),
    buildings,
    trees,
  };
}

export function parseBuildingBatchEditSubmissionResponse(
  value: unknown,
): BuildingBatchEditSubmissionPayload {
  const root = objectValue(value, 'building batch edit submission response');
  assertProtocol(root);
  const submission = objectValue(root.buildingBatchEditSubmission, 'buildingBatchEditSubmission');
  const tileLimit = isCreativeVillage(
    integerValue(submission.villageId, 'buildingBatchEditSubmission.villageId'),
  )
    ? CREATIVE_WORLD.sampleWidth - 1
    : 128;
  const status = stringValue(submission.status, 'buildingBatchEditSubmission.status');
  if (status !== 'applied' && status !== 'unchanged') {
    throw new ContractError(`Unsupported building batch edit status: ${status}`);
  }
  const ids = new Set<number>();
  const placements = arrayValue(
    submission.placements,
    'buildingBatchEditSubmission.placements',
  ).map((value, index) => {
    const label = `buildingBatchEditSubmission.placements[${String(index)}]`;
    const placement = objectValue(value, label);
    const buildingId = nonZeroSignedShortValue(placement.buildingId, `${label}.buildingId`);
    if (ids.has(buildingId)) {
      throw new ContractError('buildingBatchEditSubmission.placements contains duplicates');
    }
    ids.add(buildingId);
    return {
      buildingId,
      tileX: rangedIntegerValue(placement.tileX, `${label}.tileX`, -tileLimit, tileLimit - 1),
      tileZ: rangedIntegerValue(placement.tileZ, `${label}.tileZ`, -tileLimit, tileLimit - 1),
      rotation: rangedIntegerValue(placement.rotation, `${label}.rotation`, 0, 3),
      ...(placement.liveScreenScale === undefined
        ? {}
        : {
            liveScreenScale: liveScreenScaleValue(
              placement.liveScreenScale,
              `${label}.liveScreenScale`,
            ),
          }),
    };
  });
  if (placements.length < 1 || placements.length > 200) {
    throw new ContractError('buildingBatchEditSubmission.placements must contain 1 to 200 items');
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'buildingBatchEditSubmission.sequence'),
    villageId: positiveIntegerValue(submission.villageId, 'buildingBatchEditSubmission.villageId'),
    entityRevision: positiveIntegerValue(
      submission.entityRevision,
      'buildingBatchEditSubmission.entityRevision',
    ),
    placements,
    ...(submission.worldUpdate === undefined
      ? {}
      : { worldUpdate: parseWorldNotificationUpdate(submission.worldUpdate) }),
  };
}

export function parseBuildingBatchPickupSubmissionResponse(
  value: unknown,
): BuildingBatchPickupSubmissionPayload {
  const root = objectValue(value, 'building batch pickup submission response');
  assertProtocol(root);
  const submission = objectValue(
    root.buildingBatchPickupSubmission,
    'buildingBatchPickupSubmission',
  );
  const status = stringValue(submission.status, 'buildingBatchPickupSubmission.status');
  if (status !== 'picked-up') {
    throw new ContractError(`Unsupported building batch pickup status: ${status}`);
  }
  const ids = new Set<number>();
  const buildingIds = arrayValue(
    submission.buildingIds,
    'buildingBatchPickupSubmission.buildingIds',
  ).map((value, index) => {
    const id = nonZeroSignedShortValue(
      value,
      `buildingBatchPickupSubmission.buildingIds[${String(index)}]`,
    );
    if (ids.has(id)) {
      throw new ContractError('buildingBatchPickupSubmission.buildingIds contains duplicates');
    }
    ids.add(id);
    return id;
  });
  if (buildingIds.length < 1 || buildingIds.length > 200) {
    throw new ContractError(
      'buildingBatchPickupSubmission.buildingIds must contain 1 to 200 items',
    );
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'buildingBatchPickupSubmission.sequence'),
    villageId: positiveIntegerValue(
      submission.villageId,
      'buildingBatchPickupSubmission.villageId',
    ),
    entityRevision: positiveIntegerValue(
      submission.entityRevision,
      'buildingBatchPickupSubmission.entityRevision',
    ),
    buildingIds,
    ...(submission.worldUpdate === undefined
      ? {}
      : { worldUpdate: parseWorldNotificationUpdate(submission.worldUpdate) }),
  };
}

function buildingInteractionValue(
  value: unknown,
  label: string,
): Exclude<BuildingInteraction, null> {
  const interaction = stringValue(value, label);
  if (
    interaction !== 'open-close' &&
    interaction !== 'train-carriage' &&
    interaction !== 'entity-text' &&
    interaction !== 'sit' &&
    interaction !== 'cannon' &&
    interaction !== 'domino' &&
    interaction !== 'dice' &&
    interaction !== 'key-door' &&
    interaction !== 'friend-key-door' &&
    interaction !== 'code-key-door' &&
    interaction !== 'unmounted-key-door' &&
    interaction !== 'automatic-key-door' &&
    interaction !== 'self-unlock-door' &&
    interaction !== 'teleport' &&
    interaction !== 'friend-teleport' &&
    interaction !== 'unmounted-teleport' &&
    interaction !== 'teleport-step' &&
    interaction !== 'telefax' &&
    interaction !== 'automatic-telefax' &&
    interaction !== 'tutorial-sign' &&
    interaction !== 'vip-info' &&
    interaction !== 'player-panel' &&
    interaction !== 'theme-shop' &&
    interaction !== 'camera-toggle' &&
    interaction !== 'camera-drone' &&
    interaction !== 'wire-workshop' &&
    interaction !== 'circuit-wire' &&
    interaction !== 'circuit-machine' &&
    interaction !== 'circuit-button' &&
    interaction !== 'live-screen' &&
    interaction !== 'pet-house' &&
    interaction !== 'pet-treat' &&
    interaction !== 'external-hamsterpaj' &&
    interaction !== 'external-pimpland' &&
    interaction !== 'external-zed-radio' &&
    interaction !== 'external-arvika' &&
    interaction !== 'seasonal-badge' &&
    interaction !== 'enter-temple' &&
    interaction !== 'leave-temple' &&
    interaction !== 'treasure-island'
  ) {
    throw new ContractError(`${label} is unsupported`);
  }
  return interaction;
}

export function parseBuildingTeleportSubmissionResponse(
  value: unknown,
): BuildingTeleportSubmissionPayload {
  const root = objectValue(value, 'building teleport submission response');
  assertProtocol(root);
  const submission = objectValue(root.buildingTeleportSubmission, 'buildingTeleportSubmission');
  const status = stringValue(submission.status, 'buildingTeleportSubmission.status');
  if (status !== 'applied') {
    throw new ContractError(`Unsupported building teleport status: ${status}`);
  }
  const sourceBuildingId = signedShortValue(
    submission.sourceBuildingId,
    'buildingTeleportSubmission.sourceBuildingId',
  );
  const destinationBuildingId = signedShortValue(
    submission.destinationBuildingId,
    'buildingTeleportSubmission.destinationBuildingId',
  );
  if (sourceBuildingId === 0 || destinationBuildingId === 0) {
    throw new ContractError('buildingTeleportSubmission building identities must be non-zero');
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'buildingTeleportSubmission.sequence'),
    villageId: positiveIntegerValue(submission.villageId, 'buildingTeleportSubmission.villageId'),
    sourceBuildingId,
    destinationBuildingId,
    posX: worldPositionValue(submission.posX, 'buildingTeleportSubmission.posX'),
    posZ: worldPositionValue(submission.posZ, 'buildingTeleportSubmission.posZ'),
    rotation: signedShortValue(submission.rotation, 'buildingTeleportSubmission.rotation'),
    serverTime: nonNegativeIntegerValue(
      submission.serverTime,
      'buildingTeleportSubmission.serverTime',
    ),
    presence: parsePresencePayload(submission.presence, 'buildingTeleportSubmission.presence'),
  };
}

function keyDoorColorValue(value: unknown, label: string): KeyDoorColor {
  const color = stringValue(value, label);
  const colors: readonly string[] = [
    'blue',
    'yellow',
    'green',
    'pink',
    'orange',
    'purple',
    'white',
    'red',
    'brown',
    'gray',
    'olive',
    'black',
    'gold',
    'silver',
    'bronze',
    'custom_9',
    'custom_10',
    'custom_11',
    'custom_12',
    'custom_14',
    'custom_15',
    'custom_16',
  ];
  if (!colors.includes(color)) throw new ContractError(`${label} is unsupported`);
  return color as KeyDoorColor;
}

const gameBuildingProductAssets = new Set([
  '/game/cross-country-skis/shop.svg',
  '/game/ockelboda-market/menu.png',
  '/game/ockelboda-market-dark/menu.png',
  '/game/ockelboda-market-winter/menu.png',
  '/game/nobos/ghost.svg',
  '/camera-drone/menu-icon.png',
  '/game/sports/domino.svg',
  '/game/sports/dice.svg',
  '/game/sports/game-table.svg',
  '/game/sports/play-marker-red.svg',
  '/game/sports/play-marker-blue.svg',
  '/game/sports/play-marker-green.svg',
  '/game/sports/play-marker-yellow.svg',
  '/game/sports/play-marker-orange.svg',
  '/game/sports/play-marker-purple.svg',
  '/game/sports/play-marker-white.svg',
  '/game/sports/play-marker-black.svg',
  '/game/sports/football-preview.png',
  '/game/sports/football-goal-preview.png',
  '/game/sports/football-respawn-button.png',
  '/game/sports/football.svg',
  '/game/sports/football-goal.svg',
  '/game/grow-lights/grow-light-fluorescent.svg',
  '/game/grow-lights/grow-light-hps.svg',
  '/game/grow-lights/grow-light-led.svg',
  '/game/trains/straight.svg',
  '/game/trains/curve.svg',
  '/game/trains/steam.svg',
  '/game/trains/diesel.svg',
  '/game/trains/passenger.svg',
  '/game/trains/freight.svg',
  '/game/trains/control.svg',
  '/game/trains/christmas.svg',
  '/game/trains/christmas-freight.svg',
  '/game/trains/speed-up.svg',
  '/game/trains/slow-down.svg',
  '/game/trains/junction-left.svg',
  '/game/trains/junction-right.svg',
  '/game/trains/crossing.svg',
  ...elevatedRails.map((rail) => `/game/trains/${rail.variant}.svg`),
  '/game/vip-laptop/menu-icon.svg',
  '/game/live-screen/menu-icon.svg',
  '/game/weekly-spin-trophies/bronze.png',
  '/game/weekly-spin-trophies/silver.png',
  '/game/weekly-spin-trophies/gold.png',
]);

function isBuildingProductAsset(asset: string): boolean {
  return (
    asset.startsWith('/legacy/build-menu/products/') ||
    gameBuildingProductAssets.has(asset) ||
    isCustomBuildingAsset(asset)
  );
}

function buildingEditButtonAsset(value: unknown): string {
  const asset = stringValue(value, 'buildingEditSubmission.buttonAsset');
  if (!isBuildingProductAsset(asset)) {
    throw new ContractError(
      'buildingEditSubmission.buttonAsset is outside the extracted product assets',
    );
  }
  return buildingAssetURL(asset);
}

function buildingProductKind(value: unknown, label: string): 'building' | 'nobo' {
  const kind = stringValue(value, label);
  if (kind !== 'building' && kind !== 'nobo') {
    throw new ContractError(`${label} is unsupported`);
  }
  return kind;
}

export function parseBuildingCatalogResponse(value: unknown): BuildingCatalogPayload {
  const root = objectValue(value, 'building catalog response');
  assertProtocol(root);
  const catalog = objectValue(root.buildingCatalog, 'buildingCatalog');
  const catalogVillageId = positiveIntegerValue(catalog.villageId, 'buildingCatalog.villageId');
  const language = legacyLanguageValue(catalog.language, 'buildingCatalog.language');
  const mode = stringValue(catalog.mode, 'buildingCatalog.mode');
  if (mode !== 'build' && mode !== 'gift') {
    throw new ContractError('buildingCatalog.mode must be build or gift');
  }
  const godMode =
    catalog.godMode === undefined
      ? undefined
      : booleanValue(catalog.godMode, 'buildingCatalog.godMode');
  const creativeMode =
    catalog.creativeMode === undefined
      ? undefined
      : booleanValue(catalog.creativeMode, 'buildingCatalog.creativeMode');
  const villageExpansion = parseVillageExpansionOffer(catalog.villageExpansion, mode);
  const pickupLockedBuildingTypes = arrayValue(
    catalog.pickupLockedBuildingTypes,
    'buildingCatalog.pickupLockedBuildingTypes',
  ).map((entry, index) => {
    const buildingType = signedShortValue(
      entry,
      `buildingCatalog.pickupLockedBuildingTypes[${String(index)}]`,
    );
    if (buildingType === 0) {
      throw new ContractError('buildingCatalog.pickupLockedBuildingTypes contains reserved type 0');
    }
    return buildingType;
  });
  if (new Set(pickupLockedBuildingTypes).size !== pickupLockedBuildingTypes.length) {
    throw new ContractError('buildingCatalog.pickupLockedBuildingTypes contains duplicates');
  }
  if (mode === 'gift') {
    if (pickupLockedBuildingTypes.length !== 0) {
      throw new ContractError('gift catalog pickupLockedBuildingTypes must be empty');
    }
  } else if (godMode) {
    if (pickupLockedBuildingTypes.length !== 0) {
      throw new ContractError('godmode catalog pickupLockedBuildingTypes must be empty');
    }
  } else if (creativeMode) {
    // CommunityWorld protects the red spawnpoint for players without moderator/admin roles.
    if (pickupLockedBuildingTypes.some((buildingType) => buildingType !== -221)) {
      throw new ContractError(
        'CommunityWorld catalog pickupLockedBuildingTypes must be empty or contain only red spawnpoint type -221',
      );
    }
  } else if (pickupLockedBuildingTypes.length < 12) {
    // The original catalog has twelve protected types. New nature resources add
    // server-owned locks; accepting those additions also keeps older servers compatible.
    throw new ContractError(
      'buildingCatalog.pickupLockedBuildingTypes must contain at least 12 source-protected types',
    );
  }
  const pictureBuildingTypes = arrayValue(
    catalog.pictureBuildingTypes,
    'buildingCatalog.pictureBuildingTypes',
  ).map((entry, index) => {
    const buildingType = signedShortValue(
      entry,
      `buildingCatalog.pictureBuildingTypes[${String(index)}]`,
    );
    if (buildingType === 0) {
      throw new ContractError('buildingCatalog.pictureBuildingTypes contains reserved type 0');
    }
    return buildingType;
  });
  if (new Set(pictureBuildingTypes).size !== pictureBuildingTypes.length) {
    throw new ContractError('buildingCatalog.pictureBuildingTypes contains duplicates');
  }
  const categoryEntries = arrayValue(catalog.categories, 'buildingCatalog.categories');
  if (categoryEntries.length > 54) {
    throw new ContractError('buildingCatalog.categories exceeds the source tab count');
  }
  const buildingTypes = new Set<number>();
  const categoryIds = new Set<number>();
  const categories = categoryEntries.map((entry, categoryIndex) => {
    const label = `buildingCatalog.categories[${String(categoryIndex)}]`;
    const category = objectValue(entry, label);
    const id = positiveIntegerValue(category.id, `${label}.id`);
    if (categoryIds.has(id)) throw new ContractError(`${label}.id is duplicated`);
    categoryIds.add(id);
    const iconAsset = stringValue(category.iconAsset, `${label}.iconAsset`);
    if (
      iconAsset !== '/game/sports/category.svg' &&
      iconAsset !== '/game/grow-lights/category.svg' &&
      iconAsset !== '/game/trains/category.svg' &&
      iconAsset !== '/game/trains/steam.svg' &&
      (!iconAsset.startsWith('/legacy/build-menu/categories/') || !iconAsset.endsWith('.png'))
    ) {
      throw new ContractError(`${label}.iconAsset is outside the extracted category assets`);
    }
    const productEntries = arrayValue(category.products, `${label}.products`);
    const categoryName = stringValue(category.name, `${label}.name`);
    const expansionCategory = mode === 'build' && categoryName.toLowerCase() === 'expand';
    if (productEntries.length === 0 && !expansionCategory) {
      throw new ContractError(`${label}.products is empty`);
    }
    if (expansionCategory && villageExpansion === null) {
      throw new ContractError(`${label} has no villageExpansion offer`);
    }
    const products = productEntries.map((productEntry, productIndex) => {
      const productLabel = `${label}.products[${String(productIndex)}]`;
      const product = objectValue(productEntry, productLabel);
      const buildingType = signedShortValue(product.buildingType, `${productLabel}.buildingType`);
      if (buildingTypes.has(buildingType)) {
        throw new ContractError(`${productLabel}.buildingType is duplicated`);
      }
      buildingTypes.add(buildingType);
      const buttonAsset = stringValue(product.buttonAsset, `${productLabel}.buttonAsset`);
      if (!isBuildingProductAsset(buttonAsset)) {
        throw new ContractError(
          `${productLabel}.buttonAsset is outside the extracted product assets`,
        );
      }
      const requiredBadgeImage = stringValue(
        product.requiredBadgeImage,
        `${productLabel}.requiredBadgeImage`,
      );
      if (
        requiredBadgeImage.length > 0 &&
        !requiredBadgeImage.startsWith('/legacy/profile/badges/')
      ) {
        throw new ContractError(
          `${productLabel}.requiredBadgeImage is outside the extracted badge assets`,
        );
      }
      const inventoryBuildingIds = new Set<number>();
      const kindValue: 'building' | 'nobo' =
        product.kind === undefined
          ? 'building'
          : buildingProductKind(product.kind, `${productLabel}.kind`);
      const noboType =
        product.noboType === undefined
          ? 0
          : rangedIntegerValue(product.noboType, `${productLabel}.noboType`, 0, 32767);
      if (
        (kindValue === 'nobo') !==
        (noboType === 1 || noboType === 2 || noboType === 6 || noboType === 7 || noboType === 11)
      ) {
        throw new ContractError(`${productLabel}.kind contradicts its Nobo type`);
      }
      const petSpecies =
        product.petSpecies === null || product.petSpecies === undefined
          ? undefined
          : petShopSpeciesValue(product.petSpecies, `${productLabel}.petSpecies`);
      const legacyInventoryBuildingIds = arrayValue(
        product.inventoryBuildingIds,
        `${productLabel}.inventoryBuildingIds`,
      ).map((buildingId, inventoryIndex) => {
        const id = signedShortValue(
          buildingId,
          `${productLabel}.inventoryBuildingIds[${String(inventoryIndex)}]`,
        );
        if (id === 0) {
          throw new ContractError(`${productLabel}.inventoryBuildingIds contains reserved ID 0`);
        }
        if (inventoryBuildingIds.has(id)) {
          throw new ContractError(
            `${productLabel}.inventoryBuildingIds contains duplicate ID ${String(id)}`,
          );
        }
        inventoryBuildingIds.add(id);
        return id;
      });
      const inventoryItems =
        product.inventoryItems === undefined
          ? legacyInventoryBuildingIds.map((entityId) => ({
              sourceVillageId: catalogVillageId,
              entityId,
            }))
          : parseBuildInventoryItems(product.inventoryItems, `${productLabel}.inventoryItems`);
      return {
        buildingType,
        kind: kindValue,
        giftPlacement:
          product.giftPlacement === undefined
            ? false
            : booleanValue(product.giftPlacement, `${productLabel}.giftPlacement`),
        noboType,
        name: stringValue(product.name, `${productLabel}.name`),
        ...(product.growLightPlant === undefined
          ? {}
          : {
              growLightPlant: booleanValue(
                product.growLightPlant,
                `${productLabel}.growLightPlant`,
              ),
            }),
        ...(product.description === undefined
          ? {}
          : {
              description: boundedText(
                product.description,
                `${productLabel}.description`,
                2048,
                true,
              ),
            }),
        buttonAsset: buildingAssetURL(buttonAsset),
        keyColor:
          product.keyColor === null || product.keyColor === undefined
            ? null
            : keyDoorColorValue(product.keyColor, `${productLabel}.keyColor`),
        ...(petSpecies === undefined ? {} : { petSpecies }),
        price: nonNegativeIntegerValue(product.price, `${productLabel}.price`),
        requiredLevel: nonNegativeIntegerValue(
          product.requiredLevel,
          `${productLabel}.requiredLevel`,
        ),
        tileWidth: rangedIntegerValue(product.tileWidth, `${productLabel}.tileWidth`, 1, 256),
        tileDepth: rangedIntegerValue(product.tileDepth, `${productLabel}.tileDepth`, 1, 256),
        allowCount:
          product.allowCount === undefined
            ? -1
            : rangedIntegerValue(product.allowCount, `${productLabel}.allowCount`, -1, 32767),
        allowRotation: booleanValue(product.allowRotation, `${productLabel}.allowRotation`),
        buildAnywhere: booleanValue(product.buildAnywhere, `${productLabel}.buildAnywhere`),
        terrainModification:
          product.terrainModification === undefined
            ? false
            : booleanValue(product.terrainModification, `${productLabel}.terrainModification`),
        requiredBadge: stringValue(product.requiredBadge, `${productLabel}.requiredBadge`),
        requiredBadgeImage,
        badgeMet: booleanValue(product.badgeMet, `${productLabel}.badgeMet`),
        placementSupported: booleanValue(
          product.placementSupported,
          `${productLabel}.placementSupported`,
        ),
        inventoryBuildingIds: legacyInventoryBuildingIds,
        inventoryItems,
      };
    });
    return {
      id,
      name: categoryName,
      caption: stringValue(category.caption, `${label}.caption`),
      label: stringValue(category.label, `${label}.label`),
      iconAsset,
      products,
    };
  });
  const productCount = nonNegativeIntegerValue(
    catalog.productCount,
    'buildingCatalog.productCount',
  );
  if (productCount !== buildingTypes.size) {
    throw new ContractError('buildingCatalog.productCount contradicts its category products');
  }
  const wrappings = arrayValue(catalog.wrappings, 'buildingCatalog.wrappings').map(
    (entry, index) => {
      const label = `buildingCatalog.wrappings[${String(index)}]`;
      const wrapping = objectValue(entry, label);
      const textureAsset = stringValue(wrapping.textureAsset, `${label}.textureAsset`);
      if (!textureAsset.startsWith('/legacy/world/textures/')) {
        throw new ContractError(`${label}.textureAsset is outside extracted world textures`);
      }
      return {
        id: rangedIntegerValue(wrapping.id, `${label}.id`, 1, 32_767),
        model: stringValue(wrapping.model, `${label}.model`),
        textureAsset,
        price: nonNegativeIntegerValue(wrapping.price, `${label}.price`),
        requiredBadge: stringValue(wrapping.requiredBadge, `${label}.requiredBadge`),
        badgeMet: booleanValue(wrapping.badgeMet, `${label}.badgeMet`),
      };
    },
  );
  if (
    (mode === 'gift' && wrappings.length === 0) ||
    (mode === 'build' && !godMode && wrappings.length !== 0)
  ) {
    throw new ContractError('buildingCatalog.wrappings contradicts its mode');
  }
  const rawTexts = objectValue(catalog.texts, 'buildingCatalog.texts');
  const texts = {
    buildLabel: boundedText(rawTexts.buildLabel, 'buildingCatalog.texts.buildLabel', 200, true),
    buildTooltip: boundedText(
      rawTexts.buildTooltip,
      'buildingCatalog.texts.buildTooltip',
      200,
      true,
    ),
    rotateTooltip: boundedText(
      rawTexts.rotateTooltip,
      'buildingCatalog.texts.rotateTooltip',
      200,
      true,
    ),
    pickupTooltip: boundedText(
      rawTexts.pickupTooltip,
      'buildingCatalog.texts.pickupTooltip',
      200,
      true,
    ),
    sellTooltip: boundedText(rawTexts.sellTooltip, 'buildingCatalog.texts.sellTooltip', 200, true),
    giveGiftLabel: boundedText(
      rawTexts.giveGiftLabel,
      'buildingCatalog.texts.giveGiftLabel',
      200,
      true,
    ),
    giftMessageCaption: boundedText(
      rawTexts.giftMessageCaption,
      'buildingCatalog.texts.giftMessageCaption',
      200,
      true,
    ),
    giftCannotOpenBefore: boundedText(
      rawTexts.giftCannotOpenBefore,
      'buildingCatalog.texts.giftCannotOpenBefore',
      200,
      true,
    ),
    giftAnytimeLabel: boundedText(
      rawTexts.giftAnytimeLabel,
      'buildingCatalog.texts.giftAnytimeLabel',
      200,
      true,
    ),
    giftSenderProfileTooltip: boundedText(
      rawTexts.giftSenderProfileTooltip,
      'buildingCatalog.texts.giftSenderProfileTooltip',
      200,
      true,
    ),
    giftUnwrapTooltip: boundedText(
      rawTexts.giftUnwrapTooltip,
      'buildingCatalog.texts.giftUnwrapTooltip',
      200,
      true,
    ),
    giftCannotOpenUntilPrefix: boundedText(
      rawTexts.giftCannotOpenUntilPrefix,
      'buildingCatalog.texts.giftCannotOpenUntilPrefix',
      200,
      true,
    ),
    buyLabel: boundedText(rawTexts.buyLabel, 'buildingCatalog.texts.buyLabel', 200, true),
  };
  const rawTemplates =
    catalog.giftMessageTemplates === undefined
      ? []
      : arrayValue(catalog.giftMessageTemplates, 'buildingCatalog.giftMessageTemplates');
  if (rawTemplates.length > 20) {
    throw new ContractError('buildingCatalog.giftMessageTemplates has too many templates');
  }
  const giftMessageTemplates = rawTemplates.map((value, index) => {
    const path = `buildingCatalog.giftMessageTemplates[${String(index)}]`;
    const template = objectValue(value, path);
    const label = stringValue(template.label, `${path}.label`);
    const text = stringValue(template.text, `${path}.text`);
    if (
      !label.trim() ||
      Array.from(label).length > 60 ||
      !text.trim() ||
      Array.from(text).length > 190 ||
      /[\r\n]/u.test(label + text)
    ) {
      throw new ContractError(`${path} has an invalid label or message`);
    }
    return { label, text };
  });
  const mountSellRefunds: Record<number, number> = {};
  if (catalog.mountSellRefunds !== undefined) {
    const refunds = objectValue(catalog.mountSellRefunds, 'buildingCatalog.mountSellRefunds');
    for (const [type, value] of Object.entries(refunds)) {
      const mountType = rangedIntegerValue(
        Number(type),
        'buildingCatalog.mountSellRefunds type',
        1,
        32767,
      );
      mountSellRefunds[mountType] = nonNegativeIntegerValue(
        value,
        `buildingCatalog.mountSellRefunds.${type}`,
      );
    }
  }
  return {
    villageId: catalogVillageId,
    mountSellRefunds,
    language,
    mode,
    ...(godMode === undefined ? {} : { godMode }),
    ...(creativeMode === undefined ? {} : { creativeMode }),
    ...(catalog.communityBuildAllowed === undefined
      ? {}
      : {
          communityBuildAllowed: booleanValue(
            catalog.communityBuildAllowed,
            'buildingCatalog.communityBuildAllowed',
          ),
        }),
    playerLevel: nonNegativeIntegerValue(catalog.playerLevel, 'buildingCatalog.playerLevel'),
    credits: nonNegativeIntegerValue(catalog.credits, 'buildingCatalog.credits'),
    productCount,
    pickupLockedBuildingTypes,
    pictureBuildingTypes,
    villageExpansion,
    texts,
    wrappings,
    giftMessageTemplates,
    categories,
  };
}

function parseVillageExpansionOffer(
  value: unknown,
  mode: 'build' | 'gift',
): VillageExpansionOfferPayload | null {
  if (value === null || value === undefined) return null;
  if (mode !== 'build') {
    throw new ContractError('gift buildingCatalog.villageExpansion must be null');
  }
  const offer = objectValue(value, 'buildingCatalog.villageExpansion');
  const currentSize = rangedIntegerValue(
    offer.currentSize,
    'buildingCatalog.villageExpansion.currentSize',
    1,
    5,
  );
  const nextSize = rangedIntegerValue(
    offer.nextSize,
    'buildingCatalog.villageExpansion.nextSize',
    2,
    6,
  );
  const maximumSize = rangedIntegerValue(
    offer.maximumSize,
    'buildingCatalog.villageExpansion.maximumSize',
    6,
    6,
  );
  if (nextSize !== currentSize + 1 || nextSize > maximumSize) {
    throw new ContractError('buildingCatalog.villageExpansion has inconsistent size tiers');
  }
  return {
    currentSize,
    nextSize,
    maximumSize,
    price: nonNegativeIntegerValue(offer.price, 'buildingCatalog.villageExpansion.price'),
    requiredLevel: nonNegativeIntegerValue(
      offer.requiredLevel,
      'buildingCatalog.villageExpansion.requiredLevel',
    ),
    canAfford: booleanValue(offer.canAfford, 'buildingCatalog.villageExpansion.canAfford'),
  };
}

export function parseVillageExpansionSubmissionResponse(
  value: unknown,
): VillageExpansionSubmissionPayload {
  const root = objectValue(value, 'village expansion submission response');
  assertProtocol(root);
  const submission = objectValue(root.villageExpansionSubmission, 'villageExpansionSubmission');
  const status = stringValue(submission.status, 'villageExpansionSubmission.status');
  if (status !== 'expanded') {
    throw new ContractError(`Unsupported village expansion submission status: ${status}`);
  }
  const previousSize = rangedIntegerValue(
    submission.previousSize,
    'villageExpansionSubmission.previousSize',
    1,
    5,
  );
  const villageSize = rangedIntegerValue(
    submission.villageSize,
    'villageExpansionSubmission.villageSize',
    2,
    6,
  );
  if (villageSize !== previousSize + 1) {
    throw new ContractError('villageExpansionSubmission did not advance exactly one tier');
  }
  return {
    status,
    villageId: positiveIntegerValue(submission.villageId, 'villageExpansionSubmission.villageId'),
    previousSize,
    villageSize,
    price: nonNegativeIntegerValue(submission.price, 'villageExpansionSubmission.price'),
    creditsAfter: nonNegativeIntegerValue(
      submission.creditsAfter,
      'villageExpansionSubmission.creditsAfter',
    ),
    terrainRevision: positiveIntegerValue(
      submission.terrainRevision,
      'villageExpansionSubmission.terrainRevision',
    ),
  };
}

export function parsePlayerPicturesResponse(value: unknown): PlayerPicturesPayload {
  const root = objectValue(value, 'player pictures response');
  assertProtocol(root);
  const pictures = objectValue(root.playerPictures, 'playerPictures');
  const pictureIds = arrayValue(pictures.pictureIds, 'playerPictures.pictureIds').map(
    (entry, index) => positiveIntegerValue(entry, `playerPictures.pictureIds[${String(index)}]`),
  );
  if (new Set(pictureIds).size !== pictureIds.length) {
    throw new ContractError('playerPictures.pictureIds contains duplicates');
  }
  const capacity = integerValue(pictures.capacity, 'playerPictures.capacity');
  if (capacity !== PLAYER_PICTURE_CAPACITY) {
    throw new ContractError(`playerPictures.capacity must be ${String(PLAYER_PICTURE_CAPACITY)}`);
  }
  const sex = rangedIntegerValue(pictures.sex, 'playerPictures.sex', 0, 1) as 0 | 1;
  if (pictureIds.length > capacity) {
    throw new ContractError('playerPictures.pictureIds exceeds playerPictures.capacity');
  }
  return {
    playerId: positiveIntegerValue(pictures.playerId, 'playerPictures.playerId'),
    capacity: PLAYER_PICTURE_CAPACITY,
    sex,
    pictureIds,
  };
}

export function parsePlayerProfileResponse(value: unknown): PlayerProfilePayload {
  const root = objectValue(value, 'player profile response');
  assertProtocol(root);
  const profile = objectValue(root.playerProfile, 'playerProfile');
  const rawPictureId = profile.presentationPictureId;
  const presentationPictureId =
    rawPictureId === null
      ? null
      : positiveIntegerValue(rawPictureId, 'playerProfile.presentationPictureId');
  const preferredLanguages = arrayValue(
    profile.preferredLanguages,
    'playerProfile.preferredLanguages',
  ).map((value, index) => {
    const code = stringValue(value, `playerProfile.preferredLanguages[${String(index)}]`);
    if (!isPreferredLanguageCode(code)) {
      throw new ContractError(`playerProfile.preferredLanguages[${String(index)}] is unsupported`);
    }
    return code;
  });
  if (
    preferredLanguages.length > 3 ||
    new Set(preferredLanguages).size !== preferredLanguages.length
  ) {
    throw new ContractError(
      'playerProfile.preferredLanguages must contain up to three unique languages',
    );
  }
  return {
    playerId: positiveIntegerValue(profile.playerId, 'playerProfile.playerId'),
    firstName: stringValue(profile.firstName, 'playerProfile.firstName'),
    lastName: stringValue(profile.lastName, 'playerProfile.lastName'),
    username: stringValue(profile.username, 'playerProfile.username'),
    memberSince: nonNegativeIntegerValue(profile.memberSince, 'playerProfile.memberSince'),
    age: nonNegativeIntegerValue(profile.age, 'playerProfile.age'),
    presentation: stringValue(profile.presentation, 'playerProfile.presentation'),
    presentationPictureId,
    totalScore: nonNegativeIntegerValue(profile.totalScore, 'playerProfile.totalScore'),
    sex: rangedIntegerValue(profile.sex, 'playerProfile.sex', -128, 127),
    homeVillageId: nonNegativeIntegerValue(profile.homeVillageId, 'playerProfile.homeVillageId'),
    visitingVillageId: nonNegativeIntegerValue(
      profile.visitingVillageId,
      'playerProfile.visitingVillageId',
    ),
    online: booleanValue(profile.online, 'playerProfile.online'),
    country: stringValue(profile.country, 'playerProfile.country'),
    preferredLanguages,
    city: stringValue(profile.city, 'playerProfile.city'),
    wantsEmail: booleanValue(profile.wantsEmail, 'playerProfile.wantsEmail'),
    level: nonNegativeIntegerValue(profile.level, 'playerProfile.level'),
    theme: rangedIntegerValue(profile.theme, 'playerProfile.theme', -128, 127),
    hasYouBlocked: booleanValue(profile.hasYouBlocked, 'playerProfile.hasYouBlocked'),
    blockedByYou: booleanValue(profile.blockedByYou, 'playerProfile.blockedByYou'),
  };
}

export function parsePlayerVillagesResponse(value: unknown): PlayerVillagesPayload {
  const root = objectValue(value, 'player villages response');
  assertProtocol(root);
  const source = objectValue(root.playerVillages, 'playerVillages');
  const blocked = booleanValue(source.blocked, 'playerVillages.blocked');
  const villages = arrayValue(source.villages, 'playerVillages.villages').map((entry, index) => {
    const label = `playerVillages.villages[${String(index)}]`;
    const village = objectValue(entry, label);
    return {
      id: positiveIntegerValue(village.id, `${label}.id`),
      name: boundedText(village.name, `${label}.name`, 8_192, false),
    };
  });
  if (
    villages.length > 16 ||
    new Set(villages.map((village) => village.id)).size !== villages.length
  ) {
    throw new ContractError('playerVillages.villages is invalid');
  }
  if (blocked && villages.length !== 0) {
    throw new ContractError('blocked playerVillages must not expose villages');
  }
  return {
    playerId: positiveIntegerValue(source.playerId, 'playerVillages.playerId'),
    blocked,
    villages,
  };
}

export function parsePlayerBadgesResponse(value: unknown): PlayerBadgesPayload {
  const root = objectValue(value, 'player badges response');
  assertProtocol(root);
  const badges = objectValue(root.playerBadges, 'playerBadges');
  const values = arrayValue(badges.values, 'playerBadges.values').map((entry, index) => {
    const badgeValue = objectValue(entry, `playerBadges.values[${String(index)}]`);
    return {
      key: nonNegativeIntegerValue(badgeValue.key, `playerBadges.values[${String(index)}].key`),
      value: nonNegativeIntegerValue(
        badgeValue.value,
        `playerBadges.values[${String(index)}].value`,
      ),
    };
  });
  const keys = values.map((entry) => entry.key);
  if (new Set(keys).size !== keys.length) {
    throw new ContractError('playerBadges.values contains duplicate keys');
  }
  if (
    keys.some((key, index) => {
      const previousKey = keys[index - 1];
      return previousKey !== undefined && key <= previousKey;
    })
  ) {
    throw new ContractError('playerBadges.values is not ordered by key');
  }
  const resources = arrayValue(badges.resources, 'playerBadges.resources').map((entry, index) => {
    const label = `playerBadges.resources[${String(index)}]`;
    const resource = objectValue(entry, label);
    const button = stringValue(resource.button, `${label}.button`);
    if (
      button.length === 0 ||
      button.startsWith('/') ||
      button.includes('..') ||
      !/^[a-z0-9_-]+$/u.test(button)
    ) {
      throw new ContractError(`${label}.button is not a safe resource asset name`);
    }
    return {
      resourceId: positiveIntegerValue(resource.resourceId, `${label}.resourceId`),
      name: stringValue(resource.name, `${label}.name`),
      button,
      amount: nonNegativeIntegerValue(resource.amount, `${label}.amount`),
    };
  });
  const resourceIds = resources.map((entry) => entry.resourceId);
  if (new Set(resourceIds).size !== resourceIds.length) {
    throw new ContractError('playerBadges.resources contains duplicate resources');
  }
  for (const resource of resources) {
    const rawValue = values.find((entry) => entry.key === 20_000 + resource.resourceId)?.value;
    if (rawValue !== resource.amount) {
      throw new ContractError('playerBadges resource amount contradicts its raw badge value');
    }
  }

  const tooltips =
    badges.tooltips === undefined
      ? undefined
      : arrayValue(badges.tooltips, 'playerBadges.tooltips').map((entry, index) => {
          const label = `playerBadges.tooltips[${String(index)}]`;
          const tooltip = objectValue(entry, label);
          return {
            key: nonNegativeIntegerValue(tooltip.key, `${label}.key`),
            text: stringValue(tooltip.text, `${label}.text`),
          };
        });
  if (tooltips !== undefined) {
    const tooltipKeys = tooltips.map((entry) => entry.key);
    if (new Set(tooltipKeys).size !== tooltipKeys.length) {
      throw new ContractError('playerBadges.tooltips contains duplicate keys');
    }
    if (
      tooltipKeys.some((key, index) => {
        const previousKey = tooltipKeys[index - 1];
        return previousKey !== undefined && key <= previousKey;
      })
    ) {
      throw new ContractError('playerBadges.tooltips is not ordered by key');
    }
  }
  const barnhjalpareUrl =
    badges.barnhjalpareUrl === undefined
      ? undefined
      : stringValue(badges.barnhjalpareUrl, 'playerBadges.barnhjalpareUrl');
  if (barnhjalpareUrl !== undefined && !isSafeSocialURL(barnhjalpareUrl)) {
    throw new ContractError('playerBadges.barnhjalpareUrl is not a safe badge URL');
  }
  const contentCreatorUrl =
    badges.contentCreatorUrl === undefined
      ? undefined
      : stringValue(badges.contentCreatorUrl, 'playerBadges.contentCreatorUrl');
  if (contentCreatorUrl !== undefined && !isSafeSocialURL(contentCreatorUrl)) {
    throw new ContractError('playerBadges.contentCreatorUrl is not a safe social media URL');
  }
  return {
    playerId: positiveIntegerValue(badges.playerId, 'playerBadges.playerId'),
    vip: booleanValue(badges.vip, 'playerBadges.vip'),
    values,
    resources,
    ...(tooltips === undefined ? {} : { tooltips }),
    ...(contentCreatorUrl === undefined ? {} : { contentCreatorUrl }),
    ...(barnhjalpareUrl === undefined ? {} : { barnhjalpareUrl }),
  };
}

function isSafeSocialURL(value: string): boolean {
  if (value.length === 0 || value.length > 2048) return false;
  try {
    const parsed = new URL(value);
    return (
      (parsed.protocol === 'https:' || parsed.protocol === 'http:') &&
      parsed.hostname.length > 0 &&
      parsed.username === '' &&
      parsed.password === ''
    );
  } catch {
    return false;
  }
}

export function parseBlockedPlayerListResponse(value: unknown): BlockedPlayerListPayload {
  const root = objectValue(value, 'blocked-player list response');
  assertProtocol(root);
  const blockedPlayers = objectValue(root.blockedPlayers, 'blockedPlayers');
  const entries = arrayValue(blockedPlayers.entries, 'blockedPlayers.entries').map(
    (entry, index) => {
      const label = `blockedPlayers.entries[${String(index)}]`;
      const row = objectValue(entry, label);
      const rawPictureId = row.pictureId;
      return {
        playerId: positiveIntegerValue(row.playerId, `${label}.playerId`),
        username: stringValue(row.username, `${label}.username`),
        online: booleanValue(row.online, `${label}.online`),
        age: nonNegativeIntegerValue(row.age, `${label}.age`),
        sex: rangedIntegerValue(row.sex, `${label}.sex`, -128, 127),
        villageId: nonNegativeIntegerValue(row.villageId, `${label}.villageId`),
        villageName: stringValue(row.villageName, `${label}.villageName`),
        villageVisitors: nonNegativeIntegerValue(row.villageVisitors, `${label}.villageVisitors`),
        city: stringValue(row.city, `${label}.city`),
        country: stringValue(row.country, `${label}.country`),
        pictureId:
          rawPictureId === null ? null : positiveIntegerValue(rawPictureId, `${label}.pictureId`),
        theme: rangedIntegerValue(row.theme, `${label}.theme`, -128, 127),
        level: nonNegativeIntegerValue(row.level, `${label}.level`),
      };
    },
  );
  const playerIds = entries.map((entry) => entry.playerId);
  if (new Set(playerIds).size !== playerIds.length) {
    throw new ContractError('blockedPlayers.entries contains duplicate players');
  }
  return {
    ownerPlayerId: positiveIntegerValue(
      blockedPlayers.ownerPlayerId,
      'blockedPlayers.ownerPlayerId',
    ),
    entries,
  };
}

function featuredVillageImageURL(value: unknown, label: string, villageId: unknown): string {
  const url = stringValue(value, label);
  if (
    !/^\/api\/v2\/featured-villages\/[1-9][0-9]*\/image\?v=[a-f0-9]{64}$/.test(url) ||
    !url.startsWith(`/api/v2/featured-villages/${String(villageId)}/image?`)
  ) {
    throw new ContractError(`${label} is not a featured village image URL`);
  }
  return url;
}

export function parseOnlinePlayerListResponse(value: unknown): OnlinePlayerListPayload {
  const root = objectValue(value, 'online-player list response');
  assertProtocol(root);
  const onlinePlayers = objectValue(root.onlinePlayers, 'onlinePlayers');
  const rawTexts = objectValue(onlinePlayers.texts, 'onlinePlayers.texts');
  const texts = {
    toolbarLabel: boundedText(rawTexts.toolbarLabel, 'onlinePlayers.texts.toolbarLabel', 200, true),
    visitedLabel: boundedText(rawTexts.visitedLabel, 'onlinePlayers.texts.visitedLabel', 200, true),
    visitorsLabel: boundedText(
      rawTexts.visitorsLabel,
      'onlinePlayers.texts.visitorsLabel',
      200,
      true,
    ),
    onlineLabel: boundedText(rawTexts.onlineLabel, 'onlinePlayers.texts.onlineLabel', 200, true),
    noobDescription: boundedText(
      rawTexts.noobDescription,
      'onlinePlayers.texts.noobDescription',
      500,
      true,
    ),
    ockelbodaDescription: boundedText(
      rawTexts.ockelbodaDescription,
      'onlinePlayers.texts.ockelbodaDescription',
      500,
      true,
    ),
    tikiTikiDescription: boundedText(
      rawTexts.tikiTikiDescription,
      'onlinePlayers.texts.tikiTikiDescription',
      500,
      true,
    ),
    travelTitleTemplate: boundedText(
      rawTexts.travelTitleTemplate,
      'onlinePlayers.texts.travelTitleTemplate',
      200,
      true,
    ),
    travelMessageTemplate: boundedText(
      rawTexts.travelMessageTemplate,
      'onlinePlayers.texts.travelMessageTemplate',
      500,
      true,
    ),
  };
  const entries = arrayValue(onlinePlayers.entries, 'onlinePlayers.entries').map((entry, index) => {
    const label = `onlinePlayers.entries[${String(index)}]`;
    const row = objectValue(entry, label);
    const rawPictureId = row.pictureId;
    return {
      playerId: positiveIntegerValue(row.playerId, `${label}.playerId`),
      username: stringValue(row.username, `${label}.username`),
      status: boundedText(row.status, `${label}.status`, 100, false),
      away: booleanValue(row.away, `${label}.away`),
      age: nonNegativeIntegerValue(row.age, `${label}.age`),
      sex: rangedIntegerValue(row.sex, `${label}.sex`, -128, 127),
      villageId: nonNegativeIntegerValue(row.villageId, `${label}.villageId`),
      villageName: stringValue(row.villageName, `${label}.villageName`),
      villageVisitors: nonNegativeIntegerValue(row.villageVisitors, `${label}.villageVisitors`),
      city: stringValue(row.city, `${label}.city`),
      country: stringValue(row.country, `${label}.country`),
      score: nonNegativeIntegerValue(row.score, `${label}.score`),
      pictureId:
        rawPictureId === null ? null : positiveIntegerValue(rawPictureId, `${label}.pictureId`),
      theme: rangedIntegerValue(row.theme, `${label}.theme`, -128, 127),
      level: nonNegativeIntegerValue(row.level, `${label}.level`),
    };
  });
  const playerIds = entries.map((entry) => entry.playerId);
  if (new Set(playerIds).size !== playerIds.length) {
    throw new ContractError('onlinePlayers.entries contains duplicate players');
  }
  const featuredVillages =
    onlinePlayers.featuredVillages === undefined
      ? undefined
      : arrayValue(onlinePlayers.featuredVillages, 'onlinePlayers.featuredVillages').map(
          (entry, index) => {
            const label = `onlinePlayers.featuredVillages[${String(index)}]`;
            const row = objectValue(entry, label);
            return {
              villageId: positiveIntegerValue(row.villageId, `${label}.villageId`),
              ...(row.imageUrl === undefined
                ? {}
                : {
                    imageUrl: featuredVillageImageURL(
                      row.imageUrl,
                      `${label}.imageUrl`,
                      row.villageId,
                    ),
                  }),
              title: boundedText(row.title, `${label}.title`, 100, true),
              description: boundedText(row.description, `${label}.description`, 500, false),
            };
          },
        );
  return {
    texts,
    ...(featuredVillages === undefined ? {} : { featuredVillages }),
    ...(onlinePlayers.creativeModeEnabled === undefined
      ? {}
      : {
          creativeModeEnabled: booleanValue(
            onlinePlayers.creativeModeEnabled,
            'onlinePlayers.creativeModeEnabled',
          ),
        }),
    ...(onlinePlayers.cinemaEnabled === undefined
      ? {}
      : {
          cinemaEnabled: booleanValue(onlinePlayers.cinemaEnabled, 'onlinePlayers.cinemaEnabled'),
        }),
    viewerPlayerId: positiveIntegerValue(
      onlinePlayers.viewerPlayerId,
      'onlinePlayers.viewerPlayerId',
    ),
    entries,
  };
}

export function parseHelpDirectoryResponse(value: unknown): HelpDirectoryPayload {
  const root = objectValue(value, 'help-directory response');
  assertProtocol(root);
  const helpDirectory = objectValue(root.helpDirectory, 'helpDirectory');
  const rawTexts = objectValue(helpDirectory.texts, 'helpDirectory.texts');
  const texts = {
    toolbarLabel: boundedText(rawTexts.toolbarLabel, 'helpDirectory.texts.toolbarLabel', 200, true),
    helpersIntroduction: boundedText(
      rawTexts.helpersIntroduction,
      'helpDirectory.texts.helpersIntroduction',
      500,
      true,
    ),
    moderatorsIntroduction: boundedText(
      rawTexts.moderatorsIntroduction,
      'helpDirectory.texts.moderatorsIntroduction',
      500,
      true,
    ),
    discordIntroduction: boundedText(
      rawTexts.discordIntroduction,
      'helpDirectory.texts.discordIntroduction',
      500,
      true,
    ),
    discordLabel: boundedText(rawTexts.discordLabel, 'helpDirectory.texts.discordLabel', 200, true),
  };
  const parseEntries = (
    rawEntries: unknown,
    section: 'helpers' | 'moderators',
  ): readonly HelpDirectoryEntryPayload[] => {
    const entries = arrayValue(rawEntries, `helpDirectory.${section}`).map((entry, index) => {
      const label = `helpDirectory.${section}[${String(index)}]`;
      const row = objectValue(entry, label);
      const rawPictureId = row.pictureId;
      return {
        playerId: positiveIntegerValue(row.playerId, `${label}.playerId`),
        username: stringValue(row.username, `${label}.username`),
        online: booleanValue(row.online, `${label}.online`),
        sex: rangedIntegerValue(row.sex, `${label}.sex`, -128, 127),
        level: nonNegativeIntegerValue(row.level, `${label}.level`),
        pictureId:
          rawPictureId === null ? null : positiveIntegerValue(rawPictureId, `${label}.pictureId`),
        status: stringValue(row.status, `${label}.status`),
        theme: rangedIntegerValue(row.theme, `${label}.theme`, -128, 127),
        visitorCount: nonNegativeIntegerValue(row.visitorCount, `${label}.visitorCount`),
      };
    });
    const playerIds = entries.map((entry) => entry.playerId);
    if (new Set(playerIds).size !== playerIds.length) {
      throw new ContractError(`helpDirectory.${section} contains duplicate players`);
    }
    return entries;
  };
  return {
    texts,
    ...(helpDirectory.rulesText === undefined
      ? {}
      : {
          rulesText: communityRulesText(helpDirectory.rulesText, 'helpDirectory.rulesText'),
        }),
    helpers: parseEntries(helpDirectory.helpers, 'helpers'),
    moderators: parseEntries(helpDirectory.moderators, 'moderators'),
  };
}

export function parseSupportIdentityResponse(value: unknown): SupportIdentityPayload {
  const response = objectValue(value, 'response');
  assertProtocol(response);
  const identity = objectValue(response.supportIdentity, 'supportIdentity');
  return {
    username: boundedText(identity.username, 'supportIdentity.username', 64, true),
    email: boundedText(identity.email, 'supportIdentity.email', 254, true),
  };
}

export function parseWardrobeResponse(value: unknown): WardrobePayload {
  const root = objectValue(value, 'wardrobe response');
  assertProtocol(root);
  const wardrobe = objectValue(root.wardrobe, 'wardrobe');
  const rawTexts = objectValue(wardrobe.texts, 'wardrobe.texts');
  const texts = {
    toolbarLabel: boundedText(rawTexts.toolbarLabel, 'wardrobe.texts.toolbarLabel', 200, true),
    buyClothesLabel: boundedText(
      rawTexts.buyClothesLabel,
      'wardrobe.texts.buyClothesLabel',
      200,
      true,
    ),
    wardrobeLabel: boundedText(rawTexts.wardrobeLabel, 'wardrobe.texts.wardrobeLabel', 200, true),
    buyLabel: boundedText(rawTexts.buyLabel, 'wardrobe.texts.buyLabel', 200, true),
    confirmationMessage: boundedText(
      rawTexts.confirmationMessage,
      'wardrobe.texts.confirmationMessage',
      500,
      true,
    ),
    yesLabel: boundedText(rawTexts.yesLabel, 'wardrobe.texts.yesLabel', 200, true),
    noLabel: boundedText(rawTexts.noLabel, 'wardrobe.texts.noLabel', 200, true),
  };
  const gender = stringValue(wardrobe.gender, 'wardrobe.gender');
  if (gender !== 'man' && gender !== 'woman' && gender !== 'unknown') {
    throw new ContractError('wardrobe.gender is invalid');
  }
  const parseLayer = (value: unknown, label: string): AvatarAppearanceLayer => {
    const layer = objectValue(value, label);
    return {
      garmentId: positiveIntegerValue(layer.garmentId, `${label}.garmentId`),
      hue: numberValue(layer.hue, `${label}.hue`),
      saturation: numberValue(layer.saturation, `${label}.saturation`),
      brightness: numberValue(layer.brightness, `${label}.brightness`),
      opacity: numberValue(layer.opacity, `${label}.opacity`),
    };
  };
  const appearanceLayers = arrayValue(wardrobe.appearanceLayers, 'wardrobe.appearanceLayers').map(
    (layer, index) => {
      const label = `wardrobe.appearanceLayers[${String(index)}]`;
      const rawLayer = objectValue(layer, label);
      return {
        ...parseLayer(rawLayer, label),
        categoryId: stringValue(rawLayer.categoryId, `${label}.categoryId`),
      };
    },
  );
  const categories = arrayValue(wardrobe.categories, 'wardrobe.categories').map(
    (entry, categoryIndex) => {
      const label = `wardrobe.categories[${String(categoryIndex)}]`;
      const category = objectValue(entry, label);
      const options = arrayValue(category.options, `${label}.options`).map(
        (option, optionIndex) => {
          const optionLabel = `${label}.options[${String(optionIndex)}]`;
          const rawOption = objectValue(option, optionLabel);
          return {
            ...parseLayer(rawOption, optionLabel),
            name: stringValue(rawOption.name, `${optionLabel}.name`),
            free: booleanValue(rawOption.free, `${optionLabel}.free`),
          };
        },
      );
      if (options.length < 2) {
        throw new ContractError(`${label}.options must contain at least two garments`);
      }
      const selectedIndex = nonNegativeIntegerValue(
        category.selectedIndex,
        `${label}.selectedIndex`,
      );
      if (selectedIndex >= options.length) {
        throw new ContractError(`${label}.selectedIndex is outside its options`);
      }
      return {
        id: stringValue(category.id, `${label}.id`),
        label: stringValue(category.label, `${label}.label`),
        selectedIndex,
        options,
      };
    },
  );
  const shopCategories = arrayValue(wardrobe.shopCategories, 'wardrobe.shopCategories').map(
    (entry, categoryIndex) => {
      const label = `wardrobe.shopCategories[${String(categoryIndex)}]`;
      const category = objectValue(entry, label);
      const options = arrayValue(category.options, `${label}.options`).map(
        (entryOption, optionIndex) => {
          const optionLabel = `${label}.options[${String(optionIndex)}]`;
          const option = objectValue(entryOption, optionLabel);
          const requirements = arrayValue(option.requirements, `${optionLabel}.requirements`).map(
            (entryRequirement, requirementIndex) => {
              const requirementLabel = `${optionLabel}.requirements[${String(requirementIndex)}]`;
              const requirement = objectValue(entryRequirement, requirementLabel);
              const image = stringValue(requirement.image, `${requirementLabel}.image`);
              if (image.startsWith('/') || image.includes('..')) {
                throw new ContractError(`${requirementLabel}.image is unsafe`);
              }
              return {
                name: stringValue(requirement.name, `${requirementLabel}.name`),
                image,
                met: booleanValue(requirement.met, `${requirementLabel}.met`),
              };
            },
          );
          if (requirements.length > 4) {
            throw new ContractError(`${optionLabel}.requirements contains more than four badges`);
          }
          const requirementsMet = booleanValue(
            option.requirementsMet,
            `${optionLabel}.requirementsMet`,
          );
          if (requirementsMet !== requirements.every((requirement) => requirement.met)) {
            throw new ContractError(`${optionLabel}.requirementsMet disagrees with its badges`);
          }
          const hues = arrayValue(option.hues, `${optionLabel}.hues`).map((hue, hueIndex) =>
            numberValue(hue, `${optionLabel}.hues[${String(hueIndex)}]`),
          );
          if (hues.length > 18) {
            throw new ContractError(`${optionLabel}.hues contains more than 18 positions`);
          }
          const hueEnabled = booleanValue(option.hueEnabled, `${optionLabel}.hueEnabled`);
          if (hueEnabled && hues.length === 0) {
            throw new ContractError(`${optionLabel}.hues is empty for a color-adjustable garment`);
          }
          const hueIndex = nonNegativeIntegerValue(option.hueIndex, `${optionLabel}.hueIndex`);
          const saturationIndex = nonNegativeIntegerValue(
            option.saturationIndex,
            `${optionLabel}.saturationIndex`,
          );
          const brightnessIndex = nonNegativeIntegerValue(
            option.brightnessIndex,
            `${optionLabel}.brightnessIndex`,
          );
          const opacityIndex = nonNegativeIntegerValue(
            option.opacityIndex,
            `${optionLabel}.opacityIndex`,
          );
          if (
            hueIndex >= Math.max(1, hues.length) ||
            saturationIndex > 8 ||
            brightnessIndex > 8 ||
            opacityIndex > 8
          ) {
            throw new ContractError(`${optionLabel} contains an invalid source selector index`);
          }
          return {
            garmentId: positiveIntegerValue(option.garmentId, `${optionLabel}.garmentId`),
            name: stringValue(option.name, `${optionLabel}.name`),
            price: nonNegativeIntegerValue(option.price, `${optionLabel}.price`),
            requirementsMet,
            requirements,
            hueEnabled,
            saturationEnabled: booleanValue(
              option.saturationEnabled,
              `${optionLabel}.saturationEnabled`,
            ),
            brightnessEnabled: booleanValue(
              option.brightnessEnabled,
              `${optionLabel}.brightnessEnabled`,
            ),
            opacityEnabled: booleanValue(option.opacityEnabled, `${optionLabel}.opacityEnabled`),
            hues,
            saturationMin: numberValue(option.saturationMin, `${optionLabel}.saturationMin`),
            saturationMax: numberValue(option.saturationMax, `${optionLabel}.saturationMax`),
            brightnessMin: numberValue(option.brightnessMin, `${optionLabel}.brightnessMin`),
            brightnessMax: numberValue(option.brightnessMax, `${optionLabel}.brightnessMax`),
            opacityMin: numberValue(option.opacityMin, `${optionLabel}.opacityMin`),
            opacityMax: numberValue(option.opacityMax, `${optionLabel}.opacityMax`),
            hueIndex,
            saturationIndex,
            brightnessIndex,
            opacityIndex,
          };
        },
      );
      if (options.length === 0) {
        throw new ContractError(`${label}.options must contain at least one garment`);
      }
      return {
        id: stringValue(category.id, `${label}.id`),
        label: stringValue(category.label, `${label}.label`),
        options,
      };
    },
  );
  return {
    texts,
    gender,
    credits: nonNegativeIntegerValue(wardrobe.credits, 'wardrobe.credits'),
    appearanceLayers,
    categories,
    shopCategories,
  };
}

export function parseVillageVisitorListResponse(value: unknown): VillageVisitorListPayload {
  const root = objectValue(value, 'village-visitor list response');
  assertProtocol(root);
  const villageVisitors = objectValue(root.villageVisitors, 'villageVisitors');
  const entries = arrayValue(villageVisitors.entries, 'villageVisitors.entries').map(
    (entry, index) => {
      const label = `villageVisitors.entries[${String(index)}]`;
      const row = objectValue(entry, label);
      const rawPictureId = row.pictureId;
      return {
        playerId: positiveIntegerValue(row.playerId, `${label}.playerId`),
        username: stringValue(row.username, `${label}.username`),
        ...(row.status === undefined ? {} : { status: stringValue(row.status, `${label}.status`) }),
        online: booleanValue(row.online, `${label}.online`),
        age: nonNegativeIntegerValue(row.age, `${label}.age`),
        sex: rangedIntegerValue(row.sex, `${label}.sex`, -128, 127),
        villageId: nonNegativeIntegerValue(row.villageId, `${label}.villageId`),
        villageName: stringValue(row.villageName, `${label}.villageName`),
        villageVisitors: nonNegativeIntegerValue(row.villageVisitors, `${label}.villageVisitors`),
        city: stringValue(row.city, `${label}.city`),
        country: stringValue(row.country, `${label}.country`),
        pictureId:
          rawPictureId === null ? null : positiveIntegerValue(rawPictureId, `${label}.pictureId`),
        theme: rangedIntegerValue(row.theme, `${label}.theme`, -128, 127),
        level: nonNegativeIntegerValue(row.level, `${label}.level`),
      };
    },
  );
  const playerIds = entries.map((entry) => entry.playerId);
  if (new Set(playerIds).size !== playerIds.length) {
    throw new ContractError('villageVisitors.entries contains duplicate players');
  }
  return {
    viewerPlayerId: positiveIntegerValue(
      villageVisitors.viewerPlayerId,
      'villageVisitors.viewerPlayerId',
    ),
    villageId: positiveIntegerValue(villageVisitors.villageId, 'villageVisitors.villageId'),
    entries,
  };
}

export function parseFriendListResponse(value: unknown): FriendListPayload {
  const root = objectValue(value, 'friend list response');
  assertProtocol(root);
  return parseFriendList(root.friendList, 'friendList');
}

export function parseFriendRemovalResponse(value: unknown): FriendRemovalPayload {
  const root = objectValue(value, 'friend removal response');
  assertProtocol(root);
  const removal = objectValue(root.friendRemoval, 'friendRemoval');
  const status = stringValue(removal.status, 'friendRemoval.status');
  if (status !== 'removed') {
    throw new ContractError(`Unsupported friend removal status: ${status}`);
  }
  return {
    status,
    playerId: positiveIntegerValue(removal.playerId, 'friendRemoval.playerId'),
    friendList: parseFriendList(removal.friendList, 'friendRemoval.friendList'),
  };
}

export function parseFriendRelationshipResponse(value: unknown): FriendRelationshipPayload {
  const root = objectValue(value, 'friend relationship response');
  assertProtocol(root);
  return parseFriendRelationship(root.friendRelationship, 'friendRelationship');
}

export function parseFriendRequestMutationResponse(value: unknown): FriendRequestMutationPayload {
  const root = objectValue(value, 'friend request mutation response');
  assertProtocol(root);
  const mutation = objectValue(root.friendRequest, 'friendRequest');
  const status = stringValue(mutation.status, 'friendRequest.status');
  if (status !== 'requested' && status !== 'accepted' && status !== 'declined') {
    throw new ContractError(`Unsupported friend request status: ${status}`);
  }
  const playerId = positiveIntegerValue(mutation.playerId, 'friendRequest.playerId');
  const relationship = parseFriendRelationship(mutation.relationship, 'friendRequest.relationship');
  if (relationship.playerId !== playerId) {
    throw new ContractError('friendRequest relationship targets a different player');
  }
  const rawFriendList = mutation.friendList;
  const friendList =
    rawFriendList === undefined
      ? undefined
      : parseFriendList(rawFriendList, 'friendRequest.friendList');
  if (
    (status === 'accepted' &&
      (relationship.state !== 'friend' ||
        friendList?.entries.some((entry) => entry.playerId === playerId) !== true)) ||
    (status !== 'accepted' && (relationship.state !== 'available' || friendList !== undefined))
  ) {
    throw new ContractError('friendRequest status contradicts its authoritative relationship');
  }
  return {
    status,
    playerId,
    relationship,
    ...(friendList === undefined ? {} : { friendList }),
  };
}

function parseFriendRelationship(value: unknown, label: string): FriendRelationshipPayload {
  const relationship = objectValue(value, label);
  const state = stringValue(relationship.state, `${label}.state`);
  if (state !== 'available' && state !== 'incoming' && state !== 'friend') {
    throw new ContractError(`Unsupported friend relationship state: ${state}`);
  }
  return {
    playerId: positiveIntegerValue(relationship.playerId, `${label}.playerId`),
    state,
  };
}

function parseFriendList(value: unknown, label: string): FriendListPayload {
  const friends = objectValue(value, label);
  const entries = arrayValue(friends.entries, `${label}.entries`).map((raw, index) => {
    const entryLabel = `${label}.entries[${String(index)}]`;
    const entry = objectValue(raw, entryLabel);
    const rawPictureId = entry.pictureId;
    return {
      playerId: positiveIntegerValue(entry.playerId, `${entryLabel}.playerId`),
      username: stringValue(entry.username, `${entryLabel}.username`),
      ...(entry.status === undefined
        ? {}
        : { status: stringValue(entry.status, `${entryLabel}.status`) }),
      online: booleanValue(entry.online, `${entryLabel}.online`),
      age: nonNegativeIntegerValue(entry.age, `${entryLabel}.age`),
      sex: rangedIntegerValue(entry.sex, `${entryLabel}.sex`, -128, 127),
      villageId: nonNegativeIntegerValue(entry.villageId, `${entryLabel}.villageId`),
      villageVisitors: nonNegativeIntegerValue(
        entry.villageVisitors,
        `${entryLabel}.villageVisitors`,
      ),
      city: stringValue(entry.city, `${entryLabel}.city`),
      country: stringValue(entry.country, `${entryLabel}.country`),
      pictureId:
        rawPictureId === null
          ? null
          : positiveIntegerValue(rawPictureId, `${entryLabel}.pictureId`),
      theme: rangedIntegerValue(entry.theme, `${entryLabel}.theme`, -128, 127),
      level: nonNegativeIntegerValue(entry.level, `${entryLabel}.level`),
    };
  });
  const playerIds = entries.map((entry) => entry.playerId);
  if (new Set(playerIds).size !== playerIds.length) {
    throw new ContractError(`${label}.entries contains duplicate players`);
  }
  return {
    ownerPlayerId: positiveIntegerValue(friends.ownerPlayerId, `${label}.ownerPlayerId`),
    entries,
  };
}

export function parseVillageTravelResponse(value: unknown): VillageTravelPayload {
  const root = objectValue(value, 'village travel response');
  assertProtocol(root);
  const travel = objectValue(root.villageTravel, 'villageTravel');
  const status = stringValue(travel.status, 'villageTravel.status');
  if (status !== 'entered' && status !== 'unchanged') {
    throw new ContractError(`Unsupported village travel status: ${status}`);
  }
  const village = objectValue(travel.village, 'villageTravel.village');
  return {
    status,
    playerId: positiveIntegerValue(travel.playerId, 'villageTravel.playerId'),
    sourceVillageId: positiveIntegerValue(travel.sourceVillageId, 'villageTravel.sourceVillageId'),
    sessionGeneration: positiveIntegerValue(
      travel.sessionGeneration,
      'villageTravel.sessionGeneration',
    ),
    village: {
      id: positiveIntegerValue(village.id, 'villageTravel.village.id'),
      name: stringValue(village.name, 'villageTravel.village.name'),
      ownerPlayerId: positiveIntegerValue(travel.playerId, 'villageTravel.playerId'),
    },
  };
}

export function parseOwnedVillagesResponse(
  value: unknown,
  token: string,
): OwnedVillagesResponsePayload {
  const root = objectValue(value, 'owned villages response');
  assertProtocol(root);
  const source = objectValue(root.ownedVillages, 'ownedVillages');
  const status = stringValue(source.status, 'ownedVillages.status');
  const statuses: readonly OwnedVillageStatus[] = [
    'ok',
    'already-unlocked',
    'wrong-slot',
    'locked-level',
    'locked-coins',
    'no-tier',
    'invalid-session',
    'server-error',
  ];
  if (!statuses.includes(status as OwnedVillageStatus)) {
    throw new ContractError(`Unsupported owned-village status: ${status}`);
  }
  const slots = arrayValue(source.slots, 'ownedVillages.slots').map((entry, index) => {
    const label = `ownedVillages.slots[${String(index)}]`;
    const slot = objectValue(entry, label);
    const villageId =
      slot.villageId === null ? null : positiveIntegerValue(slot.villageId, `${label}.villageId`);
    const unlocked = booleanValue(slot.unlocked, `${label}.unlocked`);
    const canUnlock = booleanValue(slot.canUnlock, `${label}.canUnlock`);
    if (unlocked !== (villageId !== null) || (unlocked && canUnlock)) {
      throw new ContractError(`${label} has inconsistent availability`);
    }
    return {
      slot: positiveIntegerValue(slot.slot, `${label}.slot`),
      villageId,
      name: boundedText(slot.name, `${label}.name`, 8_192, false),
      requiredLevel: nonNegativeIntegerValue(slot.requiredLevel, `${label}.requiredLevel`),
      cost: nonNegativeIntegerValue(slot.cost, `${label}.cost`),
      unlocked,
      canUnlock,
    };
  });
  if (slots.length === 0 || slots.length > 16) {
    throw new ContractError('ownedVillages.slots must contain from 1 to 16 entries');
  }
  const slotNumbers = slots.map((slot) => slot.slot);
  const villageIds = slots.flatMap((slot) => (slot.villageId === null ? [] : [slot.villageId]));
  if (
    new Set(slotNumbers).size !== slotNumbers.length ||
    new Set(villageIds).size !== villageIds.length
  ) {
    throw new ContractError('ownedVillages.slots contains duplicates');
  }
  const nextSlot =
    source.nextSlot === null
      ? null
      : positiveIntegerValue(source.nextSlot, 'ownedVillages.nextSlot');
  const unlockedVillageId =
    source.unlockedVillageId === null
      ? null
      : positiveIntegerValue(source.unlockedVillageId, 'ownedVillages.unlockedVillageId');
  const session = parseSessionResponse(value, token);
  const unlockedSlots = slots.filter(
    (slot): slot is typeof slot & Readonly<{ villageId: number }> => slot.villageId !== null,
  );
  // Sessions put the selected default first; slots retain their unlock order.
  const slotsByVillageId = new Map(unlockedSlots.map((slot) => [slot.villageId, slot]));
  if (
    session.playerId !== positiveIntegerValue(source.playerId, 'ownedVillages.playerId') ||
    session.level !== nonNegativeIntegerValue(source.currentLevel, 'ownedVillages.currentLevel') ||
    session.villages.length !== unlockedSlots.length ||
    new Set(session.villages.map((village) => village.id)).size !== session.villages.length ||
    session.villages.some((village) => {
      const slot = slotsByVillageId.get(village.id);
      return (
        village.name !== slot?.name ||
        (village.ownerPlayerId !== undefined && village.ownerPlayerId !== session.playerId)
      );
    })
  ) {
    throw new ContractError('ownedVillages contradicts the refreshed session');
  }
  if (
    (nextSlot !== null && !slots.some((slot) => slot.slot === nextSlot && !slot.unlocked)) ||
    (unlockedVillageId !== null &&
      !unlockedSlots.some((slot) => slot.villageId === unlockedVillageId))
  ) {
    throw new ContractError('ownedVillages has inconsistent state pointers');
  }
  return {
    ownedVillages: {
      status: status as OwnedVillageStatus,
      message: boundedText(source.message, 'ownedVillages.message', 8_192, false),
      playerId: session.playerId,
      currentLevel: session.level,
      currentCoins: nonNegativeIntegerValue(source.currentCoins, 'ownedVillages.currentCoins'),
      nextSlot,
      unlockedVillageId,
      slots,
    },
    session,
  };
}

export function parseVisitorKickSubmissionResponse(value: unknown): VisitorKickSubmissionPayload {
  const root = objectValue(value, 'visitor kick response');
  assertProtocol(root);
  const submission = objectValue(root.visitorKickSubmission, 'visitorKickSubmission');
  const status = stringValue(submission.status, 'visitorKickSubmission.status');
  if (status !== 'kicked') {
    throw new ContractError(`Unsupported visitor kick status: ${status}`);
  }
  return {
    status,
    villageId: positiveIntegerValue(submission.villageId, 'visitorKickSubmission.villageId'),
    playerId: positiveIntegerValue(submission.playerId, 'visitorKickSubmission.playerId'),
  };
}

export function parseDoorbellSubmissionResponse(value: unknown): DoorbellSubmissionPayload {
  const root = objectValue(value, 'doorbell response');
  assertProtocol(root);
  const submission = objectValue(root.doorbellSubmission, 'doorbellSubmission');
  const status = stringValue(submission.status, 'doorbellSubmission.status');
  if (status !== 'rung') throw new ContractError(`Unsupported doorbell status: ${status}`);
  return {
    status,
    villageId: positiveIntegerValue(submission.villageId, 'doorbellSubmission.villageId'),
    playerId: positiveIntegerValue(submission.playerId, 'doorbellSubmission.playerId'),
    buildingId: nonZeroSignedShortValue(submission.buildingId, 'doorbellSubmission.buildingId'),
    buildingType: doorbellBuildingTypeValue(
      submission.buildingType,
      'doorbellSubmission.buildingType',
    ),
  };
}

export function parseSitSubmissionResponse(value: unknown): SitSubmissionPayload {
  const root = objectValue(value, 'sit response');
  assertProtocol(root);
  const submission = objectValue(root.sitSubmission, 'sitSubmission');
  const status = stringValue(submission.status, 'sitSubmission.status');
  if (status !== 'seated' && status !== 'assembled')
    throw new ContractError(`Unsupported sit status: ${status}`);
  return {
    status,
    villageId: positiveIntegerValue(submission.villageId, 'sitSubmission.villageId'),
    playerId: positiveIntegerValue(submission.playerId, 'sitSubmission.playerId'),
    buildingId: nonZeroSignedShortValue(submission.buildingId, 'sitSubmission.buildingId'),
    buildingType: signedShortValue(submission.buildingType, 'sitSubmission.buildingType'),
    action: avatarSitActionValue(submission.action, 'sitSubmission.action'),
    presence: parsePresencePayload(submission.presence, 'sitSubmission.presence'),
    ...(status === 'assembled'
      ? {
          characters: parseCharactersPayload(submission.characters, 'sitSubmission.characters'),
          worldUpdate: parseWorldNotificationUpdate(submission.worldUpdate),
        }
      : {}),
  };
}

export function parseCannonLaunchSubmissionResponse(value: unknown): CannonLaunchSubmissionPayload {
  const root = objectValue(value, 'cannon launch response');
  assertProtocol(root);
  const submission = objectValue(root.cannonLaunchSubmission, 'cannonLaunchSubmission');
  const status = stringValue(submission.status, 'cannonLaunchSubmission.status');
  if (status !== 'launched') {
    throw new ContractError(`Unsupported cannon launch status: ${status}`);
  }
  const buildingType = signedShortValue(
    submission.buildingType,
    'cannonLaunchSubmission.buildingType',
  );
  if (buildingType !== 658) {
    throw new ContractError('cannonLaunchSubmission.buildingType is not the circus cannon');
  }
  return {
    status,
    villageId: positiveIntegerValue(submission.villageId, 'cannonLaunchSubmission.villageId'),
    playerId: positiveIntegerValue(submission.playerId, 'cannonLaunchSubmission.playerId'),
    buildingId: nonZeroSignedShortValue(submission.buildingId, 'cannonLaunchSubmission.buildingId'),
    buildingType,
    presence: parsePresencePayload(submission.presence, 'cannonLaunchSubmission.presence'),
  };
}

export function parseSwingIntensitySubmissionResponse(
  value: unknown,
): SwingIntensitySubmissionPayload {
  const root = objectValue(value, 'swing intensity response');
  assertProtocol(root);
  const submission = objectValue(root.swingIntensitySubmission, 'swingIntensitySubmission');
  const status = stringValue(submission.status, 'swingIntensitySubmission.status');
  if (status !== 'updated' && status !== 'unchanged') {
    throw new ContractError(`Unsupported swing intensity status: ${status}`);
  }
  const run =
    submission.run === undefined
      ? { status: 'ready' as const, startedAt: 0, endsAt: 0, spins: 0, canStart: true }
      : ((): SwingIntensitySubmissionPayload['run'] => {
          const value = objectValue(submission.run, 'swingIntensitySubmission.run');
          const runStatus = stringValue(value.status, 'swingIntensitySubmission.run.status');
          if (runStatus !== 'ready' && runStatus !== 'active' && runStatus !== 'waiting_for_rest') {
            throw new ContractError(`Unsupported swing run status: ${runStatus}`);
          }
          return {
            status: runStatus,
            startedAt: nonNegativeIntegerValue(
              value.startedAt,
              'swingIntensitySubmission.run.startedAt',
            ),
            endsAt: nonNegativeIntegerValue(value.endsAt, 'swingIntensitySubmission.run.endsAt'),
            spins: nonNegativeIntegerValue(value.spins, 'swingIntensitySubmission.run.spins'),
            canStart: booleanValue(value.canStart, 'swingIntensitySubmission.run.canStart'),
          };
        })();
  const leaderboard =
    submission.leaderboard === undefined
      ? []
      : arrayValue(submission.leaderboard, 'swingIntensitySubmission.leaderboard').map(
          (entry, index) => {
            const value = objectValue(
              entry,
              `swingIntensitySubmission.leaderboard[${String(index)}]`,
            );
            return {
              rank: positiveIntegerValue(
                value.rank,
                `swingIntensitySubmission.leaderboard[${String(index)}].rank`,
              ),
              playerId: positiveIntegerValue(
                value.playerId,
                `swingIntensitySubmission.leaderboard[${String(index)}].playerId`,
              ),
              username: stringValue(
                value.username,
                `swingIntensitySubmission.leaderboard[${String(index)}].username`,
              ),
              spins: positiveIntegerValue(
                value.spins,
                `swingIntensitySubmission.leaderboard[${String(index)}].spins`,
              ),
            };
          },
        );
  if (leaderboard.length > 10) {
    throw new ContractError('swingIntensitySubmission.leaderboard exceeds ten entries');
  }
  return {
    status,
    buildingId: nonZeroSignedShortValue(
      submission.buildingId,
      'swingIntensitySubmission.buildingId',
    ),
    intensity: rangedIntegerValue(
      submission.intensity,
      'swingIntensitySubmission.intensity',
      0,
      100,
    ),
    angleMilli:
      submission.angleMilli === undefined
        ? 0
        : rangedIntegerValue(
            submission.angleMilli,
            'swingIntensitySubmission.angleMilli',
            -2_000_000_000,
            2_000_000_000,
          ),
    angularVelocityMilli:
      submission.angularVelocityMilli === undefined
        ? 0
        : rangedIntegerValue(
            submission.angularVelocityMilli,
            'swingIntensitySubmission.angularVelocityMilli',
            -6_500,
            6_500,
          ),
    serverTime:
      submission.serverTime === undefined
        ? 0
        : nonNegativeIntegerValue(submission.serverTime, 'swingIntensitySubmission.serverTime'),
    run,
    leaderboard,
  };
}

export function parseAwaySubmissionResponse(value: unknown): AwaySubmissionPayload {
  const root = objectValue(value, 'away response');
  assertProtocol(root);
  const submission = objectValue(root.awaySubmission, 'awaySubmission');
  const status = stringValue(submission.status, 'awaySubmission.status');
  if (status !== 'away') throw new ContractError(`Unsupported away status: ${status}`);
  return {
    status,
    villageId: positiveIntegerValue(submission.villageId, 'awaySubmission.villageId'),
    playerId: positiveIntegerValue(submission.playerId, 'awaySubmission.playerId'),
    action: avatarAwayActionValue(submission.action, 'awaySubmission.action'),
    presence: parsePresencePayload(submission.presence, 'awaySubmission.presence'),
  };
}

export function parseAvatarGestureSubmissionResponse(
  value: unknown,
): AvatarGestureSubmissionPayload {
  const root = objectValue(value, 'avatar gesture response');
  assertProtocol(root);
  const submission = objectValue(root.avatarGestureSubmission, 'avatarGestureSubmission');
  const status = stringValue(submission.status, 'avatarGestureSubmission.status');
  if (status !== 'performed') {
    throw new ContractError(`Unsupported avatar gesture status: ${status}`);
  }
  const senderPlayerId = positiveIntegerValue(
    submission.senderPlayerId,
    'avatarGestureSubmission.senderPlayerId',
  );
  const targetPlayerId = positiveIntegerValue(
    submission.targetPlayerId,
    'avatarGestureSubmission.targetPlayerId',
  );
  if (senderPlayerId === targetPlayerId) {
    throw new ContractError('avatarGestureSubmission cannot target its sender');
  }
  return {
    status,
    villageId: positiveIntegerValue(submission.villageId, 'avatarGestureSubmission.villageId'),
    senderPlayerId,
    targetPlayerId,
    action: avatarGestureActionValue(submission.action, 'avatarGestureSubmission.action'),
    presence: parsePresencePayload(submission.presence, 'avatarGestureSubmission.presence'),
  };
}

export function parseSillyGooseStareSubmissionResponse(
  value: unknown,
): SillyGooseStareSubmissionPayload {
  const root = objectValue(value, 'silly goose stare response');
  assertProtocol(root);
  const submission = objectValue(root.sillyGooseStareSubmission, 'sillyGooseStareSubmission');
  const status = stringValue(submission.status, 'sillyGooseStareSubmission.status');
  if (status !== 'queued')
    throw new ContractError(`Unsupported silly goose stare status: ${status}`);
  return {
    status,
    villageId: positiveIntegerValue(submission.villageId, 'sillyGooseStareSubmission.villageId'),
    viewerPlayerId: positiveIntegerValue(
      submission.viewerPlayerId,
      'sillyGooseStareSubmission.viewerPlayerId',
    ),
    goosePlayerId: positiveIntegerValue(
      submission.goosePlayerId,
      'sillyGooseStareSubmission.goosePlayerId',
    ),
    startsInMilliseconds: nonNegativeIntegerValue(
      submission.startsInMilliseconds,
      'sillyGooseStareSubmission.startsInMilliseconds',
    ),
    durationMilliseconds: rangedIntegerValue(
      submission.durationMilliseconds,
      'sillyGooseStareSubmission.durationMilliseconds',
      100,
      5_000,
    ),
  };
}

export function parseAvatarSpawnTeleportSubmissionResponse(
  value: unknown,
): AvatarSpawnTeleportSubmissionPayload {
  const root = objectValue(value, 'avatar spawn-teleport response');
  assertProtocol(root);
  const submission = objectValue(
    root.avatarSpawnTeleportSubmission,
    'avatarSpawnTeleportSubmission',
  );
  const status = stringValue(submission.status, 'avatarSpawnTeleportSubmission.status');
  if (status !== 'teleported') {
    throw new ContractError(`Unsupported avatar spawn-teleport status: ${status}`);
  }
  const action = avatarSpawnTeleportActionValue(
    submission.action,
    'avatarSpawnTeleportSubmission.action',
  );
  const destination = avatarSpawnPointValue(
    submission.destination,
    'avatarSpawnTeleportSubmission.destination',
    action === 'normal' ? -221 : action === 'home' ? 898 : 3018,
  );
  if (action !== 'normal' && destination === null) {
    throw new ContractError('Home and friend spawn teleports require a destination');
  }
  return {
    status,
    villageId: positiveIntegerValue(
      submission.villageId,
      'avatarSpawnTeleportSubmission.villageId',
    ),
    senderPlayerId: positiveIntegerValue(
      submission.senderPlayerId,
      'avatarSpawnTeleportSubmission.senderPlayerId',
    ),
    targetPlayerId: positiveIntegerValue(
      submission.targetPlayerId,
      'avatarSpawnTeleportSubmission.targetPlayerId',
    ),
    action,
    destination,
  };
}

export function parseAvatarEmoteSubmissionResponse(value: unknown): AvatarEmoteSubmissionPayload {
  const root = objectValue(value, 'avatar emote response');
  assertProtocol(root);
  const submission = objectValue(root.avatarEmoteSubmission, 'avatarEmoteSubmission');
  const status = stringValue(submission.status, 'avatarEmoteSubmission.status');
  if (status !== 'performed') {
    throw new ContractError(`Unsupported avatar emote status: ${status}`);
  }
  return {
    status,
    villageId: positiveIntegerValue(submission.villageId, 'avatarEmoteSubmission.villageId'),
    playerId: positiveIntegerValue(submission.playerId, 'avatarEmoteSubmission.playerId'),
    action: avatarEmoteActionValue(submission.action, 'avatarEmoteSubmission.action'),
    presence: parsePresencePayload(submission.presence, 'avatarEmoteSubmission.presence'),
  };
}

export function parseKeyDoorSubmissionResponse(value: unknown): KeyDoorSubmissionPayload {
  const root = objectValue(value, 'key door response');
  assertProtocol(root);
  const submission = objectValue(root.keyDoorSubmission, 'keyDoorSubmission');
  const status = stringValue(submission.status, 'keyDoorSubmission.status');
  if (status !== 'activated' && status !== 'cooldown') {
    throw new ContractError(`Unsupported key-door status: ${status}`);
  }
  const openWindowMillis = positiveIntegerValue(
    submission.openWindowMillis,
    'keyDoorSubmission.openWindowMillis',
  );
  if (openWindowMillis !== 5000) {
    throw new ContractError('keyDoorSubmission.openWindowMillis contradicts the legacy window');
  }
  return {
    status,
    villageId: positiveIntegerValue(submission.villageId, 'keyDoorSubmission.villageId'),
    playerId: positiveIntegerValue(submission.playerId, 'keyDoorSubmission.playerId'),
    buildingId: nonZeroSignedShortValue(submission.buildingId, 'keyDoorSubmission.buildingId'),
    buildingType: signedShortValue(submission.buildingType, 'keyDoorSubmission.buildingType'),
    color: keyDoorColorValue(submission.color, 'keyDoorSubmission.color'),
    openWindowMillis,
  };
}

export function parseSelfUnlockDoorSubmissionResponse(
  value: unknown,
): SelfUnlockDoorSubmissionPayload {
  const root = objectValue(value, 'self-unlock door response');
  assertProtocol(root);
  const submission = objectValue(root.selfUnlockDoorSubmission, 'selfUnlockDoorSubmission');
  const openWindowMillis = positiveIntegerValue(
    submission.openWindowMillis,
    'selfUnlockDoorSubmission.openWindowMillis',
  );
  if (openWindowMillis !== 5000) {
    throw new ContractError(
      'selfUnlockDoorSubmission.openWindowMillis contradicts the legacy window',
    );
  }
  return {
    villageId: positiveIntegerValue(submission.villageId, 'selfUnlockDoorSubmission.villageId'),
    playerId: positiveIntegerValue(submission.playerId, 'selfUnlockDoorSubmission.playerId'),
    buildingId: nonZeroSignedShortValue(
      submission.buildingId,
      'selfUnlockDoorSubmission.buildingId',
    ),
    buildingType: signedShortValue(
      submission.buildingType,
      'selfUnlockDoorSubmission.buildingType',
    ),
    openWindowMillis,
  };
}

export function parseGuestbookResponse(value: unknown): GuestbookPayload {
  const root = objectValue(value, 'guestbook response');
  assertProtocol(root);
  return parseGuestbook(root.guestbook, 'guestbook');
}

export function parseGuestbookAvailabilityResponse(value: unknown): GuestbookAvailabilityPayload {
  const root = objectValue(value, 'guestbook availability response');
  assertProtocol(root);
  const availability = objectValue(root.guestbookAvailability, 'guestbookAvailability');
  return {
    newEntriesAvailable: booleanValue(
      availability.newEntriesAvailable,
      'guestbookAvailability.newEntriesAvailable',
    ),
  };
}

export function parseGuestbookHistoryResponse(value: unknown): GuestbookHistoryPayload {
  const root = objectValue(value, 'guestbook history response');
  assertProtocol(root);
  const history = objectValue(root.guestbookHistory, 'guestbookHistory');
  const firstPlayerId = positiveIntegerValue(
    history.firstPlayerId,
    'guestbookHistory.firstPlayerId',
  );
  const secondPlayerId = positiveIntegerValue(
    history.secondPlayerId,
    'guestbookHistory.secondPlayerId',
  );
  if (firstPlayerId === secondPlayerId) {
    throw new ContractError('Guestbook history players must be different');
  }
  return {
    firstPlayerId,
    secondPlayerId,
    offset: nonNegativeIntegerValue(history.offset, 'guestbookHistory.offset'),
    hasNewerPosts: booleanValue(history.hasNewerPosts, 'guestbookHistory.hasNewerPosts'),
    hasOlderPosts: booleanValue(history.hasOlderPosts, 'guestbookHistory.hasOlderPosts'),
    entries: parseGuestbookEntries(history.entries, 'guestbookHistory.entries'),
  };
}

export function parseGuestbookSubmissionResponse(value: unknown): GuestbookSubmissionPayload {
  const root = objectValue(value, 'guestbook submission response');
  assertProtocol(root);
  const submission = objectValue(root.guestbookSubmission, 'guestbookSubmission');
  const status = stringValue(submission.status, 'guestbookSubmission.status');
  if (status !== 'posted') {
    throw new ContractError(`Unsupported guestbook submission status: ${status}`);
  }
  return {
    status,
    postId: positiveIntegerValue(submission.postId, 'guestbookSubmission.postId'),
    guestbook: parseGuestbook(submission.guestbook, 'guestbookSubmission.guestbook'),
  };
}

export function parseGuestbookMutationResponse(value: unknown): GuestbookMutationPayload {
  const root = objectValue(value, 'guestbook mutation response');
  assertProtocol(root);
  const mutation = objectValue(root.guestbookMutation, 'guestbookMutation');
  const status = stringValue(mutation.status, 'guestbookMutation.status');
  if (status !== 'removed' && status !== 'privacy-updated') {
    throw new ContractError(`Unsupported guestbook mutation status: ${status}`);
  }
  return {
    status,
    postId: positiveIntegerValue(mutation.postId, 'guestbookMutation.postId'),
    guestbook: parseGuestbook(mutation.guestbook, 'guestbookMutation.guestbook'),
  };
}

function parseGuestbook(value: unknown, label: string): GuestbookPayload {
  const guestbook = objectValue(value, label);
  return {
    ownerPlayerId: positiveIntegerValue(guestbook.ownerPlayerId, `${label}.ownerPlayerId`),
    ownerName: stringValue(guestbook.ownerName, `${label}.ownerName`),
    offset: nonNegativeIntegerValue(guestbook.offset, `${label}.offset`),
    hasNewerPosts: booleanValue(guestbook.hasNewerPosts, `${label}.hasNewerPosts`),
    hasOlderPosts: booleanValue(guestbook.hasOlderPosts, `${label}.hasOlderPosts`),
    entries: parseGuestbookEntries(guestbook.entries, `${label}.entries`),
  };
}

function parseGuestbookEntries(value: unknown, label: string): readonly GuestbookEntryPayload[] {
  return arrayValue(value, label).map((entryValue, index) => {
    const entryLabel = `${label}[${String(index)}]`;
    const entry = objectValue(entryValue, entryLabel);
    return {
      postId: positiveIntegerValue(entry.postId, `${entryLabel}.postId`),
      posterPlayerId: positiveIntegerValue(entry.posterPlayerId, `${entryLabel}.posterPlayerId`),
      posterName: stringValue(entry.posterName, `${entryLabel}.posterName`),
      posterPictureId:
        entry.posterPictureId === null
          ? null
          : positiveIntegerValue(entry.posterPictureId, `${entryLabel}.posterPictureId`),
      text: stringValue(entry.text, `${entryLabel}.text`),
      postedAt: nonNegativeIntegerValue(entry.postedAt, `${entryLabel}.postedAt`),
      sex: integerValue(entry.sex, `${entryLabel}.sex`),
      online: booleanValue(entry.online, `${entryLabel}.online`),
      private: booleanValue(entry.private, `${entryLabel}.private`),
      level: nonNegativeIntegerValue(entry.level, `${entryLabel}.level`),
      type: stringValue(entry.type, `${entryLabel}.type`),
    };
  });
}

export function parsePictureDeletionResponse(value: unknown): PictureDeletionPayload {
  const root = objectValue(value, 'picture deletion response');
  assertProtocol(root);
  const deletion = objectValue(root.pictureDeletion, 'pictureDeletion');
  const status = stringValue(deletion.status, 'pictureDeletion.status');
  if (status !== 'deleted') {
    throw new ContractError(`Unsupported picture deletion status: ${status}`);
  }
  return {
    status,
    pictureId: positiveIntegerValue(deletion.pictureId, 'pictureDeletion.pictureId'),
    clearedBuildingCount: nonNegativeIntegerValue(
      deletion.clearedBuildingCount,
      'pictureDeletion.clearedBuildingCount',
    ),
    buildingReferencesCleared: booleanValue(
      deletion.buildingReferencesCleared,
      'pictureDeletion.buildingReferencesCleared',
    ),
    entityRevision: nonNegativeIntegerValue(
      deletion.entityRevision,
      'pictureDeletion.entityRevision',
    ),
  };
}

export function parsePictureUploadResponse(value: unknown): PictureUploadPayload {
  const root = objectValue(value, 'picture upload response');
  assertProtocol(root);
  const upload = objectValue(root.pictureUpload, 'pictureUpload');
  const status = stringValue(upload.status, 'pictureUpload.status');
  if (status !== 'uploaded') {
    throw new ContractError(`Unsupported picture upload status: ${status}`);
  }
  return {
    status,
    pictureId: positiveIntegerValue(upload.pictureId, 'pictureUpload.pictureId'),
  };
}

export function parseScreenshotUploadResponse(value: unknown): ScreenshotUploadPayload {
  const root = objectValue(value, 'screenshot upload response');
  assertProtocol(root);
  const screenshot = objectValue(root.screenshot, 'screenshot');
  const status = stringValue(screenshot.status, 'screenshot.status');
  if (status !== 'uploaded') {
    throw new ContractError(`Unsupported screenshot upload status: ${status}`);
  }
  const shareId = positiveIntegerValue(screenshot.shareId, 'screenshot.shareId');
  const shareUrl = stringValue(screenshot.shareUrl, 'screenshot.shareUrl');
  if (shareUrl !== `/api/v2/screenshots/${String(shareId)}`) {
    throw new ContractError('screenshot.shareUrl does not match screenshot.shareId');
  }
  return { status, shareId, shareUrl };
}

export function parseBuildingProductionDeltaResponse(
  value: unknown,
): BuildingProductionDeltaPayload {
  const root = objectValue(value, 'building production delta response');
  assertProtocol(root);
  return parseBuildingProductionDeltaPayload(
    root.buildingProductionDelta,
    'buildingProductionDelta',
  );
}

export function parseBuildingProductionResponse(value: unknown): BuildingProductionPayload {
  const root = objectValue(value, 'building production response');
  assertProtocol(root);
  return parseBuildingProductionPayload(root.buildingProduction, 'buildingProduction');
}

export function parseMarketplaceResponse(value: unknown): MarketplacePayload {
  const root = objectValue(value, 'Marketplace response');
  assertProtocol(root);
  return parseMarketplacePayload(root.marketplace, 'marketplace');
}

export function parseBuildingProductionSubmissionResponse(
  value: unknown,
): BuildingProductionSubmissionPayload {
  const root = objectValue(value, 'building production submission response');
  assertProtocol(root);
  const submission = objectValue(root.buildingProductionSubmission, 'buildingProductionSubmission');
  const status = stringValue(submission.status, 'buildingProductionSubmission.status');
  if (status !== 'applied') {
    throw new ContractError(`Unsupported building production status: ${status}`);
  }
  const action = productionAction(submission.action, 'buildingProductionSubmission.action');
  const buildingId = signedShortValue(
    submission.buildingId,
    'buildingProductionSubmission.buildingId',
  );
  if (buildingId === 0) {
    throw new ContractError('buildingProductionSubmission.buildingId must be non-zero');
  }
  const productionDelta =
    root.buildingProductionDelta === undefined
      ? {
          ...parseBuildingProductionPayload(root.buildingProduction, 'buildingProduction'),
          removedBuildingIds: [],
        }
      : parseBuildingProductionDeltaPayload(
          root.buildingProductionDelta,
          'buildingProductionDelta',
        );
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'buildingProductionSubmission.sequence'),
    action,
    buildingId,
    buildingType: signedShortValue(
      submission.buildingType,
      'buildingProductionSubmission.buildingType',
    ),
    resourceId: rangedIntegerValue(
      submission.resourceId,
      'buildingProductionSubmission.resourceId',
      1,
      32_767,
    ),
    ingredientIndex: rangedIntegerValue(
      submission.ingredientIndex,
      'buildingProductionSubmission.ingredientIndex',
      -1,
      62,
    ),
    replayed: booleanValue(submission.replayed, 'buildingProductionSubmission.replayed'),
    buildingPresent: booleanValue(
      submission.buildingPresent,
      'buildingProductionSubmission.buildingPresent',
    ),
    ready: booleanValue(submission.ready, 'buildingProductionSubmission.ready'),
    delayStart: nonNegativeIntegerValue(
      submission.delayStart,
      'buildingProductionSubmission.delayStart',
    ),
    delayEnd: nonNegativeIntegerValue(submission.delayEnd, 'buildingProductionSubmission.delayEnd'),
    ingredients: nonNegativeIntegerValue(
      submission.ingredients,
      'buildingProductionSubmission.ingredients',
    ),
    serverTime: nonNegativeIntegerValue(
      submission.serverTime,
      'buildingProductionSubmission.serverTime',
    ),
    entityRevision: positiveIntegerValue(
      submission.entityRevision,
      'buildingProductionSubmission.entityRevision',
    ),
    productionDelta,
    ...(root.worldUpdate === undefined
      ? {}
      : { worldUpdate: parseWorldNotificationUpdate(root.worldUpdate) }),
  };
}

export function parseInventoryMoveSubmissionResponse(
  value: unknown,
): InventoryMoveSubmissionPayload {
  const root = objectValue(value, 'inventory move submission response');
  assertProtocol(root);
  const submission = objectValue(root.inventoryMoveSubmission, 'inventoryMoveSubmission');
  const status = stringValue(submission.status, 'inventoryMoveSubmission.status');
  if (status !== 'applied') {
    throw new ContractError(`Unsupported inventory move status: ${status}`);
  }
  const sourceIndex = rangedIntegerValue(
    submission.sourceIndex,
    'inventoryMoveSubmission.sourceIndex',
    0,
    11,
  );
  const destinationIndex = rangedIntegerValue(
    submission.destinationIndex,
    'inventoryMoveSubmission.destinationIndex',
    -1,
    11,
  );
  if (sourceIndex === destinationIndex) {
    throw new ContractError('inventoryMoveSubmission slots must be different');
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'inventoryMoveSubmission.sequence'),
    sourceIndex,
    destinationIndex,
    resourceId: rangedIntegerValue(
      submission.resourceId,
      'inventoryMoveSubmission.resourceId',
      1,
      32_767,
    ),
    replayed: booleanValue(submission.replayed, 'inventoryMoveSubmission.replayed'),
    serverTime: nonNegativeIntegerValue(
      submission.serverTime,
      'inventoryMoveSubmission.serverTime',
    ),
    production: parseBuildingProductionPayload(root.buildingProduction, 'buildingProduction'),
  };
}

export function parseInventoryActivationSubmissionResponse(
  value: unknown,
): InventoryActivationSubmissionPayload {
  const root = objectValue(value, 'inventory activation submission response');
  assertProtocol(root);
  const submission = objectValue(
    root.inventoryActivationSubmission,
    'inventoryActivationSubmission',
  );
  const status = stringValue(submission.status, 'inventoryActivationSubmission.status');
  if (status !== 'applied') {
    throw new ContractError(`Unsupported inventory activation status: ${status}`);
  }
  const shortcutIndex = rangedIntegerValue(
    submission.shortcutIndex,
    'inventoryActivationSubmission.shortcutIndex',
    0,
    7,
  );
  if (shortcutIndex === 0 || shortcutIndex === 4) {
    throw new ContractError('inventoryActivationSubmission shortcut is not source-visible');
  }
  const production = parseBuildingProductionPayload(root.buildingProduction, 'buildingProduction');
  const sourceIndex = rangedIntegerValue(
    submission.sourceIndex,
    'inventoryActivationSubmission.sourceIndex',
    0,
    11,
  );
  const resourceId = rangedIntegerValue(
    submission.resourceId,
    'inventoryActivationSubmission.resourceId',
    1,
    32_767,
  );
  const source = production.inventory.backpack[sourceIndex];
  const shortcut = production.inventory.shortcuts[shortcutIndex];
  if (
    source === undefined ||
    source.occupied ||
    shortcut?.resourceId !== resourceId ||
    !shortcut.lit
  ) {
    throw new ContractError(
      'inventoryActivationSubmission contradicts the authoritative inventory projection',
    );
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'inventoryActivationSubmission.sequence'),
    sourceIndex,
    shortcutIndex,
    resourceId,
    replayed: booleanValue(submission.replayed, 'inventoryActivationSubmission.replayed'),
    serverTime: nonNegativeIntegerValue(
      submission.serverTime,
      'inventoryActivationSubmission.serverTime',
    ),
    production,
  };
}

export function parseWeatherInventoryResponse(value: unknown): WeatherControlsPayload {
  const root = objectValue(value, 'weather inventory response');
  assertProtocol(root);
  const targets = objectValue(root.weatherTargets, 'weatherTargets');
  return {
    weather: parseWeatherInventoryPayload(root.weather, 'weather'),
    targets: {
      sunshine: parseWeatherTargetIds(targets.sunshine, 'weatherTargets.sunshine'),
      rain: parseWeatherTargetIds(targets.rain, 'weatherTargets.rain'),
      lightning: parseWeatherTargetIds(targets.lightning, 'weatherTargets.lightning'),
    },
  };
}

export function parseWeatherActivationSubmissionResponse(
  value: unknown,
): WeatherActivationSubmissionPayload {
  const root = objectValue(value, 'weather activation response');
  assertProtocol(root);
  const submission = objectValue(root.weatherActivationSubmission, 'weatherActivationSubmission');
  const status = stringValue(submission.status, 'weatherActivationSubmission.status');
  if (status !== 'applied') {
    throw new ContractError(`Unsupported weather activation status: ${status}`);
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'weatherActivationSubmission.sequence'),
    kind: weatherKindValue(submission.kind, 'weatherActivationSubmission.kind'),
    replayed: booleanValue(submission.replayed, 'weatherActivationSubmission.replayed'),
    eventSequence: positiveIntegerValue(
      submission.eventSequence,
      'weatherActivationSubmission.eventSequence',
    ),
    elapsedMilliseconds: nonNegativeIntegerValue(
      submission.elapsedMilliseconds,
      'weatherActivationSubmission.elapsedMilliseconds',
    ),
    serverTime: nonNegativeIntegerValue(
      submission.serverTime,
      'weatherActivationSubmission.serverTime',
    ),
    weather: parseWeatherInventoryPayload(root.weather, 'weather'),
    buildingEffects: parseWeatherBuildingEffects(
      submission.buildingEffects,
      'weatherActivationSubmission.buildingEffects',
    ),
  };
}

function parseWeatherBuildingEffects(
  value: unknown,
  label: string,
): readonly WeatherBuildingEffectPayload[] {
  const entries = arrayValue(value, label);
  if (entries.length > 4096) throw new ContractError(`${label} exceeds 4096 entries`);
  const buildingIds = new Set<number>();
  return entries.map((entry, index) => {
    const effectLabel = `${label}[${String(index)}]`;
    const effect = objectValue(entry, effectLabel);
    const buildingId = rangedIntegerValue(
      effect.buildingId,
      `${effectLabel}.buildingId`,
      -32768,
      32767,
    );
    if (buildingId === 0 || buildingIds.has(buildingId)) {
      throw new ContractError(`${effectLabel}.buildingId is zero or duplicated`);
    }
    buildingIds.add(buildingId);
    const visualDelayMilliseconds = nonNegativeIntegerValue(
      effect.visualDelayMilliseconds,
      `${effectLabel}.visualDelayMilliseconds`,
    );
    const applyDelayMilliseconds = nonNegativeIntegerValue(
      effect.applyDelayMilliseconds,
      `${effectLabel}.applyDelayMilliseconds`,
    );
    if (applyDelayMilliseconds < visualDelayMilliseconds) {
      throw new ContractError(`${effectLabel} applies before its visual starts`);
    }
    return {
      buildingId,
      buildingType: rangedIntegerValue(
        effect.buildingType,
        `${effectLabel}.buildingType`,
        1,
        32767,
      ),
      tileX: rangedIntegerValue(
        effect.tileX,
        `${effectLabel}.tileX`,
        -(CREATIVE_WORLD.sampleWidth - 1),
        CREATIVE_WORLD.sampleWidth - 2,
      ),
      tileZ: rangedIntegerValue(
        effect.tileZ,
        `${effectLabel}.tileZ`,
        -(CREATIVE_WORLD.sampleWidth - 1),
        CREATIVE_WORLD.sampleWidth - 2,
      ),
      rotation: rangedIntegerValue(effect.rotation, `${effectLabel}.rotation`, 0, 255),
      ready: booleanValue(effect.ready, `${effectLabel}.ready`),
      delayStart: nonNegativeIntegerValue(effect.delayStart, `${effectLabel}.delayStart`),
      delayEnd: nonNegativeIntegerValue(effect.delayEnd, `${effectLabel}.delayEnd`),
      ingredients: nonNegativeIntegerValue(effect.ingredients, `${effectLabel}.ingredients`),
      visualDelayMilliseconds,
      applyDelayMilliseconds,
    };
  });
}

function parseWeatherTargetIds(value: unknown, label: string): readonly number[] {
  const entries = arrayValue(value, label);
  if (entries.length > 4096) throw new ContractError(`${label} exceeds 4096 entries`);
  const ids = entries.map((entry, index) =>
    rangedIntegerValue(entry, `${label}[${String(index)}]`, -32768, 32767),
  );
  if (ids.some((id) => id === 0) || new Set(ids).size !== ids.length) {
    throw new ContractError(`${label} contains zero or duplicate building IDs`);
  }
  return ids;
}

function parseWeatherInventoryPayload(value: unknown, label: string): WeatherInventoryPayload {
  const inventory = objectValue(value, label);
  return {
    sunshine: nonNegativeIntegerValue(inventory.sunshine, `${label}.sunshine`),
    rain: nonNegativeIntegerValue(inventory.rain, `${label}.rain`),
    lightning: nonNegativeIntegerValue(inventory.lightning, `${label}.lightning`),
  };
}

export function parseResourceSlotMoveSubmissionResponse(
  value: unknown,
): ResourceSlotMoveSubmissionPayload {
  const root = objectValue(value, 'resource-slot move submission response');
  assertProtocol(root);
  const submission = objectValue(root.resourceSlotMoveSubmission, 'resourceSlotMoveSubmission');
  const status = stringValue(submission.status, 'resourceSlotMoveSubmission.status');
  if (status !== 'applied') {
    throw new ContractError(`Unsupported resource-slot move status: ${status}`);
  }
  const source = parseResourceSlotEndpoint(submission, 'source');
  const destination = parseResourceSlotEndpoint(submission, 'destination');
  if (
    (source.area === 'backpack' && destination.area === 'backpack') ||
    (source.area === destination.area && source.index === destination.index)
  ) {
    throw new ContractError('resourceSlotMoveSubmission endpoints are invalid');
  }
  const resourceId = rangedIntegerValue(
    submission.resourceId,
    'resourceSlotMoveSubmission.resourceId',
    1,
    32_767,
  );
  const production = parseBuildingProductionPayload(root.buildingProduction, 'buildingProduction');
  const sourceIsCleared =
    source.area === 'shortcut'
      ? production.inventory.shortcuts[source.index]?.resourceId === resourceId &&
        production.inventory.shortcuts[source.index]?.lit === false
      : production.inventory.backpack[source.index]?.occupied === false;
  const destinationIsFilled =
    destination.area === 'shortcut'
      ? production.inventory.shortcuts[destination.index]?.resourceId === resourceId &&
        production.inventory.shortcuts[destination.index]?.lit === true
      : production.inventory.backpack[destination.index]?.resourceId === resourceId &&
        production.inventory.backpack[destination.index]?.occupied === true;
  if (!sourceIsCleared || !destinationIsFilled) {
    throw new ContractError(
      'resourceSlotMoveSubmission contradicts the authoritative inventory projection',
    );
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'resourceSlotMoveSubmission.sequence'),
    source,
    destination,
    resourceId,
    replayed: booleanValue(submission.replayed, 'resourceSlotMoveSubmission.replayed'),
    serverTime: nonNegativeIntegerValue(
      submission.serverTime,
      'resourceSlotMoveSubmission.serverTime',
    ),
    production,
  };
}

export function parseTombolaResponse(value: unknown): TombolaPayload {
  const root = objectValue(value, 'Tombola response');
  assertProtocol(root);
  const tombola = objectValue(root.tombola, 'tombola');
  const actionValue = stringValue(tombola.action, 'tombola.action');
  if (
    actionValue !== 'inspect' &&
    actionValue !== 'start' &&
    actionValue !== 'stop' &&
    actionValue !== 'finish'
  ) {
    throw new ContractError(`Unsupported Tombola action: ${actionValue}`);
  }
  const action: TombolaAction = actionValue;
  const outcomeValue = stringValue(tombola.outcome, 'tombola.outcome');
  const supportedOutcomes: readonly TombolaOutcome[] = [
    'state',
    'started',
    'stopped',
    'finished',
    'no-active-resource',
    'tutorial-required',
    'spin-already-active',
    'spin-not-active',
    'stale-wheel',
    'idempotency-conflict',
  ];
  if (!supportedOutcomes.includes(outcomeValue as TombolaOutcome)) {
    throw new ContractError(`Unsupported Tombola outcome: ${outcomeValue}`);
  }
  const outcome = outcomeValue as TombolaOutcome;
  const sequence = nonNegativeIntegerValue(tombola.sequence, 'tombola.sequence');
  if ((action === 'inspect') !== (sequence === 0)) {
    throw new ContractError('tombola.sequence contradicts the action');
  }
  const targetIndex = rangedIntegerValue(tombola.targetIndex, 'tombola.targetIndex', -1, 7);
  if ((outcome === 'stopped' || outcome === 'finished') !== targetIndex >= 0) {
    throw new ContractError('tombola.targetIndex contradicts the outcome');
  }
  const slicesValue = arrayValue(tombola.slices, 'tombola.slices');
  if (slicesValue.length !== 8) {
    throw new ContractError('tombola.slices must contain eight slices');
  }
  const slices = slicesValue.map((entry, index) => {
    const label = `tombola.slices[${String(index)}]`;
    const slice = objectValue(entry, label);
    const sliceIndex = rangedIntegerValue(slice.index, `${label}.index`, 0, 7);
    if (sliceIndex !== index) throw new ContractError(`${label}.index is out of order`);
    return {
      index: sliceIndex,
      resourceId: rangedIntegerValue(slice.resourceId, `${label}.resourceId`, 0, 32_767),
      lit: booleanValue(slice.lit, `${label}.lit`),
    };
  });
  const productionDelta = parseBuildingProductionDeltaPayload(
    root.buildingProductionDelta,
    'buildingProductionDelta',
  );
  if (productionDelta.buildings.length !== 0 || productionDelta.removedBuildingIds.length !== 0) {
    throw new ContractError('Tombola production delta must only contain inventory');
  }
  for (const slice of slices) {
    const shortcut = productionDelta.inventory.shortcuts[slice.index];
    if (shortcut?.resourceId !== slice.resourceId || shortcut.lit !== slice.lit) {
      throw new ContractError(
        `tombola.slices[${String(slice.index)}] contradicts the inventory projection`,
      );
    }
  }
  const angularSpeed = numberValue(tombola.angularSpeed, 'tombola.angularSpeed');
  const angularDeceleration = numberValue(
    tombola.angularDeceleration,
    'tombola.angularDeceleration',
  );
  const landingAngle = numberValue(tombola.landingAngle, 'tombola.landingAngle');
  if (angularSpeed < 0 || angularDeceleration < 0 || landingAngle < 0) {
    throw new ContractError('Tombola physics values must be non-negative');
  }
  if (
    (outcome === 'started' || outcome === 'stopped' || outcome === 'finished') &&
    (angularSpeed <= 0 || angularDeceleration <= 0)
  ) {
    throw new ContractError('Active Tombola physics values must be positive');
  }
  return {
    action,
    sequence,
    outcome,
    replayed: booleanValue(tombola.replayed, 'tombola.replayed'),
    serverTime: nonNegativeIntegerValue(tombola.serverTime, 'tombola.serverTime'),
    startedAt: nonNegativeIntegerValue(tombola.startedAt, 'tombola.startedAt'),
    angularSpeed,
    angularDeceleration,
    landingAngle,
    targetIndex,
    targetResourceId: rangedIntegerValue(
      tombola.targetResourceId,
      'tombola.targetResourceId',
      0,
      32_767,
    ),
    won: booleanValue(tombola.won, 'tombola.won'),
    points: nonNegativeIntegerValue(tombola.points, 'tombola.points'),
    levelCompleted: booleanValue(tombola.levelCompleted, 'tombola.levelCompleted'),
    level: positiveIntegerValue(tombola.level, 'tombola.level'),
    wins: nonNegativeIntegerValue(tombola.wins, 'tombola.wins'),
    requiredWins: positiveIntegerValue(tombola.requiredWins, 'tombola.requiredWins'),
    bonusMultiplier: positiveIntegerValue(tombola.bonusMultiplier, 'tombola.bonusMultiplier'),
    credits: nonNegativeIntegerValue(tombola.credits, 'tombola.credits'),
    slices,
    productionDelta,
  };
}

function parseResourceSlotEndpoint(
  submission: Readonly<Record<string, unknown>>,
  prefix: 'source' | 'destination',
): ResourceSlotEndpoint {
  const areaValue = stringValue(
    submission[`${prefix}Area`],
    `resourceSlotMoveSubmission.${prefix}Area`,
  );
  if (areaValue !== 'shortcut' && areaValue !== 'backpack') {
    throw new ContractError(`Unsupported resource-slot area: ${areaValue}`);
  }
  const index = rangedIntegerValue(
    submission[`${prefix}Index`],
    `resourceSlotMoveSubmission.${prefix}Index`,
    0,
    areaValue === 'shortcut' ? 7 : 11,
  );
  if (areaValue === 'shortcut' && (index === 0 || index === 4)) {
    throw new ContractError(`resourceSlotMoveSubmission ${prefix} is not source-visible`);
  }
  return { area: areaValue, index };
}

function parseMarketplacePayload(value: unknown, label: string): MarketplacePayload {
  const marketplace = objectValue(value, label);
  const backpackCapacity = rangedIntegerValue(
    marketplace.backpackCapacity,
    `${label}.backpackCapacity`,
    6,
    12,
  );
  if (backpackCapacity !== 6 && backpackCapacity !== 12) {
    throw new ContractError(`${label}.backpackCapacity must be 6 or 12`);
  }
  const resources = arrayValue(marketplace.resources, `${label}.resources`).map((entry, index) =>
    parseMarketplaceResource(entry, `${label}.resources[${String(index)}]`),
  );
  const resourceIds = new Set<number>();
  for (const resource of resources) {
    if (resourceIds.has(resource.resourceId)) {
      throw new ContractError(`${label}.resources contains duplicate resource identifiers`);
    }
    resourceIds.add(resource.resourceId);
  }
  const backpack = arrayValue(marketplace.backpack, `${label}.backpack`).map((entry, index) => {
    const entryLabel = `${label}.backpack[${String(index)}]`;
    const inventoryEntry = objectValue(entry, entryLabel);
    const resource = parseMarketplaceResource(inventoryEntry.resource, `${entryLabel}.resource`);
    if (!resourceIds.has(resource.resourceId)) {
      throw new ContractError(`${entryLabel}.resource is absent from the resource catalog`);
    }
    return {
      resource,
      quantity: rangedIntegerValue(inventoryEntry.quantity, `${entryLabel}.quantity`, 1, 12),
    };
  });
  const backpackSlotEntries = arrayValue(marketplace.backpackSlots, `${label}.backpackSlots`);
  if (backpackSlotEntries.length !== 12) {
    throw new ContractError(`${label}.backpackSlots must contain 12 slots`);
  }
  const backpackSlots = backpackSlotEntries.map((entry, index) => {
    const slotLabel = `${label}.backpackSlots[${String(index)}]`;
    const slot = objectValue(entry, slotLabel);
    const slotIndex = rangedIntegerValue(slot.index, `${slotLabel}.index`, 0, 11);
    if (slotIndex !== index) throw new ContractError(`${slotLabel}.index is out of order`);
    const accessible = booleanValue(slot.accessible, `${slotLabel}.accessible`);
    if (accessible !== index < backpackCapacity) {
      throw new ContractError(`${slotLabel}.accessible contradicts backpackCapacity`);
    }
    const resource =
      slot.resource === null
        ? null
        : parseMarketplaceResource(slot.resource, `${slotLabel}.resource`);
    if (resource !== null && !resourceIds.has(resource.resourceId)) {
      throw new ContractError(`${slotLabel}.resource is absent from the resource catalog`);
    }
    return { index: slotIndex, accessible, resource };
  });
  const actualBackpackFreeSlots = backpackSlots.filter(
    ({ accessible, resource }) => accessible && resource === null,
  ).length;
  const backpackSlotCounts = new Map<number, number>();
  for (const { accessible, resource } of backpackSlots) {
    if (!accessible || resource === null) continue;
    backpackSlotCounts.set(
      resource.resourceId,
      (backpackSlotCounts.get(resource.resourceId) ?? 0) + 1,
    );
  }
  if (
    backpack.length !== backpackSlotCounts.size ||
    backpack.some(
      ({ resource, quantity }) => backpackSlotCounts.get(resource.resourceId) !== quantity,
    )
  ) {
    throw new ContractError(`${label}.backpack contradicts backpackSlots`);
  }
  const tradeInventoryCapacityValue = rangedIntegerValue(
    marketplace.tradeInventoryCapacity,
    `${label}.tradeInventoryCapacity`,
    24,
    24,
  );
  if (tradeInventoryCapacityValue !== 24) {
    throw new ContractError(`${label}.tradeInventoryCapacity must be 24`);
  }
  const tradeInventoryCapacity = 24 as const;
  const tradeInventoryEntries = arrayValue(marketplace.tradeInventory, `${label}.tradeInventory`);
  if (tradeInventoryEntries.length !== tradeInventoryCapacity) {
    throw new ContractError(`${label}.tradeInventory must contain 24 slots`);
  }
  const tradeInventory = tradeInventoryEntries.map((entry, index) => {
    const slotLabel = `${label}.tradeInventory[${String(index)}]`;
    const slot = objectValue(entry, slotLabel);
    const slotIndex = rangedIntegerValue(slot.index, `${slotLabel}.index`, 0, 23);
    if (slotIndex !== index) throw new ContractError(`${slotLabel}.index is out of order`);
    const resource =
      slot.resource === null
        ? null
        : parseMarketplaceResource(slot.resource, `${slotLabel}.resource`);
    if (resource !== null && !resourceIds.has(resource.resourceId)) {
      throw new ContractError(`${slotLabel}.resource is absent from the resource catalog`);
    }
    return { index: slotIndex, resource };
  });
  const actualTradeInventoryFreeSlots = tradeInventory.filter(
    ({ resource }) => resource === null,
  ).length;
  const parseOrders = (field: 'orders' | 'myOrders', activeOnly: boolean) => {
    const ids = new Set<number>();
    return arrayValue(marketplace[field], `${label}.${field}`).map((entry, index) => {
      const orderLabel = `${label}.${field}[${String(index)}]`;
      const order = objectValue(entry, orderLabel);
      const orderId = positiveIntegerValue(order.orderId, `${orderLabel}.orderId`);
      if (ids.has(orderId)) throw new ContractError(`${label}.${field} contains duplicate orders`);
      ids.add(orderId);
      const sideValue = stringValue(order.side, `${orderLabel}.side`);
      if (sideValue !== 'buy' && sideValue !== 'sell') {
        throw new ContractError(`${orderLabel}.side is unsupported`);
      }
      const statusValue = stringValue(order.status, `${orderLabel}.status`);
      if (
        statusValue !== 'active' &&
        statusValue !== 'fulfilled' &&
        statusValue !== 'cancelled' &&
        statusValue !== 'expired'
      ) {
        throw new ContractError(`${orderLabel}.status is unsupported`);
      }
      if (activeOnly && statusValue !== 'active') {
        throw new ContractError(`${orderLabel} must be active`);
      }
      const originalQuantity = rangedIntegerValue(
        order.originalQuantity,
        `${orderLabel}.originalQuantity`,
        1,
        24,
      );
      const remainingQuantity = rangedIntegerValue(
        order.remainingQuantity,
        `${orderLabel}.remainingQuantity`,
        0,
        originalQuantity,
      );
      if ((statusValue === 'active') !== remainingQuantity > 0) {
        throw new ContractError(`${orderLabel}.status contradicts remainingQuantity`);
      }
      const resource = parseMarketplaceResource(order.resource, `${orderLabel}.resource`);
      if (!resourceIds.has(resource.resourceId)) {
        throw new ContractError(`${orderLabel}.resource is absent from the resource catalog`);
      }
      const mode =
        order.mode === undefined ? 'fixed' : stringValue(order.mode, `${orderLabel}.mode`);
      if (mode !== 'fixed' && mode !== 'auction')
        throw new ContractError(`${orderLabel}.mode is unsupported`);
      const auctionFields =
        mode === 'auction'
          ? {
              highestBidUnitPrice: nonNegativeIntegerValue(
                order.highestBidUnitPrice,
                `${orderLabel}.highestBidUnitPrice`,
              ),
              highestBidderPlayerId: nonNegativeIntegerValue(
                order.highestBidderPlayerId,
                `${orderLabel}.highestBidderPlayerId`,
              ),
              bidCount: nonNegativeIntegerValue(order.bidCount, `${orderLabel}.bidCount`),
              expiresAt: positiveIntegerValue(order.expiresAt, `${orderLabel}.expiresAt`),
              isHighestBidder: booleanValue(order.isHighestBidder, `${orderLabel}.isHighestBidder`),
            }
          : {};
      if (
        mode === 'auction' &&
        (sideValue !== 'sell' ||
          (statusValue === 'active' && remainingQuantity !== originalQuantity) ||
          (auctionFields.highestBidUnitPrice === 0) !== (auctionFields.highestBidderPlayerId === 0))
      ) {
        throw new ContractError(`${orderLabel} contains inconsistent auction data`);
      }
      return {
        mode,
        ...auctionFields,
        orderId,
        ownerPlayerId: positiveIntegerValue(order.ownerPlayerId, `${orderLabel}.ownerPlayerId`),
        ownerName: stringValue(order.ownerName, `${orderLabel}.ownerName`),
        isMine: booleanValue(order.isMine, `${orderLabel}.isMine`),
        side: sideValue,
        resource,
        originalQuantity,
        remainingQuantity,
        unitPrice: rangedIntegerValue(order.unitPrice, `${orderLabel}.unitPrice`, 1, 1_000_000_000),
        status: statusValue,
        createdAt: nonNegativeIntegerValue(order.createdAt, `${orderLabel}.createdAt`),
        updatedAt: nonNegativeIntegerValue(order.updatedAt, `${orderLabel}.updatedAt`),
      } satisfies MarketplaceOrderPayload;
    });
  };
  return {
    ...(marketplace.weather === undefined
      ? {}
      : {
          weather: parseWeatherInventoryPayload(marketplace.weather, `${label}.weather`),
          weatherCapacity: rangedIntegerValue(
            marketplace.weatherCapacity,
            `${label}.weatherCapacity`,
            1,
            10,
          ),
        }),
    revision: nonNegativeIntegerValue(marketplace.revision, `${label}.revision`),
    credits: nonNegativeIntegerValue(marketplace.credits, `${label}.credits`),
    backpackCapacity,
    backpackFreeSlots: (() => {
      const reported = rangedIntegerValue(
        marketplace.backpackFreeSlots,
        `${label}.backpackFreeSlots`,
        0,
        backpackCapacity,
      );
      if (reported !== actualBackpackFreeSlots) {
        throw new ContractError(`${label}.backpackFreeSlots contradicts backpackSlots`);
      }
      return reported;
    })(),
    backpack,
    backpackSlots,
    tradeInventoryCapacity,
    tradeInventoryFreeSlots: (() => {
      const reported = rangedIntegerValue(
        marketplace.tradeInventoryFreeSlots,
        `${label}.tradeInventoryFreeSlots`,
        0,
        tradeInventoryCapacity,
      );
      if (reported !== actualTradeInventoryFreeSlots) {
        throw new ContractError(`${label}.tradeInventoryFreeSlots contradicts tradeInventory`);
      }
      return reported;
    })(),
    tradeInventoryReservedSlots: rangedIntegerValue(
      marketplace.tradeInventoryReservedSlots,
      `${label}.tradeInventoryReservedSlots`,
      0,
      tradeInventoryCapacity,
    ),
    tradeInventory,
    resources,
    orders: parseOrders('orders', true),
    myOrders: parseOrders('myOrders', false),
  };
}

function parseMarketplaceResource(value: unknown, label: string): MarketplaceResourcePayload {
  const resource = objectValue(value, label);
  const buttonAsset = stringValue(resource.buttonAsset, `${label}.buttonAsset`);
  const weatherKind =
    resource.weatherKind === undefined
      ? undefined
      : weatherKindValue(resource.weatherKind, `${label}.weatherKind`);
  if (
    weatherKind === undefined
      ? !buttonAsset.startsWith('/legacy/resources/') || !buttonAsset.endsWith('.png')
      : buttonAsset !== `/legacy/interface/clickactions/${weatherKind}.png`
  ) {
    throw new ContractError(`${label}.buttonAsset is invalid`);
  }
  const tradable = booleanValue(resource.tradable, `${label}.tradable`);
  const referenceValue =
    resource.referenceValue === null
      ? null
      : rangedIntegerValue(resource.referenceValue, `${label}.referenceValue`, 1, 1000);
  const parseRange = (value: unknown, rangeLabel: string) => {
    if (value === null) return null;
    const range = objectValue(value, rangeLabel);
    const minimum = rangedIntegerValue(range.minimum, `${rangeLabel}.minimum`, 1, 2000);
    const maximum = rangedIntegerValue(range.maximum, `${rangeLabel}.maximum`, minimum, 2000);
    return { minimum, maximum };
  };
  const unitPriceRange = parseRange(resource.unitPriceRange, `${label}.unitPriceRange`);
  if (tradable !== (referenceValue !== null && unitPriceRange !== null)) {
    throw new ContractError(`${label} tradability contradicts its price ranges`);
  }
  return {
    ...(weatherKind === undefined ? {} : { weatherKind }),
    resourceId: rangedIntegerValue(resource.resourceId, `${label}.resourceId`, 1, 32_767),
    name: stringValue(resource.name, `${label}.name`),
    buttonAsset,
    tradable,
    referenceValue,
    unitPriceRange,
  };
}

function parseBuildingProductionPayload(value: unknown, label: string): BuildingProductionPayload {
  const production = objectValue(value, label);
  const serverTime = nonNegativeIntegerValue(production.serverTime, `${label}.serverTime`);
  const inventoryValue = objectValue(production.inventory, `${label}.inventory`);
  const capacity = rangedIntegerValue(
    inventoryValue.capacity,
    `${label}.inventory.capacity`,
    6,
    12,
  );
  if (capacity !== 6 && capacity !== 12) {
    throw new ContractError(`${label}.inventory.capacity must be 6 or 12`);
  }
  const backpackEntries = arrayValue(inventoryValue.backpack, `${label}.inventory.backpack`);
  if (backpackEntries.length !== 12) {
    throw new ContractError(`${label}.inventory.backpack must contain 12 slots`);
  }
  const backpack = backpackEntries.map((entry, index) => {
    const slotLabel = `${label}.inventory.backpack[${String(index)}]`;
    const slot = objectValue(entry, slotLabel);
    const slotIndex = rangedIntegerValue(slot.index, `${slotLabel}.index`, 0, 11);
    if (slotIndex !== index) throw new ContractError(`${slotLabel}.index is out of order`);
    const occupied = booleanValue(slot.occupied, `${slotLabel}.occupied`);
    const resourceId = rangedIntegerValue(slot.resourceId, `${slotLabel}.resourceId`, 0, 32_767);
    if (occupied !== resourceId > 0) {
      throw new ContractError(`${slotLabel}.occupied contradicts resourceId`);
    }
    return {
      index: slotIndex,
      accessible: booleanValue(slot.accessible, `${slotLabel}.accessible`),
      occupied,
      resourceId,
      ...(occupied ? { resource: productionResource(slot, slotLabel) } : {}),
    };
  });
  const shortcutEntries = arrayValue(inventoryValue.shortcuts, `${label}.inventory.shortcuts`);
  if (shortcutEntries.length !== 8) {
    throw new ContractError(`${label}.inventory.shortcuts must contain 8 slots`);
  }
  const shortcuts = shortcutEntries.map((entry, index) => {
    const shortcutLabel = `${label}.inventory.shortcuts[${String(index)}]`;
    const shortcut = objectValue(entry, shortcutLabel);
    const shortcutIndex = rangedIntegerValue(shortcut.index, `${shortcutLabel}.index`, 0, 7);
    if (shortcutIndex !== index) {
      throw new ContractError(`${shortcutLabel}.index is out of order`);
    }
    const resourceId = rangedIntegerValue(
      shortcut.resourceId,
      `${shortcutLabel}.resourceId`,
      0,
      32_767,
    );
    return {
      index: shortcutIndex,
      resourceId,
      lit: booleanValue(shortcut.lit, `${shortcutLabel}.lit`),
      ...(resourceId > 0 ? { resource: productionResource(shortcut, shortcutLabel) } : {}),
    };
  });
  const buildingEntries = arrayValue(production.buildings, `${label}.buildings`);
  const buildingIds = new Set<number>();
  const buildings = buildingEntries.map((entry, buildingIndex) => {
    const buildingLabel = `${label}.buildings[${String(buildingIndex)}]`;
    const building = objectValue(entry, buildingLabel);
    const buildingId = signedShortValue(building.buildingId, `${buildingLabel}.buildingId`);
    if (buildingId === 0 || buildingIds.has(buildingId)) {
      throw new ContractError(`${buildingLabel}.buildingId is reserved or duplicated`);
    }
    buildingIds.add(buildingId);
    const stateValue = stringValue(building.state, `${buildingLabel}.state`);
    if (
      stateValue !== 'unavailable' &&
      stateValue !== 'ready' &&
      stateValue !== 'producing' &&
      stateValue !== 'requires-ingredients'
    ) {
      throw new ContractError(`${buildingLabel}.state is unsupported`);
    }
    const state: ProductionBuildingPayload['state'] = stateValue;
    const effectiveReady = booleanValue(building.effectiveReady, `${buildingLabel}.effectiveReady`);
    const canHarvest = booleanValue(building.canHarvest, `${buildingLabel}.canHarvest`);
    const delayEnd = nonNegativeIntegerValue(building.delayEnd, `${buildingLabel}.delayEnd`);
    const remainingMs = nonNegativeIntegerValue(
      building.remainingMs,
      `${buildingLabel}.remainingMs`,
    );
    const ingredientsMask = nonNegativeIntegerValue(
      building.ingredientsMask,
      `${buildingLabel}.ingredientsMask`,
    );
    const ingredientEntries = arrayValue(building.ingredients, `${buildingLabel}.ingredients`);
    if (ingredientEntries.length > 62) {
      throw new ContractError(`${buildingLabel}.ingredients exceeds the bitmask bound`);
    }
    const ingredients = ingredientEntries.map((ingredientEntry, ingredientIndex) => {
      const ingredientLabel = `${buildingLabel}.ingredients[${String(ingredientIndex)}]`;
      const ingredient = objectValue(ingredientEntry, ingredientLabel);
      const index = rangedIntegerValue(ingredient.index, `${ingredientLabel}.index`, 0, 61);
      if (index !== ingredientIndex) {
        throw new ContractError(`${ingredientLabel}.index is out of order`);
      }
      const applied = booleanValue(ingredient.applied, `${ingredientLabel}.applied`);
      if (applied !== (Math.floor(ingredientsMask / 2 ** index) % 2 === 1)) {
        throw new ContractError(`${ingredientLabel}.applied contradicts ingredientsMask`);
      }
      const availableCount = nonNegativeIntegerValue(
        ingredient.availableCount,
        `${ingredientLabel}.availableCount`,
      );
      const canApply = booleanValue(ingredient.canApply, `${ingredientLabel}.canApply`);
      if (canApply && (applied || availableCount === 0 || state !== 'requires-ingredients')) {
        throw new ContractError(`${ingredientLabel}.canApply contradicts production state`);
      }
      return {
        ...productionResource(ingredient, ingredientLabel),
        index,
        applied,
        availableCount,
        canApply,
      };
    });
    if (state === 'ready' && (!effectiveReady || !canHarvest || remainingMs !== 0)) {
      throw new ContractError(`${buildingLabel}.state contradicts ready production`);
    }
    if (
      state === 'producing' &&
      (effectiveReady ||
        canHarvest ||
        delayEnd === 0 ||
        (remainingMs === 0 && delayEnd > serverTime) ||
        (remainingMs > 0 && delayEnd <= serverTime))
    ) {
      throw new ContractError(`${buildingLabel}.state contradicts active production`);
    }
    if (
      (state === 'requires-ingredients' || state === 'unavailable') &&
      (effectiveReady || canHarvest || remainingMs !== 0)
    ) {
      throw new ContractError(`${buildingLabel}.state contradicts unavailable production`);
    }
    return {
      buildingId,
      buildingType: signedShortValue(building.buildingType, `${buildingLabel}.buildingType`),
      state,
      ready: booleanValue(building.ready, `${buildingLabel}.ready`),
      effectiveReady,
      canHarvest,
      delayStart: nonNegativeIntegerValue(building.delayStart, `${buildingLabel}.delayStart`),
      delayEnd,
      remainingMs,
      ingredientsMask,
      recipeId: nonNegativeIntegerValue(building.recipeId, `${buildingLabel}.recipeId`),
      produceDelayMs: nonNegativeIntegerValue(
        building.produceDelayMs,
        `${buildingLabel}.produceDelayMs`,
      ),
      product: productionResource(
        objectValue(building.product, `${buildingLabel}.product`),
        `${buildingLabel}.product`,
      ),
      ingredients,
    };
  });
  return {
    villageId: positiveIntegerValue(production.villageId, `${label}.villageId`),
    entityRevision: positiveIntegerValue(production.entityRevision, `${label}.entityRevision`),
    serverTime,
    inventory: { capacity, backpack, shortcuts },
    buildings,
  };
}

function parseBuildingProductionDeltaPayload(
  value: unknown,
  label: string,
): BuildingProductionDeltaPayload {
  const parsed = parseBuildingProductionPayload(value, label);
  const production = objectValue(value, label);
  const removedBuildingIds = arrayValue(
    production.removedBuildingIds,
    `${label}.removedBuildingIds`,
  ).map((entry, index) =>
    nonZeroSignedShortValue(entry, `${label}.removedBuildingIds[${String(index)}]`),
  );
  if (new Set(removedBuildingIds).size !== removedBuildingIds.length) {
    throw new ContractError(`${label}.removedBuildingIds contains duplicates`);
  }
  return { ...parsed, removedBuildingIds };
}

function productionResource(
  value: Readonly<Record<string, unknown>>,
  label: string,
): ProductionResourcePayload {
  const buttonAsset = stringValue(value.buttonAsset, `${label}.buttonAsset`);
  if (!buttonAsset.startsWith('/legacy/resources/') || !buttonAsset.endsWith('.png')) {
    throw new ContractError(`${label}.buttonAsset is outside the extracted resource assets`);
  }
  return {
    resourceId: rangedIntegerValue(value.resourceId, `${label}.resourceId`, 1, 32_767),
    name: stringValue(value.name, `${label}.name`),
    buttonAsset,
  };
}

function productionAction(value: unknown, label: string): BuildingProductionAction {
  const action = stringValue(value, label);
  if (action !== 'harvest' && action !== 'apply-ingredient') {
    throw new ContractError(`${label} is unsupported`);
  }
  return action;
}

export function parsePetFollowingSubmissionResponse(value: unknown): PetFollowingSubmissionPayload {
  const root = objectValue(value, 'pet following submission response');
  assertProtocol(root);
  return parsePetFollowingSubmission(root.petFollowingSubmission, 'petFollowingSubmission');
}

export function parsePetHouseSubmissionResponse(value: unknown): PetHouseSubmissionPayload {
  const root = objectValue(value, 'pet house submission response');
  assertProtocol(root);
  const submission = objectValue(root.petHouseSubmission, 'petHouseSubmission');
  const following = parsePetFollowingSubmission(submission, 'petHouseSubmission');
  if (following.following) {
    throw new ContractError('petHouseSubmission cannot retain an equipped pet');
  }
  const posX = worldPositionValue(submission.posX, 'petHouseSubmission.posX');
  const posZ = worldPositionValue(submission.posZ, 'petHouseSubmission.posZ');
  const character = following.characters.characters.find(
    (entry) => entry.characterId === following.characterId,
  );
  if (character?.posX !== posX || character.posZ !== posZ) {
    throw new ContractError('petHouseSubmission target contradicts its world pet');
  }
  return {
    ...following,
    buildingId: nonZeroSignedShortValue(submission.buildingId, 'petHouseSubmission.buildingId'),
    buildingType: nonZeroSignedShortValue(
      submission.buildingType,
      'petHouseSubmission.buildingType',
    ),
    buildingRotation: rangedIntegerValue(
      submission.buildingRotation,
      'petHouseSubmission.buildingRotation',
      0,
      3,
    ) as 0 | 1 | 2 | 3,
    posX,
    posZ,
  };
}

function parsePetFollowingSubmission(value: unknown, label: string): PetFollowingSubmissionPayload {
  const submission = objectValue(value, label);
  const status = stringValue(submission.status, `${label}.status`);
  if (status !== 'following' && status !== 'staying') {
    throw new ContractError(`Unsupported pet following submission status: ${status}`);
  }
  const following = booleanValue(submission.following, `${label}.following`);
  if (following !== (status === 'following')) {
    throw new ContractError(`${label}.status contradicts following`);
  }
  const characterId = signedShortValue(submission.characterId, `${label}.characterId`);
  if (characterId === 0) {
    throw new ContractError(`${label}.characterId must be non-zero`);
  }
  const presence = parsePresencePayload(submission.presence, `${label}.presence`);
  const characters = parseCharactersPayload(submission.characters, `${label}.characters`);
  if (characters.villageId !== presence.villageId) {
    throw new ContractError(`${label} village identifiers contradict each other`);
  }
  if (characters.streamGeneration !== presence.streamGeneration) {
    throw new ContractError(`${label} stream generations contradict each other`);
  }
  const local = presence.avatars.find((avatar) => avatar.playerId === presence.localPlayerId);
  const character = characters.characters.find((entry) => entry.characterId === characterId);
  if (local === undefined || !character?.pet) {
    throw new ContractError(`${label} omits its local avatar or world pet`);
  }
  if (following) {
    if (
      local.petType !== character.petType ||
      local.petNetworkId !== character.petNetworkId ||
      local.petName !== character.petName
    ) {
      throw new ContractError(`${label} equipped pet contradicts its world character`);
    }
  } else if (local.petType > 0) {
    throw new ContractError(`${label} staying result still has an equipped pet`);
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, `${label}.sequence`),
    following,
    characterId,
    presence,
    characters,
  };
}

export function parseMountTransitionSubmissionResponse(
  value: unknown,
): MountTransitionSubmissionPayload {
  const root = objectValue(value, 'mount transition submission response');
  assertProtocol(root);
  const submission = objectValue(root.mountTransitionSubmission, 'mountTransitionSubmission');
  const status = stringValue(submission.status, 'mountTransitionSubmission.status');
  if (status !== 'applied') {
    throw new ContractError(`Unsupported mount transition status: ${status}`);
  }
  const action = stringValue(submission.action, 'mountTransitionSubmission.action');
  if (action !== 'mount' && action !== 'dismount') {
    throw new ContractError(`Unsupported mount transition action: ${action}`);
  }
  const characterId = rangedIntegerValue(
    submission.characterId,
    'mountTransitionSubmission.characterId',
    1,
    32_767,
  );
  const mountType = signedShortValue(submission.mountType, 'mountTransitionSubmission.mountType');
  const mountCharacterId = signedShortValue(
    submission.mountCharacterId,
    'mountTransitionSubmission.mountCharacterId',
  );
  const characterConsumed = booleanValue(
    submission.characterConsumed,
    'mountTransitionSubmission.characterConsumed',
  );
  const presence = parsePresencePayload(submission.presence, 'mountTransitionSubmission.presence');
  const characters = parseCharactersPayload(
    submission.characters,
    'mountTransitionSubmission.characters',
  );
  if (
    characters.villageId !== presence.villageId ||
    characters.streamGeneration !== presence.streamGeneration
  ) {
    throw new ContractError('mountTransitionSubmission snapshot identities contradict each other');
  }
  const local = presence.avatars.find((avatar) => avatar.playerId === presence.localPlayerId);
  const character = characters.characters.find((entry) => entry.characterId === characterId);
  if (local === undefined || character === undefined || character.mountType <= 0) {
    throw new ContractError('mountTransitionSubmission omits its local avatar or mount character');
  }
  if (action === 'mount') {
    if (
      mountType <= 0 ||
      mountCharacterId !== characterId ||
      !characterConsumed ||
      local.mountType !== mountType ||
      local.mountCharacterId !== characterId
    ) {
      throw new ContractError('mountTransitionSubmission mount state is contradictory');
    }
  } else if (
    mountType !== 0 ||
    mountCharacterId !== 0 ||
    characterConsumed ||
    local.mountType !== 0 ||
    local.mountCharacterId !== 0
  ) {
    throw new ContractError('mountTransitionSubmission dismount state is contradictory');
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'mountTransitionSubmission.sequence'),
    action,
    characterId,
    mountType,
    mountName: stringValue(submission.mountName, 'mountTransitionSubmission.mountName'),
    mountCharacterId,
    characterPosX: signedShortValue(
      submission.characterPosX,
      'mountTransitionSubmission.characterPosX',
    ),
    characterPosZ: signedShortValue(
      submission.characterPosZ,
      'mountTransitionSubmission.characterPosZ',
    ),
    characterConsumed,
    replayed: booleanValue(submission.replayed, 'mountTransitionSubmission.replayed'),
    serverTime: nonNegativeIntegerValue(
      submission.serverTime,
      'mountTransitionSubmission.serverTime',
    ),
    presence,
    characters,
  };
}

export function parseMountNameSubmissionResponse(value: unknown): MountNameSubmissionPayload {
  const root = objectValue(value, 'mount name submission response');
  assertProtocol(root);
  const submission = objectValue(root.mountNameSubmission, 'mountNameSubmission');
  const status = stringValue(submission.status, 'mountNameSubmission.status');
  if (status !== 'updated') {
    throw new ContractError(`Unsupported mount name status: ${status}`);
  }
  const characterId = nonZeroSignedShortValue(
    submission.characterId,
    'mountNameSubmission.characterId',
  );
  const mountType = rangedIntegerValue(
    submission.mountType,
    'mountNameSubmission.mountType',
    1,
    32_767,
  );
  const mountName = stringValue(submission.mountName, 'mountNameSubmission.mountName');
  if (mountName.length > 25 || !isJavaTrimmed(mountName)) {
    throw new ContractError('mountNameSubmission mount name is invalid');
  }
  const mountClass = stringValue(submission.mountClass, 'mountNameSubmission.mountClass');
  if (mountClass.length === 0) {
    throw new ContractError('mountNameSubmission mount class is missing');
  }
  const characters = parseCharactersPayload(
    submission.characters,
    'mountNameSubmission.characters',
  );
  const character = characters.characters.find((entry) => entry.characterId === characterId);
  if (character?.mountType !== mountType || character.mountName !== mountName) {
    throw new ContractError('mountNameSubmission omits its authoritative mount character');
  }
  return {
    status,
    characterId,
    mountType,
    mountName,
    mountClass,
    characters,
  };
}

function parseMountTransitionReconciliation(
  value: unknown,
  label: string,
): MountTransitionReconciliationPayload {
  const reconciliation = objectValue(value, label);
  const status = mountTransitionRejectionOutcomeValue(reconciliation.status, `${label}.status`);
  const action = stringValue(reconciliation.action, `${label}.action`);
  if (action !== 'mount' && action !== 'dismount') {
    throw new ContractError(`Unsupported mount transition action: ${action}`);
  }
  const presence = parsePresencePayload(reconciliation.presence, `${label}.presence`);
  const characters = parseCharactersPayload(reconciliation.characters, `${label}.characters`);
  if (
    characters.villageId !== presence.villageId ||
    characters.streamGeneration !== presence.streamGeneration
  ) {
    throw new ContractError(`${label} snapshot identities contradict each other`);
  }
  if (!presence.avatars.some((avatar) => avatar.playerId === presence.localPlayerId)) {
    throw new ContractError(`${label} omits its local avatar`);
  }
  return {
    status,
    sequence: positiveIntegerValue(reconciliation.sequence, `${label}.sequence`),
    action,
    characterId: rangedIntegerValue(reconciliation.characterId, `${label}.characterId`, 1, 32_767),
    replayed: booleanValue(reconciliation.replayed, `${label}.replayed`),
    serverTime: nonNegativeIntegerValue(reconciliation.serverTime, `${label}.serverTime`),
    presence,
    characters,
  };
}

export function parseMountShopResponse(value: unknown): MountShopPayload {
  const root = objectValue(value, 'mount shop response');
  assertProtocol(root);
  const shop = objectValue(root.mountShop, 'mountShop');
  const shopId = stringValue(shop.shop, 'mountShop.shop');
  if (
    shopId !== 'stable' &&
    shopId !== 'racing' &&
    shopId !== 'bike' &&
    shopId !== 'arvika' &&
    shopId !== 'scooter' &&
    shopId !== 'santa' &&
    shopId !== 'cross-country-skis'
  ) {
    throw new ContractError(`Unsupported mount shop: ${shopId}`);
  }
  const credits = nonNegativeIntegerValue(shop.credits, 'mountShop.credits');
  const mounted = booleanValue(shop.mounted, 'mountShop.mounted');
  const products = arrayValue(shop.products, 'mountShop.products').map((candidate, index) => {
    const product = objectValue(candidate, `mountShop.products[${String(index)}]`);
    const price = nonNegativeIntegerValue(
      product.price,
      `mountShop.products[${String(index)}].price`,
    );
    const badgeMet = booleanValue(
      product.badgeMet,
      `mountShop.products[${String(index)}].badgeMet`,
    );
    const buyEnabled = booleanValue(
      product.buyEnabled,
      `mountShop.products[${String(index)}].buyEnabled`,
    );
    if (buyEnabled !== (!mounted && credits >= price && badgeMet)) {
      throw new ContractError(`mountShop.products[${String(index)}] buy state is contradictory`);
    }
    return {
      ...(typeof product.studioUUID === 'string' ? { studioUUID: product.studioUUID } : {}),
      ...(typeof product.studioHash === 'string' ? { studioHash: product.studioHash } : {}),
      ...(typeof product.name === 'string' ? { name: product.name } : {}),
      ...(typeof product.description === 'string' ? { description: product.description } : {}),
      ...(typeof product.icon === 'string' ? { icon: product.icon } : {}),
      mountType: rangedIntegerValue(
        product.mountType,
        `mountShop.products[${String(index)}].mountType`,
        1,
        32_767,
      ),
      price,
      speed: nonNegativeIntegerValue(product.speed, `mountShop.products[${String(index)}].speed`),
      mountClass: stringValue(
        product.mountClass,
        `mountShop.products[${String(index)}].mountClass`,
      ),
      requiredBadge: stringValue(
        product.requiredBadge,
        `mountShop.products[${String(index)}].requiredBadge`,
      ),
      requiredBadgeImage: stringValue(
        product.requiredBadgeImage,
        `mountShop.products[${String(index)}].requiredBadgeImage`,
      ),
      badgeMet,
      buyEnabled,
    };
  });
  if (products.length === 0) throw new ContractError('mountShop has no products');
  return {
    shop: shopId,
    title: stringValue(shop.title, 'mountShop.title'),
    credits,
    mounted,
    products,
  };
}

export function parseMountPurchaseSubmissionResponse(
  value: unknown,
): MountPurchaseSubmissionPayload {
  const root = objectValue(value, 'mount purchase submission response');
  assertProtocol(root);
  const submission = objectValue(root.mountPurchaseSubmission, 'mountPurchaseSubmission');
  const status = stringValue(submission.status, 'mountPurchaseSubmission.status');
  if (status !== 'applied') {
    throw new ContractError(`Unsupported mount purchase status: ${status}`);
  }
  const characterId = rangedIntegerValue(
    submission.characterId,
    'mountPurchaseSubmission.characterId',
    1,
    32_767,
  );
  const mountType = rangedIntegerValue(
    submission.mountType,
    'mountPurchaseSubmission.mountType',
    1,
    32_767,
  );
  const mountName = stringValue(submission.mountName, 'mountPurchaseSubmission.mountName');
  if (mountName.length < 2 || mountName.length > 25) {
    throw new ContractError('mountPurchaseSubmission mount name length is invalid');
  }
  const characters = parseCharactersPayload(
    submission.characters,
    'mountPurchaseSubmission.characters',
  );
  const presence = parsePresencePayload(submission.presence, 'mountPurchaseSubmission.presence');
  if (
    characters.villageId !== presence.villageId ||
    characters.streamGeneration !== presence.streamGeneration
  ) {
    throw new ContractError('mountPurchaseSubmission snapshot identities contradict each other');
  }
  const character = characters.characters.find((entry) => entry.characterId === characterId);
  const local = presence.avatars.find((avatar) => avatar.playerId === presence.localPlayerId);
  if (
    character?.mountType !== mountType ||
    character.mountName !== mountName ||
    !character.consumed ||
    local?.mountType !== mountType ||
    local.mountName !== mountName ||
    local.mountCharacterId !== characterId
  ) {
    throw new ContractError('mountPurchaseSubmission omits its equipped mount');
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'mountPurchaseSubmission.sequence'),
    characterId,
    mountType,
    mountName,
    creditsAfter: nonNegativeIntegerValue(
      submission.creditsAfter,
      'mountPurchaseSubmission.creditsAfter',
    ),
    replayed: booleanValue(submission.replayed, 'mountPurchaseSubmission.replayed'),
    serverTime: nonNegativeIntegerValue(
      submission.serverTime,
      'mountPurchaseSubmission.serverTime',
    ),
    presence,
    characters,
  };
}

export function parsePetShopResponse(value: unknown): PetShopPayload {
  const root = objectValue(value, 'pet shop response');
  assertProtocol(root);
  const shop = objectValue(root.petShop, 'petShop');
  const buildingId = rangedIntegerValue(shop.buildingId, 'petShop.buildingId', -32_768, 32_767);
  if (buildingId === 0) throw new ContractError('petShop.buildingId must be non-zero');
  const credits = nonNegativeIntegerValue(shop.credits, 'petShop.credits');
  const petEquipped = booleanValue(shop.petEquipped, 'petShop.petEquipped');
  const products = arrayValue(shop.products, 'petShop.products').map((candidate, index) => {
    const label = `petShop.products[${String(index)}]`;
    const product = objectValue(candidate, label);
    const price = nonNegativeIntegerValue(product.price, `${label}.price`);
    const badgeMet = booleanValue(product.badgeMet, `${label}.badgeMet`);
    const buyEnabled = booleanValue(product.buyEnabled, `${label}.buyEnabled`);
    if (buyEnabled !== (!petEquipped && credits >= price && badgeMet)) {
      throw new ContractError(`${label} buy state is contradictory`);
    }
    const species = petShopSpeciesValue(product.species, `${label}.species`);
    return {
      petType: rangedIntegerValue(product.petType, `${label}.petType`, 1, 32_767),
      price,
      species,
      requiredBadge: stringValue(product.requiredBadge, `${label}.requiredBadge`),
      requiredBadgeImage: stringValue(product.requiredBadgeImage, `${label}.requiredBadgeImage`),
      badgeMet,
      buyEnabled,
    };
  });
  if (products.length === 0) throw new ContractError('petShop has no products');
  return {
    shop: petShopIdValue(shop.shop, 'petShop.shop'),
    buildingId,
    title: stringValue(shop.title, 'petShop.title'),
    credits,
    petEquipped,
    products,
  };
}

function petShopIdValue(value: unknown, label: string): PetShopId {
  const shop = stringValue(value, label);
  if (shop !== 'ockelboda' && shop !== 'new-pets' && shop !== 'spider') {
    throw new ContractError(`${label} has unsupported shop ${shop}`);
  }
  return shop;
}

function petShopSpeciesValue(value: unknown, label: string): PetShopSpecies {
  const species = stringValue(value, label);
  if (
    species !== 'dog' &&
    species !== 'monkey' &&
    species !== 'butterfly' &&
    species !== 'bat' &&
    species !== 'snowman' &&
    species !== 'spindel'
  ) {
    throw new ContractError(`${label} has unsupported species ${species}`);
  }
  return species;
}

export function parsePetPurchaseSubmissionResponse(value: unknown): PetPurchaseSubmissionPayload {
  const root = objectValue(value, 'pet purchase submission response');
  assertProtocol(root);
  const submission = objectValue(root.petPurchaseSubmission, 'petPurchaseSubmission');
  const status = stringValue(submission.status, 'petPurchaseSubmission.status');
  if (status !== 'applied') {
    throw new ContractError(`Unsupported pet purchase status: ${status}`);
  }
  const characterId = rangedIntegerValue(
    submission.characterId,
    'petPurchaseSubmission.characterId',
    1,
    32_767,
  );
  const petType = rangedIntegerValue(
    submission.petType,
    'petPurchaseSubmission.petType',
    1,
    32_767,
  );
  const petName = stringValue(submission.petName, 'petPurchaseSubmission.petName');
  if (petName.length < 2 || petName.length > 25) {
    throw new ContractError('petPurchaseSubmission pet name length is invalid');
  }
  const characters = parseCharactersPayload(
    submission.characters,
    'petPurchaseSubmission.characters',
  );
  const presence = parsePresencePayload(submission.presence, 'petPurchaseSubmission.presence');
  if (
    characters.villageId !== presence.villageId ||
    characters.streamGeneration !== presence.streamGeneration
  ) {
    throw new ContractError('petPurchaseSubmission snapshot identities contradict each other');
  }
  const character = characters.characters.find((entry) => entry.characterId === characterId);
  const local = presence.avatars.find((avatar) => avatar.playerId === presence.localPlayerId);
  if (
    character?.petType !== petType ||
    character.petName !== petName ||
    !character.pet ||
    !character.consumed ||
    local?.petType !== petType ||
    local.petNetworkId !== character.petNetworkId ||
    local.petName !== petName
  ) {
    throw new ContractError('petPurchaseSubmission omits its equipped pet');
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'petPurchaseSubmission.sequence'),
    characterId,
    petType,
    petName,
    creditsAfter: nonNegativeIntegerValue(
      submission.creditsAfter,
      'petPurchaseSubmission.creditsAfter',
    ),
    replayed: booleanValue(submission.replayed, 'petPurchaseSubmission.replayed'),
    serverTime: nonNegativeIntegerValue(submission.serverTime, 'petPurchaseSubmission.serverTime'),
    presence,
    characters,
  };
}

export function parseThemeShopResponse(value: unknown): ThemeShopPayload {
  const root = objectValue(value, 'theme shop response');
  assertProtocol(root);
  const shop = objectValue(root.themeShop, 'themeShop');
  const buildingId = rangedIntegerValue(shop.buildingId, 'themeShop.buildingId', -32_768, 32_767);
  if (buildingId === 0) throw new ContractError('themeShop.buildingId must be non-zero');
  const previewImage = stringValue(shop.previewImage, 'themeShop.previewImage');
  const resourceImage = stringValue(shop.resourceImage, 'themeShop.resourceImage');
  if (!previewImage.startsWith('/legacy/interface/theme-purchase/')) {
    throw new ContractError('themeShop.previewImage is outside the extracted theme assets');
  }
  if (!resourceImage.startsWith('/legacy/resources/')) {
    throw new ContractError('themeShop.resourceImage is outside the extracted resource assets');
  }
  const targetVillages = arrayValue(shop.targetVillages, 'themeShop.targetVillages').map(
    (entry, index) => {
      const target = objectValue(entry, `themeShop.targetVillages[${String(index)}]`);
      return {
        villageId: positiveIntegerValue(
          target.villageId,
          `themeShop.targetVillages[${String(index)}].villageId`,
        ),
        slot: rangedIntegerValue(
          target.slot,
          `themeShop.targetVillages[${String(index)}].slot`,
          1,
          5,
        ),
        name: stringValue(target.name, `themeShop.targetVillages[${String(index)}].name`),
        active: booleanValue(target.active, `themeShop.targetVillages[${String(index)}].active`),
      };
    },
  );
  if (
    targetVillages.length === 0 ||
    new Set(targetVillages.map((target) => target.villageId)).size !== targetVillages.length ||
    targetVillages.some(
      (target, index) => index > 0 && target.slot <= (targetVillages[index - 1]?.slot ?? 0),
    )
  ) {
    throw new ContractError('themeShop.targetVillages must be non-empty, unique, and slot ordered');
  }
  const villageId = positiveIntegerValue(shop.villageId, 'themeShop.villageId');
  const sourceVillageId = positiveIntegerValue(shop.sourceVillageId, 'themeShop.sourceVillageId');
  if (villageId !== sourceVillageId) {
    throw new ContractError('themeShop.villageId contradicts themeShop.sourceVillageId');
  }
  return {
    villageId,
    sourceVillageId,
    buildingId,
    buildingType: rangedIntegerValue(shop.buildingType, 'themeShop.buildingType', -32_768, 32_767),
    themeId: rangedIntegerValue(shop.themeId, 'themeShop.themeId', 0, 127),
    title: stringValue(shop.title, 'themeShop.title'),
    description: stringValue(shop.description, 'themeShop.description'),
    previewImage,
    resourceImage,
    displayPrice: nonNegativeIntegerValue(shop.displayPrice, 'themeShop.displayPrice'),
    purchasePrice: nonNegativeIntegerValue(shop.purchasePrice, 'themeShop.purchasePrice'),
    credits: nonNegativeIntegerValue(shop.credits, 'themeShop.credits'),
    owned: booleanValue(shop.owned, 'themeShop.owned'),
    active: booleanValue(shop.active, 'themeShop.active'),
    targetVillages,
  };
}

export function parseThemePurchaseSubmissionResponse(
  value: unknown,
): ThemePurchaseSubmissionPayload {
  const root = objectValue(value, 'theme purchase submission response');
  assertProtocol(root);
  const submission = objectValue(root.themePurchaseSubmission, 'themePurchaseSubmission');
  const status = stringValue(submission.status, 'themePurchaseSubmission.status');
  if (status !== 'applied') {
    throw new ContractError(`Unsupported theme purchase status: ${status}`);
  }
  const buildingId = rangedIntegerValue(
    submission.buildingId,
    'themePurchaseSubmission.buildingId',
    -32_768,
    32_767,
  );
  if (buildingId === 0) {
    throw new ContractError('themePurchaseSubmission.buildingId must be non-zero');
  }
  const targetVillageId = positiveIntegerValue(
    submission.targetVillageId,
    'themePurchaseSubmission.targetVillageId',
  );
  const villageId = positiveIntegerValue(submission.villageId, 'themePurchaseSubmission.villageId');
  if (villageId !== targetVillageId) {
    throw new ContractError(
      'themePurchaseSubmission.villageId contradicts themePurchaseSubmission.targetVillageId',
    );
  }
  return {
    status,
    sequence: positiveIntegerValue(submission.sequence, 'themePurchaseSubmission.sequence'),
    sourceVillageId: positiveIntegerValue(
      submission.sourceVillageId,
      'themePurchaseSubmission.sourceVillageId',
    ),
    targetVillageId,
    villageId,
    buildingId,
    themeId: rangedIntegerValue(submission.themeId, 'themePurchaseSubmission.themeId', 0, 127),
    chargedPrice: nonNegativeIntegerValue(
      submission.chargedPrice,
      'themePurchaseSubmission.chargedPrice',
    ),
    creditsAfter: nonNegativeIntegerValue(
      submission.creditsAfter,
      'themePurchaseSubmission.creditsAfter',
    ),
    newlyOwned: booleanValue(submission.newlyOwned, 'themePurchaseSubmission.newlyOwned'),
    replayed: booleanValue(submission.replayed, 'themePurchaseSubmission.replayed'),
    serverTime: nonNegativeIntegerValue(
      submission.serverTime,
      'themePurchaseSubmission.serverTime',
    ),
  };
}

export function parseVillageChatResponse(value: unknown): VillageChatPayload {
  const root = objectValue(value, 'village chat response');
  assertProtocol(root);
  return parseVillageChatPayload(root.villageChat, 'villageChat');
}

export function parseLiveMessagesResponse(value: unknown): LiveMessagesPayload {
  const root = objectValue(value, 'Live messages response');
  assertProtocol(root);
  return parseLiveMessagesPayload(root.live, 'live');
}

export function parseForumIndexResponse(value: unknown): ForumIndexPayload {
  const root = objectValue(value, 'Forum index response');
  assertProtocol(root);
  const forum = objectValue(root.forum, 'forum');
  const categoryRows = arrayValue(forum.categories, 'forum.categories');
  if (categoryRows.length > 100) {
    throw new ContractError('forum.categories exceeds 100 entries');
  }
  const categoryIds = new Set<number>();
  const forumIds = new Set<number>();
  return {
    categories: categoryRows.map((categoryValue, categoryIndex) => {
      const label = `forum.categories[${String(categoryIndex)}]`;
      const category = objectValue(categoryValue, label);
      const id = positiveIntegerValue(category.id, `${label}.id`);
      if (categoryIds.has(id)) throw new ContractError(`${label}.id is duplicated`);
      categoryIds.add(id);
      const name = boundedText(category.name, `${label}.name`, 255, true);
      const forumRows = arrayValue(category.forums, `${label}.forums`);
      if (forumRows.length > 500) {
        throw new ContractError(`${label}.forums exceeds 500 entries`);
      }
      return {
        id,
        name,
        forums: forumRows.map((forumValue, forumIndex) => {
          const forumLabel = `${label}.forums[${String(forumIndex)}]`;
          const entry = objectValue(forumValue, forumLabel);
          const forumId = positiveIntegerValue(entry.id, `${forumLabel}.id`);
          if (forumIds.has(forumId)) {
            throw new ContractError(`${forumLabel}.id is duplicated`);
          }
          forumIds.add(forumId);
          return {
            id: forumId,
            name: boundedText(entry.name, `${forumLabel}.name`, 255, true),
            description: boundedText(entry.description, `${forumLabel}.description`, 2_048, false),
            lastWrite: nullableNonNegativeInteger(entry.lastWrite, `${forumLabel}.lastWrite`),
            lastRead: nullableNonNegativeInteger(entry.lastRead, `${forumLabel}.lastRead`),
          };
        }),
      };
    }),
  };
}

export function parseForumThreadsResponse(value: unknown): ForumThreadPagePayload {
  const root = objectValue(value, 'Forum threads response');
  assertProtocol(root);
  const page = objectValue(root.forumThreads, 'forumThreads');
  const pageNumber = positiveIntegerValue(page.page, 'forumThreads.page');
  const pageCount = positiveIntegerValue(page.pageCount, 'forumThreads.pageCount');
  if (pageNumber > pageCount) {
    throw new ContractError('forumThreads.page exceeds pageCount');
  }
  const rows = arrayValue(page.threads, 'forumThreads.threads');
  if (rows.length > 100) throw new ContractError('forumThreads.threads exceeds 100 entries');
  const ids = new Set<number>();
  return {
    forumId: positiveIntegerValue(page.forumId, 'forumThreads.forumId'),
    forumName: boundedText(page.forumName, 'forumThreads.forumName', 255, true),
    page: pageNumber,
    pageCount,
    canModerate: booleanValue(page.canModerate, 'forumThreads.canModerate'),
    canCreate: booleanValue(page.canCreate, 'forumThreads.canCreate'),
    threads: rows.map((rowValue, index) => {
      const label = `forumThreads.threads[${String(index)}]`;
      const row = objectValue(rowValue, label);
      const id = positiveIntegerValue(row.id, `${label}.id`);
      if (ids.has(id)) throw new ContractError(`${label}.id is duplicated`);
      ids.add(id);
      return {
        id,
        name: boundedText(row.name, `${label}.name`, 255, true),
        lastWrite: nonNegativeIntegerValue(row.lastWrite, `${label}.lastWrite`),
        lastRead: nullableNonNegativeInteger(row.lastRead, `${label}.lastRead`),
        locked: booleanValue(row.locked, `${label}.locked`),
        sticky: booleanValue(row.sticky, `${label}.sticky`),
        messageCount: nonNegativeIntegerValue(row.messageCount, `${label}.messageCount`),
      };
    }),
  };
}

export function parseForumMessagesResponse(value: unknown): ForumMessagePagePayload {
  const root = objectValue(value, 'Forum messages response');
  assertProtocol(root);
  const page = objectValue(root.forumMessages, 'forumMessages');
  const pageNumber = positiveIntegerValue(page.page, 'forumMessages.page');
  const pageCount = positiveIntegerValue(page.pageCount, 'forumMessages.pageCount');
  if (pageNumber > pageCount) {
    throw new ContractError('forumMessages.page exceeds pageCount');
  }
  const rows = arrayValue(page.messages, 'forumMessages.messages');
  if (rows.length > 100) throw new ContractError('forumMessages.messages exceeds 100 entries');
  const ids = new Set<number>();
  return {
    forumId: positiveIntegerValue(page.forumId, 'forumMessages.forumId'),
    threadId: positiveIntegerValue(page.threadId, 'forumMessages.threadId'),
    threadName: boundedText(page.threadName, 'forumMessages.threadName', 255, true),
    forumName: boundedText(page.forumName, 'forumMessages.forumName', 255, true),
    page: pageNumber,
    pageCount,
    canModerate: booleanValue(page.canModerate, 'forumMessages.canModerate'),
    locked: booleanValue(page.locked, 'forumMessages.locked'),
    sticky: booleanValue(page.sticky, 'forumMessages.sticky'),
    messages: rows.map((rowValue, index) => {
      const label = `forumMessages.messages[${String(index)}]`;
      const row = objectValue(rowValue, label);
      const id = positiveIntegerValue(row.id, `${label}.id`);
      if (ids.has(id)) throw new ContractError(`${label}.id is duplicated`);
      ids.add(id);
      return {
        id,
        message: boundedText(row.message, `${label}.message`, 32_768, true),
        pictureId:
          row.pictureId === null ? null : positiveIntegerValue(row.pictureId, `${label}.pictureId`),
        username: boundedText(row.username, `${label}.username`, 255, true),
        playerId: positiveIntegerValue(row.playerId, `${label}.playerId`),
        online: booleanValue(row.online, `${label}.online`),
        written: nonNegativeIntegerValue(row.written, `${label}.written`),
        sex: boundedText(row.sex, `${label}.sex`, 32, true),
      };
    }),
  };
}

export function parseForumSearchResponse(value: unknown): ForumSearchPagePayload {
  const root = objectValue(value, 'Forum search response');
  assertProtocol(root);
  const result = objectValue(root.forumSearch, 'forumSearch');
  const page = positiveIntegerValue(result.page, 'forumSearch.page');
  const pageCount = positiveIntegerValue(result.pageCount, 'forumSearch.pageCount');
  if (page > pageCount) throw new ContractError('forumSearch.page exceeds pageCount');
  const rows = arrayValue(result.matches, 'forumSearch.matches');
  if (rows.length > 100) throw new ContractError('forumSearch.matches exceeds 100 entries');
  return {
    query: boundedText(result.query, 'forumSearch.query', 255, false),
    page,
    pageCount,
    matches: rows.map((rowValue, index) => {
      const label = `forumSearch.matches[${String(index)}]`;
      const row = objectValue(rowValue, label);
      return {
        threadId: positiveIntegerValue(row.threadId, `${label}.threadId`),
        threadName: boundedText(row.threadName, `${label}.threadName`, 255, true),
        highlight: boundedText(row.highlight, `${label}.highlight`, 4_096, false),
        pictureId:
          row.pictureId === null ? null : positiveIntegerValue(row.pictureId, `${label}.pictureId`),
        username: boundedText(row.username, `${label}.username`, 255, true),
        playerId: positiveIntegerValue(row.playerId, `${label}.playerId`),
        online: booleanValue(row.online, `${label}.online`),
        written: nonNegativeIntegerValue(row.written, `${label}.written`),
      };
    }),
  };
}

export function parseForumThreadCreationResponse(value: unknown): ForumThreadCreationPayload {
  const mutation = forumMutation(value, 'created');
  return {
    status: 'created',
    forumId: positiveIntegerValue(mutation.forumId, 'forumMutation.forumId'),
    threadId: positiveIntegerValue(mutation.threadId, 'forumMutation.threadId'),
  };
}

export function parseForumReplyCreationResponse(value: unknown): ForumReplyCreationPayload {
  const mutation = forumMutation(value, 'created');
  return {
    status: 'created',
    threadId: positiveIntegerValue(mutation.threadId, 'forumMutation.threadId'),
  };
}

export function parseForumMessageEditResponse(value: unknown): ForumMessageEditPayload {
  const mutation = forumMutation(value, 'updated');
  return {
    status: 'updated',
    threadId: positiveIntegerValue(mutation.threadId, 'forumMutation.threadId'),
    messageId: positiveIntegerValue(mutation.messageId, 'forumMutation.messageId'),
  };
}

export function parseForumMessageDeletionResponse(value: unknown): ForumMessageDeletionPayload {
  const mutation = forumMutation(value, 'deleted');
  return {
    status: 'deleted',
    threadId: positiveIntegerValue(mutation.threadId, 'forumMutation.threadId'),
    messageId: positiveIntegerValue(mutation.messageId, 'forumMutation.messageId'),
    removedThread: booleanValue(mutation.removedThread, 'forumMutation.removedThread'),
  };
}

export function parseForumThreadStateResponse(
  value: unknown,
  field: 'sticky' | 'locked',
): ForumThreadStatePayload {
  const mutation = forumMutation(value, 'updated');
  return {
    status: 'updated',
    threadId: positiveIntegerValue(mutation.threadId, 'forumMutation.threadId'),
    value: booleanValue(mutation[field], `forumMutation.${field}`),
  };
}

function forumMutation(
  value: unknown,
  expectedStatus: 'created' | 'updated' | 'deleted',
): Readonly<Record<string, unknown>> {
  const root = objectValue(value, 'Forum mutation response');
  assertProtocol(root);
  const mutation = objectValue(root.forumMutation, 'forumMutation');
  const status = stringValue(mutation.status, 'forumMutation.status');
  if (status !== expectedStatus) {
    throw new ContractError(`forumMutation.status must be ${expectedStatus}, received ${status}`);
  }
  return mutation;
}

function parseLiveMessagesPayload(value: unknown, label: string): LiveMessagesPayload {
  const snapshot = objectValue(value, label);
  const revision = nonNegativeIntegerValue(snapshot.revision, `${label}.revision`);
  const entries = arrayValue(snapshot.messages, `${label}.messages`);
  if (entries.length > 40) throw new ContractError(`${label}.messages exceeds 40 entries`);
  let previousSequence = 0;
  const messages = entries.map((entry, index) => {
    const entryLabel = `${label}.messages[${String(index)}]`;
    const message = objectValue(entry, entryLabel);
    const sequence = positiveIntegerValue(message.sequence, `${entryLabel}.sequence`);
    if (sequence <= previousSequence || sequence > revision) {
      throw new ContractError(
        `${entryLabel}.sequence is not monotonic within the snapshot revision`,
      );
    }
    previousSequence = sequence;
    const playerName = stringValue(message.playerName, `${entryLabel}.playerName`);
    const text = stringValue(message.text, `${entryLabel}.text`);
    if (playerName.length > 100) throw new ContractError(`${entryLabel}.playerName is too long`);
    if (
      text.length === 0 ||
      Array.from(text).length > LIVE_MESSAGE_MAXIMUM_CHARACTERS ||
      /[\n\r\v\f\u0085\u2028\u2029]/u.test(text)
    ) {
      throw new ContractError(
        `${entryLabel}.text must be one non-empty line of at most 300 characters`,
      );
    }
    return {
      sequence,
      signType: rangedIntegerValue(message.signType, `${entryLabel}.signType`, 0, 32_767),
      playerId: positiveIntegerValue(message.playerId, `${entryLabel}.playerId`),
      playerName,
      text,
      pictureId: nonNegativeIntegerValue(message.pictureId, `${entryLabel}.pictureId`),
      sex: rangedIntegerValue(message.sex, `${entryLabel}.sex`, 0, 255),
      age: rangedIntegerValue(message.age, `${entryLabel}.age`, 0, 255),
      level: nonNegativeIntegerValue(message.level, `${entryLabel}.level`),
      online: booleanValue(message.online, `${entryLabel}.online`),
      receivedAt: nonNegativeIntegerValue(message.receivedAt, `${entryLabel}.receivedAt`),
      recipientPlayerId:
        message.recipientPlayerId === undefined
          ? 0
          : nonNegativeIntegerValue(message.recipientPlayerId, `${entryLabel}.recipientPlayerId`),
    };
  });
  return {
    ...(snapshot.serverTime === undefined
      ? {}
      : { serverTime: nonNegativeIntegerValue(snapshot.serverTime, `${label}.serverTime`) }),
    streamGeneration: positiveIntegerValue(snapshot.streamGeneration, `${label}.streamGeneration`),
    revision,
    messages,
  };
}

function parseVillageChatPayload(value: unknown, label: string): VillageChatPayload {
  const snapshot = objectValue(value, label);
  const revision = nonNegativeIntegerValue(snapshot.revision, `${label}.revision`);
  const entries = arrayValue(snapshot.messages, `${label}.messages`);
  if (entries.length > 40) throw new ContractError(`${label}.messages exceeds 40 entries`);
  let previousSequence = 0;
  const messages = entries.map((entry, index) => {
    const entryLabel = `${label}.messages[${String(index)}]`;
    const message = objectValue(entry, entryLabel);
    const sequence = positiveIntegerValue(message.sequence, `${entryLabel}.sequence`);
    if (sequence <= previousSequence || sequence > revision) {
      throw new ContractError(
        `${entryLabel}.sequence is not monotonic within the snapshot revision`,
      );
    }
    previousSequence = sequence;
    const fromPlayerName = stringValue(message.fromPlayerName, `${entryLabel}.fromPlayerName`);
    const text = stringValue(message.text, `${entryLabel}.text`);
    if (fromPlayerName.length > 100) {
      throw new ContractError(`${entryLabel}.fromPlayerName is too long`);
    }
    if (text.length > 8_192) throw new ContractError(`${entryLabel}.text is too long`);
    return {
      sequence,
      fromPlayerId: integerValue(message.fromPlayerId, `${entryLabel}.fromPlayerId`),
      fromPlayerName,
      text,
      createdAt: nonNegativeIntegerValue(message.createdAt, `${entryLabel}.createdAt`),
      ...(message.diceRoll === undefined
        ? {}
        : { diceRoll: booleanValue(message.diceRoll, `${entryLabel}.diceRoll`) }),
      ...(message.bellRing === undefined
        ? {}
        : { bellRing: booleanValue(message.bellRing, `${entryLabel}.bellRing`) }),
      ...(message.speakerCharacterId === undefined
        ? {}
        : {
            speakerCharacterId: signedShortValue(
              message.speakerCharacterId,
              `${entryLabel}.speakerCharacterId`,
            ),
          }),
    };
  });
  const soundEffectEntries =
    snapshot.soundEffects === undefined
      ? []
      : arrayValue(snapshot.soundEffects, `${label}.soundEffects`);
  if (soundEffectEntries.length > 40) {
    throw new ContractError(`${label}.soundEffects exceeds 40 entries`);
  }
  let previousSoundEffectSequence = 0;
  const soundEffects = soundEffectEntries.map((entry, index) => {
    const entryLabel = `${label}.soundEffects[${String(index)}]`;
    const soundEffect = objectValue(entry, entryLabel);
    const sequence = positiveIntegerValue(soundEffect.sequence, `${entryLabel}.sequence`);
    if (sequence <= previousSoundEffectSequence || sequence > revision) {
      throw new ContractError(
        `${entryLabel}.sequence is not monotonic within the snapshot revision`,
      );
    }
    previousSoundEffectSequence = sequence;
    const cue = stringValue(soundEffect.cue, `${entryLabel}.cue`);
    if (!isExtraSoundEffectCue(cue)) {
      throw new ContractError(`${entryLabel}.cue is unsupported`);
    }
    return { sequence, cue };
  });
  const moonwalkStateEntries =
    snapshot.moonwalkStates === undefined
      ? []
      : arrayValue(snapshot.moonwalkStates, `${label}.moonwalkStates`);
  if (moonwalkStateEntries.length > 40) {
    throw new ContractError(`${label}.moonwalkStates exceeds 40 entries`);
  }
  let previousMoonwalkSequence = 0;
  const moonwalkStates = moonwalkStateEntries.map((entry, index) => {
    const entryLabel = `${label}.moonwalkStates[${String(index)}]`;
    const state = objectValue(entry, entryLabel);
    const sequence = positiveIntegerValue(state.sequence, `${entryLabel}.sequence`);
    if (sequence <= previousMoonwalkSequence || sequence > revision) {
      throw new ContractError(
        `${entryLabel}.sequence is not monotonic within the snapshot revision`,
      );
    }
    previousMoonwalkSequence = sequence;
    return {
      sequence,
      playerId: positiveIntegerValue(state.playerId, `${entryLabel}.playerId`),
      enabled: booleanValue(state.enabled, `${entryLabel}.enabled`),
      synchronization: booleanValue(state.synchronization, `${entryLabel}.synchronization`),
    };
  });
  return {
    ...(snapshot.serverTime === undefined
      ? {}
      : { serverTime: nonNegativeIntegerValue(snapshot.serverTime, `${label}.serverTime`) }),
    streamGeneration: positiveIntegerValue(snapshot.streamGeneration, `${label}.streamGeneration`),
    villageId: positiveIntegerValue(snapshot.villageId, `${label}.villageId`),
    revision,
    ...(snapshot.godMode === undefined
      ? {}
      : { godMode: booleanValue(snapshot.godMode, `${label}.godMode`) }),
    messages,
    ...(snapshot.soundEffects === undefined ? {} : { soundEffects }),
    ...(snapshot.moonwalkStates === undefined ? {} : { moonwalkStates }),
  };
}

function isExtraSoundEffectCue(value: string): value is ExtraSoundEffectCue {
  return (
    value === 'faah' ||
    value === 'glassbil' ||
    value === 'postmanpat' ||
    value === 'rizz' ||
    value === 'fart'
  );
}

export function parseNotificationCursorResponse(value: unknown): NotificationCursorPayload {
  const root = objectValue(value, 'notification response');
  assertProtocol(root);
  const notifications = objectValue(root.notifications, 'notifications');
  const topics = arrayValue(notifications.topics, 'notifications.topics').map((entry, index) => {
    const topic = stringValue(entry, `notifications.topics[${String(index)}]`);
    if (
      topic !== 'world' &&
      topic !== 'presence' &&
      topic !== 'movement' &&
      topic !== 'balls' &&
      topic !== 'characters' &&
      topic !== 'chat' &&
      topic !== 'live' &&
      topic !== 'guestbook' &&
      topic !== 'admin' &&
      topic !== 'village-lighting' &&
      topic !== 'vehicle-lighting' &&
      topic !== 'creative-mode' &&
      topic !== 'cinema' &&
      topic !== 'yeet' &&
      topic !== 'announcement' &&
      topic !== 'rules' &&
      topic !== 'daily-challenges' &&
      topic !== 'catalog' &&
      topic !== 'race' &&
      topic !== 'football-goal' &&
      topic !== 'credits'
    ) {
      throw new ContractError(`notifications.topics[${String(index)}] is unsupported`);
    }
    return topic;
  });
  if (new Set(topics).size !== topics.length) {
    throw new ContractError('notifications.topics contains duplicates');
  }
  const worldUpdate =
    notifications.worldUpdate === undefined
      ? undefined
      : parseWorldNotificationUpdate(notifications.worldUpdate);
  const productionDelta =
    notifications.productionDelta === undefined
      ? undefined
      : parseBuildingProductionDeltaPayload(
          notifications.productionDelta,
          'notifications.productionDelta',
        );
  if (worldUpdate !== undefined && !topics.includes('world')) {
    throw new ContractError('notifications.worldUpdate requires the world topic');
  }
  if (productionDelta !== undefined && worldUpdate === undefined) {
    throw new ContractError('notifications.productionDelta requires a world update');
  }
  const reset = booleanValue(notifications.reset, 'notifications.reset');
  const catalogRevision =
    notifications.catalogRevision === undefined
      ? undefined
      : positiveIntegerValue(notifications.catalogRevision, 'notifications.catalogRevision');
  if (catalogRevision !== undefined && !topics.includes('catalog')) {
    throw new ContractError('notifications.catalogRevision requires the catalog topic');
  }
  if (reset && worldUpdate !== undefined) {
    throw new ContractError('notifications.worldUpdate cannot accompany a stream reset');
  }
  const streamGeneration = positiveIntegerValue(
    notifications.streamGeneration,
    'notifications.streamGeneration',
  );
  const villageId = positiveIntegerValue(notifications.villageId, 'notifications.villageId');
  const creditsUpdate =
    notifications.creditsUpdate === undefined
      ? undefined
      : nonNegativeIntegerValue(notifications.creditsUpdate, 'notifications.creditsUpdate');
  if (creditsUpdate !== undefined && (!topics.includes('credits') || reset)) {
    throw new ContractError(
      'notifications.creditsUpdate requires the credits topic without a stream reset',
    );
  }
  const presenceUpdate =
    notifications.presenceUpdate === undefined
      ? undefined
      : parsePresencePayload(notifications.presenceUpdate, 'notifications.presenceUpdate');
  const presenceDelta =
    notifications.presenceDelta === undefined
      ? undefined
      : parsePresenceDeltaPayload(notifications.presenceDelta, 'notifications.presenceDelta');
  if (presenceUpdate !== undefined && presenceDelta !== undefined) {
    throw new ContractError('notifications cannot contain both presenceUpdate and presenceDelta');
  }
  const movementUpdate =
    notifications.movementUpdate === undefined
      ? undefined
      : parseMovementUpdate(notifications.movementUpdate);
  const ballUpdate =
    notifications.ballUpdate === undefined
      ? undefined
      : parseBallUpdate(notifications.ballUpdate, 'notifications.ballUpdate');
  const footballGoalEvents =
    notifications.footballGoalEvents === undefined
      ? undefined
      : arrayValue(notifications.footballGoalEvents, 'notifications.footballGoalEvents').map(
          (entry, index): FootballGoalEventPayload => {
            const label = `notifications.footballGoalEvents[${String(index)}]`;
            const event = objectValue(entry, label);
            return {
              eventId: positiveIntegerValue(event.eventId, `${label}.eventId`),
              villageId: positiveIntegerValue(event.villageId, `${label}.villageId`),
              goalBuildingId: nonZeroSignedShortValue(
                event.goalBuildingId,
                `${label}.goalBuildingId`,
              ),
              ballBuildingId: nonZeroSignedShortValue(
                event.ballBuildingId,
                `${label}.ballBuildingId`,
              ),
              playerId: positiveIntegerValue(event.playerId, `${label}.playerId`),
              epoch: positiveIntegerValue(event.epoch, `${label}.epoch`),
              sequence: positiveIntegerValue(event.sequence, `${label}.sequence`),
              occurredAt: nonNegativeIntegerValue(event.occurredAt, `${label}.occurredAt`),
            };
          },
        );
  const characterUpdate =
    notifications.characterUpdate === undefined
      ? undefined
      : parseCharactersPayload(notifications.characterUpdate, 'notifications.characterUpdate');
  const chatUpdate =
    notifications.chatUpdate === undefined
      ? undefined
      : parseVillageChatPayload(notifications.chatUpdate, 'notifications.chatUpdate');
  const liveUpdate =
    notifications.liveUpdate === undefined
      ? undefined
      : parseLiveMessagesPayload(notifications.liveUpdate, 'notifications.liveUpdate');
  const guestbookAvailabilityUpdate =
    notifications.guestbookAvailabilityUpdate === undefined
      ? undefined
      : booleanValue(
          notifications.guestbookAvailabilityUpdate,
          'notifications.guestbookAvailabilityUpdate',
        );
  const adminMessages =
    notifications.adminMessages === undefined
      ? undefined
      : arrayValue(notifications.adminMessages, 'notifications.adminMessages').map(
          (entry, index): AdminNotificationPayload => {
            const label = `notifications.adminMessages[${String(index)}]`;
            const message = objectValue(entry, label);
            const kind = stringValue(message.kind, `${label}.kind`);
            if (
              kind !== 'popup' &&
              kind !== 'village-speech-bubble' &&
              kind !== 'vehicle-message'
            ) {
              throw new ContractError(`${label}.kind is unsupported`);
            }
            return {
              id: positiveIntegerValue(message.id, `${label}.id`),
              kind,
              messageText: stringValue(message.messageText, `${label}.messageText`),
              createdAt: nonNegativeIntegerValue(message.createdAt, `${label}.createdAt`),
              ...(message.senderName === undefined
                ? {}
                : { senderName: stringValue(message.senderName, `${label}.senderName`) }),
              ...(message.villageId === undefined
                ? {}
                : { villageId: positiveIntegerValue(message.villageId, `${label}.villageId`) }),
              ...(message.tileX === undefined
                ? {}
                : { tileX: integerValue(message.tileX, `${label}.tileX`) }),
              ...(message.tileY === undefined
                ? {}
                : { tileY: integerValue(message.tileY, `${label}.tileY`) }),
              ...(message.mountType === undefined
                ? {}
                : { mountType: nonNegativeIntegerValue(message.mountType, `${label}.mountType`) }),
            };
          },
        );
  const rulesText =
    notifications.rulesText === undefined
      ? undefined
      : communityRulesText(notifications.rulesText, 'notifications.rulesText');
  const creativeModeEnabled =
    notifications.creativeModeEnabled === undefined
      ? undefined
      : booleanValue(notifications.creativeModeEnabled, 'notifications.creativeModeEnabled');
  if (creativeModeEnabled !== undefined && !topics.includes('creative-mode')) {
    throw new ContractError('notifications.creativeModeEnabled requires the creative-mode topic');
  }
  const yeetEnabled =
    notifications.yeetEnabled === undefined
      ? undefined
      : booleanValue(notifications.yeetEnabled, 'notifications.yeetEnabled');
  if (yeetEnabled !== undefined && !topics.includes('yeet')) {
    throw new ContractError('notifications.yeetEnabled requires the yeet topic');
  }
  const cinemaEnabled =
    notifications.cinemaEnabled === undefined
      ? undefined
      : booleanValue(notifications.cinemaEnabled, 'notifications.cinemaEnabled');
  if (cinemaEnabled !== undefined && !topics.includes('cinema')) {
    throw new ContractError('notifications.cinemaEnabled requires the cinema topic');
  }
  const announcement =
    notifications.announcement === undefined
      ? undefined
      : announcementText(notifications.announcement, 'notifications.announcement');
  const raceHeatUpdate =
    notifications.raceHeatUpdate === undefined
      ? undefined
      : (parseRaceHeatResponse({
          protocolVersion: root.protocolVersion,
          raceHeat: notifications.raceHeatUpdate,
        }) ?? undefined);
  const raceQueueUpdate = (() => {
    if (notifications.raceQueueUpdate === undefined) return undefined;
    const update = objectValue(notifications.raceQueueUpdate, 'notifications.raceQueueUpdate');
    const status = stringValue(update.status, 'notifications.raceQueueUpdate.status');
    if (!['queued', 'position-updated', 'promoted', 'cancelled'].includes(status)) {
      throw new ContractError('notifications.raceQueueUpdate.status is unsupported');
    }
    const typedStatus = status as RaceQueueUpdatePayload['status'];
    const raceQueue =
      update.raceQueue === null
        ? null
        : parseRaceQueueValue(update.raceQueue, 'notifications.raceQueueUpdate.raceQueue');
    const raceHeat =
      update.raceHeat === undefined
        ? undefined
        : (parseRaceHeatResponse({
            protocolVersion: root.protocolVersion,
            raceHeat: update.raceHeat,
          }) ?? undefined);
    if (
      ((typedStatus === 'queued' || typedStatus === 'position-updated') && raceQueue === null) ||
      ((typedStatus === 'cancelled' || typedStatus === 'promoted') && raceQueue !== null) ||
      (typedStatus === 'promoted' && raceHeat === undefined) ||
      (typedStatus !== 'promoted' && raceHeat !== undefined)
    ) {
      throw new ContractError('notifications.raceQueueUpdate payload is inconsistent');
    }
    const queueId = stringValue(update.queueId, 'notifications.raceQueueUpdate.queueId');
    const updateVillageId = positiveIntegerValue(
      update.villageId,
      'notifications.raceQueueUpdate.villageId',
    );
    if (
      raceQueue !== null &&
      (raceQueue.id !== queueId || raceQueue.villageId !== updateVillageId)
    ) {
      throw new ContractError('notifications.raceQueueUpdate queue is contradictory');
    }
    if (raceHeat !== undefined && raceHeat.villageId !== updateVillageId) {
      throw new ContractError('notifications.raceQueueUpdate heat is contradictory');
    }
    return {
      status: typedStatus,
      queueId,
      villageId: updateVillageId,
      raceQueue,
      ...(raceHeat === undefined ? {} : { raceHeat }),
    };
  })();
  if (adminMessages !== undefined && !topics.includes('admin')) {
    throw new ContractError('notifications.adminMessages requires the admin topic');
  }
  if (rulesText !== undefined && !topics.includes('rules')) {
    throw new ContractError('notifications.rulesText requires the rules topic');
  }
  if (announcement !== undefined && !topics.includes('announcement')) {
    throw new ContractError('notifications.announcement requires the announcement topic');
  }
  if (raceHeatUpdate !== undefined && (!topics.includes('race') || reset)) {
    throw new ContractError('notifications.raceHeatUpdate requires the race topic without reset');
  }
  if (raceHeatUpdate !== undefined && raceHeatUpdate.villageId !== villageId) {
    throw new ContractError('notifications.raceHeatUpdate has a contradictory village');
  }
  if (raceQueueUpdate !== undefined && (!topics.includes('race') || reset)) {
    throw new ContractError('notifications.raceQueueUpdate requires the race topic without reset');
  }
  if (raceQueueUpdate !== undefined && raceQueueUpdate.villageId !== villageId) {
    throw new ContractError('notifications.raceQueueUpdate has a contradictory village');
  }
  if (
    footballGoalEvents !== undefined &&
    (!topics.includes('football-goal') ||
      reset ||
      footballGoalEvents.length === 0 ||
      footballGoalEvents.length > 16 ||
      footballGoalEvents.some((event) => event.villageId !== villageId) ||
      new Set(footballGoalEvents.map(({ eventId }) => eventId)).size !== footballGoalEvents.length)
  ) {
    throw new ContractError('notifications.footballGoalEvents is inconsistent');
  }
  const semanticUpdates = [
    ['presence', presenceUpdate],
    ['presence', presenceDelta],
    ['movement', movementUpdate],
    ['balls', ballUpdate],
    ['characters', characterUpdate],
    ['chat', chatUpdate],
  ] as const;
  for (const [topic, update] of semanticUpdates) {
    if (update === undefined) continue;
    if (!topics.includes(topic)) {
      throw new ContractError(`notifications.${topic} update requires its topic`);
    }
    if (reset) {
      throw new ContractError(`notifications.${topic} update cannot accompany a stream reset`);
    }
    if (update.streamGeneration !== streamGeneration) {
      throw new ContractError(
        `notifications.${topic} update has a contradictory stream generation`,
      );
    }
    if (update.villageId !== villageId) {
      throw new ContractError(`notifications.${topic} update has a contradictory village`);
    }
  }
  if (liveUpdate !== undefined) {
    if (!topics.includes('live')) {
      throw new ContractError('notifications.liveUpdate requires the live topic');
    }
    if (reset) {
      throw new ContractError('notifications.liveUpdate cannot accompany a stream reset');
    }
    if (liveUpdate.streamGeneration !== streamGeneration) {
      throw new ContractError('notifications.liveUpdate has a contradictory stream generation');
    }
  }
  if (guestbookAvailabilityUpdate !== undefined) {
    if (!topics.includes('guestbook')) {
      throw new ContractError(
        'notifications.guestbookAvailabilityUpdate requires the guestbook topic',
      );
    }
    if (reset) {
      throw new ContractError(
        'notifications.guestbookAvailabilityUpdate cannot accompany a stream reset',
      );
    }
  }
  if (worldUpdate !== undefined && worldUpdate.villageId !== villageId) {
    throw new ContractError('notifications.worldUpdate has a contradictory village');
  }
  if (productionDelta !== undefined && productionDelta.villageId !== villageId) {
    throw new ContractError('notifications.productionDelta has a contradictory village');
  }
  return {
    villageId,
    streamGeneration,
    cursor: nonNegativeIntegerValue(notifications.cursor, 'notifications.cursor'),
    reset,
    timedOut: booleanValue(notifications.timedOut, 'notifications.timedOut'),
    topics,
    ...(creditsUpdate === undefined ? {} : { creditsUpdate }),
    ...(worldUpdate === undefined ? {} : { worldUpdate }),
    ...(productionDelta === undefined ? {} : { productionDelta }),
    ...(presenceUpdate === undefined ? {} : { presenceUpdate }),
    ...(presenceDelta === undefined ? {} : { presenceDelta }),
    ...(movementUpdate === undefined ? {} : { movementUpdate }),
    ...(ballUpdate === undefined ? {} : { ballUpdate }),
    ...(footballGoalEvents === undefined ? {} : { footballGoalEvents }),
    ...(characterUpdate === undefined ? {} : { characterUpdate }),
    ...(chatUpdate === undefined ? {} : { chatUpdate }),
    ...(liveUpdate === undefined ? {} : { liveUpdate }),
    ...(guestbookAvailabilityUpdate === undefined ? {} : { guestbookAvailabilityUpdate }),
    ...(adminMessages === undefined ? {} : { adminMessages }),
    ...(creativeModeEnabled === undefined ? {} : { creativeModeEnabled }),
    ...(notifications.adminTruckUnderglow === undefined
      ? {}
      : { adminTruckUnderglow: parseAdminTruckUnderglow(notifications.adminTruckUnderglow) }),
    ...(cinemaEnabled === undefined ? {} : { cinemaEnabled }),
    ...(yeetEnabled === undefined ? {} : { yeetEnabled }),
    ...(notifications.enhancedLightingForced === undefined
      ? {}
      : {
          enhancedLightingForced: booleanValue(
            notifications.enhancedLightingForced,
            'notifications.enhancedLightingForced',
          ),
        }),
    ...(notifications.communityBuildAllowed === undefined
      ? {}
      : {
          communityBuildAllowed: booleanValue(
            notifications.communityBuildAllowed,
            'notifications.communityBuildAllowed',
          ),
        }),
    ...(announcement === undefined ? {} : { announcement }),
    ...(rulesText === undefined ? {} : { rulesText }),
    ...(catalogRevision === undefined ? {} : { catalogRevision }),
    ...(raceHeatUpdate === undefined ? {} : { raceHeatUpdate }),
    ...(raceQueueUpdate === undefined ? {} : { raceQueueUpdate }),
  };
}

export function parseWorldNotificationUpdate(value: unknown): WorldNotificationUpdatePayload {
  const update = objectValue(value, 'notifications.worldUpdate');
  const villageId = positiveIntegerValue(update.villageId, 'notifications.worldUpdate.villageId');
  const chunkWidth = isCreativeVillage(villageId) ? (CREATIVE_WORLD.sampleWidth - 1) / 16 : 8;
  const dirtyChunks = arrayValue(update.dirtyChunks, 'notifications.worldUpdate.dirtyChunks').map(
    (entry, index) => {
      const label = `notifications.worldUpdate.dirtyChunks[${String(index)}]`;
      const chunk = objectValue(entry, label);
      const chunkX = nonNegativeIntegerValue(chunk.chunkX, `${label}.chunkX`);
      const chunkZ = nonNegativeIntegerValue(chunk.chunkZ, `${label}.chunkZ`);
      if (chunkX >= chunkWidth || chunkZ >= chunkWidth)
        throw new ContractError(`${label} is outside the world`);
      return { chunkX, chunkZ };
    },
  );
  if (dirtyChunks.length > chunkWidth * chunkWidth) {
    throw new ContractError('notifications.worldUpdate.dirtyChunks exceeds the world');
  }
  if (
    new Set(dirtyChunks.map(({ chunkX, chunkZ }) => `${String(chunkX)}:${String(chunkZ)}`)).size !==
    dirtyChunks.length
  ) {
    throw new ContractError('notifications.worldUpdate.dirtyChunks contains duplicates');
  }
  const fullRefreshRequired = booleanValue(
    update.fullRefreshRequired,
    'notifications.worldUpdate.fullRefreshRequired',
  );
  if (fullRefreshRequired && dirtyChunks.length > 0) {
    throw new ContractError('notifications.worldUpdate full refresh cannot include dirty chunks');
  }
  const resourceEffects =
    update.resourceEffects === undefined
      ? undefined
      : arrayValue(update.resourceEffects, 'notifications.worldUpdate.resourceEffects').map(
          (entry, index): WorldResourceEffectPayload => {
            const label = `notifications.worldUpdate.resourceEffects[${String(index)}]`;
            const effect = objectValue(entry, label);
            const buildingId =
              effect.buildingId === undefined
                ? undefined
                : signedShortValue(effect.buildingId, `${label}.buildingId`);
            if (buildingId === 0) {
              throw new ContractError(`${label}.buildingId must be non-zero`);
            }
            return {
              playerId: positiveIntegerValue(effect.playerId, `${label}.playerId`),
              ...(buildingId === undefined ? {} : { buildingId }),
              ...productionResource(effect, label),
            };
          },
        );
  if (resourceEffects !== undefined && resourceEffects.length > 512) {
    throw new ContractError('notifications.worldUpdate.resourceEffects exceeds the journal');
  }
  const treasureEffects =
    update.treasureEffects === undefined
      ? undefined
      : arrayValue(update.treasureEffects, 'notifications.worldUpdate.treasureEffects').map(
          (entry, index): WorldTreasureEffectPayload => {
            const label = `notifications.worldUpdate.treasureEffects[${String(index)}]`;
            const effect = objectValue(entry, label);
            const rewardKind = stringValue(effect.rewardKind, `${label}.rewardKind`);
            if (
              rewardKind !== 'coins' &&
              rewardKind !== 'sunshine' &&
              rewardKind !== 'rain' &&
              rewardKind !== 'lightning' &&
              rewardKind !== 'building'
            )
              throw new ContractError(`Unsupported treasure reward kind: ${rewardKind}`);
            return {
              playerId: positiveIntegerValue(effect.playerId, `${label}.playerId`),
              rewardKind,
              amount: positiveIntegerValue(effect.amount, `${label}.amount`),
              ...(rewardKind === 'building'
                ? { rewardBuildingAsset: buildingEditButtonAsset(effect.rewardBuildingAsset) }
                : {}),
            };
          },
        );
  if (treasureEffects !== undefined && treasureEffects.length > 512) {
    throw new ContractError('notifications.worldUpdate.treasureEffects exceeds the journal');
  }
  return {
    villageId: positiveIntegerValue(update.villageId, 'notifications.worldUpdate.villageId'),
    revision: positiveIntegerValue(update.revision, 'notifications.worldUpdate.revision'),
    fullRefreshRequired,
    snapshotBuildingCount: nonNegativeIntegerValue(
      update.snapshotBuildingCount,
      'notifications.worldUpdate.snapshotBuildingCount',
    ),
    snapshotTreeCount: nonNegativeIntegerValue(
      update.snapshotTreeCount,
      'notifications.worldUpdate.snapshotTreeCount',
    ),
    ...(update.occupancyChanged === undefined
      ? {}
      : {
          occupancyChanged: booleanValue(
            update.occupancyChanged,
            'notifications.worldUpdate.occupancyChanged',
          ),
        }),
    ...(update.collisionChanged === undefined
      ? {}
      : {
          collisionChanged: booleanValue(
            update.collisionChanged,
            'notifications.worldUpdate.collisionChanged',
          ),
        }),
    ...(resourceEffects === undefined ? {} : { resourceEffects }),
    ...(treasureEffects === undefined ? {} : { treasureEffects }),
    ...(update.terrainChanges === undefined
      ? {}
      : { terrainChanges: parseTerrainChanges(update.terrainChanges) }),
    ...(update.dirtyChunksComplete === undefined
      ? {}
      : {
          dirtyChunksComplete: booleanValue(
            update.dirtyChunksComplete,
            'notifications.worldUpdate.dirtyChunksComplete',
          ),
        }),
    dirtyChunks,
  };
}

export function parseChatSubmissionResponse(value: unknown): ChatSubmissionPayload {
  const root = objectValue(value, 'chat submission response');
  assertProtocol(root);
  const submission = objectValue(root.chatSubmission, 'chatSubmission');
  const status = stringValue(submission.status, 'chatSubmission.status');
  if (status !== 'submitted')
    throw new ContractError(`Unsupported chat submission status: ${status}`);
  return {
    status,
    ...(submission.godMode === undefined
      ? {}
      : { godMode: booleanValue(submission.godMode, 'chatSubmission.godMode') }),
    ...(submission.clientCommand === undefined
      ? {}
      : {
          clientCommand: clientCommandValue(
            submission.clientCommand,
            'chatSubmission.clientCommand',
          ),
        }),
  };
}

export function parseVIPWebsiteResponse(value: unknown): VIPWebsitePayload {
  const root = objectValue(value, 'VIP website response');
  assertProtocol(root);
  const website = objectValue(root.vipWebsite, 'vipWebsite');
  const document = objectValue(website.document, 'vipWebsite.document');
  return {
    villageId: positiveIntegerValue(website.villageId, 'vipWebsite.villageId'),
    port: positiveIntegerValue(website.port, 'vipWebsite.port'),
    url: stringValue(website.url, 'vipWebsite.url'),
    editable: booleanValue(website.editable, 'vipWebsite.editable'),
    aiReady: booleanValue(website.aiReady, 'vipWebsite.aiReady'),
    document: {
      title: stringValue(document.title, 'vipWebsite.document.title'),
      tagline: stringValue(document.tagline, 'vipWebsite.document.tagline'),
      accent: stringValue(document.accent, 'vipWebsite.document.accent'),
      background: stringValue(document.background, 'vipWebsite.document.background'),
      cta: stringValue(document.cta, 'vipWebsite.document.cta'),
      revision: positiveIntegerValue(document.revision, 'vipWebsite.document.revision'),
      updatedAt: integerValue(document.updatedAt, 'vipWebsite.document.updatedAt'),
      sections: arrayValue(document.sections, 'vipWebsite.document.sections').map(
        (entry, index) => {
          const section = objectValue(entry, `vipWebsite.document.sections[${String(index)}]`);
          return {
            heading: stringValue(section.heading, 'VIP website section heading'),
            body: stringValue(section.body, 'VIP website section body'),
          };
        },
      ),
    },
  };
}

export function parseVIPWebsiteAgentResponse(value: unknown): VIPWebsiteAgentResult {
  const root = objectValue(value, 'VIP website agent response');
  return {
    website: parseVIPWebsiteResponse(value),
    message: stringValue(root.message, 'VIP website agent response message'),
  };
}

function clientCommandValue(value: unknown, label: string): 'freecam' | 'gpuwarn' {
  const command = stringValue(value, label);
  if (command !== 'freecam' && command !== 'gpuwarn')
    throw new ContractError(`Unsupported client command: ${command}`);
  return command;
}

export function parseLiveMessageSubmissionResponse(value: unknown): LiveMessageSubmissionPayload {
  const root = objectValue(value, 'Live message submission response');
  assertProtocol(root);
  const submission = objectValue(root.liveSubmission, 'liveSubmission');
  const outcome = stringValue(submission.outcome, 'liveSubmission.outcome');
  if (!isLiveMessageOutcome(outcome)) {
    throw new ContractError(`Unsupported Live message outcome: ${outcome}`);
  }
  return {
    sequence: positiveIntegerValue(submission.sequence, 'liveSubmission.sequence'),
    outcome,
    replayed: booleanValue(submission.replayed, 'liveSubmission.replayed'),
    creditsAfter: integerValue(submission.creditsAfter, 'liveSubmission.creditsAfter'),
    serverTime: nonNegativeIntegerValue(submission.serverTime, 'liveSubmission.serverTime'),
    live: parseLiveMessagesPayload(submission.live, 'liveSubmission.live'),
  };
}

function isLiveMessageOutcome(value: string): value is LiveMessageOutcome {
  return (
    value === 'applied' ||
    value === 'level-required' ||
    value === 'insufficient-credits' ||
    value === 'invalid-request' ||
    value === 'idempotency-conflict' ||
    value === 'server-error'
  );
}

function parseAvatarJuggling(value: unknown, label: string): AvatarJugglingPayload | null {
  if (value === undefined || value === null) return null;
  const juggling = objectValue(value, label);
  const siteswap = stringValue(juggling.siteswap, `${label}.siteswap`);
  let parsed;
  try {
    parsed = parseSiteswap(siteswap);
  } catch {
    throw new ContractError(`${label}.siteswap is invalid`);
  }
  if (parsed.propCount > 64) throw new ContractError(`${label}.siteswap exceeds 64 props`);
  const prop = stringValue(juggling.prop, `${label}.prop`);
  if (prop !== 'b' && prop !== 'c' && prop !== 'r') {
    throw new ContractError(`${label}.prop is invalid`);
  }
  return {
    sequence: positiveIntegerValue(juggling.sequence, `${label}.sequence`),
    siteswap: parsed.canonical,
    prop,
    elapsedMilliseconds: nonNegativeIntegerValue(
      juggling.elapsedMilliseconds,
      `${label}.elapsedMilliseconds`,
    ),
  };
}

function parsePresenceDeltaPayload(value: unknown, label: string): VillagePresenceDeltaPayload {
  const presence = objectValue(value, label);
  const parsed = parsePresencePayload(value, label, true);
  const removedPlayerIds = arrayValue(presence.removedPlayerIds, `${label}.removedPlayerIds`).map(
    (entry, index) => positiveIntegerValue(entry, `${label}.removedPlayerIds[${String(index)}]`),
  );
  if (removedPlayerIds.length > 100 || new Set(removedPlayerIds).size !== removedPlayerIds.length) {
    throw new ContractError(`${label}.removedPlayerIds exceeds village capacity or has duplicates`);
  }
  const upsertedPlayerIds = new Set(parsed.avatars.map((avatar) => avatar.playerId));
  if (removedPlayerIds.some((playerId) => upsertedPlayerIds.has(playerId))) {
    throw new ContractError(`${label} cannot upsert and remove the same player`);
  }
  const localPlayerWasKicked = (parsed.visitorKickEvents ?? []).some(
    (event) => event.targetPlayerId === parsed.localPlayerId,
  );
  if (removedPlayerIds.includes(parsed.localPlayerId) && !localPlayerWasKicked) {
    throw new ContractError(`${label} cannot remove the local player`);
  }
  return { ...parsed, removedPlayerIds };
}

function parsePresencePayload(
  value: unknown,
  label: string,
  partialAvatars = false,
): VillagePresencePayload {
  const presence = objectValue(value, label);
  const positionValue = presence.villageId === 2147483640 ? worldPositionValue : signedShortValue;
  const avatars = arrayValue(presence.avatars, `${label}.avatars`).map((entry, index) => {
    const avatarLabel = `${label}.avatars[${String(index)}]`;
    const avatar = objectValue(entry, avatarLabel);
    const positionKnown = booleanValue(avatar.positionKnown, `${avatarLabel}.positionKnown`);
    const sourceText = stringValue(avatar.positionSource, `${avatarLabel}.positionSource`);
    if (!isAvatarPositionSource(sourceText)) {
      throw new ContractError(`Unsupported avatar position source: ${sourceText}`);
    }
    const pendingMovementSequence = nonNegativeIntegerValue(
      avatar.pendingMovementSequence,
      `${avatarLabel}.pendingMovementSequence`,
    );
    const acceptedMovementSequence =
      avatar.acceptedMovementSequence === undefined
        ? 0
        : nonNegativeIntegerValue(
            avatar.acceptedMovementSequence,
            `${avatarLabel}.acceptedMovementSequence`,
          );
    if (positionKnown === (sourceText === 'unknown')) {
      throw new ContractError(`${avatarLabel} position source contradicts positionKnown`);
    }
    if ((sourceText === 'client-predicted') !== pendingMovementSequence > 0) {
      throw new ContractError(`${avatarLabel} pending sequence contradicts position source`);
    }
    const appearanceLayers = arrayValue(avatar.appearanceLayers, `${avatarLabel}.appearanceLayers`);
    if (appearanceLayers.length > 128) {
      throw new ContractError(`${avatarLabel}.appearanceLayers exceeds the legacy bound`);
    }
    const sit =
      avatar.sit === undefined || avatar.sit === null
        ? null
        : (() => {
            const value = objectValue(avatar.sit, `${avatarLabel}.sit`);
            return {
              buildingId: nonZeroSignedShortValue(
                value.buildingId,
                `${avatarLabel}.sit.buildingId`,
              ),
              action: avatarSitActionValue(value.action, `${avatarLabel}.sit.action`),
              ...(value.swingIntensity === undefined
                ? {}
                : {
                    swingIntensity: rangedIntegerValue(
                      value.swingIntensity,
                      `${avatarLabel}.sit.swingIntensity`,
                      0,
                      100,
                    ),
                  }),
              ...(value.swingAngleMilli === undefined
                ? {}
                : {
                    swingAngleMilli: rangedIntegerValue(
                      value.swingAngleMilli,
                      `${avatarLabel}.sit.swingAngleMilli`,
                      -2_000_000_000,
                      2_000_000_000,
                    ),
                  }),
              ...(value.swingVelocityMilli === undefined
                ? {}
                : {
                    swingVelocityMilli: rangedIntegerValue(
                      value.swingVelocityMilli,
                      `${avatarLabel}.sit.swingVelocityMilli`,
                      -6_500,
                      6_500,
                    ),
                  }),
            };
          })();
    const away =
      avatar.away === undefined ? false : booleanValue(avatar.away, `${avatarLabel}.away`);
    const friend =
      avatar.friend === undefined ? false : booleanValue(avatar.friend, `${avatarLabel}.friend`);
    const emote =
      avatar.emote === undefined || avatar.emote === null
        ? null
        : avatarEmoteActionValue(avatar.emote, `${avatarLabel}.emote`);
    const juggling = parseAvatarJuggling(avatar.juggling, `${avatarLabel}.juggling`);
    if (
      (sit?.action === 'sit-afk' && !away) ||
      (away && sit !== null && sit.action !== 'sit-afk') ||
      (emote !== null && (away || sit !== null)) ||
      (juggling !== null && (away || sit !== null || emote !== null))
    ) {
      throw new ContractError(`${avatarLabel} action states contradict one another`);
    }
    return {
      playerId: positiveIntegerValue(avatar.playerId, `${avatarLabel}.playerId`),
      name: stringValue(avatar.name, `${avatarLabel}.name`),
      friend,
      ...(avatar.speedBoostMode === undefined
        ? {}
        : { speedBoostMode: booleanValue(avatar.speedBoostMode, `${avatarLabel}.speedBoostMode`) }),
      ...(avatar.ghostMode === undefined
        ? {}
        : { ghostMode: booleanValue(avatar.ghostMode, `${avatarLabel}.ghostMode`) }),
      policeLightsActive:
        avatar.policeLightsActive === undefined
          ? false
          : booleanValue(avatar.policeLightsActive, `${avatarLabel}.policeLightsActive`),
      mountType: signedShortValue(avatar.mountType, `${avatarLabel}.mountType`),
      ...parseOptionalCustomVehicle(avatar.customVehicle, `${avatarLabel}.customVehicle`),
      ...(avatar.underglow === undefined
        ? {}
        : { underglow: parseAdminTruckUnderglow(avatar.underglow, `${avatarLabel}.underglow`) }),
      mountCharacterId:
        avatar.mountCharacterId === undefined
          ? 0
          : signedShortValue(avatar.mountCharacterId, `${avatarLabel}.mountCharacterId`),
      petType: signedShortValue(avatar.petType, `${avatarLabel}.petType`),
      petNetworkId:
        avatar.petNetworkId === undefined
          ? 0
          : signedShortValue(avatar.petNetworkId, `${avatarLabel}.petNetworkId`),
      ...(avatar.noboType === undefined
        ? {}
        : { noboType: avatarNoboTypeValue(avatar.noboType, `${avatarLabel}.noboType`) }),
      avatarData: stringValue(avatar.avatarData, `${avatarLabel}.avatarData`),
      gender: avatarGenderValue(avatar.gender, `${avatarLabel}.gender`),
      appearanceLayers: appearanceLayers.map((entry, layerIndex) => {
        const layerLabel = `${avatarLabel}.appearanceLayers[${String(layerIndex)}]`;
        const layer = objectValue(entry, layerLabel);
        return {
          garmentId: positiveIntegerValue(layer.garmentId, `${layerLabel}.garmentId`),
          hue: appearanceAdjustment(layer.hue, `${layerLabel}.hue`),
          saturation: appearanceAdjustment(layer.saturation, `${layerLabel}.saturation`),
          brightness: appearanceAdjustment(layer.brightness, `${layerLabel}.brightness`),
          opacity: appearanceAdjustment(layer.opacity, `${layerLabel}.opacity`),
        };
      }),
      mountName: stringValue(avatar.mountName, `${avatarLabel}.mountName`),
      petName: stringValue(avatar.petName, `${avatarLabel}.petName`),
      petLevel: rangedIntegerValue(avatar.petLevel, `${avatarLabel}.petLevel`, 0, 7),
      petPointsWithinLevel: rangedIntegerValue(
        avatar.petPointsWithinLevel,
        `${avatarLabel}.petPointsWithinLevel`,
        0,
        32_767,
      ),
      petCoolIdleUnlockedUntil: nonNegativeIntegerValue(
        avatar.petCoolIdleUnlockedUntil,
        `${avatarLabel}.petCoolIdleUnlockedUntil`,
      ),
      level: nonNegativeIntegerValue(avatar.level, `${avatarLabel}.level`),
      posX: positionValue(avatar.posX, `${avatarLabel}.posX`),
      posY: avatar.posY === undefined ? 0 : signedShortValue(avatar.posY, `${avatarLabel}.posY`),
      posZ: positionValue(avatar.posZ, `${avatarLabel}.posZ`),
      rotation: signedShortValue(avatar.rotation, `${avatarLabel}.rotation`),
      positionKnown,
      positionSource: sourceText,
      pendingMovementSequence,
      acceptedMovementSequence,
      away,
      emote,
      sit,
      juggling,
    };
  });
  for (const avatar of avatars) {
    if (avatar.emote !== null && avatar.mountType > 0) {
      throw new ContractError(
        `${label}.avatars contains a mounted avatar with an unmounted-only emote`,
      );
    }
    if (avatar.juggling !== null && avatar.mountType > 0) {
      throw new ContractError(`${label}.avatars contains a mounted avatar with juggling state`);
    }
  }
  const playerIds = new Set(avatars.map((avatar) => avatar.playerId));
  if (playerIds.size !== avatars.length)
    throw new ContractError(`${label}.avatars contains duplicates`);
  const eventEntries =
    presence.petEvents === undefined ? [] : arrayValue(presence.petEvents, `${label}.petEvents`);
  if (eventEntries.length > 64) throw new ContractError(`${label}.petEvents exceeds 64 entries`);
  let previousEventSequence = 0;
  const petEvents = eventEntries.map((entry, index) => {
    const eventLabel = `${label}.petEvents[${String(index)}]`;
    const event = objectValue(entry, eventLabel);
    const sequence = positiveIntegerValue(event.sequence, `${eventLabel}.sequence`);
    if (sequence <= previousEventSequence) {
      throw new ContractError(`${eventLabel}.sequence is not monotonic`);
    }
    previousEventSequence = sequence;
    const playerId = positiveIntegerValue(event.playerId, `${eventLabel}.playerId`);
    const eventAvatar = avatars.find((avatar) => avatar.playerId === playerId);
    if (
      (!partialAvatars && eventAvatar === undefined) ||
      (eventAvatar !== undefined && eventAvatar.petType <= 0)
    ) {
      throw new ContractError(`${eventLabel}.playerId has no equipped pet`);
    }
    const eventId = petEventIdValue(event.eventId, `${eventLabel}.eventId`);
    const animationState = stringValue(event.animationState, `${eventLabel}.animationState`);
    const pointsGained = rangedIntegerValue(
      event.pointsGained,
      `${eventLabel}.pointsGained`,
      0,
      10,
    );
    const valid =
      (eventId === 40 && /^TRICK[1-7]$/.test(animationState) && pointsGained === 0) ||
      (eventId === 41 && animationState === 'PET_ROYAL' && pointsGained >= 1) ||
      (eventId === 42 && animationState === 'PET' && pointsGained === 0);
    if (!valid) throw new ContractError(`${eventLabel} fields contradict its legacy event ID`);
    return {
      sequence,
      playerId,
      eventId,
      animationState: animationState as AvatarPetEventPayload['animationState'],
      pointsGained,
    };
  });
  const friendEventEntries =
    presence.friendEvents === undefined
      ? []
      : arrayValue(presence.friendEvents, `${label}.friendEvents`);
  if (friendEventEntries.length > 64) {
    throw new ContractError(`${label}.friendEvents exceeds 64 entries`);
  }
  let previousFriendEventSequence = 0;
  const localPlayerId = positiveIntegerValue(presence.localPlayerId, `${label}.localPlayerId`);
  const friendEvents = friendEventEntries.map((entry, index) => {
    const eventLabel = `${label}.friendEvents[${String(index)}]`;
    const event = objectValue(entry, eventLabel);
    const sequence = positiveIntegerValue(event.sequence, `${eventLabel}.sequence`);
    if (sequence <= previousFriendEventSequence) {
      throw new ContractError(`${eventLabel}.sequence is not monotonic`);
    }
    previousFriendEventSequence = sequence;
    const senderPlayerId = positiveIntegerValue(
      event.senderPlayerId,
      `${eventLabel}.senderPlayerId`,
    );
    if (!partialAvatars && !playerIds.has(senderPlayerId)) {
      throw new ContractError(`${eventLabel}.senderPlayerId is absent from the village`);
    }
    const targetPlayerId = positiveIntegerValue(
      event.targetPlayerId,
      `${eventLabel}.targetPlayerId`,
    );
    if (targetPlayerId !== localPlayerId) {
      throw new ContractError(`${eventLabel}.targetPlayerId is not the local player`);
    }
    if (senderPlayerId === targetPlayerId) {
      throw new ContractError(`${eventLabel} cannot target its sender`);
    }
    const eventId = rangedIntegerValue(event.eventId, `${eventLabel}.eventId`, 1, 3);
    return {
      sequence,
      senderPlayerId,
      targetPlayerId,
      eventId: eventId as AvatarFriendEventPayload['eventId'],
    };
  });
  const raceEventEntries =
    presence.raceEvents === undefined ? [] : arrayValue(presence.raceEvents, `${label}.raceEvents`);
  if (raceEventEntries.length > 64) {
    throw new ContractError(`${label}.raceEvents exceeds 64 entries`);
  }
  let previousRaceEventSequence = 0;
  const raceEvents = raceEventEntries.map((entry, index) => {
    const eventLabel = `${label}.raceEvents[${String(index)}]`;
    const event = objectValue(entry, eventLabel);
    const sequence = positiveIntegerValue(event.sequence, `${eventLabel}.sequence`);
    if (sequence <= previousRaceEventSequence) {
      throw new ContractError(`${eventLabel}.sequence is not monotonic`);
    }
    previousRaceEventSequence = sequence;
    const playerId = positiveIntegerValue(event.playerId, `${eventLabel}.playerId`);
    if (!partialAvatars && !playerIds.has(playerId)) {
      throw new ContractError(`${eventLabel}.playerId is absent from the village`);
    }
    const playerName = stringValue(event.playerName, `${eventLabel}.playerName`);
    if (playerName.length === 0) {
      throw new ContractError(`${eventLabel}.playerName is empty`);
    }
    return {
      sequence,
      playerId,
      playerName,
      elapsedMilliseconds: positiveIntegerValue(
        event.elapsedMilliseconds,
        `${eventLabel}.elapsedMilliseconds`,
      ),
    };
  });
  const weatherEventEntries =
    presence.weatherEvents === undefined
      ? []
      : arrayValue(presence.weatherEvents, `${label}.weatherEvents`);
  if (weatherEventEntries.length > 64) {
    throw new ContractError(`${label}.weatherEvents exceeds 64 entries`);
  }
  let previousWeatherEventSequence = 0;
  const weatherEvents = weatherEventEntries.map((entry, index) => {
    const eventLabel = `${label}.weatherEvents[${String(index)}]`;
    const event = objectValue(entry, eventLabel);
    const sequence = positiveIntegerValue(event.sequence, `${eventLabel}.sequence`);
    if (sequence <= previousWeatherEventSequence) {
      throw new ContractError(`${eventLabel}.sequence is not monotonic`);
    }
    previousWeatherEventSequence = sequence;
    const playerId = positiveIntegerValue(event.playerId, `${eventLabel}.playerId`);
    if (!partialAvatars && !playerIds.has(playerId)) {
      throw new ContractError(`${eventLabel}.playerId is absent from the village`);
    }
    const effectEntries = arrayValue(event.effects, `${eventLabel}.effects`);
    if (effectEntries.length > 4096) {
      throw new ContractError(`${eventLabel}.effects exceeds 4096 entries`);
    }
    const buildingIds = new Set<number>();
    const effects = effectEntries.map((entry, effectIndex) => {
      const effectLabel = `${eventLabel}.effects[${String(effectIndex)}]`;
      const effect = objectValue(entry, effectLabel);
      const buildingId = rangedIntegerValue(
        effect.buildingId,
        `${effectLabel}.buildingId`,
        -32768,
        32767,
      );
      if (buildingId === 0 || buildingIds.has(buildingId)) {
        throw new ContractError(`${effectLabel}.buildingId is zero or duplicated`);
      }
      buildingIds.add(buildingId);
      const visualDelayMilliseconds = nonNegativeIntegerValue(
        effect.visualDelayMilliseconds,
        `${effectLabel}.visualDelayMilliseconds`,
      );
      const applyDelayMilliseconds = nonNegativeIntegerValue(
        effect.applyDelayMilliseconds,
        `${effectLabel}.applyDelayMilliseconds`,
      );
      if (applyDelayMilliseconds < visualDelayMilliseconds) {
        throw new ContractError(`${effectLabel} applies before its visual starts`);
      }
      return { buildingId, visualDelayMilliseconds, applyDelayMilliseconds };
    });
    return {
      sequence,
      playerId,
      kind: weatherKindValue(event.kind, `${eventLabel}.kind`),
      elapsedMilliseconds: nonNegativeIntegerValue(
        event.elapsedMilliseconds,
        `${eventLabel}.elapsedMilliseconds`,
      ),
      effects,
    };
  });
  const doorbellEventEntries =
    presence.doorbellEvents === undefined
      ? []
      : arrayValue(presence.doorbellEvents, `${label}.doorbellEvents`);
  if (doorbellEventEntries.length > 64) {
    throw new ContractError(`${label}.doorbellEvents exceeds 64 entries`);
  }
  let previousDoorbellEventSequence = 0;
  const doorbellEvents = doorbellEventEntries.map((entry, index) => {
    const eventLabel = `${label}.doorbellEvents[${String(index)}]`;
    const event = objectValue(entry, eventLabel);
    const sequence = positiveIntegerValue(event.sequence, `${eventLabel}.sequence`);
    if (sequence <= previousDoorbellEventSequence) {
      throw new ContractError(`${eventLabel}.sequence is not monotonic`);
    }
    previousDoorbellEventSequence = sequence;
    const playerId = positiveIntegerValue(event.playerId, `${eventLabel}.playerId`);
    if (!partialAvatars && !playerIds.has(playerId)) {
      throw new ContractError(`${eventLabel}.playerId is absent from the village`);
    }
    return {
      sequence,
      playerId,
      buildingId: nonZeroSignedShortValue(event.buildingId, `${eventLabel}.buildingId`),
    };
  });
  const keyDoorEventEntries =
    presence.keyDoorEvents === undefined
      ? []
      : arrayValue(presence.keyDoorEvents, `${label}.keyDoorEvents`);
  if (keyDoorEventEntries.length > 64) {
    throw new ContractError(`${label}.keyDoorEvents exceeds 64 entries`);
  }
  let previousKeyDoorEventSequence = 0;
  const keyDoorEvents = keyDoorEventEntries.map((entry, index) => {
    const eventLabel = `${label}.keyDoorEvents[${String(index)}]`;
    const event = objectValue(entry, eventLabel);
    const sequence = positiveIntegerValue(event.sequence, `${eventLabel}.sequence`);
    if (sequence <= previousKeyDoorEventSequence) {
      throw new ContractError(`${eventLabel}.sequence is not monotonic`);
    }
    previousKeyDoorEventSequence = sequence;
    const playerId = positiveIntegerValue(event.playerId, `${eventLabel}.playerId`);
    if (!partialAvatars && !playerIds.has(playerId)) {
      throw new ContractError(`${eventLabel}.playerId is absent from the village`);
    }
    return {
      sequence,
      playerId,
      color: keyDoorColorValue(event.color, `${eventLabel}.color`),
    };
  });
  const selfUnlockDoorEventEntries =
    presence.selfUnlockDoorEvents === undefined
      ? []
      : arrayValue(presence.selfUnlockDoorEvents, `${label}.selfUnlockDoorEvents`);
  if (selfUnlockDoorEventEntries.length > 64) {
    throw new ContractError(`${label}.selfUnlockDoorEvents exceeds 64 entries`);
  }
  let previousSelfUnlockDoorEventSequence = 0;
  const selfUnlockDoorEvents = selfUnlockDoorEventEntries.map((entry, index) => {
    const eventLabel = `${label}.selfUnlockDoorEvents[${String(index)}]`;
    const event = objectValue(entry, eventLabel);
    const sequence = positiveIntegerValue(event.sequence, `${eventLabel}.sequence`);
    if (sequence <= previousSelfUnlockDoorEventSequence) {
      throw new ContractError(`${eventLabel}.sequence is not monotonic`);
    }
    previousSelfUnlockDoorEventSequence = sequence;
    const playerId = positiveIntegerValue(event.playerId, `${eventLabel}.playerId`);
    if (!partialAvatars && !playerIds.has(playerId)) {
      throw new ContractError(`${eventLabel}.playerId is absent from the village`);
    }
    return {
      sequence,
      playerId,
      buildingId: nonZeroSignedShortValue(event.buildingId, `${eventLabel}.buildingId`),
    };
  });
  const cannonEventEntries =
    presence.cannonEvents === undefined
      ? []
      : arrayValue(presence.cannonEvents, `${label}.cannonEvents`);
  if (cannonEventEntries.length > 64) {
    throw new ContractError(`${label}.cannonEvents exceeds 64 entries`);
  }
  let previousCannonEventSequence = 0;
  const cannonEvents = cannonEventEntries.map((entry, index) => {
    const eventLabel = `${label}.cannonEvents[${String(index)}]`;
    const event = objectValue(entry, eventLabel);
    const sequence = positiveIntegerValue(event.sequence, `${eventLabel}.sequence`);
    if (sequence <= previousCannonEventSequence) {
      throw new ContractError(`${eventLabel}.sequence is not monotonic`);
    }
    previousCannonEventSequence = sequence;
    const playerId = positiveIntegerValue(event.playerId, `${eventLabel}.playerId`);
    if (!partialAvatars && !playerIds.has(playerId)) {
      throw new ContractError(`${eventLabel}.playerId is absent from the village`);
    }
    return {
      sequence,
      playerId,
      ...(event.yeet === undefined ? {} : { yeet: booleanValue(event.yeet, `${eventLabel}.yeet`) }),
      ...(event.warning === undefined
        ? {}
        : { warning: booleanValue(event.warning, `${eventLabel}.warning`) }),
      buildingId:
        event.yeet === true
          ? integerValue(event.buildingId, `${eventLabel}.buildingId`)
          : nonZeroSignedShortValue(event.buildingId, `${eventLabel}.buildingId`),
      startX: positionValue(event.startX, `${eventLabel}.startX`),
      startZ: positionValue(event.startZ, `${eventLabel}.startZ`),
      cannonX: positionValue(event.cannonX, `${eventLabel}.cannonX`),
      cannonZ: positionValue(event.cannonZ, `${eventLabel}.cannonZ`),
      landingX: positionValue(event.landingX, `${eventLabel}.landingX`),
      landingZ: positionValue(event.landingZ, `${eventLabel}.landingZ`),
      rotation: signedShortValue(event.rotation, `${eventLabel}.rotation`),
      elapsedMilliseconds: nonNegativeIntegerValue(
        event.elapsedMilliseconds,
        `${eventLabel}.elapsedMilliseconds`,
      ),
    };
  });
  const visitorKickEventEntries =
    presence.visitorKickEvents === undefined
      ? []
      : arrayValue(presence.visitorKickEvents, `${label}.visitorKickEvents`);
  if (visitorKickEventEntries.length > 64) {
    throw new ContractError(`${label}.visitorKickEvents exceeds 64 entries`);
  }
  let previousVisitorKickEventSequence = 0;
  const visitorKickEvents = visitorKickEventEntries.map((entry, index) => {
    const eventLabel = `${label}.visitorKickEvents[${String(index)}]`;
    const event = objectValue(entry, eventLabel);
    const sequence = positiveIntegerValue(event.sequence, `${eventLabel}.sequence`);
    if (sequence <= previousVisitorKickEventSequence) {
      throw new ContractError(`${eventLabel}.sequence is not monotonic`);
    }
    previousVisitorKickEventSequence = sequence;
    const senderPlayerId = positiveIntegerValue(
      event.senderPlayerId,
      `${eventLabel}.senderPlayerId`,
    );
    if (!partialAvatars && !playerIds.has(senderPlayerId)) {
      throw new ContractError(`${eventLabel}.senderPlayerId is absent from the village`);
    }
    const targetPlayerId = positiveIntegerValue(
      event.targetPlayerId,
      `${eventLabel}.targetPlayerId`,
    );
    if (targetPlayerId !== localPlayerId || senderPlayerId === targetPlayerId) {
      throw new ContractError(`${eventLabel} is not a remote kick of the local player`);
    }
    return { sequence, senderPlayerId, targetPlayerId };
  });
  const gestureEventEntries =
    presence.gestureEvents === undefined
      ? []
      : arrayValue(presence.gestureEvents, `${label}.gestureEvents`);
  if (gestureEventEntries.length > 64) {
    throw new ContractError(`${label}.gestureEvents exceeds 64 entries`);
  }
  let previousGestureEventSequence = 0;
  const gestureEvents = gestureEventEntries.map((entry, index) => {
    const eventLabel = `${label}.gestureEvents[${String(index)}]`;
    const event = objectValue(entry, eventLabel);
    const sequence = positiveIntegerValue(event.sequence, `${eventLabel}.sequence`);
    if (sequence <= previousGestureEventSequence) {
      throw new ContractError(`${eventLabel}.sequence is not monotonic`);
    }
    previousGestureEventSequence = sequence;
    const senderPlayerId = positiveIntegerValue(
      event.senderPlayerId,
      `${eventLabel}.senderPlayerId`,
    );
    const targetPlayerId = positiveIntegerValue(
      event.targetPlayerId,
      `${eventLabel}.targetPlayerId`,
    );
    if (
      senderPlayerId === targetPlayerId ||
      (!partialAvatars && (!playerIds.has(senderPlayerId) || !playerIds.has(targetPlayerId)))
    ) {
      throw new ContractError(`${eventLabel} does not connect two active village players`);
    }
    return {
      sequence,
      senderPlayerId,
      targetPlayerId,
      action: avatarGestureActionValue(event.action, `${eventLabel}.action`),
    };
  });
  const sillyGooseStareEventEntries =
    presence.sillyGooseStareEvents === undefined
      ? []
      : arrayValue(presence.sillyGooseStareEvents, `${label}.sillyGooseStareEvents`);
  if (sillyGooseStareEventEntries.length > 64) {
    throw new ContractError(`${label}.sillyGooseStareEvents exceeds 64 entries`);
  }
  let previousSillyGooseStareSequence = 0;
  const sillyGooseStareEvents = sillyGooseStareEventEntries.map((entry, index) => {
    const eventLabel = `${label}.sillyGooseStareEvents[${String(index)}]`;
    const event = objectValue(entry, eventLabel);
    const sequence = positiveIntegerValue(event.sequence, `${eventLabel}.sequence`);
    if (sequence <= previousSillyGooseStareSequence) {
      throw new ContractError(`${eventLabel}.sequence is not monotonic`);
    }
    previousSillyGooseStareSequence = sequence;
    const viewerPlayerId = positiveIntegerValue(
      event.viewerPlayerId,
      `${eventLabel}.viewerPlayerId`,
    );
    const goosePlayerId = positiveIntegerValue(event.goosePlayerId, `${eventLabel}.goosePlayerId`);
    if (!partialAvatars && (!playerIds.has(viewerPlayerId) || !playerIds.has(goosePlayerId))) {
      throw new ContractError(`${eventLabel} references a player outside the village`);
    }
    return {
      sequence,
      viewerPlayerId,
      goosePlayerId,
      viewerPosX: positionValue(event.viewerPosX, `${eventLabel}.viewerPosX`),
      viewerPosZ: positionValue(event.viewerPosZ, `${eventLabel}.viewerPosZ`),
      startsInMilliseconds: nonNegativeIntegerValue(
        event.startsInMilliseconds,
        `${eventLabel}.startsInMilliseconds`,
      ),
      elapsedMilliseconds: nonNegativeIntegerValue(
        event.elapsedMilliseconds,
        `${eventLabel}.elapsedMilliseconds`,
      ),
      durationMilliseconds: rangedIntegerValue(
        event.durationMilliseconds,
        `${eventLabel}.durationMilliseconds`,
        100,
        5_000,
      ),
    };
  });
  const spawnPoints =
    presence.spawnPoints === undefined
      ? undefined
      : avatarSpawnPointsValue(presence.spawnPoints, `${label}.spawnPoints`);
  const relocationEntries =
    presence.avatarRelocationEvents === undefined
      ? []
      : arrayValue(presence.avatarRelocationEvents, `${label}.avatarRelocationEvents`);
  if (relocationEntries.length > 64)
    throw new ContractError(`${label}.avatarRelocationEvents exceeds 64 entries`);
  let previousRelocationSequence = 0;
  const avatarRelocationEvents = relocationEntries.map((entry, index) => {
    const eventLabel = `${label}.avatarRelocationEvents[${String(index)}]`;
    const event = objectValue(entry, eventLabel);
    const sequence = positiveIntegerValue(event.sequence, `${eventLabel}.sequence`);
    if (sequence <= previousRelocationSequence)
      throw new ContractError(`${eventLabel}.sequence is not monotonic`);
    previousRelocationSequence = sequence;
    return {
      sequence,
      playerId: positiveIntegerValue(event.playerId, `${eventLabel}.playerId`),
      posX: positionValue(event.posX, `${eventLabel}.posX`),
      posZ: positionValue(event.posZ, `${eventLabel}.posZ`),
      rotation: signedShortValue(event.rotation, `${eventLabel}.rotation`),
    };
  });
  const spawnTeleportEventEntries =
    presence.spawnTeleportEvents === undefined
      ? []
      : arrayValue(presence.spawnTeleportEvents, `${label}.spawnTeleportEvents`);
  if (spawnTeleportEventEntries.length > 64) {
    throw new ContractError(`${label}.spawnTeleportEvents exceeds 64 entries`);
  }
  let previousSpawnTeleportEventSequence = 0;
  const spawnTeleportEvents = spawnTeleportEventEntries.map((entry, index) => {
    const eventLabel = `${label}.spawnTeleportEvents[${String(index)}]`;
    const event = objectValue(entry, eventLabel);
    const sequence = positiveIntegerValue(event.sequence, `${eventLabel}.sequence`);
    if (sequence <= previousSpawnTeleportEventSequence) {
      throw new ContractError(`${eventLabel}.sequence is not monotonic`);
    }
    previousSpawnTeleportEventSequence = sequence;
    const senderPlayerId = positiveIntegerValue(
      event.senderPlayerId,
      `${eventLabel}.senderPlayerId`,
    );
    const targetPlayerId = positiveIntegerValue(
      event.targetPlayerId,
      `${eventLabel}.targetPlayerId`,
    );
    if (targetPlayerId !== localPlayerId || senderPlayerId === targetPlayerId) {
      throw new ContractError(`${eventLabel} is not a remote teleport of the local player`);
    }
    return {
      sequence,
      senderPlayerId,
      targetPlayerId,
      action: avatarSpawnTeleportActionValue(event.action, `${eventLabel}.action`),
    };
  });
  return {
    streamGeneration: positiveIntegerValue(presence.streamGeneration, `${label}.streamGeneration`),
    villageId: positiveIntegerValue(presence.villageId, `${label}.villageId`),
    localPlayerId,
    revision: positiveIntegerValue(presence.revision, `${label}.revision`),
    avatars,
    petEvents,
    friendEvents,
    raceEvents,
    weatherEvents,
    doorbellEvents,
    keyDoorEvents,
    selfUnlockDoorEvents,
    ...(presence.tabletopDrags === undefined
      ? {}
      : {
          tabletopDrags: arrayValue(presence.tabletopDrags, `${label}.tabletopDrags`).map(
            parseTabletopDrag,
          ),
        }),
    ...(presence.diceRolls === undefined
      ? {}
      : {
          diceRolls: arrayValue(presence.diceRolls, `${label}.diceRolls`).map(parseDiceRoll),
        }),
    ...(presence.cannonEvents === undefined ? {} : { cannonEvents }),
    visitorKickEvents,
    gestureEvents,
    ...(presence.sillyGooseStareEvents === undefined ? {} : { sillyGooseStareEvents }),
    ...(spawnPoints === undefined ? {} : { spawnPoints }),
    spawnTeleportEvents,
    ...(presence.avatarRelocationEvents === undefined ? {} : { avatarRelocationEvents }),
  };
}

function weatherKindValue(value: unknown, label: string): WeatherKind {
  const kind = stringValue(value, label);
  if (kind !== 'sunshine' && kind !== 'rain' && kind !== 'lightning') {
    throw new ContractError(`Unsupported weather kind: ${kind}`);
  }
  return kind;
}

function avatarSitActionValue(value: unknown, label: string): AvatarSitAction {
  const action = stringValue(value, label);
  if (
    action !== 'sit' &&
    action !== 'sit-afk' &&
    action !== 'roman-rings' &&
    action !== 'upside-down-high' &&
    action !== 'upside-down-low'
  ) {
    throw new ContractError(`Unsupported avatar sit action: ${action}`);
  }
  return action;
}

function avatarAwayActionValue(value: unknown, label: string): AvatarAwayAction {
  const action = stringValue(value, label);
  if (action === 'sleep' || action === 'sit-afk') return action;
  throw new ContractError(`Unsupported avatar away action: ${action}`);
}

function avatarGestureActionValue(value: unknown, label: string): AvatarGestureAction {
  const action = stringValue(value, label);
  if (action === 'wave' || action === 'blow-kiss') return action;
  throw new ContractError(`Unsupported avatar gesture action: ${action}`);
}

function avatarSpawnTeleportActionValue(value: unknown, label: string): AvatarSpawnTeleportAction {
  const action = stringValue(value, label);
  if (action === 'normal' || action === 'home' || action === 'friend') return action;
  throw new ContractError(`Unsupported avatar spawn-teleport action: ${action}`);
}

function avatarSpawnPointsValue(value: unknown, label: string): AvatarSpawnPointsPayload {
  const points = objectValue(value, label);
  return {
    normal: avatarSpawnPointValue(points.normal, `${label}.normal`, -221),
    home: avatarSpawnPointValue(points.home, `${label}.home`, 898),
    friend: avatarSpawnPointValue(points.friend, `${label}.friend`, 3018),
  };
}

function avatarSpawnPointValue(
  value: unknown,
  label: string,
  expectedBuildingType: AvatarSpawnPointPayload['buildingType'],
): AvatarSpawnPointPayload | null {
  if (value === null) return null;
  const point = objectValue(value, label);
  const buildingType = signedShortValue(point.buildingType, `${label}.buildingType`);
  if (buildingType !== expectedBuildingType) {
    throw new ContractError(`${label}.buildingType does not match its spawn-point role`);
  }
  return {
    buildingId: nonZeroSignedShortValue(point.buildingId, `${label}.buildingId`),
    buildingType,
    posX: worldPositionValue(point.posX, `${label}.posX`),
    posZ: worldPositionValue(point.posZ, `${label}.posZ`),
  };
}

function avatarEmoteActionValue(value: unknown, label: string): AvatarEmoteAction {
  const action = stringValue(value, label);
  if (/^(dance-[1-5]|wacky-[1-6]|relax-[1-5])$/.test(action)) {
    return action as AvatarEmoteAction;
  }
  throw new ContractError(`Unsupported avatar emote action: ${action}`);
}

function isAvatarPositionSource(value: string): value is AvatarPositionSource {
  return ['unknown', 'server-replay', 'server-notification', 'client-predicted'].includes(value);
}

function avatarNoboTypeValue(value: unknown, label: string): number {
  const type = nonNegativeIntegerValue(value, label);
  if (
    type === 0 ||
    type === 1 ||
    type === 2 ||
    type === 3 ||
    type === 6 ||
    type === 7 ||
    type === 8 ||
    type === 9 ||
    type === 10 ||
    type === 11
  ) {
    return type;
  }
  throw new ContractError(`${label} is not a supported player Nobo type`);
}

function avatarGenderValue(value: unknown, label: string): AvatarGender {
  const gender = stringValue(value, label);
  if (gender === 'man' || gender === 'woman' || gender === 'unknown') return gender;
  throw new ContractError(`${label} must be man, woman, or unknown`);
}

function mountTransitionRejectionOutcomeValue(
  value: unknown,
  label: string,
): MountTransitionRejectionOutcome {
  const outcome = stringValue(value, label);
  if (
    outcome === 'not-home-village' ||
    outcome === 'position-unavailable' ||
    outcome === 'position-changed' ||
    outcome === 'too-far' ||
    outcome === 'drop-blocked' ||
    outcome === 'character-not-found' ||
    outcome === 'character-unavailable' ||
    outcome === 'not-mounted' ||
    outcome === 'already-mounted' ||
    outcome === 'mount-mismatch' ||
    outcome === 'idempotency-conflict' ||
    outcome === 'invalid-request' ||
    outcome === 'server-error'
  ) {
    return outcome;
  }
  throw new ContractError(`Unsupported mount transition rejection outcome: ${outcome}`);
}

export type MovementRecoveryPayload = Readonly<{
  villageId: number;
  sessionGeneration: number;
  acceptedSequence: number;
  authoritativePose: MovementPosePayload;
}>;

function parseMovementRecovery(value: unknown): MovementRecoveryPayload {
  const recovery = objectValue(value, 'error.details.movementRecovery');
  return {
    villageId: positiveIntegerValue(recovery.villageId, 'movementRecovery.villageId'),
    sessionGeneration: positiveIntegerValue(
      recovery.sessionGeneration,
      'movementRecovery.sessionGeneration',
    ),
    acceptedSequence: nonNegativeIntegerValue(
      recovery.acceptedSequence,
      'movementRecovery.acceptedSequence',
    ),
    authoritativePose: parseMovementPose(
      recovery.authoritativePose,
      'movementRecovery.authoritativePose',
    ),
  };
}

export type BuildingBatchErrorDetails = Readonly<{
  currentRevision?: number;
  buildingId?: number;
  placementReason?: string;
}>;

export function parseErrorResponse(value: unknown): {
  buildingBatch?: BuildingBatchErrorDetails;
  code: string;
  message: string;
  creditsAfter?: number;
  blockedUntil?: number;
  blockReason?: string;
  requiredVersion?: string;
  serverMessage?: string;
  mountTransitionReconciliation?: MountTransitionReconciliationPayload;
  marketplaceTradeLimit?: MarketplaceTradeLimitPayload;
  movementRecovery?: MovementRecoveryPayload;
} {
  const root = objectValue(value, 'error response');
  assertProtocol(root);
  const error = objectValue(root.error, 'error');
  const details =
    error.details === undefined ? undefined : objectValue(error.details, 'error.details');
  const blockDetails = error.code === 'account_blocked' ? details : undefined;
  const blockedUntil =
    blockDetails?.blockedUntil === undefined ? error.blockedUntil : blockDetails.blockedUntil;
  const blockReason =
    blockDetails?.blockReason === undefined ? error.blockReason : blockDetails.blockReason;
  return {
    ...(typeof error.code === 'string' &&
    error.code.startsWith('building_batch_') &&
    details !== undefined
      ? {
          buildingBatch: {
            ...(details.currentRevision === undefined
              ? {}
              : {
                  currentRevision: nonNegativeIntegerValue(
                    details.currentRevision,
                    'error.details.currentRevision',
                  ),
                }),
            ...(details.buildingId === undefined
              ? {}
              : {
                  buildingId: rangedIntegerValue(
                    details.buildingId,
                    'error.details.buildingId',
                    -32768,
                    32767,
                  ),
                }),
            ...(details.placementReason === undefined
              ? {}
              : {
                  placementReason: stringValue(
                    details.placementReason,
                    'error.details.placementReason',
                  ),
                }),
          },
        }
      : {}),
    ...(details?.movementRecovery === undefined
      ? {}
      : { movementRecovery: parseMovementRecovery(details.movementRecovery) }),
    code: stringValue(error.code, 'error.code'),
    message: stringValue(error.message, 'error.message'),
    ...(error.creditsAfter === undefined
      ? {}
      : {
          creditsAfter: nonNegativeIntegerValue(error.creditsAfter, 'error.creditsAfter'),
        }),
    ...(blockedUntil === undefined
      ? {}
      : {
          blockedUntil: nonNegativeIntegerValue(blockedUntil, 'error.blockedUntil'),
        }),
    ...(blockReason === undefined
      ? {}
      : { blockReason: stringValue(blockReason, 'error.blockReason') }),
    ...(error.requiredVersion === undefined
      ? {}
      : { requiredVersion: stringValue(error.requiredVersion, 'error.requiredVersion') }),
    ...(error.serverMessage === undefined
      ? {}
      : { serverMessage: stringValue(error.serverMessage, 'error.serverMessage') }),
    ...(error.mountTransitionReconciliation === undefined
      ? {}
      : {
          mountTransitionReconciliation: parseMountTransitionReconciliation(
            error.mountTransitionReconciliation,
            'error.mountTransitionReconciliation',
          ),
        }),
    ...(error.details === undefined ||
    (error.code !== 'trade_price_out_of_range' && error.code !== 'resource_not_tradable')
      ? {}
      : {
          marketplaceTradeLimit: parseMarketplaceTradeLimit(error.details, 'error.details'),
        }),
  };
}

function parseMarketplaceTradeLimit(value: unknown, label: string): MarketplaceTradeLimitPayload {
  const limit = objectValue(value, label);
  return {
    resourceId: rangedIntegerValue(limit.resourceId, `${label}.resourceId`, 1, 32_767),
    referenceValue: nonNegativeIntegerValue(limit.referenceValue, `${label}.referenceValue`),
    minimumUnitPrice: nonNegativeIntegerValue(limit.minimumUnitPrice, `${label}.minimumUnitPrice`),
    maximumUnitPrice: nonNegativeIntegerValue(limit.maximumUnitPrice, `${label}.maximumUnitPrice`),
  };
}

function assertProtocol(root: Readonly<Record<string, unknown>>): void {
  const version = stringValue(root.protocolVersion, 'protocolVersion');
  if (version !== BROWSER_PROTOCOL_VERSION) {
    throw new ContractError(
      `Browser protocol v${version} is incompatible with v${BROWSER_PROTOCOL_VERSION}`,
    );
  }
}

function objectValue(value: unknown, label: string): Readonly<Record<string, unknown>> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new ContractError(`${label} must be an object`);
  }
  return value as Readonly<Record<string, unknown>>;
}

function arrayValue(value: unknown, label: string): readonly unknown[] {
  if (!Array.isArray(value)) {
    throw new ContractError(`${label} must be an array`);
  }
  return value;
}

function stringValue(value: unknown, label: string): string {
  if (typeof value !== 'string') {
    throw new ContractError(`${label} must be text`);
  }
  return value;
}

function legacyLanguageValue(value: unknown, label: string): LegacyLanguage {
  const language = stringValue(value, label);
  if (
    language === 'swedish' ||
    language === 'english' ||
    language === 'danish' ||
    language === 'norwegian'
  ) {
    return language;
  }
  throw new ContractError(`${label} is unsupported`);
}

function loginBackdropAssetValue(value: unknown, label: string): string {
  const path = stringValue(value, label);
  if (
    path !== '/legacy/startup/loginbackdrop.jpg' &&
    path !== '/legacy/startup/loginbackdrop_eng.jpg'
  ) {
    throw new ContractError(`${label} is unsupported`);
  }
  return path;
}

function loginNewsImageAssetValue(value: unknown, label: string): string {
  const path = stringValue(value, label);
  if (
    path === '/legacy/startup/accountbg.png' ||
    /^\/api\/v2\/login-news\/image\?v=[a-f0-9]+(?:&date=\d{4}-\d{2}-\d{2})?$/.test(path)
  ) {
    return path;
  }
  throw new ContractError(`${label} is unsupported`);
}

function accountTermsAssetValue(
  value: unknown,
  label: string,
): AccountCreationPresentationPayload['termsAsset'] {
  const path = stringValue(value, label);
  if (path === '/legacy/startup/EULA.txt' || path === '/legacy/startup/EULAENG.txt') {
    return path;
  }
  throw new ContractError(`${label} is unsupported`);
}

function sourceText(value: unknown, field: string): string {
  return boundedText(value, `news.accountCreation.texts.${field}`, 500, true);
}

function isJavaTrimmed(value: string): boolean {
  return (
    (value.length === 0 || value.charCodeAt(0) > 0x20) &&
    (value.length === 0 || value.charCodeAt(value.length - 1) > 0x20)
  );
}

function boundedText(
  value: unknown,
  label: string,
  maximumLength: number,
  required: boolean,
): string {
  const parsed = stringValue(value, label);
  if (parsed.length > maximumLength || (required && parsed.trim().length === 0)) {
    throw new ContractError(`${label} has an invalid length`);
  }
  return parsed;
}

function communityRulesText(value: unknown, label: string): string {
  const parsed = stringValue(value, label);
  if (Array.from(parsed).length > 20000 || parsed.trim().length === 0) {
    throw new ContractError(`${label} has an invalid length`);
  }
  return parsed;
}

function announcementText(value: unknown, label: string): string {
  const parsed = stringValue(value, label);
  if (Array.from(parsed).length > 500) {
    throw new ContractError(`${label} has an invalid length`);
  }
  return parsed;
}

function integerValue(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value)) {
    throw new ContractError(`${label} must be an integer`);
  }
  return value;
}

function nonNegativeIntegerValue(value: unknown, label: string): number {
  const parsed = integerValue(value, label);
  if (parsed < 0) throw new ContractError(`${label} must be non-negative`);
  return parsed;
}

function nullableNonNegativeInteger(value: unknown, label: string): number | null {
  return value === null ? null : nonNegativeIntegerValue(value, label);
}

function positiveIntegerValue(value: unknown, label: string): number {
  const parsed = integerValue(value, label);
  if (parsed <= 0) throw new ContractError(`${label} must be positive`);
  return parsed;
}

function nonZeroSafeIntegerValue(value: unknown, label: string): number {
  const parsed = integerValue(value, label);
  if (parsed === 0) throw new ContractError(`${label} must not be zero`);
  return parsed;
}

function rangedIntegerValue(
  value: unknown,
  label: string,
  minimum: number,
  maximum: number,
): number {
  const parsed = integerValue(value, label);
  if (parsed < minimum || parsed > maximum) {
    throw new ContractError(`${label} must be between ${String(minimum)} and ${String(maximum)}`);
  }
  return parsed;
}

function signedShortValue(value: unknown, label: string): number {
  const parsed = integerValue(value, label);
  if (parsed < -32_768 || parsed > 32_767) {
    throw new ContractError(`${label} must fit a signed 16-bit integer`);
  }
  return parsed;
}

function nonZeroSignedShortValue(value: unknown, label: string): number {
  const parsed = signedShortValue(value, label);
  if (parsed === 0) throw new ContractError(`${label} must not be zero`);
  return parsed;
}

function doorbellBuildingTypeValue(value: unknown, label: string): 472 | 473 | 475 | 476 {
  const parsed = integerValue(value, label);
  if (parsed !== 472 && parsed !== 473 && parsed !== 475 && parsed !== 476) {
    throw new ContractError(`${label} is not a shipped doorbell`);
  }
  return parsed;
}

function petEventIdValue(value: unknown, label: string): 40 | 41 | 42 {
  const parsed = integerValue(value, label);
  if (parsed !== 40 && parsed !== 41 && parsed !== 42) {
    throw new ContractError(`${label} must be 40, 41, or 42`);
  }
  return parsed;
}

function numberValue(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new ContractError(`${label} must be a finite number`);
  }
  return value;
}

function appearanceAdjustment(value: unknown, label: string): number {
  const parsed = numberValue(value, label);
  if (Math.abs(parsed) > 10) throw new ContractError(`${label} exceeds the legacy safety bound`);
  return parsed;
}

function parseAdminTruckUnderglow(
  value: unknown,
  label = 'adminTruckUnderglow',
): AdminTruckUnderglowSettings {
  const settings = objectValue(value, label);
  const color = stringValue(settings.color, `${label}.color`);
  if (!/^#[0-9a-f]{6}$/i.test(color))
    throw new ContractError(`${label}.color must be a six-digit hex color`);
  return {
    enabled: booleanValue(settings.enabled, `${label}.enabled`),
    color: color.toLowerCase(),
  };
}

function booleanValue(value: unknown, label: string): boolean {
  if (typeof value !== 'boolean') throw new ContractError(`${label} must be boolean`);
  return value;
}

export class ContractError extends Error {
  override readonly name = 'ContractError';
}

export function parseGateOfferingResources(value: unknown): readonly ProductionResourcePayload[] {
  const root = objectValue(value, 'gate offering resources');
  assertProtocol(root);
  return arrayValue(root.resources, 'resources').map((r) =>
    productionResource(objectValue(r, 'resource'), 'resource'),
  );
}
export function parseGateOfferingRevision(value: unknown): number {
  const root = objectValue(value, 'gate offering configuration');
  assertProtocol(root);
  return nonNegativeIntegerValue(root.entityRevision, 'entityRevision');
}

function parseOptionalCustomVehicle(
  value: unknown,
  label: string,
): { customVehicle?: CustomVehiclePayload } {
  if (value === undefined || value === null) return {};
  const vehicle = objectValue(value, label);
  const parts = arrayValue(vehicle.parts, `${label}.parts`);
  if (parts.length < 5 || parts.length > 16)
    throw new ContractError(`${label} requires 5–16 parts`);
  return {
    customVehicle: {
      seatX: worldPositionValue(vehicle.seatX, `${label}.seatX`),
      seatZ: worldPositionValue(vehicle.seatZ, `${label}.seatZ`),
      rotation: rangedIntegerValue(vehicle.rotation, `${label}.rotation`, 0, 3),
      parts: parts.map((value, index) => {
        const partLabel = `${label}.parts[${String(index)}]`;
        const part = objectValue(value, partLabel);
        return {
          type: nonZeroSignedShortValue(part.type, `${partLabel}.type`),
          tileX: signedShortValue(part.tileX, `${partLabel}.tileX`),
          tileZ: signedShortValue(part.tileZ, `${partLabel}.tileZ`),
          rotation: rangedIntegerValue(part.rotation, `${partLabel}.rotation`, 0, 3),
        };
      }),
    },
  };
}

export function parseCustomVehiclePickupSubmissionResponse(
  value: unknown,
): CustomVehiclePickupSubmissionPayload {
  const root = objectValue(value, 'custom vehicle pickup response');
  assertProtocol(root);
  const submission = objectValue(
    root.customVehiclePickupSubmission,
    'customVehiclePickupSubmission',
  );
  if (submission.status !== 'picked-up')
    throw new ContractError('Unsupported custom vehicle pickup status');
  return {
    status: 'picked-up',
    sequence: positiveIntegerValue(submission.sequence, 'sequence'),
    characterId: positiveIntegerValue(submission.characterId, 'characterId'),
    buildingCount: rangedIntegerValue(submission.buildingCount, 'buildingCount', 5, 32),
    characters: parseCharactersPayload(
      submission.characters,
      'customVehiclePickupSubmission.characters',
    ),
    presence: parsePresencePayload(submission.presence, 'customVehiclePickupSubmission.presence'),
  };
}

function worldPositionValue(value: unknown, label: string): number {
  const limit = (CREATIVE_WORLD.sampleWidth - 1) * 250;
  return rangedIntegerValue(value, label, -limit, limit);
}

function parseCreativeSpatial(value: unknown): CreativeSpatialPayload {
  const source = objectValue(value, 'creativeSpatial');
  const bits = (key: string): string => {
    const text = stringValue(source[key], `creativeSpatial.${key}`);
    let decoded: string;
    try {
      decoded = atob(text);
    } catch {
      throw new ContractError('Invalid Creative spatial encoding');
    }
    if (decoded.length !== 128) throw new ContractError('Invalid Creative spatial dimensions');
    return text;
  };
  return {
    treeOccupiedCells: bits('treeOccupiedCells'),
    regularBlockedCells: bits('regularBlockedCells'),
    vipBlockedCells: bits('vipBlockedCells'),
    occupiedCells: bits('occupiedCells'),
    buildingOccupiedCells: bits('buildingOccupiedCells'),
    creativeBlockedCells: bits('creativeBlockedCells'),
    noboHouses: arrayValue(source.noboHouses, 'creativeSpatial.noboHouses').map((v) => {
      const h = objectValue(v, 'house');
      return {
        buildingId: integerValue(h.buildingId, 'buildingId'),
        tileX: integerValue(h.tileX, 'tileX'),
        tileZ: integerValue(h.tileZ, 'tileZ'),
        tileWidth: integerValue(h.tileWidth, 'tileWidth'),
        tileDepth: integerValue(h.tileDepth, 'tileDepth'),
      };
    }),
  };
}

function parseTerrainChunk(value: unknown): TerrainChunkPayload {
  const p = objectValue(value, 'terrainChunk');
  const width = rangedIntegerValue(p.width, 'terrainChunk.width', 1, 29);
  const depth = rangedIntegerValue(p.depth, 'terrainChunk.depth', 1, 29);
  const sampleX = rangedIntegerValue(
    p.sampleX,
    'terrainChunk.sampleX',
    0,
    CREATIVE_WORLD.sampleWidth - width,
  );
  const sampleZ = rangedIntegerValue(
    p.sampleZ,
    'terrainChunk.sampleZ',
    0,
    CREATIVE_WORLD.sampleWidth - depth,
  );
  const heightSamples = stringValue(p.heightSamples, 'terrainChunk.heightSamples');
  if (atob(heightSamples).length !== width * depth)
    throw new ContractError('Invalid terrain chunk dimensions');
  return {
    revision: positiveIntegerValue(p.revision, 'terrainChunk.revision'),
    sampleX,
    sampleZ,
    width,
    depth,
    heightSamples,
  };
}
